<template>
	<div class="w-100" :class="{ 'h-100': !isMobileLayout }">
		<!-- LSP-16462: the stacked (smAndDown) layout must NOT pin itself to the viewport height.
		     `h-100` resolves against a `min-height: 100vh` ancestor in WebKit, so on iPad Safari the
		     card became exactly one viewport tall while holding ~2.8 viewports of media — and because
		     Vuetify's `.v-card` sets `overflow: hidden`, everything past the first screen had NO
		     user-scrollable ancestor at all: the media grid simply could not be scrolled to its
		     bottom. Chromium resolves the same `h-100` to `auto`, lets the document scroll, and shows
		     the whole grid, which is why desktop and CI never saw it. Leaving the height unpinned in
		     the stacked layout gives both engines the same document scroll — and it matches
		     Timeline.vue's own infinite-scroll observer, which uses `root: null` (the viewport) for
		     conversation/project. The desktop two-pane layout keeps `h-100`: there the row stretches
		     its columns on the cross axis, so the inner `v-window` scrolls as intended. -->
		<v-card v-test="'entity-detail-card'" class="d-flex flex-column" :class="{ 'h-100': !isMobileLayout }" rounded="0" :elevation="isMobileLayout ? 0 : 2">
			<v-alert v-if="!isOnline" type="info" class="ma-3 flex-0-0" data-test-id="offline-error">
				{{ offlineMessage() }}
			</v-alert>
			<v-alert v-if="isOnline && entityType === 'project' && projectVerdictErrorVisible" type="warning" class="ma-3 flex-0-0" data-test-id="project-verdict-error">
				{{ projectAccessFailureMessage() }}
				<v-btn variant="text" @click="retryDetailLoad">Retry</v-btn>
			</v-alert>
			<v-alert v-if="isOnline && detailLoad.recoveryStage.value" type="warning" class="ma-3 flex-0-0" data-test-id="project-recovery-error">
				{{ couldNot(['auth', 'account', 'trial'].includes(detailLoad.recoveryStage.value ?? '') ? 'check your account' : detailLoad.recoveryStage.value === 'project' ? (entityType === 'project' ? 'refresh the project' : 'load this page') : 'refresh the timeline') }}
				<v-btn variant="text" @click="retryDetailLoad">Retry</v-btn>
			</v-alert>
			<v-alert v-if="isOnline && detailLoad.phase.value === 'error' && !verdictUnavailable" type="warning" class="ma-3 flex-0-0" data-test-id="detail-load-error">
				{{ couldNot('load this page') }}
				<v-btn variant="text" @click="retryDetailLoad">Retry</v-btn>
			</v-alert>
			<!-- Anonymous mobile projects already show their identity in mobile-info below. -->
			<div v-if="entityType === 'project' && verdictStatus === 'loading'" data-test-id="project-permissions-loading" role="status" aria-label="Loading project permissions" class="px-4 py-2">
				<v-skeleton-loader type="button" />
			</div>
			<EntityHeader
				v-if="!!entity && !!entityType && !(isMobileLayout && !isAuthenticated && entityType === 'project')"
				:entity-id="entity?.id"
				:entity-type="entityType"
				:entity="entity"
				:me="me"
				:name="entity?.name ?? ''"
				:number="entityNumber"
				:created-at="entity?.createdAt ?? new Date()"
				:is-view-only="viewOnly"
				:permissions-loading="entityType === 'project' && verdictStatus === 'loading'"
				:permissions-unavailable="entityType === 'project' && verdictStatus === 'unavailable'"
				:free-trial-ended="freeTrialEnded"
				:authenticated="isAuthenticated"
				:access-type="AccessType.Editor"
				:saving="isSaving"
				:save-state="saveState"
				:signing-up="false"
				:on-back="handleBack"
				:project-name="projectName"
				:has-scribe-feature="hasScribeFeature"
				:is-external-editor="isExternalEditor"
				:can-share-project="!viewOnly && projectVerdict?.hasAccess === true && projectVerdict?.can.manageAccess === true"
				:can-delete-project="!viewOnly && projectVerdict?.hasAccess === true && projectVerdict?.can.delete === true"
				:delivery="showDelivery ? delivery : null"
				:delivery-in-card="isScheduled"
				:can-repair-delivery="canRepairDelivery"
				@toggleComments="toggleComments"
				@reset="runReset"
				@save="runSave"
				@refreshProject="refreshProject"
				@refreshProjectPermissions="refreshProjectPermissions"
				@conversationCreated="conversationCreated"
				@uploadCounts="handleUploadCounts"
				@navigate-to-project="navigateToProject"
				@runReport="handleRunReport"
				@invitationSent="refetchDeliveryAfterSend"
				@deliveryResent="refetchDeliveryAfterSend"
				@deliveryRefresh="loadEntity()"
				@deliveryCustomerUpdated="mergeDeliveryCustomer" />
			<!-- LSP-17773: guest "Ask to edit" — a view-only visitor can request edit access from the owner. -->
			<div
				v-if="canRequestEditAccess && entity"
				class="d-flex align-center justify-space-between ga-3 px-4 py-2 request-access-bar"
				v-test="'request-access-bar'">
				<!-- Sparky nudge on the left; the edit prompt sits next to its "Ask to edit" button on the right. -->
				<span class="text-body-2 text-grey-darken-2">Ask for edit access and unlock Sparky's AI report.</span>
				<div class="d-flex align-center ga-3">
					<span class="text-body-2 text-grey-darken-2">You're viewing this project — want to help edit it?</span>
					<v-btn
						v-test="'request-access-open'"
						color="primary"
						variant="outlined"
						rounded="xl"
						size="small"
						@click="openRequestAccessDialog">
						Ask to edit
					</v-btn>
				</div>
			</div>
			<RequestAccessDialog
				v-if="canRequestEditAccess && entity"
				ref="requestAccessDialogRef"
				:project-id="entity.id"
				:share-id="(entity as Project)?.shareId || sharingId || ''"
				:prefill-email="me?.user?.email || ''" />
			<v-row
				no-gutters
				class="w-100"
				:class="{ 'flex-column': isMobileLayout, 'h-100': !isMobileLayout }"
				:style="{ marginTop: display.mdAndUp.value && !isMobileLayout ? '2px' : '0px' }">
				<v-col
					v-if="entity"
					cols="12"
					sm="12"
					:md="isMobileLayout ? 12 : 7"
					:style="isMobileLayout ? { flex: '0 0 auto', minWidth: '0' } : {}">
					<div v-if="isMobileLayout" class="mobile-info px-4 py-2">
						<div class="d-flex" :class="{ 'justify-space-between': entityType === 'conversation' }">
							<span v-if="entityType === 'conversation'" class="d-flex align-center">
								<v-icon class="hashtag">icon-hashtag</v-icon>
								<span class="font-weight-bold"> {{ entityNumber }} </span>
							</span>
							<v-tooltip v-else :open-on-hover="false" open-on-click location="bottom start">
								<template #activator="{ props }">
									<div
										v-bind="props"
										class="font-weight-bold mt-1 mb-1 text-truncate flex"
										style="flex: 1 1 auto; min-width: 0">
										{{ entity.name }}
									</div>
								</template>
								<span>{{ entity.name }}</span>
							</v-tooltip>
							<EntityStatus
								:status="entity.status"
								:entity-type="entityType ?? 'conversation'"
								:initiator="entity?.initiator ?? Initiator.RECORDING"
								:appointment-name="entity?.appointmentName ?? ''"
								:source-type="entity?.sourceType ?? ''" />
						</div>
						<div v-if="entityType === 'conversation'" class="font-weight-bold mt-1 mb-1">
							{{ entity.name }}
						</div>
						<!-- LSP-20787: below md EntityHeader's delivery row is hidden, so the chip, its actions and its box sit
						     here — unless the scheduled-call card carries the delivery. -->
						<div v-if="showDelivery && !isScheduled && delivery" class="d-flex flex-wrap align-center ga-2 mt-1 mb-1">
							<SmsDeliveryChip :state="delivery.state" :since="delivery.since" size="detail" />
							<SmsDeliveryActions
								v-if="canRepairDelivery && entityId"
								:conversation-id="entityId"
								:state="delivery.state"
								:email-invitations-allowed="emailInvitationsAllowed"
								@select="deliveryMode = $event"
								@resent="refetchDeliveryAfterSend" />
							<!-- :key remounts the box on a switch between the two, so typed text never carries over (the banner's rule). -->
							<SmsDeliveryRepairPanel
								v-if="deliveryMode && canRepairDelivery && entityId"
								:key="deliveryMode"
								class="w-100"
								:conversation-id="entityId"
								:mode="deliveryMode"
								:customer="customers[0] ?? null"
								:participants="(entity as Conversation).Participants"
								@resent="refetchDeliveryAfterSend"
								@customerUpdated="mergeDeliveryCustomer"
								@refresh="loadEntity()"
								@close="deliveryMode = null" />
						</div>
						<div class="icon-detail-field">
							<v-icon> icon-clock-stroke </v-icon>
							<span> {{ formatDateOnlyShort(entity.createdAt) }} {{ formatTimeOnly(entity.createdAt) }} </span>
						</div>
						<div class="icon-detail-field">
							<v-icon> icon-users-3</v-icon>
							<span>
								{{ entity.User?.name }}
							</span>
						</div>
						<div v-if="customers.length > 0" class="icon-detail-field">
							<v-icon>icon-contacts-stroke</v-icon>
							<span>
								<template v-for="(customer, index) in customers" :key="customer.id">
									<!-- LSP-19649: the pointer is gated on !freeTrialEnded because navigateToCustomer
									     returns early in that state, and a pointer for a click that does nothing is the
									     exact complaint this ticket is about. -->
									<span
										class="text-decoration-underline"
										:class="{ 'cursor-pointer': !freeTrialEnded }"
										@click="navigateToCustomer(customer.id)">
										{{ getCustomerDisplayName(customer) }}
									</span>
									<span v-if="index < customers.length - 1">, </span>
								</template>
							</span>
						</div>
						<div
							v-if="entityType === 'conversation' && (projectName || canAssignConversationProject)"
							class="icon-detail-field relative">
							<!-- TODO: Not a great work around but we don't have the icon in the font style sheet  -->
							<img :src="LsContactProjectSvg" width="20" alt="Project" class="relative" style="left: -3px" />
							<template v-if="projectName">
								<button
									data-test-id="mobile-session-project-link"
									class="project-meta-action text-decoration-underline cursor-pointer"
									type="button"
									@click="navigateToProject">
									Project {{ projectName }}
								</button>
								<EntityStatus
									v-if="currentConversationProject?.status"
									:status="currentConversationProject.status"
									entity-type="project"
									:initiator="Initiator.RECORDING"
									appointment-name=""
									source-type="" />
								<button
									v-if="canAssignConversationProject"
									data-test-id="mobile-session-project-change"
									class="project-meta-action text-decoration-underline cursor-pointer"
									type="button"
									@click="projectAssignmentDialogVisible = true">
									change
								</button>
							</template>
							<button
								v-else
								data-test-id="mobile-session-project-add"
								class="project-meta-action text-decoration-underline cursor-pointer"
								type="button"
								@click="projectAssignmentDialogVisible = true">
								Add session to a project
							</button>
						</div>
						<div class="icon-detail-field">
							<v-icon> icon-shop-tag-stroke</v-icon>
							<SingleLineDisplay v-if="entity.Labels?.length > 0" v-model="entity.Labels" :small="true" :max-visible="3" />
							<span v-else>-</span>
						</div>

						<SessionIncompleteUploadNotice
							v-if="isAuthenticated && showIncompleteUploadBanner"
							:conversation-id="entityId"
							:has-customer="uploadReminderReachesCustomer"
							:pending-kind="incompleteUploadBannerKind"
							:can-send-reminder="canOfferUploadReminder"
							:notice-message="incompleteUploadNoticeMessage" />
						<MediaShortfallNotice
							v-if="isAuthenticated"
							:media-shortfall="publishedMediaShortfall"
							:completeness-unverified="entityType === 'conversation' && uploadStatus?.hasUnverifiedRecording === true" />
						<div v-if="isAuthenticated" class="d-flex flex-column mt-2 pr-2">
							<SearchInput
								v-model="currentSearchTerm"
								:search-callback="searchTimeline"
								:show-filters="true"
								placeholder="Search in Media"
								@toggleFilters="toggleFilters" />

							<Filters
								v-if="filtersVisible"
								v-model="searchRequest"
								:parent-type="'conversation'"
								:hidden-filters="hiddenFilters"
								@update:modelValue="onSearchRequestChange" />
						</div>
					</div>
					<!-- LSP-16658: `:key` is the whole design. This child owns the reschedule dialog and its
					     state, and keying it by conversation means leaving the call DESTROYS that state
					     rather than leaving the page to remember to clear it — which it got wrong four
					     times. -->
					<ScheduledCallActions
						v-if="isScheduled && entityType === 'conversation' && entityId"
						:key="entityId"
						ref="scheduledCallActionsRef"
						:conversation-id="entityId"
						:appointment="appointmentForConfirmation"
						:conversation="conversationForConfirmation"
						:customer-name="customerName ?? ''"
						:customer-phone="customerPhone"
						:contact-first-name="contactFirstName"
						:time-zone="timeZone"
						:date-format="organizationDateFormat"
						:message-template="scheduledCallMessageTemplate"
						:message-time-zone="scheduledCallMessageTimeZone"
						:team-member-name="scheduledCallTeamMemberName"
						:organization-name="organizationName"
						:disabled="freeTrialEnded"
						:is-about-form-dirty="isDirty"
						:delivery="showDelivery ? delivery : null"
						:can-repair-delivery="canRepairDelivery"
						:email-invitations-allowed="emailInvitationsAllowed"
						@cancel-call="cancelScheduledCall"
						@rescheduled="onConversationRescheduled"
						@delivery-resent="refetchDeliveryAfterSend"
						@delivery-refresh="loadEntity()"
						@delivery-customer-updated="mergeDeliveryCustomer" />
					<div v-if="isMobileLayout && !isAuthenticated" data-test-id="mobile-unauthenticated-signup">
						<SignUpPrompt :project-id="entityId" :share-id="shareId" />
					</div>
					<div v-if="!isMobileLayout" class="px-4">
						<SessionIncompleteUploadNotice
							v-if="isAuthenticated && showIncompleteUploadBanner"
							:conversation-id="entityId"
							:has-customer="uploadReminderReachesCustomer"
							:pending-kind="incompleteUploadBannerKind"
							:can-send-reminder="canOfferUploadReminder"
							:notice-message="incompleteUploadNoticeMessage" />
						<MediaShortfallNotice
							v-if="isAuthenticated"
							:media-shortfall="publishedMediaShortfall"
							:completeness-unverified="entityType === 'conversation' && uploadStatus?.hasUnverifiedRecording === true" />
						<div v-if="isAuthenticated && !!searchRequest" class="d-flex flex-column mt-2 pr-2">
							<SearchInput
								v-model="currentSearchTerm"
								:search-callback="searchTimeline"
								:show-filters="true"
								placeholder="Search in Media" />

							<Filters
								v-model="searchRequest"
								:parent-type="'conversation'"
								:hidden-filters="hiddenFilters"
								@update:modelValue="onSearchRequestChange" />
						</div>

						<v-alert
							v-if="isDebugMode && uploadStatus && !uploadStatus.isComplete"
							type="info"
							variant="tonal"
							density="compact"
							class="mt-2 mr-2">
							Upload status:
							{{ uploadStatus.pendingFiles }} file(s), {{ uploadStatus.pendingSnapshots }} photo(s),
							{{ uploadStatus.pendingRecordings }} recording(s) pending
						</v-alert>
						<v-alert
							v-else-if="isDebugMode && uploadStatus && uploadStatus.isComplete"
							type="success"
							variant="tonal"
							density="compact"
							class="mt-2 mr-2">
							All media uploaded
						</v-alert>

						<AsyncReminderEmptyState
							v-if="showAsyncReminderPrompt"
							:conversation="entity as Conversation"
							:free-trial-ended="freeTrialEnded"
							:me="me"
							class="pr-2 pt-2"
							@invitationSent="refetchDeliveryAfterSend" />
						<!-- LSP-15573: NOT v-else-if. A session can genuinely hold both facts at once — an upload
						     that died AND a customer still worth nudging — and the reminder pre-empting the
						     Timeline meant only one of them could ever be shown. The Timeline therefore also
						     mounts whenever there is a failed upload to report, so the reminder renders as a
						     prompt above it instead of replacing it. See showFailedUploadAlongsideReminder. -->
						<!-- LSP-16659: the drag-and-drop upload target moved here from the retired Documents tab. -->
						<div
							v-if="!!mediaResponse && (!showAsyncReminderPrompt || showFailedUploadAlongsideReminder)"
							class="position-relative"
							data-test-id="media-grid-drop-zone"
							style="flex: 1; min-height: 0"
							@dragenter="mediaGridDropTarget.onDragenter"
							@dragover="mediaGridDropTarget.onDragover"
							@dragleave="mediaGridDropTarget.onDragleave"
							@drop="mediaGridDropTarget.onDrop">
							<Timeline
								ref="timeline"
								class="pr-2 pt-2"
								:loading="loading"
								:media-items="mediaResponse?.documents"
								:count="mediaResponse?.count"
								:search-term="timelineResultsSearchTerm"
								:filters-applied="timelineResultsFiltersApplied"
								heading="Media & Documents"
								:heading-suffix="timelineHeadingSuffix"
								:parent-type="entityType"
								:parent-id="entityId"
								:parent-name="entity?.name ?? ''"
								:is-view-only="viewOnly"
								:has-free-trial-ended="freeTrialEnded"
								:is-authenticated="isAuthenticated"
								:pending-images="pendingImages"
								:pending-docs="pendingDocs"
								:pending-videos="pendingVideos"
								:pending-media-count="displayedPendingMediaCount"
								:pending-recording-count="pendingRecordingCount"
								:failed-video-upload-count="failedVideoUploadCount"
								:has-scribe-feature="hasScribeFeature"
								@page="pageTimeline"
								@item-click="openLightBox"
								@document-click="openTimelineDocument"
								@on-selected-items="onSelectedMediaItems"
								@on-deleted-items="onDeletedMediaItems"
								@update:media-items="mediaResponse.documents = $event"
								@reload="reloadTimeline"
								@create-sheet="handleCreateSheetFromTimeline"
								@run-sparky-report="handleRunReport"
								@selection-cancelled="clearChecklistMediaSelection" />
							<MediaGridDropOverlay :visible="mediaGridDropTarget.isDraggingFiles.value" />
						</div>
					</div>
				</v-col>

				<v-col
					v-if="entity"
					cols="12"
					sm="12"
					:md="isMobileLayout ? 12 : 5"
					:style="isMobileLayout ? { flex: '1 1 auto', minWidth: '0' } : {}"
					:class="{ 'md-divider-left': display.mdAndUp.value && !isMobileLayout }"
					class="d-flex flex-column flex-nowrap">
					<div class="tabs-container-wrapper">
						<v-tabs
							v-model="activeTab"
							class="flex-shrink-0 entity-tabs"
							height="48"
							bg-color="#f3f5f9"
							slider-color="black"
							:grow="true"
							mandatory="force">
							<v-tab v-for="tab in tabs" :id="tab.id" :key="tab.id" :ripple="false" class="entity-tab"> {{ tab.name }}</v-tab>
						</v-tabs>
						<div
							v-if="tabsScrollable && !tabsAtStart"
							class="tab-scroll-indicator tab-scroll-left"
							:class="{ 'mobile-only': display.smAndDown.value }"
							role="button"
							tabindex="0"
							aria-label="Scroll tabs left"
							@click="scrollTabs(-1)"
							@keydown.enter.prevent="scrollTabs(-1)"
							@keydown.space.prevent="scrollTabs(-1)">
							<v-icon size="small">icon-chevron-left</v-icon>
						</div>
						<div
							v-if="tabsScrollable && !tabsAtEnd"
							class="tab-scroll-indicator tab-scroll-right"
							:class="{ 'mobile-only': display.smAndDown.value }"
							role="button"
							tabindex="0"
							aria-label="Scroll tabs right"
							@click="scrollTabs(1)"
							@keydown.enter.prevent="scrollTabs(1)"
							@keydown.space.prevent="scrollTabs(1)">
							<v-icon size="small">icon-chevron-right</v-icon>
						</div>
					</div>
					<v-window
						v-if="entity && entityType"
						v-model="activeTab"
						:touch="false"
						class="pa-4"
						style="flex: 1; min-height: 0; overflow-y: auto">
						<v-window-item v-if="tabs.some((tab) => tab.id === tabIds.media)">
							<AsyncReminderEmptyState
								v-if="showAsyncReminderPrompt"
								:conversation="entity as Conversation"
								:free-trial-ended="freeTrialEnded"
								:me="me"
								@invitationSent="refetchDeliveryAfterSend" />
							<!-- LSP-15573: NOT v-else-if — see the desktop block above. -->
							<div
								v-if="!!mediaResponse && (!showAsyncReminderPrompt || showFailedUploadAlongsideReminder)"
								class="position-relative"
								data-test-id="media-grid-drop-zone"
								style="flex: 1; min-height: 0"
								@dragenter="mediaGridDropTarget.onDragenter"
								@dragover="mediaGridDropTarget.onDragover"
								@dragleave="mediaGridDropTarget.onDragleave"
								@drop="mediaGridDropTarget.onDrop">
								<Timeline
									ref="timeline"
									:loading="loading"
									:media-items="mediaResponse?.documents"
									:count="mediaResponse?.count"
									:search-term="timelineResultsSearchTerm"
									:filters-applied="timelineResultsFiltersApplied"
									heading="Media & Documents"
									:heading-suffix="timelineHeadingSuffix"
									:parent-type="entityType"
									:parent-id="entityId"
									:parent-name="entity?.name ?? ''"
									:is-view-only="viewOnly"
									:has-free-trial-ended="freeTrialEnded"
									:is-authenticated="isAuthenticated"
									:pending-images="pendingImages"
									:pending-docs="pendingDocs"
									:pending-videos="pendingVideos"
									:pending-media-count="displayedPendingMediaCount"
									:pending-recording-count="pendingRecordingCount"
									:failed-video-upload-count="failedVideoUploadCount"
									:has-scribe-feature="hasScribeFeature"
									@page="pageTimeline"
									@item-click="openLightBox"
									@document-click="openTimelineDocument"
									@on-selected-items="onSelectedMediaItems"
									@on-deleted-items="onDeletedMediaItems"
									@update:media-items="mediaResponse.documents = $event"
									@reload="reloadTimeline"
									@create-sheet="handleCreateSheetFromTimeline"
									@run-sparky-report="handleRunReport"
									@selection-cancelled="clearChecklistMediaSelection" />
								<MediaGridDropOverlay :visible="mediaGridDropTarget.isDraggingFiles.value" />
							</div>
						</v-window-item>
						<v-window-item
							v-if="tabs.some((tab) => tab.id === tabIds.about)"
							:data-id="tabs.find((tab) => tab.id === tabIds.about)?.id">
							<SignUpPrompt
								v-if="!isAuthenticated"
								:project-id="entityId"
								:share-id="shareId"
								style="flex: 1; min-height: 0" />
							<EntityAbout
								v-else
								ref="entityAboutRef"
								v-model:is-dirty="isDirty"
								:entity="entity"
								:entity-type="entityType"
								:is-view-only="viewOnly"
								:is-admin="isAdmin"
								:current-user-id="me?.user?.id"
								:has-free-trial-ended="freeTrialEnded"
								:is-same-organization="isSameOrganization"
								:permissions-loading="entityType === 'project' && verdictStatus === 'loading'"
								:is-external-editor="isExternalEditor"
								:is-scheduled="isScheduled"
								:is-mobile="isMobileLayout"
								@hide-lucky="hideLucky"
								@show-lucky="showLucky"
								@update:isDirty="(v) => (isDirty = v)"
								@update:entity="(e) => (entity = e)"
								@update:saveState="(state) => (saveState = state)"
								@reassigned-away="handleReassignedAway" />
						</v-window-item>
						<v-window-item
							v-if="tabs.some((tab) => tab.id === tabIds['sparkys-reports'])"
							:data-id="tabs.find((tab) => tab.id === tabIds['sparkys-reports'])?.id">
							<EntityReports
								ref="entityReportsRef"
								:entity="entity"
								:entity-type="entityType"
								:is-view-only="viewOnly"
								:sub-entity-id="subEntityId"
								:additional-entities="reportConversations"
								:is-authenticated="isAuthenticated"
								:has-free-trial-ended="freeTrialEnded"
								:is-same-organization="isSameOrganization"
								:permissions-loading="entityType === 'project' && verdictStatus === 'loading'"
								:has-share-access="accessOrigin === 'share'"
								@create="handleRunReport" />
						</v-window-item>
						<v-window-item
							v-if="tabs.some((tab) => tab.id === tabIds.checklists)"
							:data-id="tabs.find((tab) => tab.id === tabIds.checklists)?.id">
							<ProjectChecklists
								ref="entityChecklistsRef"
								v-model:checklist-menu="isChecklistMenuOpen"
								:project-id="entityId"
								:can-add-templates="isAdmin && !isExternalEditor"
								:is-external-editor="isExternalEditor"
								:external-access-resolved="projectAccessResolved"
								:is-view-only="viewOnly"
								:is-authenticated="isAuthenticated"
								:has-free-trial-ended="freeTrialEnded"
								:media-count="photoAndVideoCount"
								:me="me"
								:project="entity"
								:share-id="shareId"
								@conversation-created="conversationCreated"
								@select-media-from-project="selectMediaFromProject"
								@cancel-select-media-from-project="cancelSelectMediaFromProject"
								@item-click="openLightBox"
								@share="share"
								@load-timeline="loadTimeline(true)"
								@hide-lucky="hideLucky"
								@show-lucky="showLucky" />
						</v-window-item>
						<v-window-item
							v-if="tabs.some((tab) => tab.id === tabIds.sheets)"
							:data-id="tabs.find((tab) => tab.id === tabIds.sheets)?.id">
							<SheetList
								ref="sheetListRef"
								:project-id="entityId"
								:is-admin="isAdmin"
								:is-view-only="viewOnly"
								:is-authenticated="isAuthenticated"
								:has-free-trial-ended="freeTrialEnded"
								:me="me"
								@sheet-created="handleSheetCreated"
								@edit-sheet="handleEditSheet"
								@view-sheet="handleViewSheet" />
						</v-window-item>

						<v-window-item
							v-if="tabs.some((tab) => tab.id === tabIds.comments)"
							:data-id="tabs.find((tab) => tab.id === tabIds.comments)?.id">
							<EntityComments
								v-if="isAuthenticated"
								ref="entityCommentsRef"
								:entity="entity"
								:is-view-only="viewOnly"
								:is-authenticated="isAuthenticated"
								:has-free-trial-ended="freeTrialEnded"
								:entity-type="entityType"
								@show-lucky="showLucky"
								@hide-lucky="hideLucky" />
						</v-window-item>
					</v-window>
				</v-col>
				<v-col v-if="!entity" cols="12">
					<div class="h-100 w-100 d-flex align-center justify-center flex-column ga-2">
						<v-progress-circular indeterminate color="primary" size="100"></v-progress-circular>
						<div>Loading record.</div>
					</div>
				</v-col>
			</v-row>
		</v-card>
		<ScribeFab
			ref="scribeFabRef"
			v-if="hasScribeFeature"
			:id="entityId"
			:is-visible="showScribeFAB"
			:disabled="!hasMedia || !isAuthenticated || (viewOnly && !freeTrialEnded)"
			:media-processing="!hasMedia && hasMediaProcessing"
			:free-trial-ended="freeTrialEnded"
			:type="scribeType"
			:suppress-auto-tooltip="isChecklistMenuOpen"
			:selected-file-ids="selectedFileIds"
			:is-external-editor="isExternalEditor"
			@request-upgrade="openLuckyUpgradeDialog"
			@analysis-created="switchToReportsTab"
			@analysis-starting="switchToReportsTab"
			@dismiss="
				fabManuallyDismissed = true;
				showScribeFAB = false;
			" />

		<v-dialog v-model="lightboxOpen" content-class="no-scroll" fullscreen persistent no-click-animation>
			<MediaLightBox
				ref="mediaLightBox"
				v-model="lightboxMedia"
				v-test="'search-artifacts-lightbox'"
				:me="me"
				:is-view-only="viewOnly"
				:project-id="entityType === 'project' ? entity?.id : undefined"
				:project="entityType === 'project' ? entity : undefined"
				:project-access-denied-handled-globally="entityType === 'project'"
				:share-id="shareId || sharingId"
				:has-free-trial-ended="freeTrialEnded"
				:parent-type="'search'"
				:is-external-editor="isExternalEditor"
				:external-access-resolved="projectAccessResolved"
				:upload-reminder-verdict="lightboxUploadReminderVerdict"
				:upload-status="uploadStatus"
				@share="share"
				@close-lightbox="closeLightBox"
				@artifact-deleted="artifactDeleted"
				@load-recordings="loadTimeline(true)"
				@clip-saved="onClipSaved"
				@annotation-saved="handleAnnotationSaved"
				@date-updated="pendingReloads.timeline = true"
				@rotate="handleRotate"
				@save-tags="handleTags"
				@update-notes="handleUpdateNotes" />
		</v-dialog>

		<!-- LSP-16659: documents open here, never in MediaLightBox — same viewer and props the Documents tab used. -->
		<PDFViewerDialog
			v-if="timelinePdfDocument"
			:visible="!!timelinePdfDocument"
			:pdf-url="timelinePdfDocument.mediaUrl"
			:file-name="getTimelineDocumentName(timelinePdfDocument)"
			:file-data="timelinePdfDocument"
			:is-view-only="viewOnly || !isAuthenticated"
			:is-same-organization="isSameOrganization !== false"
			:has-free-trial-ended="freeTrialEnded"
			@close="closeTimelinePdfDocument" />

		<SheetEditor
			:dialog-visible="sheetEditorVisible"
			:sheet="editingSheet"
			:project-id="entityId"
			:is-view-only="viewOnly"
			@close="handleSheetEditorClose"
			@saved="handleSheetSaved" />

		<SessionProjectAssignmentDialog
			v-if="entityType === 'conversation' && entity"
			v-model="projectAssignmentDialogVisible"
			:conversation="entity as Conversation"
			:current-project="currentConversationProject"
			:is-saving="projectAssignmentSaving"
			@select="assignConversationProject" />

		<!-- LSP-17773: recipient onboarding — "getting around a project" tour, shown once per user
		     to a shared editor on their first landing (see maybeShowWelcomeVideo). -->
		<Teleport to="body">
			<ShareWelcomeOverlay
				v-if="showWelcomeVideo"
				:video-url="welcomeVideoUrl"
				:video-duration="welcomeVideoDuration"
				:project-name="entity?.name"
				@close="dismissWelcomeVideo" />
		</Teleport>
	</div>
</template>

<script setup lang="ts">
	import { isSearchTooLong } from "@/helpers/searchLimit";
	// ─────────────────────────────────────────────────────────────────────────
	// AI/maintainer note — DUAL-FLOW INVARIANT (authenticated + shared-link).
	// This page renders for BOTH authenticated users AND unauthenticated
	// shared-project visitors (the URL carries ?shareId=...). Any API call ADDED
	// or CHANGED here MUST cover both paths: authenticated routes return 401 for
	// share-link visitors, so unauthenticated reads need a shareId-gated public
	// mirror. Reference pattern: fetchUploadStatus() →
	// /projects/:id/public/:shareId/upload-status (LSP-16142). Unauthenticated
	// polling is project-only (no public conversation mirror). Path choice uses
	// `shareId` from the URL query; `api.get(url, true)` drops the auth header
	// only for that public project call.
	// ─────────────────────────────────────────────────────────────────────────
	import EntityHeader from "./common/entity/EntityHeader.vue";
	import RequestAccessDialog from "./RequestAccessDialog.vue";
	import ShareWelcomeOverlay from "./share/ShareWelcomeOverlay.vue";
	import Timeline from "./search/Timeline.vue";
	import AsyncReminderEmptyState from "./AsyncReminderEmptyState.vue";
	import MediaLightBox from "./MediaLightBox.vue";
	import SessionIncompleteUploadNotice from "@/components/SessionIncompleteUploadNotice.vue";
	import MediaShortfallNotice from "@/components/MediaShortfallNotice.vue";
	import { hasDefinitiveUploadItem, isIncompleteRecordingUpload, type UploadStatusItem } from "@/helpers/incompleteRecordingUpload";
	import { clearDismissal as clearIncompleteUploadDismissal } from "@/helpers/incompleteUploadReminderState";
	import { hasConsumedUploadHandoff, markUploadHandoffConsumed, forgetUploadHandoffConsumed } from "@/helpers/uploadHandoffState";
	import EntityAbout from "@/components/common/entity/EntityAbout.vue";
	import { IDLE_SAVE_STATE, type SaveState } from "@/composables/useAutosave";
	import EntityStatus from "@/components/common/entity/EntityStatus.vue";
	import SingleLineDisplay from "@/components/tags/SingleLineDisplay.vue";
	import Filters from "@/components/search/Filters.vue";
	import SearchInput from "@/components/search/SearchInput.vue";
	import SignUpPrompt from "@/components/SignUpPage.vue";
	import ScheduledCallActions from "@/components/ScheduledCallActions.vue";
	import SessionProjectAssignmentDialog from "@/components/SessionProjectAssignmentDialog.vue";
	// LSP-20787
	import SmsDeliveryChip from "@/components/SmsDeliveryChip.vue";
	import SmsDeliveryActions from "@/components/SmsDeliveryActions.vue";
	import SmsDeliveryRepairPanel from "@/components/SmsDeliveryRepairPanel.vue";
	import type { ConversationDelivery } from "@/api/index.d";
	import { isAttentionState, isDeliveryRelevantStatus, type SmsDeliveryRepairCustomer } from "@/helpers/smsDelivery";

	import { Initiator } from "@/enums/Initiator";
	import { TabIds as tabIds } from "@/enums/TabIds";
	import MediaGridDropOverlay from "@/components/search/MediaGridDropOverlay.vue";
	import EntityReports from "@/components/common/entity/EntityReports.vue";
	import EntityComments from "@/components/common/entity/EntityComments.vue";
	import ScribeFab from "@/components/scribe/ScribeFab.vue";
	import { useEntityDetailLoad, type EntityDetailLoadPhase, type EntityDetailRecoveryStage, type EntityDetailStopReason } from "@/composables/useEntityDetailLoad";
	import { useProjectForbiddenFlow } from "@/composables/useProjectForbiddenFlow";
	import { ref, onMounted, onUnmounted, onBeforeUnmount, getCurrentInstance, computed, nextTick, watch, defineAsyncComponent } from "vue";
	import { onBeforeRouteLeave, useRoute, useRouter } from "vue-router";
	import { Project } from "@/types/Project";
	import { AccessType } from "@/enums/AccessType";
	import { shareRowMatchesIdentity } from "@/helpers/projectAccess";
	import type { ProjectAccessVerdict, ProjectVerdictOrigin } from "@/api/index.d";
	import { useProjectVerdictsStore, projectVerdictFailure } from "@/stores/projectVerdicts";
	import { useAuthSessionStore } from "@/stores/authSession";
	import { provisionalOwnerVerdict, type ClientProjectAccessVerdict } from "@/helpers/projectVerdictFallback";
	import { projectVerdictGates } from "@/helpers/projectVerdictGates";
	import type { EntityType } from "@/types/EntityType";
	import { BaseEntityService } from "@/services/BaseEntityService";
	import { Conversation } from "@/types/Conversation";
	import { ConversationService } from "@/services/ConversationService";
	import { ProjectService } from "@/services/ProjectService";
	import { emptyEntitySearchResponse, type EntitySearchResponse } from "@/types/search/EntitySearchResponse";
	import type { IndexableDocument } from "@/types/search/IndexableDocument";
	import { shareArtifact } from "@/helpers/artifacts";
	import { isScrolledPastContent, maxScrollLeft, nextScrollTarget } from "@/helpers/tabStripScroll";
	import { mountTabStripDebugOverlay } from "@/helpers/tabStripDebugOverlay";
	import { countReadyPdfDocuments } from "@/helpers/files";
	import {
		adjustCountsByEntityType,
		decrementCountsByEntityType,
		getTimelineDocumentName,
		hasPhotoOrVideoMedia,
		isLightboxMediaRow,
		isPdfTimelineDocument,
		isTimelineDocument,
		mergeLightboxMediaWrite
	} from "@/helpers/timelineDocuments";
	import { useMediaGridDropUpload } from "@/composables/useMediaGridDropUpload";
	import timezones from "@/helpers/timezones";
	import api from "@/api";
	import { SearchRequest } from "@/types/search/SearchRequest";
	import { FilterType } from "@/types/search/FilterType";
	import { ArtifactType } from "@/enums/ArtifactType";
	import UnauthenticatedError from "@/types/errors/UnauthenticatedError";
	import { calculateTimeout, getMessageFromError, isProjectAccessDeniedError } from "@/helpers/utilities";
	import {
		mergeTimelineDelta,
		getNewestCreated,
		deltaStartDate,
		userFiltersActive,
		timelineDocumentsChanged,
		userFiltersNarrowResults,
		preserveUnchangedDocuments,
		reconcileTimelinePages,
		mergeTimelineHead
	} from "@/helpers/timelineDelta";
	import { createTimelineReloadScheduler, type ReloadSurfaces, type ReloadMode } from "@/helpers/timelineReloadScheduler";
	import { logError, logInfo, logWarn } from "@/helpers/logError";
	import { getApiErrorStatus, isEntityGoneError } from "@/helpers/apiErrorShape";
	import { isTransientAuthTokenError } from "@/types/errors/TransientAuthTokenError";

	/**
	 * LSP-18854: is this failure the user being OFFLINE, specifically?
	 *
	 * Deliberately NOT `isNetworkShapedBootAccountLoadError` from `@/router`, which was the first choice
	 * here and is wrong for this question. ls-heroku-ci caught why: `api/index.ts` `_fetch` turns one
	 * network TypeError into two DIFFERENT messages depending on a liveness probe:
	 * "You appear to be offline..." when the server is unreachable, and
	 * "Sorry, the request did not go through..." when the server is REACHABLE but the request failed
	 * (a Heroku H12 dyno stall, say). Neither carries an HTTP status. The router predicate matches both,
	 * because its fragment list includes "request did not go through" - correct for its own question
	 * (should boot defer this route?) and wrong for ours, since it would downgrade a sustained backend
	 * outage to `info` and produce zero Datadog errors from these poll sites.
	 *
	 * So this matches only the offline message. A reachable-server failure stays an error, which is the
	 * distinction the API layer went to the trouble of making.
	 *
	 * ACCEPTED TRADE-OFF, raised in review and decided rather than overlooked. `_serverIsReachable()`
	 * returns false for a dead dyno, a DNS failure or a network partition just as it does for a user
	 * with no connection, and `_fetch` gives all of them the same "You appear to be offline" message —
	 * so a full backend outage IS logged at info here. That is deliberate, for two reasons:
	 *
	 *  1. The client cannot tell those cases apart even in principle: the probe that would distinguish
	 *     them is itself unable to reach anything. There is no correct classification available, so
	 *     either choice mislabels one case.
	 *  2. Backend-down detection does not depend on this log level. Heroku's own router alerting
	 *     (H10 app crashed, H12 request timeout, H14 no web dynos) fires on a real outage, and the
	 *     author confirmed that is the signal ops actually relies on.
	 *
	 * Given the tie, these sites are 5s self-recovering polls, and treating a connectivity condition as
	 * an error is what produced the 2399-errors-in-one-session storm this ticket exists to remove. The
	 * reachable-but-failing case — the one the client CAN identify, and the one that adds per-user
	 * detail Heroku's alert does not carry — is what stays an error above.
	 */
	const OFFLINE_MESSAGE_FRAGMENT = "appear to be offline";

	const isOfflineFailure = (error: unknown): boolean => {
		if (!(error instanceof TypeError) || typeof error.message !== "string") return false;
		// A status means the server answered, so it cannot be an offline failure whatever the wording.
		const status = (error as { status?: unknown }).status;
		if (typeof status === "number" && status >= 400 && status <= 599) return false;
		return error.message.toLowerCase().includes(OFFLINE_MESSAGE_FRAGMENT);
	};
	import StorageWrapper from "@/helpers/storage";
	import { isViewOnlyUser } from "@/helpers/utilities";
	import { hasFreeTrialEnded as checkHasFreeTrialEnded, hasFreeTrialEnded } from "@/helpers/hasFreeTrialEnded.js";
	import { Customer } from "@/types/Customer";
	import ProjectChecklists from "@/components/ProjectChecklists.vue";
	import SheetList from "@/components/SheetList.vue";
	import SheetEditor from "@/components/SheetEditor.vue";
	import constants from "@/api/constants";
	import EventBus from "@/helpers/eventBus";
	import { SelectedMediaItem } from "@/types/media/SelectedMediaItem";
	import Socket from "@/plugins/socket";
	import moment from "moment-timezone";
	import { getCustomerDisplayName } from "@/formatters/customers";
	import { useDisplay } from "vuetify";
	import { useNow, useOnline } from "@vueuse/core";
	import LsContactProjectSvg from "@/assets/ls-contact-project.svg";
	import { useToast } from "vue-toastification";
	import {
		UNSAVED_CHANGES_DIALOG_OPTIONS,
		UNSAVED_CHANGES_DIALOG_TEXT,
		INVALID_CHANGES_DIALOG_TEXT,
		confirmDiscardChanges
	} from "@/composables/useUnsavedChangesGuard";
	import { useFilterPersistence } from "@/composables/useFilterPersistence";
	import { useScribeStore } from "@/stores/scribe";

	import type { FileUploadCounts } from "@/types/FileUploadCounts";
	import { couldNot, couldNotLoad, offlineMessage, projectAccessFailureMessage } from "@/helpers/userMessages";
	import { fetchAllProjectFiles } from "@/helpers/projectMediaPaging";
	const PDFViewerDialog = defineAsyncComponent(() => import("@/components/PDFViewerDialog.vue"));

	const isDebugMode = new URLSearchParams(window.location.search).get("debug") === "true";

	// Constants
	const PAGE_SIZE = 75;
	// LSP-16086: hard ceiling on a single full-reload window so a deep-scrolled
	// session can never issue a multi-thousand-row media search query (the request
	// weight that starved request slots in the load-test incident).
	// 10 pages of loaded media is far past what a reload needs to reconcile.
	const MAX_RELOAD_PAGE_SIZE = PAGE_SIZE * 10; // 750
	// LSP-16086: coalescing-scheduler timings. WAIT is the trailing-debounce window;
	// MAX_WAIT forces a flush during a continuous upload stream so viewers still see media.
	const RELOAD_DEBOUNCE_MS = 5000;
	const RELOAD_MAX_WAIT_MS = 10000;
	const UPLOAD_FINISHED_RELOAD_DELAY_MS = 3500;
	// LSP-19529: a scheduler-driven "full" flush fetches ONE page from the top (the server's MAX_PAGE_SIZE)
	// and merges it over the loaded head instead of running the multi-page reconcile over the whole
	// scrolled window. See headReloadTimeline.
	const SCHEDULED_FULL_RELOAD_PAGE_SIZE = 150;
	// LSP-19529 (AI PR review): the head page cannot see rows below the newest 150 — a recording whose
	// created date (session start) sorts below the head floor, a deep delete this tab missed, or a burst of
	// more than one page of new rows — and the offset-based pager cannot recover such a gap. So a tab
	// scrolled past the head window still runs the whole-window reconcile (loadTimeline(true)), but at most
	// once per this interval instead of on every flush; head reloads fill the gaps in between.
	const SCHEDULED_WHOLE_WINDOW_RECONCILE_MIN_INTERVAL_MS = 60000;
	let lastWholeWindowReconcileAt = Date.now();
	// Non-socket safety reconcile (keeps the timeline fresh even when a socket event is
	// missed or the client never had a socket). Adaptive: while an upload is pending we
	// reconcile fast so new media surfaces promptly; idle, we fall back to a slow gap so
	// connected idle tabs stay quiet. The interval just ticks the evaluator; the effective
	// cadence is the min-gap below for the current state.
	const TIMELINE_RECONCILE_CHECK_MS = 15000;
	const TIMELINE_RECONCILE_ACTIVE_MS = 15000;
	const TIMELINE_RECONCILE_IDLE_MS = 60000;
	const MIN_RECONCILE_GAP_MS = 3000;

	// Components
	const mediaLightBox = ref<InstanceType<typeof MediaLightBox> | null>(null);
	const timeline = ref<InstanceType<typeof Timeline> | null>(null);
	const entityAboutRef = ref<InstanceType<typeof EntityAbout> | null>(null);
	const entityReportsRef = ref<InstanceType<typeof EntityReports> | null>(null);
	const entityCommentsRef = ref<InstanceType<typeof EntityComments> | null>(null);
	const entityChecklistsRef = ref<InstanceType<typeof ProjectChecklists> | null>(null);

	const selectedFileIds = computed(() => {
		return timeline.value?.selectedMediaItems?.map((item: any) => item.id) ?? [];
	});

	// State
	const instance = getCurrentInstance();
	const display = useDisplay();
	const isOnline = useOnline();
	const toast = useToast();
	const router = useRouter();
	const route = useRoute();

	/**
	 * LSP-18879: the copy an external recipient sees when a shared-project link does not resolve.
	 * Deliberately the SAME sentence the well-formed-but-unknown path already produces (and the same
	 * one LSP-11619 accepted) — the point of this ticket is that one shared-project failure should
	 * not be worded differently from another, not that this failure needs new wording.
	 */
	const SHARED_PROJECT_NOT_FOUND_MESSAGE = "Sorry, we couldn't find that project.";

	/**
	 * LSP-18879: read the share id off the ROUTE, not off the `sharingId` ref, which is only
	 * populated part-way through the entity load.
	 *
	 * Verified against the API (2026-08-27) — the generic copy comes from a MALFORMED id, not from a
	 * merely unknown one:
	 *   /projects/<valid-uuid>/public/<valid-uuid>  -> 404 "Sorry, we couldn't find that project."
	 *   /projects/not-a-uuid/public/abc             -> 404 "Resource not found"
	 * The second is `validateUuidParam` rejecting before the controller is reached, and it is what a
	 * recipient gets from a share link that has been truncated or mangled in transit — the case the
	 * ticket's own repro steps (both ids well-formed) do not actually hit.
	 */
	const isSharedProjectLink = (): boolean => !!route.query?.shareId;

	/**
	 * LSP-18879: pick the message for a failed entity load. On a shared-project link, a
	 * not-found / not-permitted failure is reported with the shared-project copy above whichever
	 * layer produced it — a recipient cannot act on "Resource not found", and the transport guard
	 * and the controller word the same outcome differently (see above). Any other failure (a 500, a
	 * network error) keeps its own message so real faults stay diagnosable. Nothing about the
	 * project, its organization, its owner or the recipient is disclosed either way.
	 */
	const getEntityLoadErrorMessage = (err: unknown): string => {
		const resolvedMessage = getMessageFromError(err);
		const status =
			(err as { code?: number })?.code ??
			(err as { status?: number })?.status ??
			(err as { response?: { status?: number } })?.response?.status;
		const normalizedMessage = (resolvedMessage ?? "").trim().toLowerCase();
		const isGenericNotFound =
			normalizedMessage === "resource not found" || normalizedMessage === "sorry, we couldn't find that project.";
		if (entityType.value === "project" && (status === 404 || isGenericNotFound)) {
			return SHARED_PROJECT_NOT_FOUND_MESSAGE;
		}
		if (!isSharedProjectLink()) return resolvedMessage;
		const isNotFoundOrForbidden = status === 404 || status === 403 || status === 401;
		return isNotFoundOrForbidden || isGenericNotFound ? SHARED_PROJECT_NOT_FOUND_MESSAGE : resolvedMessage;
	};
	const scribeStore = useScribeStore();
	const me = ref<any>(null);
	const viewOnly = ref(false);
	const projectAssignmentDialogVisible = ref(false);
	const projectAssignmentSaving = ref(false);
	const verdictStore = useProjectVerdictsStore();
	const authSession = useAuthSessionStore();
	const projectVerdictEnabled = ref(false);
	const projectAccountReady = ref(false);
	const projectVerdict = computed<ClientProjectAccessVerdict | undefined>(() => {
		if (!projectVerdictEnabled.value) return undefined;
		const access = verdictStore.access(entityId.value);
		return access.verdict ?? (entity.value?.id === entityId.value
			? provisionalOwnerVerdict(entity.value, me.value, access, isAuthenticated.value && !authSession.sessionLost) : undefined);
	});
	const verdictStatus = computed(() => projectVerdictEnabled.value
		? projectVerdict.value ? "ready" : verdictStore.access(entityId.value).verdictStatus
		: verdictUnavailable.value ? "unavailable" : projectAccessResolved.value && !isAuthenticated.value ? "ready" : "loading");
	const projectVerdictErrorVisible = computed(() => {
		// No project on screen: its not-found dialog or recovery banner owns the outcome.
		const loadedId = entity.value?.id;
		if (!loadedId || loadedId.toLowerCase() !== entityId.value.toLowerCase()) return false;
		const access = verdictStore.access(entityId.value);
		return access.failures >= 3 || (verdictUnavailable.value && !access.transientFailure);
	});
	let verdictRequestSequence = 0;
	const verdictUnavailable = ref(false);
	const accessOrigin = ref<ProjectVerdictOrigin | null>(null);
	const projectAccessResolved = ref(false);
	const forbiddenFlow = useProjectForbiddenFlow();
	const accessRevoked = forbiddenFlow.handling;
	let contextReload: Promise<void> | null = null;
	let dismissAccessOutcome: (() => void) | null = null;
	const rootAuthentication = () => (instance?.proxy?.$root as { isAuthenticated?: boolean } | undefined)?.isAuthenticated;
	// The shared token context is authoritative; the shell may lag a real restore.
	const establishedSession = ref(rootAuthentication() === true || !!authSession.organizationId);
	let lostAtGeneration: number | null = null;
	const contextAvailable = () => !authSession.sessionLost && (!!authSession.organizationId || isAnonymousShareVisitor());
	watch(
		() => [authSession.organizationId, authSession.sessionLost, rootAuthentication()] as const,
		([organizationId, sessionLost, authenticated], [previousOrganizationId, previouslyLost, previouslyAuthenticated]) => {
			if (authenticated === true) establishedSession.value = true;
			const restored = previouslyLost && !sessionLost;
			if (restored) lostAtGeneration = null;
			// Restore wins over a lagging signed-out shell. Only observed loss edges retire work.
			if (!restored && (sessionLost || (previouslyAuthenticated === true && authenticated === false))) {
				if ((!previouslyLost && sessionLost) || (previouslyAuthenticated === true && authenticated === false)) markSessionLost();
				return;
			}
			// A shell catching up after token recovery must not start a second replacement.
			if (!restored && organizationId === previousOrganizationId &&
				(authenticated !== true || isAuthenticated.value || contextReload)) return;
			forbiddenFlow.cancel();
			const { switchToken } = advanceEntityLoad(false);
			// Token rotations publish several fields synchronously. Only the final context restarts.
			const replacement = nextTick(async () => {
				if (isComponentUnmounted || switchToken !== entitySwitchSequence || !contextAvailable()) return;
				logInfo("EntityDetailPage: restarting detail load for recovered context", { switchToken });
				await initializeComponent(switchToken);
				if (switchToken === entitySwitchSequence && contextAvailable()) await restartUploadStatusPollForCurrentEntity();
			});
			contextReload = replacement;
			void replacement.then(
				() => {
					if (contextReload === replacement) contextReload = null;
				},
				(error) => {
					if (contextReload === replacement) contextReload = null;
					logWarn("EntityDetailPage: context replacement failed", projectVerdictFailure(error));
				}
			);
		},
		{ flush: "sync" }
	);
	const isAdmin = ref(false);
	const freeTrialEnded = ref(false);
	const entityId = ref("");
	const detailLoad = useEntityDetailLoad(() => authSession.sessionLost || lostAtGeneration !== null);
	const loading = detailLoad.loading;
	const retryAfterReconnect = ref(false);
	const hasRecoverableDetailError = () =>
		detailLoad.recoveryStage.value !== null || detailLoad.phase.value === "error" || projectVerdictErrorVisible.value;
	const retryRecoverableDetail = () => {
		if (isComponentUnmounted || authSession.sessionLost) return;
		if (detailLoad.loading.value) {
			retryAfterReconnect.value = true;
			logInfo("EntityDetailPage: deferring reconnect recovery until the offline load settles", { entityId: entityId.value });
			return;
		}
		if (!hasRecoverableDetailError()) {
			retryAfterReconnect.value = false;
			return;
		}
		retryAfterReconnect.value = false;
		logInfo("EntityDetailPage: retrying detail recovery after reconnect", { entityId: entityId.value });
		void retryDetailLoad().catch((error) => logWarn("EntityDetailPage: reconnect recovery failed", projectVerdictFailure(error)));
	};
	watch(isOnline, (online, wasOnline) => {
		if (online && !wasOnline) retryRecoverableDetail();
	});
	watch(
		() => detailLoad.phase.value,
		(phase) => {
			if (phase !== "loading" && retryAfterReconnect.value && isOnline.value) retryRecoverableDetail();
		}
	);
	const startingLoading = ref(false);
	const lightboxOpen = ref(false);
	// LSP-16086 (P3): per-surface pending flags so events arriving with the lightbox
	// open don't collapse onto a single timeline-only flag and drop document/checklist
	// refreshes on close.
	const pendingReloads = ref({ timeline: false, documents: false, checklists: false, checklistMedia: false });
	// LSP-16086 (P3): while the tab is backgrounded, accumulate one trailing reload
	// and flush it when the user returns, instead of fetching against a hidden tab.
	// LSP-16086 (P3): the reload MODE deferred while the tab is hidden. We must
	// remember the mode (not just a boolean) — a recording update needs a FULL
	// reload (its `created` can predate the delta window), so flushing it as a delta
	// on return would silently miss it. "full" is the safe superset; a mixed batch
	// escalates to "full" and never downgrades.
	const pendingVisibilityReloadMode = ref<"delta" | "full" | null>(null);
	// LSP-16086: coalesce socket-driven (and periodic / reconnect / visibility-return)
	// reloads into a single bounded /search/media reconcile per burst, with a ceiling
	// so a continuous upload stream still refreshes. See helpers/timelineReloadScheduler.
	const timelineReloadScheduler = createTimelineReloadScheduler({
		wait: RELOAD_DEBOUNCE_MS,
		maxWait: RELOAD_MAX_WAIT_MS,
		flush: (surfaces, mode) => flushScheduledReload(surfaces, mode)
	});
	// Timestamp of the last timeline reconcile, so the periodic safety tick and the
	// tab-visibility-return reconcile skip when the timeline is already fresh.
	// Initialised to mount time (not 0) so the reconcile-gap is measured from when the
	// timeline first loads — otherwise Date.now()-0 is always huge and the first periodic
	// tick would reconcile immediately regardless of the idle/active cadence.
	let lastTimelineReconcileAt = Date.now();
	// LSP-16706 (ls-heroku-ci, liveswitch-ai-pr-reviewer): consecutive "incomplete" deferrals in a row,
	// regardless of whether the specific unrecovered id set is identical or keeps shifting — either shape
	// means retrying immediately isn't converging. A one-off incomplete deferral is still transient churn
	// worth retrying promptly; only the SECOND (or later) consecutive one backs off.
	let consecutiveIncompleteCount = 0;
	// LSP-16706 (ls-heroku-ci): consecutive "restarts-exhausted" deferrals in a row. A one-off restart
	// exhaustion is transient churn worth retrying promptly, but a conversation that exhausts restarts on
	// EVERY attempt is sustained enough that an immediate retry can't do better either — same reasoning as
	// consecutiveIncompleteCount above.
	let consecutiveRestartsExhaustedCount = 0;
	let timelineReloadSequence = 0;
	let timelineSafetyTimer: ReturnType<typeof setInterval> | null = null;
	// onMounted is async (awaits initializeComponent); if the component unmounts during
	// that await, onUnmounted runs first and the post-await interval arming would leak a
	// timer that's never cleared. This flag lets the resumed onMounted bail out.
	let isComponentUnmounted = false;
	// Track socket connectivity so a RE-connect (not the initial connect) triggers a
	// reconcile to catch media created while the socket was down — socket is not
	// reliable for every client, so this is part of the bulletproof guarantee.
	let socketWasDisconnected = false;
	// A reconnect scheduled during the UploadFinished delay must merge instead of replacing the timeline.
	// Bound this exception to the upload delay plus one reconnect debounce;
	// reconnects outside that race retain the authoritative full reload (including deletion recovery).
	let preserveUploadMediaOnReconnectUntil = 0;
	// LSP-16086: avoid force-pulling 1000 checklist-media rows on every socket event.
	// Defer until the checklist tab is actually visible, then flush once.
	const checklistMediaDirty = ref(false);
	const checklistsTabIndex = computed(() => tabs.value.findIndex((tab) => tab.id === tabIds.checklists));
	const searchRequest = ref<SearchRequest | null>(null);
	const entityType = ref<EntityType | null>(null);
	const currentSearchTerm = ref("");
	// LSP-20922: the search term the DISPLAYED media results were fetched for, bound to Timeline's
	// `searchTerm` so an empty search result says "No media match …" instead of "There's no media here
	// yet.". Deliberately not `currentSearchTerm`: SearchInput's v-model updates that on every keystroke,
	// so it would name a term whose results have not arrived yet — and clearing the box would flash
	// "There's no media here yet." over the stale empty result for the debounce + fetch window. Set only
	// where `mediaResponse` is replaced by a fetched response, from that response's own request.
	const timelineResultsSearchTerm = ref("");
	// LSP-20939: the filter half of the same snapshot — whether the DISPLAYED results were narrowed by user
	// filters (the search term aside), so a filtered-to-nothing session says "No media match these filters"
	// instead of "There's no media here yet.". Derived from the applied request, never the live Filters UI.
	const timelineResultsFiltersApplied = ref(false);
	// LSP-16659: the displayed results were narrowed to documents and nothing else ("· Showing documents").
	const timelineResultsShowingDocumentsOnly = ref(false);
	function showTimelineResultsFor(request: Partial<SearchRequest> | null | undefined): void {
		timelineResultsSearchTerm.value = request?.searchTerm ?? "";
		timelineResultsFiltersApplied.value = userFiltersNarrowResults({ ...(request ?? {}), searchTerm: undefined });
		const appliedContentTypes = request?.contentTypes ?? [];
		timelineResultsShowingDocumentsOnly.value =
			appliedContentTypes.length === 1 &&
			appliedContentTypes[0] === "document" &&
			!userFiltersNarrowResults({ ...(request ?? {}), searchTerm: undefined, contentTypes: undefined });
	}
	const isAuthenticated = ref(false);

	// LSP-17773: recipient onboarding tour — shown once per user when a shared EDITOR first lands
	// on a project (accessOrigin==="share", not view-only, authenticated) — e.g. a newly-signed-up
	// invitee redirected here after accepting an editor invite. Never for owners, org members, or
	// view-only visitors. Triggered from the handleProjectAccess decision; persisted via StorageWrapper.
	// Legacy unscoped key — read-only now. Kept so anyone who dismissed the video before the key
	// was scoped per user (below) never sees it again; a legacy-key hit counts as "seen".
	const WELCOME_VIDEO_SEEN_KEY = "projectWelcomeVideoSeen";
	// "Once per user" needs a per-user key: a shared browser (or a user switch on the same
	// machine) must not let one user's dismissal hide the tour from the next user.
	function welcomeVideoSeenKey(): string {
		const userId = me.value?.user?.id;
		return userId ? `${WELCOME_VIDEO_SEEN_KEY}:${userId}` : WELCOME_VIDEO_SEEN_KEY;
	}
	function hasSeenWelcomeVideo(): boolean {
		return !!StorageWrapper.getItem(welcomeVideoSeenKey()) || !!StorageWrapper.getItem(WELCOME_VIDEO_SEEN_KEY);
	}
	// LSP-21935: the dedicated "Project Sharing Editor View" guide (Vimeo 1229922433) shown to a shared
	// editor on first landing. VITE_SHARE_INSTRUCTIONAL_VIDEO_URL still overrides it per environment.
	const welcomeVideoUrl = import.meta.env.VITE_SHARE_INSTRUCTIONAL_VIDEO_URL || "https://player.vimeo.com/video/1229922433";
	// The poster's length label belongs to the default video only; an override's length is unknown.
	const welcomeVideoDuration = import.meta.env.VITE_SHARE_INSTRUCTIONAL_VIDEO_URL ? undefined : "0:44";
	const showWelcomeVideo = ref(false);
	function maybeShowWelcomeVideo() {
		// Only greet a genuine EXTERNAL recipient — a share-origin editor from a different org
		// (isExternalEditor). The owner and same-org members can also match a ProjectShare row
		// (accessOrigin==="share"), so gating on that alone wrongly showed them the tour. Reuse the
		// same external-editor signal the About-readonly / hidden-Share mirrors key off.
		if (isExternalEditor.value && isAuthenticated.value && !hasSeenWelcomeVideo()) {
			showWelcomeVideo.value = true;
		}
	}
	function dismissWelcomeVideo() {
		StorageWrapper.setItem(welcomeVideoSeenKey(), "true");
		showWelcomeVideo.value = false;
	}
	const isDirty = ref(false);
	const bypassUnsavedChangesCheck = ref(false);
	// LSP-16420 — live autosave status surfaced by EntityAbout, rendered as the header's "Last saved …" indicator.
	const saveState = ref<SaveState>({ ...IDLE_SAVE_STATE });

	// LSP-15740: when a recipient opens a shared-project link, the URL carries
	// `?shareId=...`. Surfacing it here lets us pass it to ProjectChecklists so
	// the unauthenticated tab loads via the public route instead of being
	// bounced to login.
	const shareId = computed(() => {
		const value = route.query.shareId;
		return typeof value === "string" ? value : "";
	});

	const filterPageKey = computed(() => `${entityType.value ?? "unknown"}:${entityId.value}`);
	const filterRouteName = computed(() => (entityType.value === "project" ? "project" : "conversation"));
	const filterPersistence = useFilterPersistence({
		pageKey: filterPageKey,
		routeName: filterRouteName,
		buildBaseRequest: getSearchRequest
	});
	const filtersVisible = filterPersistence.filtersVisible;
	const showScribeFAB = ref(true);
	const fabManuallyDismissed = ref(false);
	const scribeFabRef = ref<InstanceType<typeof ScribeFab> | null>(null);
	const isSaving = ref(false);
	let reloadTimeout: ReturnType<typeof setTimeout> | null = null;

	// LSP-15266 — separate timeout for master-table delete handlers so they
	// don't share state with reloadTimeline() (which is called from many
	// paths and only refreshes the timeline). Flags accumulate across
	// coalesced calls so a recording-delete + file-delete burst within 5s
	// still ends up running BOTH the timeline and documents reload at the
	// end of the window — sharing a single timeout without these flags
	// would let the second call's clearTimeout drop the first's reload work.
	// Each flag's eventName is captured at flag-set time so error logs cite
	// the event that originally requested that reload, not whichever event
	// happened to schedule the trailing-edge timer.
	let masterDeleteReloadTimeout: ReturnType<typeof setTimeout> | null = null;
	const masterDeleteReloadFlags = { timeline: false, documents: false, checklists: false, media: false };
	const masterDeleteReloadEventNames = { timeline: "", documents: "", checklists: "", media: "" };

	// Data
	const service = ref<BaseEntityService<Project | Conversation> | null>(null);
	const entity = ref<Project | Conversation | undefined>();
	const sourceEntity = ref<Project | Conversation | undefined>();
	const mediaResponse = ref<EntitySearchResponse>(emptyEntitySearchResponse());
	const readyPdfDocumentCount = ref(0);
	const newSelectedCustomer = ref<Customer | undefined>();
	const newSelectedEntity = ref<Project | Conversation | undefined>();
	const projectConversationIds = ref<string[]>([]);
	const reportConversations = ref<Partial<Conversation>[]>([]);
	// LSP-15648: the "Sparky's Reports" tab is a lazy v-window-item, so EntityReports (which used to be the
	// only place that started composite project+children Scribe monitoring) does not mount until the tab is
	// first opened. On re-navigation the project opens on the About tab, so a running child report was never
	// monitored until it completed. Seed the composite scope here at the page level instead.
	const projectScribeMonitoringReady = ref(false);
	let skipNextProjectConversationIdsWatchSeed = false;

	// LSP-16028 (Bugbot, PR #4154): arming the warm-up is not enough on its own. Every warm-up tick
	// calls scribeStore.refreshAnalyses, which returns immediately while the store has no
	// externalId/externalType. The project path happens to avoid that because it calls
	// seedProjectScribeMonitoring() (i.e. startMonitoring) first; the session path had no equivalent,
	// so on the exact cold boot this warm-up exists for — scribe-monitor's bootstrap tick calling
	// stopMonitoring() before auth is ready, and EntityReports unmounted on the default tab — the
	// ticks were a no-op and the FAB kept showing the READY dog for a running report.
	//
	// startMonitoring only clears currentAnalyses when the PRIMARY entity changes, so re-seeding the
	// entity we are already monitoring is harmless — which is what lets the warm-up tick call this
	// defensively on every attempt to heal a stopMonitoring() that lands mid-window.
	function seedScribeMonitoring() {
		if (!entityId.value) return;
		if (entityType.value === "project") {
			seedProjectScribeMonitoring();
			return;
		}
		scribeStore.startMonitoring(entityId.value, scribeType.value);
	}

	function seedProjectScribeMonitoring() {
		if (entityType.value !== "project" || !entityId.value || !projectScribeMonitoringReady.value) {
			return;
		}

		scribeStore.startMonitoring(entityId.value, "project", undefined, undefined, projectConversationIds.value);
	}

	// Re-seed when the project's child conversation set changes after the initial load (e.g. a conversation
	// is added). The load itself seeds explicitly and sets the skip flag so this watch does not double-seed.
	watch(
		() => projectConversationIds.value.slice(),
		() => {
			if (skipNextProjectConversationIdsWatchSeed) {
				skipNextProjectConversationIdsWatchSeed = false;
				return;
			}
			seedProjectScribeMonitoring();
		}
	);

	// LSP-15648: on a hard refresh the scribe token is not ready, so the initial composite refresh is
	// blocked and a running child never surfaces; the EntityReports fallback poll is gated on activeRows>0
	// so it never starts to recover. This bounded warm-up retries the composite refresh for a short window
	// after load so the running indicator heals within seconds once the token resolves — instead of staying
	// empty until the report completes. Per-instance timer (cleared on reset/unmount).
	// Front-loaded backoff: retry quickly first so the running report appears in ~1-2s once the data/token
	// are ready, then back off to cover slower cold-starts. Bounded (no unbounded polling).
	//
	// LSP-16028: this warm-up was project-pages-only, and the SESSION interior has exactly the same cold-
	// boot hole — arguably worse. scribe-monitor's bootstrap tick runs before the initial navigation
	// resolves, so it sees START_LOCATION and calls stopMonitoring(); the afterEach re-arm then hits
	// refreshAnalyses' `!isAuthenticated` early return and nothing retries. EntityReports would heal it,
	// but it lives in a lazy <v-window-item> and a session's default tab is About/Media, so on a plain F5
	// it never mounts. Net effect: currentAnalyses stays [] and the FAB + toolbar render the READY dog for
	// a report that is actually still running. Same fix, same bounded retry — just no longer gated on type.
	const SCRIBE_WARMUP_DELAYS_MS = [1000, 2000, 4000, 7000, 11000, 16000];
	let scribeWarmupTimeout: ReturnType<typeof setTimeout> | null = null;
	let scribeWarmupAttempts = 0;

	function stopScribeWarmup() {
		if (scribeWarmupTimeout) {
			clearTimeout(scribeWarmupTimeout);
			scribeWarmupTimeout = null;
		}
	}

	function scheduleScribeWarmupTick() {
		if (scribeWarmupAttempts >= SCRIBE_WARMUP_DELAYS_MS.length) {
			return;
		}
		const delay = SCRIBE_WARMUP_DELAYS_MS[scribeWarmupAttempts];
		scribeWarmupTimeout = setTimeout(() => {
			// Stop once a running/awaiting analysis has surfaced — the live indicator is correct from here.
			if (scribeStore.anyAnalysisIsRunning) {
				stopScribeWarmup();
				return;
			}
			scribeWarmupAttempts++;
			// Re-seed before refreshing: the route plugin can call stopMonitoring() at any point in
			// this window (it does so whenever auth.isAuthenticated() is false), which would leave
			// every remaining tick refreshing against a cleared scope.
			seedScribeMonitoring();
			scribeStore.refreshAnalyses(true);
			scheduleScribeWarmupTick();
		}, delay);
	}

	function startScribeWarmup() {
		stopScribeWarmup();
		scribeWarmupAttempts = 0;
		scheduleScribeWarmupTick();
	}

	const subEntityId = ref<string | undefined>();
	const searchOrganizationId = ref<string | undefined>();

	function toReportConversation(conversation: Partial<Conversation>): Partial<Conversation> {
		return {
			id: conversation.id,
			name: conversation.name,
			number: conversation.number,
			type: conversation.type,
			status: conversation.status
		};
	}

	// Project specific

	const sharingId = ref("");
	const projectUser = ref<any>(null);
	const showProjectActionFAB = ref(false);
	const address = ref("");
	const isProjectOwner = ref(false);
	const isChecklistMenuOpen = ref(false);

	// Sheets
	const sheetListRef = ref<InstanceType<typeof SheetList> | null>(null);
	const sheetEditorVisible = ref(false);
	const editingSheet = ref<any>(null);

	// Timeline
	const hasMoreMedia = ref<boolean>(true);
	const pagingTimeline = ref<boolean>(false);
	const currentTimelinePage = ref<number>(1);
	// LSP-16706: monotonic paging cursor — the highest page pageTimeline has requested since the last
	// replacement/reconcile. It lets the next page advance past a "barren" page (one that returns only
	// stubs/duplicates and adds no new rows) instead of re-requesting it forever, while still
	// re-requesting a partial last page after a reconcile. Re-baselined to floor(loaded/PAGE_SIZE) by
	// normalizeTimelinePagingAfterReplacement (ls-heroku-ci: this comment previously said "reset to 0").
	let lastTimelinePageRequested = 0;

	// Pending upload counts for skeleton placeholders
	const pendingImages = ref<number>(0);
	const pendingDocs = ref<number>(0);
	const pendingVideos = ref<number>(0);

	// Sticks true when a timeline fetch fails so the async reminder prompt does not treat an error as an empty timeline.
	const timelineLoadFailed = ref<boolean>(false);

	// Upload status from backend — drives the LSP-15966 pending-media warning
	// banner and run-a-report interlock. The server endpoint is authoritative
	// across all timeline pages (the legacy client-side document filter was
	// scoped to the first PAGE_SIZE=75 items and missed off-page pending docs).
	const uploadStatus = ref<{
		pendingFiles: number;
		pendingSnapshots: number;
		pendingRecordings: number;
		// LSP-18027: display-only count — pendingRecordings minus the sessions whose recording is already
		// playable and on screen. Optional: a response from before this shipped (or a cached one) omits it,
		// and the pending computeds fall back to pendingRecordings.
		pendingRecordingsVisibleToAdmin?: number;
		pendingTranscoding?: number;
		// LSP-15573: admin video FILE uploads that started and never finished, already aged out of every
		// pending count above. Optional: a response from before this shipped (or a cached one) omits it.
		abandonedVideoUploads?: number;
		// LSP-18264: true when a web recording upload is genuinely abandoned (incomplete + quiet past the
		// admin window), computed server-side. Lets the manual Send Reminder banner fire for an incomplete
		// recording that never got indexed as a search document (a killed-mid-upload take), which the
		// index-based `hasIncompleteRecordingUpload` cannot see. Optional: absent on pre-this cached responses.
		hasAbandonedRecordingUpload?: boolean;
		// LSP-18264 (ls-heroku-ci #1): same, for photos/files — a web-sourced pending photo/file quiet past the
		// short admin window. Drives the banner instead of the raw pending count so it never fires mid-upload.
		hasAbandonedPhotoOrFileUpload?: boolean;
		// True while the conversation's newest web recording take is incomplete; a newer complete take
		// supersedes an abandoned one (server: hasIncompleteLatestRecordingUpload). Read by the auto-Reviewed
		// gate. Optional: absent on older cached responses.
		hasIncompleteLatestRecording?: boolean;
		hasUnverifiedRecording?: boolean;
		// F-02 (media-upload resilience review): can a reminder be sent for this conversation AT ALL? Every
		// send path — the deliverability preflight, both senders, and the manual endpoint's 422 — refuses a
		// non-ASYNC conversation, so on a SYNC/live session Send Reminder can only ever fail. The server
		// publishes the verdict explicitly (`conversations.ts` getUploadStatus) rather than leaving the admin
		// to infer it from two flags that quietly went false. Optional: a response from before this shipped
		// (or a cached one) omits it, and the banner keeps its previous behavior.
		canSendUploadReminder?: boolean;
		isComplete: boolean;
		uploaded?: number;
		expected?: number;
		recordingCount?: number;
		mediaState?: "complete" | "in_progress" | "expected_not_started";
		// LSP-17328: the server's per-item banner answer (`abandoned`, plus an informational verdict and origin), from
		// the same evidence load as every count above. The session latch and the lightbox (via its `upload-status` prop) both hand
		// this response to `isIncompleteRecordingUpload`, so they read one answer per recording. Optional: absent on responses from
		// before this shipped, and the predicate then falls back to the index flag unchanged.
		items?: UploadStatusItem[];
	} | null>(null);

	// LSP-16842: has an upload-status fetch finished at least once, whatever the outcome? Distinct
	// from `uploadStatus !== null`, which stays false after an empty response, and from the
	// committed-sequence counter, which only advances on a response with a body.
	const uploadStatusFetchSettled = ref(false);
	const isUploading = ref<boolean>(false);
	let uploadStatusTimer: ReturnType<typeof setTimeout> | null = null;
	// LSP-16326: monotonic request id so a slower OLDER fetchUploadStatus (it can run
	// concurrently from the 5s poll, UploadStarted, visibility-resume, and the click-time
	// run-report refresh) cannot resolve last and overwrite uploadStatus with stale data.
	// Mirrors the timelineReloadSequence guard used by loadTimeline/deltaReloadTimeline.
	let uploadStatusRequestSequence = 0;
	// LSP-16326: the sequence of the most recent fetch that actually COMMITTED a response to
	// uploadStatus. handleRunReport uses this to tell "my click-time fetch was superseded by a
	// newer SUCCESSFUL poll" (data is fresh → proceed) from "could not verify" (→ fail closed).
	let uploadStatusCommittedSequence = 0;
	let entityResetSequence = 0;
	type EntityContextSnapshot = {
		resetSequence: number;
		entityId: string;
		entityType: EntityType | null;
	};
	type FetchUploadStatusOptions = {
		allowIdleUploadFinalization?: boolean;
	};
	type RestartUploadStatusPollOptions = FetchUploadStatusOptions;
	// LSP-16086: adaptive poll cadence. Fast while an upload is in-flight / recently active
	// or the socket is down (polling is then the only freshness channel); a slow heartbeat
	// when the socket is connected and idle — still catches another user's in-flight upload,
	// far cheaper than a 5s drumbeat on every idle tab.
	const UPLOAD_POLL_FAST_MS = 5000;
	const UPLOAD_POLL_IDLE_MS = 30000;
	const UPLOAD_ACTIVE_WINDOW_MS = 30000;
	const RUN_REPORT_UPLOAD_VERIFY_RETRIES = 3;
	const RUN_REPORT_UPLOAD_VERIFY_DELAY_MS = 250;
	const FILE_UPLOADED_AFTER_FINISH_GRACE_MS = 1000;
	let lastUploadActivityAt = 0;
	let uploadFinishedSinceLastStart = false;
	let uploadFinishedAt = 0;
	let uploadMayFinalizeAfterIdle = false;
	let latestUploadStatusFetchIsComplete = false;
	let pendingDocumentReloadPromise: Promise<void> | null = null;
	// LSP-16072: once the polled conversation/project is gone (404), stop polling for
	// good — an open tab on a deleted conversation otherwise 404s every 5s forever,
	// flooding the server error handler. This latches so the visibility-return resume
	// never restarts it.
	let uploadStatusPollDisabled = false;

	// Edge-trigger state for the periodic-reconcile mode log; see periodicTimelineReconcile. Cleared in
	// reset() like every other per-entity mutable — otherwise navigating in-place from a conversation that
	// already escalated into another one that also holds an unfinished upload logs nothing for the new
	// conversation, because the edge-trigger sees no change (ls-heroku-ci review).
	let lastReconcileEscalationLogged: boolean | null = null;

	/**
	 * LSP-18027: how many pending recordings should the UI actually DRAW?
	 *
	 * When an async upload is interrupted, the reconcile worker still merges and transcodes the chunks that
	 * did arrive, so the session produces a real, playable video tile — while its `isUploadComplete` stays
	 * unset, because the upload genuinely never finished. `pendingRecordings` counts that session, and the
	 * Timeline drew a second, grey placeholder beside the video the admin can already play ("Media: (1) -
	 * 1 Pending", one video plus one empty tile for the same recording).
	 *
	 * `pendingRecordingsVisibleToAdmin` is the server's display-only count with those sessions removed.
	 * `pendingRecordings` deliberately keeps its original meaning on the response — it is the input to the
	 * internal readiness gates (getIfAllRecordingsAreReadyForReviewForConversation, AsyncWorker Phase-4) and
	 * to `isComplete`/the poll cadence, where "this upload is unfinished" is still the correct answer.
	 *
	 * Falls back to `pendingRecordings` when absent (older/cached response, or the share-link mirror
	 * mid-deploy) — falling back to 0 would erase every legitimate processing placeholder.
	 */
	function displayablePendingRecordings(status: { pendingRecordings: number; pendingRecordingsVisibleToAdmin?: number }): number {
		return status.pendingRecordingsVisibleToAdmin ?? status.pendingRecordings;
	}

	// DB-backed count of media still uploading or transcoding, shown beside the Timeline's
	// "Media (N)" total as "- N Pending". Snapshots + videos only (documents aren't media);
	// countPendingUploads already drops stale/abandoned stubs. LSP-16121.
	const pendingMediaCount = computed(() => {
		const status = uploadStatus.value;
		if (!status) return 0;
		return status.pendingSnapshots + displayablePendingRecordings(status) + (status.pendingTranscoding ?? 0);
	});

	// Recordings that media search already counts in "Media (N)" but that aren't viewable yet — still
	// uploading or transcoding. The Timeline subtracts this so the displayed total reflects only playable
	// media. Pending snapshots are excluded by media search, so they are NOT subtracted here. LSP-16121.
	const pendingRecordingCount = computed(() => {
		const status = uploadStatus.value;
		if (!status) return 0;
		return displayablePendingRecordings(status) + (status.pendingTranscoding ?? 0);
	});

	/**
	 * LSP-15573: admin video file uploads this session started and never finished.
	 *
	 * Deliberately NOT part of `pendingRecordingCount`. That count means "still arriving" and drives the
	 * grey processing skeleton; this one means "gave up" and drives the Timeline's failed-upload state.
	 * The server computes them from mutually exclusive halves of the pending-upload activity window
	 * (`getRecordingSessionUploadActivityWhere` / `getRecordingSessionUploadInactiveWhere`), so one upload
	 * can only ever be in one of them and the two placeholders can never stack.
	 */
	// Dedupe keys for the diagnostics below: one line per conversation per reason, not one per poll.
	// Plain Set (not a ref) — nothing renders from it, so it must not participate in reactivity.
	const reportedUnusableAbandonedCounts = new Set<string>();

	// Entities for which a real abandoned-upload count has actually been seen. Plain Set (not a ref):
	// nothing renders from it, so it must not participate in reactivity.
	const observedAbandonedCountEntities = new Set<string>();

	function entityScopeKey(): string {
		return `${entityType.value}:${entityId.value}`;
	}

	// Review: both Sets are keyed by entity, but nothing ever cleared them, so re-opening a session later in
	// the same tab inherited the earlier visit's "already reported" state and a genuinely NEW episode stayed
	// silent for the life of the page. Cleared on an entity switch, matching how loggedInconclusiveRecordingIds
	// is reset for the same reason.
	watch(entityScopeKey, () => {
		reportedUnusableAbandonedCounts.clear();
		observedAbandonedCountEntities.clear();
	});

	function reportUnusableAbandonedCount(reason: "absent" | "malformed", detail: string): void {
		const key = `${entityType.value}:${entityId.value}:${reason}`;
		if (reportedUnusableAbandonedCounts.has(key)) return;
		reportedUnusableAbandonedCounts.add(key);
		console.warn(
			`[LSP-15573] upload-status gave no usable abandoned-upload count for ${entityType.value} ${entityId.value} ` +
				`(${reason}): ${detail}. Treating it as 0, so the failed-upload placeholder will not render — ` +
				`indistinguishable on screen from "no failed uploads", which is why it is said here.`
		);
	}

	/**
	 * Which endpoints are contracted to return `abandonedVideoUploads`.
	 *
	 * Only the conversation upload-status route carries it. Keyed off the entity type rather than the
	 * response body on purpose: the question being asked is "should this field have been here?", which
	 * cannot be answered from whether the field is here.
	 */
	const endpointCarriesAbandonedCount = computed(() => entityType.value === "conversation");

	const failedVideoUploadCount = computed(() => {
		// break-it round 2: the server count is CONVERSATION-wide, but the timeline it is compared against
		// is only as wide as the current search/filter. On a narrowed request an empty result set means
		// "nothing matched", not "nothing was uploaded" — surfacing the failed-upload state there would
		// replace an accurate no-results answer with an unrelated warning, the same reason
		// `showAsyncReminderPrompt` bails out on a narrowed request. The count comes back the moment the
		// filter is cleared. (Timeline has its own `searchTerm` / `filtersApplied` guards, bound here since
		// LSP-20922 / LSP-20939, but those follow the DISPLAYED results; this one follows the live request.)
		const activeRequest = searchRequest.value;
		const requestIsNarrowed =
			!!currentSearchTerm.value ||
			!!activeRequest?.searchTerm ||
			userFiltersNarrowResults((activeRequest ?? {}) as Partial<SearchRequest>);
		if (requestIsNarrowed) return 0;

		// Nothing to read yet: uploadStatus is null before the first poll answers. Not reportable — it is
		// the ordinary startup state, not a lost signal.
		if (!uploadStatus.value) return 0;

		const reported = uploadStatus.value.abandonedVideoUploads;
		// break-it round 1: this response field is the ONLY input to a user-facing warning that is rendered
		// verbatim. Anything that is not a safe non-negative integer would print "NaN"/"Infinity video
		// uploads didn't finish", so only a real count is trusted.
		//
		// Review symmetry fix: BOTH rejection branches now report. They have the same consequence — the
		// failed-upload placeholder silently never renders — and previously only the malformed one said so,
		// which is exactly the asymmetry the silent-failure rule exists to catch. Deduped per conversation
		// because this is a computed re-evaluated on every ~5s poll; an unconditional warn would repeat the
		// same line indefinitely and bury itself (the malformed branch had that flaw too).
		if (reported === undefined) {
			// Review: absence means two different things depending on WHICH endpoint answered, and the
			// difference is not inferable from the missing field itself — "missing" is the thing being judged.
			// `/projects/:id/upload-status` is designed never to carry this count (getProjectUploadStatus
			// returns pending counts + isComplete only, and the cross-team contract test pins that shape), so on
			// a project page absence is the contract being honoured, not a signal being lost. Warning there
			// fired on every poll of a normal path — and a diagnostic that cries wolf on a designed path is
			// worse than none, because it trains the reader to skip the line that will one day matter.
			// Only a DISAPPEARANCE is reportable. Absence-from-the-start is indistinguishable from an API that
			// simply does not carry the field yet — which is the normal state during rollout, and the state
			// every partial test fixture is in — so warning on it fires constantly on healthy paths, the same
			// cry-wolf failure the project-page gate above just fixed. Once we have SEEN a real count for this
			// entity, a later absence is genuine evidence: a dropped or renamed response field, or a stale
			// cached response. That is the regression this line exists to catch, and it is now the only case
			// that trips it.
			if (endpointCarriesAbandonedCount.value && observedAbandonedCountEntities.has(entityScopeKey())) {
				reportUnusableAbandonedCount(
					"absent",
					`the response omits abandonedVideoUploads (server predates the field, or a cached response)`
				);
			}
			return 0;
		}
		if (typeof reported !== "number" || !Number.isSafeInteger(reported) || reported < 0) {
			reportUnusableAbandonedCount("malformed", `abandonedVideoUploads is ${typeof reported}: ${String(reported)}`);
			return 0;
		}
		// Positive observation: this endpoint really does carry the field for this entity, so a later absence
		// would be a regression rather than a version gap.
		observedAbandonedCountEntities.add(entityScopeKey());
		return reported;
	});

	/**
	 * LSP-15573: mount the Timeline alongside AsyncReminderEmptyState when there is a failed upload to
	 * report, so the two stop being alternatives. The Timeline still decides for itself whether the tile is
	 * appropriate (it requires an otherwise-empty timeline); this only ensures it gets the chance.
	 */
	const showFailedUploadAlongsideReminder = computed(() => failedVideoUploadCount.value > 0);

	/**
	 * LSP-17328: does this SESSION hold a recording whose upload never finished?
	 *
	 * The reminder is per-session — one text or email covers every unfinished recording in it — so the
	 * actionable banner belongs here rather than on each recording in the lightbox, where it offered the
	 * same customer several identical messages and mounted a request-issuing component per carousel item.
	 *
	 * Deliberately NOT `pendingRecordingCount`: that derives from the server's uploadStatus, which applies
	 * the PENDING_UPLOAD_STALENESS_MINUTES inactivity window, so an upload that died days ago — precisely
	 * the case this button exists for — is not counted there. It uses `isIncompleteRecordingUpload`, the
	 * SAME exported predicate the lightbox notice uses, so the two cannot drift apart. They did for one
	 * commit, when this gate omitted the Ready/Completed status check, and the session offered to text a
	 * customer about a recording still mid-upload.
	 *
	 * STICKY, and that is the point. `mediaResponse.documents` is the CURRENT timeline response — page-sized
	 * (75), search-filtered, and content-type-filtered. Reading it directly meant the banner vanished the
	 * moment an admin typed in the search field it now sits directly above, or filtered to Documents, or
	 * paged past the unfinished recording. Latching means a filter can never retract a warning the session
	 * has already earned; only navigating to a different session clears it.
	 *
	 * KNOWN LIMITATION, unchanged by the latch: an incomplete recording that has never appeared in any
	 * loaded page cannot raise this. Closing that needs a per-session flag from the server rather than a
	 * client scan of the timeline.
	 *
	 * Sticky is not the same as permanent, which is why this tracks IDS rather than a boolean (break-it
	 * round 1). The latch has to survive a recording leaving the view for a reason that says nothing about
	 * the upload (a filter, a search term, a page boundary) while still retracting when the upload actually
	 * finishes — and finishing is the SUCCESS CASE of the reminder this banner sends. A boolean could not
	 * tell those apart, so the customer resuming their upload left the session still warning about it and
	 * still offering to text them again, while the lightbox notice for the very same recording correctly
	 * went quiet. Widening what raises the latch (an abandoned upload is reversible by design, where the
	 * old Ready/Completed-only condition rarely was) turned that from a corner into the expected path.
	 *
	 * An id is dropped only on POSITIVE evidence — the same recording seen again and no longer judged
	 * unfinished — never on absence, which is what keeps a filter from retracting a warning the session has
	 * earned. Resolution is decided by the SAME predicate that raised it (break-it round 2), not by a
	 * hand-written "is it complete now" test: a second condition would be a second definition of finished,
	 * and it immediately disagreed — an upload that RESUMED (chunks arriving again, so the server withdraws
	 * its abandonment verdict) is no longer unfinished to the lightbox while `isUploadComplete` is still
	 * false, so an `isUploadComplete === true` check left the session warning about it. One predicate, both
	 * directions, and the two surfaces cannot disagree by construction.
	 */
	const observedIncompleteRecordingIds = ref<Set<string>>(new Set());

	// GETTERS THAT READ THE ROWS, not the array reference. Timeline paging appends in place, so the
	// reference never changes and a `deep: false` watch on it never fired — scrolling to an unfinished
	// recording on page 2 left Send Reminder hidden for the whole session (Bugbot, and it was right).
	// Iterating inside the getter makes Vue track the elements and the length, so an in-place push is
	// observed without paying for a deep watch over 75 search documents.
	//
	// Id-less rows are dropped entirely. A document with no id contributed `undefined` as a Set key, so every
	// id-less recording shared ONE entry: a single resolved one then erased the warning earned by a
	// different, still-unfinished recording (break-it round 2).
	//
	// Rows are then DEDUPED BY ID (break-it round 3): offset pagination can return the same recording twice
	// when the underlying result set shifts between page fetches, and the two copies can disagree — a stale
	// abandoned row alongside a completed one. Classifying every row independently let one copy re-raise the
	// id the other had just resolved, so the session kept offering a real SMS for a recording that was fine.
	//
	// FIRST occurrence wins, because that is the one MediaLightBox opens. Round 3 chose the LAST copy on the
	// theory that page order makes it the freshest, and round 4 showed that broke the very parity the dedupe
	// was added to protect: the lightbox resolves an id with `Array.find`/`findIndex` (three call sites,
	// including carousel navigation), so it renders the FIRST copy. Last-wins therefore had the banner
	// describing a row the viewer was not looking at — warning while the open recording showed as fine, or
	// the reverse. Aligning the lightbox to the latch instead would change which artifact actually opens for
	// a given id, which is user-visible behaviour well outside this fix. "Freshest" was an assumption about
	// page ordering that is not guaranteed anyway; "the row on screen" is the property that matters, since
	// the banner exists to explain what the viewer is looking at.
	/**
	 * What a row has to carry before it is allowed to RESOLVE a latched id (LSP-18016).
	 *
	 * `isIncompleteRecordingUpload` returning false is not, on its own, evidence that the upload is fine. It
	 * is also what an UNHYDRATED row returns. `IndexService.hydrateRecordings` stamps `isUploadComplete` and
	 * `isUploadAbandoned` together, from the `MasterRecording` it looked up by `sourceId` — so when that
	 * lookup misses (a recording deleted between the index write and the query, a stale index row, a
	 * cross-organization document) the search document comes back with BOTH fields absent, and the predicate
	 * fails closed on `isUploadComplete !== false`. The latch then read that "no" as the customer having
	 * finished their upload and silently dropped a warning the session had earned.
	 *
	 * This is the same "absence proves nothing" doctrine the reconcile paths below already apply to a row
	 * leaving the view, applied one level in: an absent VERDICT is as inconclusive as an absent ROW.
	 *
	 * The bar is deliberately "the server said something", not "the server said it is fine". A row carrying
	 * `isUploadComplete === false` with no `isUploadAbandoned` WAS hydrated — the async branch simply had no
	 * abandonment verdict to stamp — and the predicate's status gate is then entitled to exempt it (a
	 * recording that has gone back to PROCESSING is not an abandoned upload, and both surfaces agree it is
	 * not). Requiring an affirmative `isUploadAbandoned === false` / `isUploadComplete === true` instead
	 * would have left those latched forever, which is the opposite failure: a permanent banner offering to
	 * send a real SMS about a recording that is processing normally.
	 */
	function carriesDefinitiveUploadVerdict(document: IndexableDocument): boolean {
		// LSP-17328: an upload-status item with a boolean `abandoned` for this recording is the server saying something too, and it
		// is the answer the predicate just read, so a "not incomplete" answer from it may resolve the latch. Matched by the
		// predicate's own rule (legacyId, then id), never re-spelled here.
		if (hasDefinitiveUploadItem(document, uploadStatus.value)) return true;
		return typeof document.isUploadComplete === "boolean" || typeof document.isUploadAbandoned === "boolean";
	}

	type ResolvedLatchCandidate = {
		id: string;
		isUploadComplete: unknown;
		isUploadAbandoned: unknown;
		status: unknown;
		masterRecordingId: unknown;
		conversationId: unknown;
	};

	const latchClassificationInView = computed<{
		incomplete: string[];
		resolved: ResolvedLatchCandidate[];
		inconclusive: ResolvedLatchCandidate[];
	}>(() => {
		const openedById = new Map<string, IndexableDocument>();
		for (const document of mediaResponse.value?.documents ?? []) {
			// Matches Array.find: the first row for an id is kept and later duplicates are ignored.
			if (document?.id && !openedById.has(document.id)) openedById.set(document.id, document);
		}
		const incomplete: string[] = [];
		const resolved: ResolvedLatchCandidate[] = [];
		const inconclusive: ResolvedLatchCandidate[] = [];
		for (const [id, document] of openedById) {
			if (isIncompleteRecordingUpload(document, uploadStatus.value)) incomplete.push(id);
			else if (document.entityType === "recording" && carriesDefinitiveUploadVerdict(document)) {
				// The evidence travels WITH the id rather than being re-read at log time: the watcher below runs
				// after `mediaResponse` may already have been replaced by the next poll, and a log line that
				// describes a different response than the decision it explains is worse than no log line.
				resolved.push({
					id,
					isUploadComplete: document.isUploadComplete,
					isUploadAbandoned: document.isUploadAbandoned,
					status: document.status,
					masterRecordingId: (document as { masterRecordingId?: unknown }).masterRecordingId,
					conversationId: (document as { conversationId?: unknown }).conversationId
				});
			} else if (document.entityType === "recording") {
				// LSP-18016 follow-up (ls-heroku-ci review, MEDIUM) — the THIRD outcome of this loop, and the only
				// one that used to say nothing. This row is a recording that neither reads as an incomplete upload
				// NOR carries a definitive verdict: `isUploadComplete` and `isUploadAbandoned` are BOTH absent,
				// which is the unhydrated-row case the docstring above describes (the `sourceId` lookup in
				// `IndexService.hydrateRecordings` missed). Falling through is the correct VERDICT — an absent
				// verdict must neither latch nor resolve — but it is also exactly the state that cost LSP-18016 a
				// live investigation, so a recurrence has to be attributable from the log alone.
				//
				// Collected here and logged in the watcher below, deliberately, rather than warned inline: this is
				// a COMPUTED, re-evaluated on every timeline poll, so an inline warn would repeat the same line
				// every ~15s for as long as the row stays unhydrated and bury itself. Same reason the evidence is
				// snapshotted onto the candidate — `mediaResponse` may be replaced before the watcher reads it.
				inconclusive.push({
					id,
					isUploadComplete: document.isUploadComplete,
					isUploadAbandoned: document.isUploadAbandoned,
					status: document.status,
					masterRecordingId: (document as { masterRecordingId?: unknown }).masterRecordingId,
					conversationId: (document as { conversationId?: unknown }).conversationId
				});
			}
		}
		return { incomplete, resolved, inconclusive };
	});

	// Dedupe state for the inconclusive-evidence diagnostic below. Plain Set (not a ref): nothing renders from
	// it, so it must not participate in reactivity.
	const loggedInconclusiveRecordingIds = new Set<string>();

	watch(
		[latchClassificationInView, entityId],
		([classification], previous) => {
			// A new session starts clean: a latch belongs to the session that earned it.
			const switchedSession = !!previous && previous[1] !== entityId.value;
			if (switchedSession && observedIncompleteRecordingIds.value.size) {
				// LSP-18016: the wipe is correct, but it used to be the one latch mutation that said nothing.
				// "The banner was there and then it wasn't" has to be attributable to a session switch from the
				// log alone, otherwise it is indistinguishable from the resolve below — which is exactly the
				// ambiguity that cost a live investigation.
				console.warn(
					`[incomplete-upload] latch wiped by session switch. from=${previous?.[1] ?? "none"}, to=${entityId.value}, ` +
						`wiped=${[...observedIncompleteRecordingIds.value].join(",")}`
				);
			}
			const next = switchedSession ? new Set<string>() : new Set(observedIncompleteRecordingIds.value);
			// Resolve first, then raise. After deduping an id appears in exactly one list — but raising last
			// means that if it ever appeared in both, the warning survives rather than being silently lost.
			//
			// LSP-18016: logged per id ACTUALLY removed, not per resolved candidate. Most polls re-classify rows
			// that were never latched, and logging those would bury the one line that matters.
			classification.resolved.forEach((candidate) => {
				if (!next.delete(candidate.id)) return;
				console.warn(
					`[incomplete-upload] latch resolved in view. conversationId=${entityId.value}, ` +
						`recordingId=${candidate.id}, masterRecordingId=${String(candidate.masterRecordingId ?? "none")}, ` +
						`documentConversationId=${String(candidate.conversationId ?? "none")}, status=${String(candidate.status)}, ` +
						`isUploadComplete=${String(candidate.isUploadComplete)}, isUploadAbandoned=${String(candidate.isUploadAbandoned)}`
				);
			});
			classification.incomplete.forEach((id) => next.add(id));
			observedIncompleteRecordingIds.value = next;
			// LSP-18016 follow-up (ls-heroku-ci review, MEDIUM): the inconclusive-evidence fall-through. Once per
			// id per session, for the same reason the resolve log is per id ACTUALLY removed — the classification
			// re-runs on every poll and an unhydrated row stays unhydrated, so an unbounded line would repeat every
			// ~15s until the timeline is closed. The set is reset on a session switch so the same recording seen
			// again in a fresh session is reported again.
			if (switchedSession) loggedInconclusiveRecordingIds.clear();
			classification.inconclusive.forEach((candidate) => {
				if (loggedInconclusiveRecordingIds.has(candidate.id)) return;
				loggedInconclusiveRecordingIds.add(candidate.id);
				console.warn(
					`[incomplete-upload] inconclusive evidence, row neither latched nor resolved (unhydrated?). ` +
						`conversationId=${entityId.value}, recordingId=${candidate.id}, ` +
						`masterRecordingId=${String(candidate.masterRecordingId ?? "absent")}, ` +
						`documentConversationId=${String(candidate.conversationId ?? "absent")}, ` +
						`status=${String(candidate.status ?? "absent")}, ` +
						`isUploadComplete=${candidate.isUploadComplete === undefined ? "absent" : String(candidate.isUploadComplete)}, ` +
						`isUploadAbandoned=${candidate.isUploadAbandoned === undefined ? "absent" : String(candidate.isUploadAbandoned)} — ` +
						`the row carries NEITHER verdict, so it can neither raise nor clear the incomplete-upload banner. ` +
						`This is the LSP-18016 unhydrated-row shape: IndexService.hydrateRecordings stamps both fields together, ` +
						`so both absent means its MasterRecording lookup missed.`
				);
			});
		},
		{ immediate: true }
	);

	const hasIncompleteRecordingUploadLatched = computed(
		() => entityType.value === "conversation" && observedIncompleteRecordingIds.value.size > 0
	);

	/**
	 * Minimum time the banner stays on screen once it has appeared (LSP-18016).
	 *
	 * `isUploadComplete` / `isUploadAbandoned` are NOT stored in the search index — `IndexService` hydrates
	 * them live on every `/search/media` call — so every timeline poll re-derives the verdict and a single
	 * unlucky response can retract a banner the previous response raised. The observed symptom was a banner
	 * that appeared correctly and vanished seconds later with nothing on screen or in the logs to say why.
	 *
	 * DISPLAY LAYER ONLY. The latch above keeps updating in real time — the reconcile paths, the delete
	 * retirement and the lightbox all read it and must see the truth immediately. This only governs how soon
	 * the TEMPLATE is allowed to act on a retraction, so a genuinely finished upload clears the banner a few
	 * seconds later than it otherwise would and a one-poll flicker never renders as an appear/disappear.
	 *
	 * MUST OUTLAST ONE POLL INTERVAL (ls-heroku-ci review, MEDIUM #4). The motivating scenario above is
	 * explicitly CROSS-poll — "a single unlucky response can retract a banner the PREVIOUS response raised" —
	 * and the original 10s dwell could not deliver it: the reconcile ticks every `TIMELINE_RECONCILE_CHECK_MS`
	 * (15s) and, while an upload is pending, runs on the `TIMELINE_RECONCILE_ACTIVE_MS` (15s) cadence, so the
	 * retraction the docstring describes arrived a full 5s AFTER the dwell had already expired and the banner
	 * flickered in exactly the case the feature exists for. The value is therefore DERIVED from the poll
	 * cadence rather than written as a literal, so the two cannot silently drift apart again; the margin
	 * covers the request's own round-trip and the tick's alignment jitter (the evaluator fires on an interval
	 * that is not phase-locked to the response that raised the banner).
	 *
	 * Deliberately keyed to the ACTIVE cadence, not the idle one (`TIMELINE_RECONCILE_IDLE_MS`, 60s): a
	 * conversation showing this banner has a pending upload by definition, which is precisely the state that
	 * selects the active cadence. Keying it to the idle gap would hold a genuinely finished upload's banner on
	 * screen for over a minute to protect a case that cannot occur.
	 *
	 * The hold can still only ever delay a hide, never suppress a warning that is still true: a persistently
	 * abandoned upload re-raises the latch on the very next poll.
	 */
	const INCOMPLETE_UPLOAD_BANNER_MIN_VISIBLE_MS = TIMELINE_RECONCILE_ACTIVE_MS + 10000;

	const incompleteUploadBannerHeldVisible = ref(false);
	let incompleteUploadBannerShownAt: number | null = null;
	let incompleteUploadBannerHoldTimer: ReturnType<typeof setTimeout> | null = null;

	/**
	 * The ids the hold is standing in for (Cursor Bugbot on this commit).
	 *
	 * For the whole dwell the latch is EMPTY — that is what the dwell is: the latch has already retracted and
	 * only the display layer is lagging. So the retirement path below cannot ask the latch which recording the
	 * banner is about; without this it could only choose between ignoring every delete that lands inside the
	 * window (the bug) and dropping the dwell on any delete at all (which re-opens the flicker whenever an
	 * admin happens to delete an unrelated photo mid-window).
	 *
	 * Mirrored from the latch itself rather than from the `latched` watcher below, because that watcher fires
	 * on the boolean and so misses a second id joining an already-raised latch.
	 */
	let incompleteUploadBannerHeldIds: string[] = [];

	watch(
		observedIncompleteRecordingIds,
		(ids) => {
			if (ids.size) incompleteUploadBannerHeldIds = [...ids];
		},
		{ immediate: true }
	);

	function cancelIncompleteUploadBannerHoldTimer(): void {
		if (incompleteUploadBannerHoldTimer === null) return;
		clearTimeout(incompleteUploadBannerHoldTimer);
		incompleteUploadBannerHoldTimer = null;
	}

	function releaseIncompleteUploadBannerHold(): void {
		cancelIncompleteUploadBannerHoldTimer();
		incompleteUploadBannerHeldVisible.value = false;
		incompleteUploadBannerShownAt = null;
	}

	// A session switch drops the hold outright. The dwell exists to smooth one session's verdict flapping;
	// carrying it across a navigation would mount a banner over a conversation that never earned one — the
	// exact leak the latch wipe above prevents. Registered BEFORE the dwell watcher so that within a single
	// flush the reset happens first and the incoming session's own verdict is what starts the clock.
	watch(entityId, () => releaseIncompleteUploadBannerHold());

	watch(hasIncompleteRecordingUploadLatched, (latched) => {
		if (latched) {
			cancelIncompleteUploadBannerHoldTimer();
			// Kept from the FIRST appearance, not reset per re-raise: the dwell measures how long the banner has
			// been continuously on screen, and a hold that was never released never left the screen.
			if (incompleteUploadBannerShownAt === null) incompleteUploadBannerShownAt = Date.now();
			incompleteUploadBannerHeldVisible.value = true;
			return;
		}
		if (incompleteUploadBannerShownAt === null) {
			releaseIncompleteUploadBannerHold();
			return;
		}
		const remainingMs = INCOMPLETE_UPLOAD_BANNER_MIN_VISIBLE_MS - (Date.now() - incompleteUploadBannerShownAt);
		if (remainingMs <= 0) {
			releaseIncompleteUploadBannerHold();
			return;
		}
		console.warn(
			`[incomplete-upload] banner retraction held for its minimum dwell. conversationId=${entityId.value}, ` +
				`remainingMs=${remainingMs}, minVisibleMs=${INCOMPLETE_UPLOAD_BANNER_MIN_VISIBLE_MS}`
		);
		cancelIncompleteUploadBannerHoldTimer();
		incompleteUploadBannerHoldTimer = setTimeout(() => {
			incompleteUploadBannerHoldTimer = null;
			// Re-raised while we were holding: the latch is the truth and it says the warning still stands.
			if (hasIncompleteRecordingUploadLatched.value) return;
			releaseIncompleteUploadBannerHold();
		}, remainingMs);
	});

	const hasIncompleteRecordingUpload = computed(
		() => hasIncompleteRecordingUploadLatched.value || incompleteUploadBannerHeldVisible.value
	);

	// LSP-18264: pending photos/files also warrant the Send Reminder banner — the manual reminder endpoint
	// now covers them (countIncompleteUploads counts File + Snapshot), matching the automatic reminder.
	// Unlike recordings, a not-yet-uploaded photo/file isn't a hydrated search document with an
	// isUploadComplete/isUploadAbandoned verdict, so it can't be classified from the timeline — the
	// authoritative server-side pending count (uploadStatus) is the signal. Origin isn't filtered here, for
	// the same reason the recording gate doesn't: the endpoint is the authoritative refuse (a mobile-only
	// pending item gets a clear "uploading from the mobile app" message, never a silent no-op).
	// LSP-18264 (ls-heroku-ci #1): a photo/file killed mid-upload should nudge the recipient, but the raw
	// pending count (pendingFiles/pendingSnapshots) is true the INSTANT an upload starts, so gating the banner
	// on it fired — and let a manual Send Reminder go out — while the photo/file was still actively arriving.
	// The server-computed hasAbandonedPhotoOrFileUpload keys off the upload-staleness window (File/Snapshot
	// have no per-chunk timestamp like recordings), so it is true only once the upload is genuinely stale,
	// never while a large/slow transfer is still arriving.
	const hasAbandonedPhotoOrFileUpload = computed(
		() => entityType.value === "conversation" && uploadStatus.value?.hasAbandonedPhotoOrFileUpload === true
	);

	// LSP-18264: a recording killed mid-upload is a DB row but not a search-index document, so the
	// index-based `hasIncompleteRecordingUpload` never sees it and the banner stays hidden even while the
	// auto-reminder fires. The server-computed `hasAbandonedRecordingUpload` closes that gap precisely — it
	// carries the SAME abandoned verdict the index would (5-min quiet window), so it fires only for
	// genuinely-abandoned uploads, never for one still actively arriving.
	const hasAbandonedRecordingUpload = computed(
		() => entityType.value === "conversation" && uploadStatus.value?.hasAbandonedRecordingUpload === true
	);

	const showIncompleteUploadBanner = computed(
		() => hasIncompleteRecordingUpload.value || hasAbandonedPhotoOrFileUpload.value || hasAbandonedRecordingUpload.value
	);

	/**
	 * LSP-18274: the DURABLE shortfall recorded by contact-api when this conversation was released to
	 * Ready For Review with uploads still outstanding. Unlike `showIncompleteUploadBanner` above —
	 * recomputed live from a pending-upload predicate, and false again the moment the staleness valve
	 * stops counting — this describes the published artefact and never goes away. Projects never carry
	 * it, so it is read only for a conversation.
	 */
	const publishedMediaShortfall = computed(() =>
		entityType.value === "conversation" ? ((entity.value as Conversation | undefined)?.mediaShortfall ?? null) : null
	);

	/**
	 * F-02 residual (media-upload resilience review, Phase 1 item 2).
	 *
	 * The server now gates its own two verdicts — `hasAbandonedRecordingUpload` and
	 * `hasAbandonedPhotoOrFileUpload` are false whenever no reminder could be sent — but
	 * `showIncompleteUploadBanner` ORs in `hasIncompleteRecordingUpload`, which is derived from the
	 * SEARCH INDEX and carries no conversation-type verdict at all. So a non-ASYNC conversation with an
	 * indexed incomplete recording still mounted `IncompleteUploadBanner`, whose only action is a
	 * Send Reminder the endpoint answers with 422. That is the "button that can never work" shape this
	 * whole review exists to remove.
	 *
	 * Visibility and action are SEPARATE decisions here, exactly as they already are in
	 * `MediaLightBox.shouldShowIncompleteBanner` / `canSendUploadReminder`: the admin still learns the
	 * upload is unfinished, but the offered control is replaced by the working alternative
	 * (Resend Invitation, in the session's More options menu — `EntityActions.vue`).
	 *
	 * Undefined is treated as "allowed", not "denied": the field is absent on a response from before it
	 * shipped, and failing closed there would hide a live, working button on every cached response.
	 */
	const canSendUploadReminder = computed(() => uploadStatus.value?.canSendUploadReminder !== false);

	/**
	 * LSP-19110: whether to offer the Send Reminder CONTROL, as opposed to whether the server would accept
	 * one. Two separate questions, and only this one is role-aware.
	 *
	 * A view-only user can now see the notice (that is the point of LSP‑19110 — the 5-minute abandoned-upload
	 * verdict was being withheld from the role most likely to be staring at a short video wondering why), but
	 * must never be offered the button: every write path behind it refuses them, so it would be another
	 * instance of the "button that can never work" shape the F-02 review removed elsewhere.
	 *
	 * `canSendUploadReminder` deliberately keeps its unqualified server-verdict meaning — it still feeds the
	 * suppression diagnostic below, which is about the CONVERSATION TYPE being ineligible, not about the
	 * viewer's role. Conflating them would make that log line lie about why the button is absent.
	 */
	const canOfferUploadReminder = computed(() => !viewOnly.value && canSendUploadReminder.value);

	/**
	 * F-02 residual, second surface: the SAME verdict, threaded to `MediaLightBox`.
	 *
	 * The lightbox renders its own Send Reminder per artifact and had no eligibility gate at all, so the
	 * dead button this review removed from the session banner survived one click away — on a surface that
	 * is ALSO mounted from ProjectChecklists and SearchResults. It cannot decide for itself: the verdict
	 * comes from `/conversations/:id/upload-status`, and `LightBoxArtifact` is mapped from a search-index
	 * document that carries no conversation type.
	 *
	 * Carried WITH the conversation it describes, and only for a conversation entity: on a PROJECT page
	 * `uploadStatus` is the aggregate from `/projects/:id/upload-status`, whose verdict says nothing about
	 * any individual conversation in the carousel. Null (a project page, or a response from before the
	 * field shipped) leaves the lightbox exactly as it behaves today.
	 */
	const lightboxUploadReminderVerdict = computed(() =>
		entityType.value === "conversation" && entityId.value && typeof uploadStatus.value?.canSendUploadReminder === "boolean"
			? { conversationId: entityId.value, canSendUploadReminder: uploadStatus.value.canSendUploadReminder }
			: null
	);

	// Silent-failure rule (root CLAUDE.md): the suppression is invisible on screen — the banner simply
	// stops carrying a button — so "why is there no Send Reminder on this session?" has to be
	// answerable from the logs. Logged once per conversation per suppression episode; the endpoint is
	// polled roughly every 5s, so an unguarded log here would be a stream.
	let uploadReminderSuppressionLoggedFor: string | null = null;
	watch(
		[showIncompleteUploadBanner, canSendUploadReminder, entityId],
		([bannerVisible, reminderSendable, conversationId]) => {
			if (!bannerVisible || reminderSendable) {
				if (uploadReminderSuppressionLoggedFor === conversationId) uploadReminderSuppressionLoggedFor = null;
				return;
			}
			if (uploadReminderSuppressionLoggedFor === conversationId) return;
			uploadReminderSuppressionLoggedFor = conversationId;
			console.warn(
				`[incomplete-upload] Send Reminder suppressed: conversationId=${conversationId}, reason=server-says-not-sendable, ` +
					`indexedIncompleteRecording=${hasIncompleteRecordingUpload.value}, ` +
					`abandonedRecording=${hasAbandonedRecordingUpload.value}, abandonedPhotoOrFile=${hasAbandonedPhotoOrFileUpload.value}. ` +
					`Every reminder send path refuses this conversation type (422); the banner now shows the fact plus ` +
					`Resend Invitation as the working alternative.`
			);
		},
		{ immediate: true }
	);

	// LSP-18264: pick the banner copy that matches what is actually unfinished. A recording problem
	// (incomplete or abandoned) keeps the recording/video wording; a conversation whose only unfinished
	// uploads are photos/files gets media-neutral copy so the banner never mislabels the media type.
	const incompleteUploadBannerKind = computed<"recording" | "media">(() =>
		hasIncompleteRecordingUpload.value || hasAbandonedRecordingUpload.value ? "recording" : "media"
	);

	/**
	 * F-02 residual: the view-only variant of the banner, mounted when `canSendUploadReminder` is false.
	 * Same shape MediaLightBox already uses for a viewer who cannot act — the fact stays, the dead
	 * control goes — and it names the alternative that DOES work rather than leaving a dead end.
	 * Uses the same media-accurate wording split as the actionable banner.
	 */
	const incompleteUploadNoticeMessage = computed(() => {
		// LSP-19110: a view-only reader needs the FACT and nothing else. The editor copy below ends by naming
		// Resend Invitation, which lives in the session's More options menu — a control this role cannot see,
		// so pointing them at it is worse than saying nothing about remedies: it reads as "you failed to do
		// something" for an action that was never available. State what happened and stop.
		if (viewOnly.value) {
			return incompleteUploadBannerKind.value === "recording"
				? "This recording didn’t finish uploading from the device it was taken on, so part of it may be missing."
				: "Some files didn’t finish uploading from the device they were added on, so some may be missing.";
		}
		return incompleteUploadBannerKind.value === "recording"
			? "This recording didn’t finish uploading from the device it was taken on. We can’t send an upload reminder for this kind of session — try Resend Invitation from the session menu to get your contact back on their device."
			: "Some files didn’t finish uploading from the device they were added on. We can’t send an upload reminder for this kind of session — try Resend Invitation from the session menu to get your contact back on their device.";
	});

	/**
	 * Retire deleted recordings from the latch (break-it round 6).
	 *
	 * The latch deliberately ignores ABSENCE — a row leaving the view says nothing about the upload, which is
	 * what lets it survive a filter, a search term or a page boundary. A DELETE is not that: it is a definite
	 * outcome the app performed and observed, and both delete paths fire only after the API call resolves. So
	 * the recording is not merely out of view, it is gone.
	 *
	 * Without this the session kept offering Send Reminder for media that no longer existed — and an admin
	 * deleting the broken recording before chasing the customer again is exactly the expected workflow, so the
	 * button would have sent a real SMS and email about a recording nobody could look at.
	 *
	 * Called from ALL THREE delete routes: the lightbox's single-artifact delete, the timeline's bulk delete,
	 * and the `masterrecording_delete` socket broadcast when another user or tab removes it. Each was found
	 * separately — the first two by break-it round 6, the third by Bugbot on PR #3242 — which is the argument
	 * for one helper rather than inline cleanup: every route that makes a recording disappear has to say so,
	 * and a route that forgets is indistinguishable from a filter.
	 */
	/**
	 * Reconcile the latch against an AUTHORITATIVE timeline response (break-it round 7).
	 *
	 * The latch's rule is that absence proves nothing — a row can vanish because of a filter, a search term
	 * or a page boundary, none of which say anything about the upload. But that is only true of an
	 * INCONCLUSIVE response. An unfiltered, unsearched full reload that returned its entire result set is
	 * different: if a latched recording is not in that, it does not exist any more.
	 *
	 * Without this, a delete that happened while this tab's socket was down stayed latched forever. The
	 * socket-delete retirement never ran (the event was missed, which is precisely why the reconnect
	 * full-reload path exists), and the reload that recovered the missed event was treated as mere absence.
	 * The session went on offering a real SMS and email for media already proven gone.
	 *
	 * `documents.length >= count` is the gate, and it is a CONSERVATIVE SUFFICIENT CONDITION — not a
	 * truncation test. The distinction matters and an earlier comment here got it wrong (Cursor Bugbot):
	 * `IndexService.searchMedia` strips snapshot stubs from `documents` and deliberately does NOT lower
	 * `count` ("it reflects the true index total, and subtracting per-page removals breaks pagination math"),
	 * so a COMPLETE window can legitimately report fewer documents than its count. The gate therefore does
	 * not mean "this response was not paged".
	 *
	 * What it does guarantee is the direction that matters: stripping only ever REMOVES documents, so it
	 * makes `length >= count` harder to satisfy, never easier. The gate can be too shy, never too bold — it
	 * can fail to retire an id, but it cannot retire one that is still present. Since a false retirement
	 * would silently drop a real warning, being shy is the correct bias.
	 *
	 * The known cost, stated rather than implied: in a conversation holding stub snapshots this reconcile may
	 * do nothing, so a delete missed during a socket outage there stays latched until the next
	 * `onMasterRecordingDelete`, a resolved-in-view observation, or a revisit. Closing that needs the server
	 * to say which recordings exist rather than the client inferring it from a response shape never designed
	 * to answer the question.
	 */
	function reconcileLatchAgainstAuthoritativeTimeline(
		response: EntitySearchResponse | null | undefined,
		requestWasNarrowed: boolean
	): void {
		// LSP-18016 review follow-up (finding 1): the dwell has to be reconcilable from HERE too, not only
		// from the three direct delete routes. For the whole dwell the latch is EMPTY by construction, so an
		// empty-latch early return would have made this entire function unreachable exactly when the banner is
		// standing on the display-only hold — and a full authoritative reload (the socket-outage recovery
		// path) proving the held recording gone would still have left Send Reminder mounted over it for the
		// rest of the window. Same mirror the direct routes use: while the dwell holds, the ids it stands for
		// are the subjects to reconcile.
		const dwellHeldIds =
			incompleteUploadBannerHeldVisible.value && !observedIncompleteRecordingIds.value.size
				? incompleteUploadBannerHeldIds.filter(Boolean)
				: [];
		// Deliberately UNLOGGED: with nothing latched and no dwell standing in for it there is no question to
		// answer, and this runs on every full reload, so logging it would be pure noise. Every gate BELOW
		// this point is logged, because from here on "the banner didn't clear" is a real question and
		// CLAUDE.md's silent-failure rule applies — this PR exists because exactly that question had to be
		// reconstructed from indirect signals.
		if (!observedIncompleteRecordingIds.value.size && !dwellHeldIds.length) return;
		// `latched` keeps its name in the logs when the latch is real; a dwell-only reconcile says so.
		const latched = observedIncompleteRecordingIds.value.size ? [...observedIncompleteRecordingIds.value] : dwellHeldIds;
		if (!response) {
			console.warn(
				`[incomplete-upload] latch reconcile skipped: no timeline response. conversationId=${entityId.value}, latched=${latched.join(",")}`
			);
			return;
		}
		// Judged from the REQUEST THAT PRODUCED THIS RESPONSE, never from live state (Cursor Bugbot).
		// `searchTimeline` and `onSearchRequestChange` do not join `timelineReloadSequence`, so a slower
		// NARROWED fetch can land after the admin has cleared the search or filter. Reading live state then saw
		// an unnarrowed view, passed this guard, and retired ids that were absent only because of the old
		// restriction — silently dropping Send Reminder for an upload that is still abandoned. The caller
		// computes this before awaiting, so a late response is still judged by its own narrowing.
		if (requestWasNarrowed) {
			console.warn(
				`[incomplete-upload] latch reconcile skipped: the request that produced this response was ` +
					`narrowed, so absence proves nothing. conversationId=${entityId.value}, latched=${latched.join(",")}`
			);
			return;
		}
		const documents = response.documents ?? [];
		// Too shy rather than too bold — see above. Never concludes anything from a response that returned
		// fewer rows than its count, whether that came from paging or from stub stripping.
		if (documents.length < (response.count ?? 0)) {
			console.warn(
				`[incomplete-upload] latch reconcile skipped: response returned fewer rows than it counted ` +
					`(paging or stripped stubs), so absence proves nothing. conversationId=${entityId.value}, ` +
					`documents=${documents.length}, count=${response.count ?? 0}, latched=${latched.join(",")}`
			);
			return;
		}
		const presentIds = new Set(documents.map((item) => item.id).filter(Boolean));
		const missing = latched.filter((id) => !presentIds.has(id));
		if (!missing.length) return;
		console.warn(
			`[incomplete-upload] latch reconcile retiring ids absent from a fully-accounted response. ` +
				`conversationId=${entityId.value}, retiring=${missing.join(",")}, documents=${documents.length}, ` +
				`count=${response.count ?? 0}, source=${dwellHeldIds.length ? "banner-dwell-hold" : "latch"}`
		);
		retireIncompleteRecordingLatch(missing);
	}

	function retireIncompleteRecordingLatch(deletedIds: string[]): void {
		// LSP-18016 (Cursor Bugbot): the dwell release is evaluated HERE, on entry, and not only after a
		// successful mutation at the bottom of this function. A resolve empties the latch instantly and leaves
		// the banner standing on the display-only hold alone; a delete arriving inside that window found
		// nothing to remove, fell through the empty-latch shortcut below, and returned — so Send Reminder
		// stayed mounted over media that was already gone until the timer expired on its own. That is exactly
		// the failure the bypass at the bottom exists to prevent, reached by the one path it did not cover.
		//
		// Matched against the ids the hold is standing in for, not released blindly: deleting an unrelated
		// snapshot mid-window says nothing about the recording the banner is about, and dropping the dwell for
		// it would let the very flicker this mechanism was added to hide render again on the next poll.
		if (incompleteUploadBannerHeldVisible.value && !observedIncompleteRecordingIds.value.size) {
			const retiredFromHold = deletedIds.filter((id) => id && incompleteUploadBannerHeldIds.includes(id));
			if (retiredFromHold.length) {
				console.warn(
					`[incomplete-upload] banner dwell released early: the held recording was retired. ` +
						`conversationId=${entityId.value}, retired=${retiredFromHold.join(",")}`
				);
				releaseIncompleteUploadBannerHold();
			} else {
				// LSP-18016 review follow-up (finding 2): the dwell CORRECTLY continuing is still an outcome
				// somebody will have to explain — "I deleted it and the banner stayed" is the same complaint
				// whether the code was right or wrong. Per CLAUDE.md, every arm of the gate logs, not only the
				// one that caused the incident, and both sides are needed to tell them apart.
				console.warn(
					`[incomplete-upload] banner dwell kept: the delete did not match the held recording. ` +
						`conversationId=${entityId.value}, deleted=${deletedIds.filter(Boolean).join(",") || "none"}, ` +
						`held=${incompleteUploadBannerHeldIds.join(",") || "none"}`
				);
			}
		}
		// Unlogged when there is nothing latched — no latch, no question to answer, and the delete paths call
		// this on every delete. Once a latch EXISTS, every outcome below is logged, per CLAUDE.md's
		// silent-failure rule: "the banner is still offering a reminder for something I deleted" must be
		// answerable from one log line rather than reconstructed.
		if (!observedIncompleteRecordingIds.value.size) return;
		if (!deletedIds.length) {
			console.warn(
				`[incomplete-upload] latch retirement called with no ids. conversationId=${entityId.value}, ` +
					`latched=${[...observedIncompleteRecordingIds.value].join(",")}`
			);
			return;
		}
		const next = new Set(observedIncompleteRecordingIds.value);
		const retired: string[] = [];
		for (const id of deletedIds) {
			if (id && next.delete(id)) retired.push(id);
		}
		if (!retired.length) {
			console.warn(
				`[incomplete-upload] latch retirement matched nothing. conversationId=${entityId.value}, ` +
					`requested=${deletedIds.join(",")}, latched=${[...observedIncompleteRecordingIds.value].join(",")}`
			);
			return;
		}
		console.warn(
			`[incomplete-upload] latch retired. conversationId=${entityId.value}, retired=${retired.join(",")}, ` +
				`remaining=${[...next].join(",") || "none"}`
		);
		observedIncompleteRecordingIds.value = next;
		// LSP-18016: retirement BYPASSES the banner's minimum dwell. The dwell exists for the one retraction
		// source that can flap — the per-poll, live-hydrated verdict in `latchClassificationInView` — and every
		// caller of this function is the opposite of that: a delete the app performed and observed, or an
		// authoritative response that proves the recording is gone. Holding a Send Reminder button over media
		// that has just been deleted would be a worse bug than the flicker this dwell was added to hide.
		if (!next.size) releaseIncompleteUploadBannerHold();
	}

	// LSP-16618: the "- N Pending" badge beside "Media (N)" must reflect what the user can
	// actually see queuing. The server uploadStatus (pendingMediaCount) lags behind the
	// client-side skeleton placeholders (pendingImages/pendingVideos) right after an upload
	// starts — before the server has created the pending stubs — so a freshly added placeholder
	// would otherwise show with no count. Take the max so the badge never under-reports a
	// visible placeholder, and never double-counts (both sides describe the same in-flight media).
	// Snapshots + videos only; documents are not media-timeline items.
	const displayedPendingMediaCount = computed(() => {
		return Math.max(pendingMediaCount.value, pendingImages.value + pendingVideos.value);
	});

	// LSP-15966 (Cursor Bugbot, post-merge): the helper returns true when the
	// fetch succeeded (or was deliberately skipped for non-conversation entities)
	// and false when the request errored. Background polling and event-driven
	// refreshes ignore the return value — a transient 5xx during background
	// poll is not worth surfacing. handleRunReport DOES consult the return value
	// so a failed click-time refresh fails closed instead of opening Sparky on
	// stale state (the exact partial-report regression LSP-15966 closes).
	function shouldPollUploadStatus(): boolean {
		if (authSession.sessionLost) return false;
		if (uploadStatusPollDisabled) return false; // LSP-16072: deleted entity — never poll again
		if (!entityId.value) return false;
		if (entityType.value !== "conversation" && entityType.value !== "project") return false;
		if (isAuthenticated.value) return true;
		// LSP-16142: only shared projects have a public upload-status mirror.
		return entityType.value === "project" && !!shareId.value;
	}

	function hasPendingUploads(): boolean {
		const status = uploadStatus.value;
		if (!status) return false;
		return (
			!status.isComplete ||
			(status.pendingFiles ?? 0) > 0 ||
			(status.pendingSnapshots ?? 0) > 0 ||
			(status.pendingRecordings ?? 0) > 0 ||
			(status.pendingTranscoding ?? 0) > 0
		);
	}

	function hasRecentUploadActivity(): boolean {
		return Date.now() - lastUploadActivityAt < UPLOAD_ACTIVE_WINDOW_MS;
	}

	// LSP-16086: pick the next poll delay from current state — fast when the socket is down
	// (polling is then the only freshness channel) or an upload is pending/recently active;
	// otherwise a slow idle heartbeat so connected idle tabs stay quiet.
	function nextUploadPollDelay(): number {
		if (!Socket.connected) return UPLOAD_POLL_FAST_MS;
		if (hasPendingUploads() || hasRecentUploadActivity()) return UPLOAD_POLL_FAST_MS;
		return UPLOAD_POLL_IDLE_MS;
	}

	// LSP-16659: documents live in the media grid now, so "reload the documents" means reload the grid
	// and the ready-PDF count Sparky gates on. `pendingDocumentReloadPromise` (awaited by handleRunReport)
	// therefore still means "the new PDF is in the grid and counted".
	function reloadDocumentsAfterUploadComplete(): void {
		const reloadPromise = Promise.all([deltaReloadTimeline(), refreshReadyPdfDocumentCount()])
			.then(() => undefined)
			.catch((error) => {
				logError("Failed to reload documents after upload completion", error);
			});
		pendingDocumentReloadPromise = reloadPromise;
		void reloadPromise.finally(() => {
			if (pendingDocumentReloadPromise === reloadPromise) {
				pendingDocumentReloadPromise = null;
				uploadHandedOffOnMount = false;
				handedOffUploadConversationId = null;
				handedOffUploadEntityId = null;
			}
		});
	}

	function logFlushStaleCountersSkipped(reason: "uploading" | "upload-status-not-complete"): void {
		logInfo("[Upload] flushStaleCounters skipped", {
			reason,
			entityId: entityId.value,
			entityType: entityType.value,
			isUploading: isUploading.value,
			latestUploadStatusFetchIsComplete,
			pendingImages: pendingImages.value,
			pendingDocs: pendingDocs.value,
			pendingVideos: pendingVideos.value
		});
	}

	/**
	 * Review follow-up (upload-handoff race). ActionUploadPhoto hands an in-flight upload back to this
	 * page by navigating to it, but every EventBus signal for that upload has already fired by then:
	 * "UploadStarted" is emitted synchronously inside openDialog BEFORE the router push, "close-menu"
	 * has no parent on the standalone /upload route (so "conversation-created" is never relayed), and
	 * the FileUploaded/UploadFinished listeners below are registered only AFTER
	 * `await initializeComponent(...)` — a window that spans the entity + timeline fetches, which a
	 * small file can easily upload and finish inside.
	 *
	 * So the handoff is READ from the url instead of heard. Set when this page mounts for a project
	 * whose upload just started; it makes the completion path reload documents even though no
	 * FileUploaded event ever incremented pendingDocs.
	 */
	let uploadHandedOffOnMount = false;
	/** LSP-16659: a document upload was counted since the last flushStaleCounters (see there). */
	let documentUploadedSinceLastFlush = false;
	/**
	 * The handed-off conversation survives here because `projectConversationIds` is overwritten twice
	 * during a load — once to `[entityId]` at the top of initializeComponent, and again from the
	 * server's `project.Conversations` once it arrives. A conversation created seconds ago is very
	 * likely absent from that server list, so without holding it separately and re-applying it at both
	 * seeds, the just-uploaded media drops straight back out of the project's filter.
	 */
	let handedOffUploadConversationId: string | null = null;
	/**
	 * The entity the handoff BELONGS to. Review follow-up: the id was cleared only by
	 * reloadDocumentsAfterUploadComplete — a *completion* event — so its lifetime was "until this
	 * upload finishes" when it needed to be "while we are on this entity". Those diverge the moment
	 * the user switches projects, and the id then leaked into the next project's conversation filter:
	 * one project's upload showing up in another is a cross-entity visibility bug, worse than the
	 * display glitch the union was added to fix. Scoping by owner is safer than trying to clear at
	 * exactly the right moment, because a value that does not apply can never leak.
	 */
	let handedOffUploadEntityId: string | null = null;
	/** Whether the handed-off conversation belongs to the entity currently on screen. */
	function handedOffConversationApplies(): boolean {
		return !!handedOffUploadConversationId && handedOffUploadEntityId === entityId.value;
	}

	/**
	 * THE rule, in one place: "the server's list, plus the handed-off conversation if it is not in it
	 * yet". Expressed once for ids and once for report rows because the two seeds carry different
	 * shapes, but both gate on the same handedOffConversationApplies().
	 */
	function withHandedOffConversation(conversationIds: string[]): string[] {
		if (!handedOffConversationApplies() || conversationIds.includes(handedOffUploadConversationId as string)) {
			return conversationIds;
		}
		return [...conversationIds, handedOffUploadConversationId as string];
	}

	function withHandedOffReportConversation<T extends { id: string }>(reports: T[]): T[] {
		if (!handedOffConversationApplies() || reports.some((report) => report.id === handedOffUploadConversationId)) {
			return reports;
		}
		// Preserve whatever conversationCreated already pushed — it holds the fetched conversation,
		// which this seed cannot reconstruct. If it has not resolved yet its own post-await push still
		// lands (and is idempotent), so both orderings end up with exactly one row.
		const alreadyFetched = reportConversations.value.find((report) => report.id === handedOffUploadConversationId);
		return alreadyFetched ? [...reports, alreadyFetched as unknown as T] : reports;
	}

	/**
	 * Single seeding path for the two adjacent server-driven assignments, so the handoff rule cannot
	 * be applied to one and forgotten on the other (it already had been: the id list was unioned, the
	 * report list was not).
	 */
	function seedConversationsFromServer(serverConversations: { id: string }[] | undefined): void {
		projectConversationIds.value = withHandedOffConversation(
			serverConversations?.map((conversation) => conversation.id) ?? [entityId.value]
		);
		reportConversations.value = withHandedOffReportConversation(serverConversations?.map(toReportConversation) ?? []);
	}

	/**
	 * Called from initializeComponent AFTER reset() and after the id seed, alongside the
	 * ?pendingImages/Docs/Videos handoff it mirrors. Returns the consumed id so the caller can strip
	 * the query key in the same router.replace those params already use.
	 *
	 * Running this at mount-time (before `await initializeComponent`) was inert: reset() clears
	 * isUploading / uploadMayFinalizeAfterIdle immediately afterwards, and the id seed overwrites
	 * whatever conversationCreated pushed.
	 */
	function consumeUploadHandoff(): string {
		const handedOffConversationId = route.query.uploadStarted;
		const rawId = Array.isArray(handedOffConversationId) ? handedOffConversationId[0] : handedOffConversationId;
		if (typeof rawId !== "string" || rawId.trim().length === 0) {
			return "";
		}
		const conversationId = rawId.trim();
		// The handoff is a PROJECT-flow concept: ActionUploadPhoto only ever navigates to
		// { name: "project", params: { id } } with this query, so a conversation entity carrying
		// ?uploadStarted is a stale bookmark or a hand-edited URL, never a real hand-off.
		//
		// Refusing here matters because the ownership check downstream cannot catch it: it compares the
		// fetched conversation's projectId against the MOUNTED project, and a conversation entity has no
		// mounted project, so the refusal branch is unreachable there. Adopting anyway put the foreign id
		// into projectConversationIds, which on a conversation entity IS the media search's parentIds
		// (see the search request below) -- so another conversation's media rendered in this timeline.
		if (entityType.value !== "project") {
			console.warn("EntityDetailPage.consumeUploadHandoff: ignoring an ?uploadStarted handoff outside a project", {
				tags: { method: "consumeUploadHandoff", file: "EntityDetailPage" },
				extra: { conversationId, entityId: entityId.value, entityType: entityType.value }
			});
			return "";
		}
		if (hasConsumedUploadHandoff(conversationId)) {
			// Already adopted. Tracked at MODULE scope, not per instance: a param-only project switch
			// keeps the query AND re-creates this component, so per-instance state would let the next
			// project adopt the previous project's upload as its own.
			//
			// ls-heroku-ci: this is the one exit in this function that drops a real handoff and said
			// nothing -- the two branches either side of it both log. This PR exists because a dropped
			// upload left no trace, so the branch that drops one silently is exactly the one that has to
			// name itself.
			logInfo("[Upload] consumeUploadHandoff: handoff already consumed for this conversation; ignoring", {
				conversationId,
				entityId: entityId.value,
				entityType: entityType.value
			});
			return "";
		}
		markUploadHandoffConsumed(conversationId);
		uploadHandedOffOnMount = true;
		handedOffUploadConversationId = conversationId;
		handedOffUploadEntityId = entityId.value;
		logInfo("[Upload] EntityDetailPage: adopting an upload handed off through the navigation", {
			conversationId,
			entityId: entityId.value,
			entityType: entityType.value
		});
		// Stands in for the "UploadStarted" this page was never mounted to hear. Deliberately NOT
		// markUploadInProgress(): that sets uploadMayFinalizeAfterIdle = false, betting a real
		// UploadFinished will still arrive — but the whole premise of this handoff (see the doc
		// comment above) is that the page was not mounted to hear it and it may already have fired.
		// With that bet lost, canFinalizeActiveUploadFromServer() can never be satisfied,
		// finalizeActiveUpload() never runs, isUploading latches true and flushStaleCounters()
		// early-returns forever. skipActivityStamp keeps hasRecentUploadActivity() false so idle
		// finalization fires on the first server poll that reports complete — the same reasoning as
		// the ?pendingImages/Docs/Videos path below (LSP-16677).
		markUploadInProgressWithoutTerminalEvent({ skipActivityStamp: true });
		// Stands in for the "conversation-created" that ProjectActionFAB never relayed, so the new
		// upload conversation is in the report filter without waiting for a full page reload.
		void conversationCreated(conversationId).catch((error) => {
			console.warn("EntityDetailPage.consumeUploadHandoff: could not register the handed-off conversation", {
				tags: { method: "consumeUploadHandoff", file: "EntityDetailPage" },
				extra: { conversationId, error }
			});
		});
		return conversationId;
	}

	function flushStaleCounters(): void {
		if (isUploading.value) {
			logFlushStaleCountersSkipped("uploading");
			return;
		}
		if (!latestUploadStatusFetchIsComplete) {
			logFlushStaleCountersSkipped("upload-status-not-complete");
			return;
		}
		// `pendingDocs` is fed ONLY by FileUploaded events. An upload handed off through the navigation
		// can complete before this page is listening, leaving the counter at 0 and the Documents tab
		// showing the pre-upload load forever. The handoff flag is the read-state equivalent.
		// LSP-16659: `documentUploadedSinceLastFlush` too — the grid reconcilers now retire pendingDocs as
		// document rows arrive, so the counter can already be 0 here while the ready-PDF count Sparky gates
		// on has not been refreshed for the new file yet.
		const hadPendingDocs = pendingDocs.value > 0 || uploadHandedOffOnMount || documentUploadedSinceLastFlush;
		pendingImages.value = 0;
		pendingDocs.value = 0;
		pendingVideos.value = 0;
		uploadHandedOffOnMount = false;
		documentUploadedSinceLastFlush = false;
		if (hadPendingDocs) {
			reloadDocumentsAfterUploadComplete();
		}
	}

	function canFinalizeActiveUploadFromServer(options: FetchUploadStatusOptions = {}): boolean {
		const canFinalizeFromIdle =
			(options.allowIdleUploadFinalization === true || uploadMayFinalizeAfterIdle) && !hasRecentUploadActivity();
		return latestUploadStatusFetchIsComplete && (uploadFinishedSinceLastStart || canFinalizeFromIdle);
	}

	function finalizeActiveUpload(): void {
		isUploading.value = false;
		uploadMayFinalizeAfterIdle = false;
	}

	function markUploadActivity(): void {
		isUploading.value = true;
		latestUploadStatusFetchIsComplete = false;
		lastUploadActivityAt = Date.now();
	}

	function markUploadInProgress(): void {
		uploadMayFinalizeAfterIdle = false;
		markUploadActivity();
		uploadFinishedSinceLastStart = false;
	}

	function markUploadInProgressWithoutTerminalEvent(options: { skipActivityStamp?: boolean } = {}): void {
		uploadMayFinalizeAfterIdle = true;
		isUploading.value = true;
		latestUploadStatusFetchIsComplete = false;
		if (!options.skipActivityStamp) {
			lastUploadActivityAt = Date.now();
		}
		// When skipActivityStamp is true (URL-param path), hasRecentUploadActivity() stays false so
		// idle finalization fires as soon as the first server poll returns complete, preventing Sparky
		// from being blocked by a ?pendingImages/Docs/Videos URL for an already-finished upload (LSP-16677).
		uploadFinishedSinceLastStart = false;
	}

	function getEntityContextSnapshot(): EntityContextSnapshot {
		return {
			resetSequence: entityResetSequence,
			entityId: entityId.value,
			entityType: entityType.value
		};
	}

	function isEntityContextCurrent(context: EntityContextSnapshot): boolean {
		return (
			context.resetSequence === entityResetSequence && context.entityId === entityId.value && context.entityType === entityType.value
		);
	}

	// LSP-16086: self-rescheduling poll (replaces a fixed setInterval) so the cadence adapts
	// and the tab stays quiet while idle. Paused while hidden (resumed on visibilitychange)
	// and permanently off once the entity 404s (LSP-16072).
	function scheduleUploadStatusPoll(): void {
		// LSP-16086 (Andriy F-4): bail if the component is unmounted. Covers both the
		// initial caller (post-await race in onMounted) AND the self re-arm inside the
		// setTimeout callback — without this, a unmount-during-fetch leaves the re-arm
		// scheduling a fresh setTimeout that outlives the component.
		if (isComponentUnmounted) return;
		clearUploadStatusTimer();
		if (uploadStatusPollDisabled || !shouldPollUploadStatus()) return;
		if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
		const scheduledEntityId = entityId.value;
		const scheduledEntityType = entityType.value;
		uploadStatusTimer = setTimeout(async () => {
			uploadStatusTimer = null;
			await fetchUploadStatus();
			if (scheduledEntityId === entityId.value && scheduledEntityType === entityType.value) {
				scheduleUploadStatusPoll();
			}
		}, nextUploadPollDelay());
	}

	// Returns true (fresh commit or preserved-state success), false (real failure), or
	// "superseded" — a NEWER sibling request owns the outcome (LSP-17772 QA: the run-report
	// verify loop must RETRY that case, not fail closed; see verifyUploadStatusForReport).
	async function fetchUploadStatus(options: FetchUploadStatusOptions = {}): Promise<boolean | "superseded" | "unmounted"> {
		if (authSession.sessionLost) return false;
		if (!entityId.value) return true;
		if (entityType.value !== "conversation" && entityType.value !== "project") return true;
		// No public upload-status route for conversations — skip rather than 401 → Auth0.
		if (!isAuthenticated.value && entityType.value !== "project") return true;
		// LSP-15966: projects need aggregate pending counts across all their
		// conversations. /projects/:id/upload-status is the mirror endpoint
		// to /conversations/:id/upload-status — same response shape.
		const path = entityType.value === "project" ? "projects" : "conversations";
		// Unauthenticated shared-project visitors use the shareId-gated public mirror
		// (URL ?shareId=..., not the authenticated /projects/:id/upload-status route).
		const isUnauthProject = !isAuthenticated.value && entityType.value === "project";
		if (isUnauthProject && !shareId.value) return true;
		const url = isUnauthProject
			? `/projects/${entityId.value}/public/${shareId.value}/upload-status`
			: `/${path}/${entityId.value}/upload-status`;
		// LSP-16086 (Bugbot #4): capture the entityId AT REQUEST TIME so a slow 404
		// arriving after the user navigated to a different entity doesn't latch the
		// LSP-16072 disable-flag against the NEW entity.
		// LSP-16326: also capture a monotonic request id so an older same-entity
		// response that resolves after a newer one is dropped instead of overwriting.
		const requestSequence = ++uploadStatusRequestSequence;
		const requestedEntityId = entityId.value;
		const requestedEntityType = entityType.value;
		// Merge resolution (LSP-15573 × LSP-17772 QA round 2): reset() bumps entityResetSequence AND
		// uploadStatusRequestSequence, and under the previous started-sequence gate that second bump was
		// what invalidated an in-flight fetch. The commit-monotonic gate below deliberately stopped
		// reading uploadStatusRequestSequence — which silently made reset()'s invalidation a no-op, so a
		// response in flight across a reset re-committed into freshly-cleared state. initializeComponent()
		// calls reset() BEFORE entityId is repointed, so the id/type arms cannot cover that window either.
		// Captured here and checked alongside the commit gate: overlapping requests still commit (the
		// LSP-17772 fix is untouched), a reset still cancels (the pre-existing guarantee is restored).
		// Every sibling guard in this file still checks resetSequence via isEntityContextCurrent.
		const requestedResetSequence = entityResetSequence;
		// LSP-16326: both supersession gates (post-success and post-error) drop a stale response
		// silently; log via logInfo (not console.debug) so the breadcrumb actually reaches Datadog —
		// console.debug/info are dropped by forwardConsoleLogs: ["error","warn"]. Expected race, info-level.
		const logSupersededDrop = () =>
			logInfo("[Upload] fetchUploadStatus superseded; dropping stale response", {
				requestSequence,
				uploadStatusRequestSequence,
				// Staleness is judged against the last COMMIT, not the last started request
				// (LSP-17772 QA round 2) — this field is the one that explains a drop.
				uploadStatusCommittedSequence,
				requestedResetSequence,
				entityResetSequence,
				requestedEntityId,
				entityId: entityId.value,
				requestedEntityType,
				entityType: entityType.value
			});
		try {
			// LSP-16314: skip the _cacheBust query param so this 5s poll reuses the
			// cached CORS preflight instead of firing an OPTIONS before every GET.
			// The upload-status routes send `Cache-Control: no-store`, so the stable
			// URL still returns a fresh response.
			const response = await api.get(url, isUnauthProject, true);
			if (isComponentUnmounted) {
				// Distinct from a sibling race (review): a torn-down component is NOT retryable —
				// the verify loop fails fast on this sentinel instead of burning bounded retries.
				logSupersededDrop();
				return "unmounted";
			}
			// LSP-17772 (QA round 2): compare against the last COMMITTED sequence, not the last
			// STARTED one. Judging staleness by "a newer request began" starved the run-report
			// verify loop whenever fetches overlapped (verify retries + the 5s poll + the upload
			// event handlers): every response was dropped, nothing ever committed, and all 3
			// bounded retries exhausted. A response is stale only when a same-or-newer response
			// has ALREADY COMMITTED — commits stay monotonic (the actual LSP-16326 guarantee)
			// while overlapping in-flight requests no longer cancel each other out.
			if (
				requestSequence <= uploadStatusCommittedSequence ||
				requestedResetSequence !== entityResetSequence ||
				requestedEntityId !== entityId.value ||
				requestedEntityType !== entityType.value
			) {
				logSupersededDrop();
				return "superseded";
			}
			// Guard against a no-content response wiping the last-known state.
			// Important for the LSP-15966 interlock: handleRunReport calls this
			// synchronously, and a transient empty response should not flip a
			// real pending-state banner off mid-click.
			if (response) {
				latestUploadStatusFetchIsComplete = response.isComplete === true;
				// LSP-16618: capture the media-pending counts BEFORE committing the new status
				// so we can detect a drop (uploads finished / transcoded) and refresh the timeline
				// immediately — the now-available media replaces its skeleton placeholder without
				// waiting for a socket event or the periodic reconcile. LSP-21085: checklist
				// attachments are created by the same upload completion, so refresh that bounded
				// read model in the same fallback path. Otherwise Media updates from this poll while
				// the open checklist stays stale until a page reload.
				const previousPendingMedia = pendingMediaCount.value;
				const previousPendingRecording = pendingRecordingCount.value;
				uploadStatus.value = response;
				// LSP-16326: record that THIS request committed, so handleRunReport can treat a
				// superseded-but-freshly-committed state as verified rather than failing closed.
				uploadStatusCommittedSequence = requestSequence;
				// LSP-16086: keep the poll fast while there's pending/in-flight work.
				if (hasPendingUploads()) lastUploadActivityAt = Date.now();
				if (canFinalizeActiveUploadFromServer(options)) {
					finalizeActiveUpload();
				}
				flushStaleCounters();
				// LSP-16618: keep the timeline converging on the server while media is pending.
				// Refresh on EVERY poll where media is pending now, AND once more on the poll that
				// observes the count reach 0 — that final poll is exactly when the last item becomes
				// available, so a drop-only trigger leaves the timeline one refresh short. Once the
				// count is 0 and was already 0, stop refreshing the timeline (the upload-status poll
				// itself keeps running via scheduleUploadStatusPoll). The refresh is routed through
				// the coalescing scheduler, which is lightbox- and visibility-aware.
				const pendingMediaNow = pendingMediaCount.value;
				if (pendingMediaNow > 0 || previousPendingMedia > 0) {
					// A recording's `created` (session start) can predate the newest loaded item, so a
					// created-windowed delta would miss it — use a FULL reload whenever a recording is
					// (or was) part of the pending set; a snapshot-only refresh stays a cheap delta.
					const involvesRecording = pendingRecordingCount.value > 0 || previousPendingRecording > 0;
					const preserveVisibleUploadMedia = Date.now() <= preserveUploadMediaOnReconnectUntil;
					timelineReloadScheduler.schedule(
						{ timeline: true, checklists: true },
						preserveVisibleUploadMedia ? "delta" : involvesRecording ? "full" : "delta"
					);
				}
			} else {
				latestUploadStatusFetchIsComplete = uploadStatus.value?.isComplete === true;
				if (!isUploading.value || !hasRecentUploadActivity()) {
					if (canFinalizeActiveUploadFromServer(options)) {
						finalizeActiveUpload();
					}
					flushStaleCounters();
				}
			}
			return true;
		} catch (error) {
			// LSP-16072: a 404 means the conversation/project was deleted — stop polling
			// permanently (and don't let visibility-return resume it) so we don't 404 every
			// 5s forever. Other errors (transient 5xx, network) are left to retry.
			const status = (error as { code?: number; status?: number } | null)?.code ?? (error as { status?: number } | null)?.status;
			if (isComponentUnmounted) {
				// Distinct from a sibling race (review): a torn-down component is NOT retryable —
				// the verify loop fails fast on this sentinel instead of burning bounded retries.
				logSupersededDrop();
				return "unmounted";
			}
			// Same commit-monotonic staleness rule as the success path (LSP-17772 QA round 2):
			// an error is stale only if a same-or-newer response already committed, or the
			// entity changed under us — a merely-overlapping newer request doesn't erase it.
			if (
				requestSequence <= uploadStatusCommittedSequence ||
				requestedResetSequence !== entityResetSequence ||
				requestedEntityId !== entityId.value ||
				requestedEntityType !== entityType.value
			) {
				logSupersededDrop();
				return "superseded";
			}
			// LSP-16086 (Bugbot #4): only latch the LSP-16072 disable-flag if the 404 is
			// for the entity we are STILL viewing. A stale 404 for a prior entityId
			// after in-place navigation must not poison the new entity's poll.
			if (status === 404) {
				logInfo("[Upload] upload-status 404 — entity gone, disabling poll", {
					entityId: entityId.value,
					entityType: entityType.value
				});
				latestUploadStatusFetchIsComplete = false;
				uploadStatusPollDisabled = true;
				clearUploadStatusTimer();
				// ls-heroku-ci: the poll is the only thing that can ever clear isUploading, so switching it
				// off permanently would strand the spinner on forever -- the exact "stuck uploading, nothing
				// visible" symptom this ticket is about. Not finalizeActiveUpload(): that reloads the entity's
				// documents, and the 404 just told us the entity is gone. Only drop the indicator.
				isUploading.value = false;
				return false;
			}
			latestUploadStatusFetchIsComplete = false;
			if (isTransientAuthTokenError(error)) {
				// LSP-17079: getAccessToken() now THROWS on a transient/offline token failure instead of
				// returning a null bearer. This 5s poll just skips the cycle and the next poll retries, so
				// log at info (not error) — a routine offline blip must not inflate the Datadog error rate.
				logInfo("[Upload] fetchUploadStatus skipped; transient auth token failure (will retry next poll)", {
					entityId: entityId.value,
					entityType: entityType.value
				});
				return false;
			}
			// LSP-18854: the sibling of the branch above, and the one LSP-17079 missed. Its comment states
			// the intent — "a routine offline blip must not inflate the Datadog error rate" — but the gate
			// only recognised TransientAuthTokenError. Going offline surfaces a plain TypeError from the
			// fetch layer, so it fell through to logError once per 5s poll: production RUM shows a single
			// session emitting 2399 of these against one entityId. Control flow is unchanged (still
			// returns false, still retries next poll); only the severity is corrected.
			if (isOfflineFailure(error)) {
				logInfo("[Upload] fetchUploadStatus skipped; offline/network failure (will retry next poll)", {
					entityId: entityId.value,
					entityType: entityType.value
				});
				return false;
			}
			logError("[Upload] fetchUploadStatus failed", error, { entityId: entityId.value, entityType: entityType.value });
			return false;
		} finally {
			// LSP-16842: mark the attempt settled however it ended — committed, empty response, or
			// error. Consumers that must not mistake "not known yet" for "nothing pending" gate on
			// this rather than on uploadStatus being non-null, because a no-content response is
			// deliberately not committed (it must not wipe the last-known state) and would
			// otherwise look identical to an in-flight request forever.
			uploadStatusFetchSettled.value = true;
		}
	}

	function clearUploadStatusTimer(): void {
		if (uploadStatusTimer) {
			clearTimeout(uploadStatusTimer);
			uploadStatusTimer = null;
		}
	}

	async function restartUploadStatusPollForCurrentEntity(options: RestartUploadStatusPollOptions = {}): Promise<void> {
		clearUploadStatusTimer();
		if (!shouldPollUploadStatus()) return;
		const restartingEntityId = entityId.value;
		const restartingEntityType = entityType.value;
		await fetchUploadStatus(options);
		if (isComponentUnmounted) return;
		if (restartingEntityId !== entityId.value || restartingEntityType !== entityType.value) return;
		scheduleUploadStatusPoll();
	}

	const props = defineProps<{
		projectId?: string;
		shareId?: string;
	}>();

	// LSP-16768: track the active tab by its STABLE id, not by its numeric index.
	// The `tabs` list reorders across the smAndDown breakpoint (desktop leads with
	// "About", mobile leads with "Media"), so a raw index would point at a
	// different tab after a window resize and silently switch tabs on the user.
	// `activeTab` stays an index-backed v-model for <v-tabs>/<v-window> (which
	// address items positionally), but it derives that index from `activeTabId`
	// against the CURRENT tabs array, so the same tab stays selected when the
	// layout flips. The getter reads `tabs` lazily (declared further down), so
	// nothing must read `activeTab.value` synchronously during setup before that
	// declaration — watchers below intentionally watch `activeTabId` to avoid it.
	const activeTabId = ref<string>("");

	const activeTab = computed<number>({
		get() {
			const index = tabs.value.findIndex((tab) => tab.id === activeTabId.value);
			return index === -1 ? 0 : index;
		},
		set(index: number) {
			const tab = tabs.value[index];
			if (tab) activeTabId.value = tab.id;
		}
	});

	const isMobileLayout = computed<boolean>(() => {
		return display.smAndDown.value;
	});

	// Tab scroll tracking
	const tabsScrollable = ref(false);
	const tabsAtStart = ref(true);
	const tabsAtEnd = ref(false);
	let tabsContainer: HTMLElement | null = null;
	let handleTabsContainerScroll: (() => void) | null = null;
	let tabsGeometryObserver: ResizeObserver | null = null;
	// Set in onBeforeUnmount — see isTabStripAlive for why isComponentUnmounted alone is too late.
	let tabStripTornDown = false;
	let tabsGeometryFrame = 0;
	let handleTabsViewportChange: (() => void) | null = null;
	let teardownTabStripDebugOverlay: (() => void) | null = null;

	// EventBus handler for file uploads
	const fileUploadedHandler = (counts: unknown) => {
		handleUploadCounts(counts as FileUploadCounts);
	};

	const uploadFinishedHandler = async () => {
		const uploadContext = getEntityContextSnapshot();
		uploadFinishedSinceLastStart = true;
		uploadFinishedAt = Date.now();
		uploadMayFinalizeAfterIdle = false;
		preserveUploadMediaOnReconnectUntil = Date.now() + UPLOAD_FINISHED_RELOAD_DELAY_MS + RELOAD_DEBOUNCE_MS;
		// LSP-15966: refresh server-side upload status the moment an upload
		// finishes so the banner clears without waiting for the next 5s poll.
		// Done in parallel with the timeline reload that already exists here.
		await fetchUploadStatus();
		if (!isEntityContextCurrent(uploadContext)) return;
		if (lightboxOpen.value) {
			pendingReloads.value.timeline = true;
			return;
		}
		reloadTimelineAfterUploadFinished();
	};

	// LSP-15966: fire the moment FileUploadDialog opens (EventBus is emitted at
	// FileUploadDialog.vue:206 right after the user picks files). Pulling
	// upload status immediately lets the banner render before the 5s poll
	// catches up — important on fast uploads that otherwise complete inside
	// one polling window.
	const uploadStartedHandler = () => {
		// LSP-16086: a local upload starting bumps the poll to its fast cadence immediately.
		markUploadInProgress();
		fetchUploadStatus();
		scheduleUploadStatusPoll();
	};

	// Checklist search request
	const checklistSearchRequest = ref<SearchRequest | null>(null);

	// Centralized confirm-leave and back handling — returns true to navigate, false to stay
	async function promptSaveBeforeLeave(): Promise<boolean> {
		if (bypassUnsavedChangesCheck.value) {
			return true;
		}
		const canLeaveComment = await entityCommentsRef.value?.confirmLeaveWithUnsavedComment?.();
		if (canLeaveComment === false) {
			return false;
		}
		const canLeaveChecklist = await entityChecklistsRef.value?.confirmLeaveWithUnsavedChecklist?.();
		if (canLeaveChecklist === false) {
			return false;
		}
		if (!isDirty.value) {
			return true;
		}
		// When the About form CANNOT be saved, do not offer "Save" — it will fail and drop the user back
		// where they started with a toast that doesn't say what's wrong. Jake hit exactly this: "if I hit
		// save it complains it couldn't and goes no where." Warn-don't-cap (LSP-16465) made it common by
		// design: an over-limit note is now a normal state the user types through, where before it was
		// hard-capped and the form was never length-invalid.
		//
		// Use the repo's existing invalid-form prompt (Cancel / Don't save) — the same one
		// TemplateEditPage already uses — and name the offending field and count so the message is
		// actionable rather than "some fields need corrections". The message keeps the shared
		// prompt's voice (LSP-16869): the blocker, then the same "Would you like to…?" question.
		const blockingReason = entityAboutRef.value?.blockingValidationReason ?? null;
		if (blockingReason) {
			console.warn("EntityDetailPage.promptSaveBeforeLeave: About form cannot be saved — offering discard/keep-editing", {
				entityId: entity.value?.id ?? null,
				entityType: entityType.value,
				blockingReason
			});
			const discardResult = await confirmDiscardChanges(instance?.proxy, {
				...INVALID_CHANGES_DIALOG_TEXT,
				message: `We can't save because ${blockingReason}. Would you like to leave without saving?`
			});
			// Discard -> leave (dropping the unsavable edit is the user's explicit choice); anything else
			// keeps them here to fix it.
			return discardResult === true;
		}

		const result = await (instance?.proxy as any).$root.$confirm.open(
			UNSAVED_CHANGES_DIALOG_TEXT.title,
			UNSAVED_CHANGES_DIALOG_TEXT.message,
			false,
			UNSAVED_CHANGES_DIALOG_TEXT.agreeText,
			UNSAVED_CHANGES_DIALOG_TEXT.cancelText,
			false,
			false,
			UNSAVED_CHANGES_DIALOG_OPTIONS
		);
		if (result === null) {
			return false;
		}
		if (result) {
			try {
				// LSP-16279: EntityAbout.save() catches its own errors and returns false rather than
				// throwing, so a false result (nothing persisted: invalid form, blocked reassignment,
				// or a server/offline error that already toasted) must keep the user on the page
				// instead of discarding their About-tab edits.
				if (!(await runSave())) {
					console.warn(
						"EntityDetailPage.promptSaveBeforeLeave: save did not persist — blocking navigation so the user can see the error/toast"
					);
					return false;
				}
				isDirty.value = false;
			} catch (err) {
				if (!accessRevoked.value) {
					const errorMessage = `Failed to save changes before leaving: ${getMessageFromError(err)}`;
					toast.error(errorMessage, { timeout: calculateTimeout(errorMessage) });
					// Stay on the page so the user sees the error and can retry — don't discard their work.
					return false;
				}
				// LSP-16279: save threw unexpectedly. Block navigation so the user stays on the dirty
				// page and can see the error — silently navigating away would discard their changes.
				console.warn("EntityDetailPage.promptSaveBeforeLeave: save threw — blocking navigation so the user can see the error", err);
				return false;
			}
		}
		return true;
	}

	const { formatDateOnlyShort, formatTimeOnly } = timezones;

	function handleBeforeUnload(e: BeforeUnloadEvent) {
		const hasUnsavedComment = entityCommentsRef.value?.hasUnsavedComment?.() ?? false;
		const hasUnsavedChecklist = entityChecklistsRef.value?.hasUnsavedChecklist?.() ?? false;
		if (bypassUnsavedChangesCheck.value || (!isDirty.value && !hasUnsavedComment && !hasUnsavedChecklist)) {
			return;
		}
		// Trigger native browser prompt for unsaved changes on refresh/close
		e.preventDefault();
		e.returnValue = "";
	}

	// Note: handleBack implicitly calls promptSaveBeforeLeave before navigating back through the route guard.
	async function handleBack(): Promise<void> {
		const hasFromQuery = !!route.query.from;
		const hasHistory = window.history.length > 1;

		if (hasFromQuery || hasHistory) {
			router.go(-1);
		} else {
			router.push("/");
		}
	}

	function navigateToCustomer(id?: string): void {
		if (freeTrialEnded.value) {
			return;
		}
		const customerIdToNavigate = id ?? customerId.value;
		if (customerIdToNavigate) {
			router.push(`/contacts/${customerIdToNavigate}`);
		}
	}

	function navigateToProject(): void {
		if (projectId.value) {
			router.push(`/projects/${projectId.value}`);
		}
	}

	function handleReassignedAway(): void {
		// LSP-15744: use .replace so Back doesn't return into the now-inaccessible detail page,
		// and .catch the navigation failure so a route rename / guard doesn't surface as an
		// unhandled rejection that strands the user on a 403/empty conversation.
		router.replace({ name: "conversations" }).catch((err) => {
			console.warn("handleReassignedAway: navigation to conversations failed.", err);
			router.replace("/").catch(() => undefined);
		});
	}

	onMounted(async () => {
		addEventListeners();
		// Observe session failures from the initial requests as well as retained background work.
		api.on("error", onApiError);
		// Registered BEFORE initializeComponent, not after: initialization awaits the timeline/media
		// requests, and on a fast entity + slow timeline the About form is already editable during that
		// window. A user who typed a note and then closed or refreshed the tab got NO unsaved-changes
		// prompt and lost the edit outright — the same "never lose work" guarantee (LSP-16420) the flush
		// path protects. The handler checks dirtiness itself, so arming it early is safe.
		// break-it round 6
		window.addEventListener("beforeunload", handleBeforeUnload);
		// LSP-17483: the initial mount load is switch #0 — entitySwitchSequence hasn't been incremented by
		// anything yet, so capturing it here gives applyEntityForSwitch the same ownership guarantee the
		// in-place-switch watcher gets (e.g. it correctly discards this load if the route.params.id watcher
		// somehow fires — and increments the sequence — before this mount load finishes).
		const initialLoadSwitchToken = entitySwitchSequence;
		await initializeComponent(initialLoadSwitchToken);
		// LSP-16086 (Andriy F-2): if the component unmounted during the init await,
		// onUnmounted has already run — its removeEventListener calls no-op because
		// nothing is added yet. Bail BEFORE any post-init listener/timer registration
		// so we don't leak listeners/timers onto a torn-down component.
		if (isComponentUnmounted) {
			// The beforeunload listener above WAS added before the await, so onUnmounted's removal
			// no-opped for it — take it off here or it outlives the component. break-it round 6
			window.removeEventListener("beforeunload", handleBeforeUnload);
			return;
		}

		addPostInitEventListeners();

		// Handle hash-based tab navigation
		handleHashNavigation();

		// Listen for hash changes to update tab when URL changes
		window.addEventListener("hashchange", handleHashNavigation);
		// (beforeunload is armed before initializeComponent above, so a dirty edit made while the
		// timeline is still loading is still protected.)
		// LSP-16086 (P3): flush any deferred reload when the user returns to this tab.
		document.addEventListener("visibilitychange", onVisibilityChange);
		// Listen for file upload events from ActionUploadPhoto (adds shimmer after each file uploads)
		EventBus.$on("FileUploaded", fileUploadedHandler);
		EventBus.$on("UploadFinished", uploadFinishedHandler);
		EventBus.$on("UploadStarted", uploadStartedHandler);
		// The API error listener is armed before initialization above.

		// LSP-15966: uploadStatus drives the pending-media warning banner for the
		// real-world upload-in-flight case (server has a pending multipart stub,
		// chunks still in transit). Previously gated to debug only, which left the
		// banner unable to fire during a real upload. Polling cost is one GET
		// Authenticated: /{conversations|projects}/:id/upload-status every 5s.
		// Unauthenticated: project + ?shareId= only, via the LSP-16142 public mirror.

		// Await so the first cadence reflects fresh state (fast if you opened the page
		// mid-upload), not the idle default computed before the fetch resolves.
		await restartUploadStatusPollForCurrentEntity();

		// LSP-16086: periodic non-socket safety reconcile (bulletproof — see
		// periodicTimelineReconcile). Visible-gated and skip-if-recent keep it cheap.
		// LSP-16086 (Andriy F-3): re-check the unmount flag — the await above is a
		// second window where onUnmounted may have fired.
		if (isComponentUnmounted) return;
		timelineSafetyTimer = setInterval(periodicTimelineReconcile, TIMELINE_RECONCILE_CHECK_MS);
	});

	onUnmounted(() => {
		isComponentUnmounted = true;
		dismissAccessOutcome?.();
		dismissAccessOutcome = null;
		detailLoad.supersede();
		forbiddenFlow.cancel();
		uploadStatusRequestSequence += 1;
		if (uploadStatusTimer) clearTimeout(uploadStatusTimer);
		if (timelineSafetyTimer) clearInterval(timelineSafetyTimer);
		// LSP-18016: the banner's minimum-dwell timer outlives the component otherwise, and it writes to a ref
		// the unmounted instance still holds — a leaked timer per session visited.
		cancelIncompleteUploadBannerHoldTimer();
		timelineReloadScheduler.cancel();
		stopScribeWarmup();
		// LSP-19423: the reconcile retry writes to the entity ref, so an armed tick must not outlive the
		// component — same reason cancelIncompleteUploadBannerHoldTimer is here.
		stopNotesReconcileRetry();
		// LSP-20787: the delivery refetch (refreshAfter or the post-send +5 s) must not outlive the page either.
		clearDeliveryRefresh();
		// Clean up hash change listener
		window.removeEventListener("hashchange", handleHashNavigation);
		window.removeEventListener("beforeunload", handleBeforeUnload);
		// Clean up EventBus listeners
		EventBus.$off("FileUploaded", fileUploadedHandler);
		EventBus.$off("UploadFinished", uploadFinishedHandler);
		EventBus.$off("UploadStarted", uploadStartedHandler);
		// Clean up API error listener
		api.off("error", onApiError);

		Socket.off(constants.SOCKET_EVENT.MASTERDOCUMENT_CREATE as any, onMasterDocument);
		Socket.off(constants.SOCKET_EVENT.MASTERDOCUMENT_DELETE as any, onMasterDocumentDelete);
		Socket.off(constants.SOCKET_EVENT.MASTERRECORDING_DELETE as any, onMasterRecordingDelete);
		Socket.off(constants.SOCKET_EVENT.RECORDINGSESSION_UPDATE as any, onRecordingSessionUpdate);
		Socket.off(constants.SOCKET_EVENT.CONVERSATION_UPDATE as any, onConversationUpdate);
		Socket.off("connect" as any, onSocketConnect);
		Socket.off("disconnect" as any, onSocketDisconnect);

		if (reloadTimeout) {
			clearTimeout(reloadTimeout);
		}
		if (masterDeleteReloadTimeout) {
			clearTimeout(masterDeleteReloadTimeout);
		}
	});

	// Watch for tab changes and update URL hash. LSP-16768: watch the stable id, not
	// the index — a breakpoint reorder shifts the index without changing the selected
	// tab, and we must not rewrite the URL hash (or fire the checklist flush below) for
	// that. Watching activeTabId also avoids reading activeTab (and thus `tabs`, declared
	// later) synchronously during setup, which would hit a temporal-dead-zone error.
	const updatingTabFromHash = ref(false);
	watch(activeTabId, () => {
		if (updatingTabFromHash.value) return;
		updateUrlHash(activeTab.value);
	});

	// Watch for when ProjectChecklists component is mounted and checklists tab is active
	watch(entityChecklistsRef, async (newRef) => {
		if (newRef && checklistSearchRequest.value) {
			const checklistsTabIndex = tabs.value.findIndex((tab) => tab.id === tabIds.checklists);
			if (activeTab.value === checklistsTabIndex) {
				await newRef.loadMedia(checklistSearchRequest.value);
			}
		}
	});

	// LSP-16086: flush a deferred checklist-media load when the user switches to the checklists tab.
	// LSP-16768: keyed on the stable tab id so it fires on an actual tab switch, not on a
	// breakpoint reorder that merely shifts the checklists index.
	watch(activeTabId, (current) => {
		if (current === tabIds.checklists && checklistMediaDirty.value) {
			refreshChecklistMediaIfVisible();
		}
	});

	// Mirrors EntityAbout's flushPendingSave contract. Kept local (rather than imported) because
	// entityAboutRef is a loosely-typed template ref; the union is what we branch on below.
	type FlushPendingSaveResult = "saved" | "blocked" | "failed";
	// Non-null while an in-place-switch flush is running, so rapid switches can join it instead of
	// starting a second flush of the same form. See the watcher below. break-it round 1
	let inFlightOutgoingFlush: Promise<FlushPendingSaveResult | undefined> | null = null;
	// Monotonic id for each in-place entity switch. Every switch re-reads it after each await and bails
	// if a newer switch has started, so only the LATEST switch reloads and applies an entity. Without
	// this, rapid A -> B -> C switches raced two initializeComponent() calls and a slower stale response
	// could overwrite the fresher one. It also covers unmount-mid-flush. break-it round 2
	let entitySwitchSequence = 0;

	// Every context advance either claims a successor or synchronously settles the page.
	function advanceEntityLoad(successor: boolean) {
		settledAccess = null;
		const switchToken = ++entitySwitchSequence;
		if (successor) return { switchToken, loadToken: detailLoad.begin() };
		detailLoad.supersede();
		detailLoad.resetStages();
		startingLoading.value = false;
		verdictRequestSequence++;
		timelineReloadSequence++;
		projectVerdictEnabled.value = false;
		accessOrigin.value = null;
		projectAccountReady.value = false;
		entity.value = undefined;
		reset();
		if (entityType.value === "project") {
			viewOnly.value = true;
			verdictUnavailable.value = false;
			projectAccessResolved.value = false;
		}
		logWarn("EntityDetailPage: context retired detail load", { entityId: entityId.value });
		return { switchToken, loadToken: undefined };
	}

	// entitySwitchSequence tracks route switches and terminal auth/context changes. It must NOT advance for a
	// non-switch refresh (see the watcher's own isSupersededSwitch, which would otherwise wrongly treat an
	// in-flight route switch as superseded by an unrelated refresh). projectLoadSequence is a SEPARATE
	// monotonic counter that orders AUXILIARY (non-switch) refreshes of the current project against each
	// other — e.g. two overlapping socket-reconnect refreshes, which share an identical switchToken/entityId
	// snapshot since no real switch happens between them.
	//
	// break-it round 4: it must NEVER gate the switch-driven loadUserAndProject call itself (see that
	// function's isStaleProjectLoad) — an unrelated auxiliary refresh finishing after an actual route
	// switch's own call started would otherwise wrongly invalidate the switch's own successful response,
	// since this is a single shared counter. Only onSocketConnect's refresh claims a token from it; the
	// switch-driven path never does.
	//
	// Review round 2 (cursor): refreshProject used to claim from this counter too, which was wrong for
	// the same reason round 4 exempted the switch-driven call — the two do DIFFERENT work, so ordering
	// them against each other loses data. refreshProject only re-reads the entity (getEntity +
	// applyEntityForSwitch + sourceEntity); the socket-reconnect refresh goes through
	// loadUserAndProject, which also seeds projectConversationIds, reportConversations, projectUser and
	// Scribe monitoring. A user-triggered refreshProject bumping this counter made an in-flight
	// reconnect refresh bail via isStaleProjectLoad BEFORE seeding any of that, silently dropping the
	// reconciliation — so conversations created while the socket was down could stay invisible until a
	// full page reload. Each kind of refresh now orders only against its OWN kind.
	let projectLoadSequence = 0;

	// Review round 2 (cursor, second pass): the first attempt gave refreshProject its own private
	// counter, which fixed the dropped reconciliation but removed ALL ordering between the two kinds of
	// auxiliary refresh — and they both write the same entity.value for the same project, so an older
	// socket-reconnect response could land after a newer user refresh and replace it with a staler
	// snapshot (and vice versa). One token was doing two different jobs; they are now separate:
	//
	//   projectEntityWriteSequence — claimed by BOTH kinds. Orders only the entity.value WRITE, so the
	//     newest auxiliary response always wins it, whichever kind it came from.
	//   projectLoadSequence        — claimed only by the socket-reconnect refresh. Orders the SEEDING
	//     work (projectConversationIds, reportConversations, Scribe monitoring) that only that refresh
	//     performs, so a user-triggered refreshProject — which never does that work — cannot cancel it.
	//
	// Being superseded on the entity write is therefore no longer a reason to abandon the rest of the
	// call: the winner wrote a FRESHER entity, so the seeding below reads fresher state than this call
	// carried. Only losing switch ownership (a different entity, or unmount) aborts.
	let projectEntityWriteSequence = 0;

	// cursor review round 5: a monotonic count of entity.value writes that are ORDERED against each other
	// — i.e. only those made by a refresh that claimed a projectEntityWriteSequence token. Round 4 counted
	// EVERY applyEntityForSwitch write, which was wrong: the switch/mount-driven loadUserAndProject call
	// claims no token and is therefore exempt from the superseded check, so it can write an older snapshot
	// after an auxiliary refresh has already started. Counting that write made a superseded refresh seed
	// from a snapshot it could not order itself against, losing the reconciliation it existed to perform.
	//
	// Distinct from projectEntityWriteSequence, which counts refresh STARTS. The gap between "started" and
	// "wrote" is what made round 3's fix wrong in one ordering and round 4's wrong in another; the gap
	// between "wrote" and "wrote in a way I can order myself against" is what made round 4 wrong here.
	let orderedEntityWriteCount = 0;

	// break-it round 4: orders overlapping cancelScheduledCall() invocations for the SAME conversation
	// against each other — e.g. a double-click opening two confirm dialogs. Neither call is a route switch,
	// so entityId/switchToken/route are identical for both; only the token matching the current
	// cancelActionSequence value (i.e. the LATEST invocation) may proceed to the actual destructive call.
	let cancelActionSequence = 0;

	// break-it round 5: cancelActionSequence only orders confirmations BEFORE the destructive API call — it
	// cannot stop a SECOND cancel (confirmed while an earlier one for the SAME conversation is already
	// mid-flight, past its own check) from also reaching the API. This lock, keyed by conversation id, blocks
	// that: a concurrent attempt for a conversation already in flight is rejected outright.
	let cancelRequestInFlightForConversationId: string | null = null;

	// LSP-16658: the reschedule's own in-flight lock lives in ScheduledCallActions.vue, scoped to the
	// one conversation it belongs to. Nothing about it is page state any more.

	// LSP-17483: usable from ANY load-path function, not just the watcher body. LSP-17265's guard only
	// rechecked ownership AFTER initializeComponent() had already returned — by which time a stale
	// assignment had already landed, because entity.value is written from several sites INSIDE that load
	// path (loadEntity / handleProjectAccess / loadUserAndProject). Two switches to the SAME id are still
	// two different switches, so this checks the monotonic sequence token, not entityId equality.
	function isStaleSwitch(switchToken: number): boolean {
		return switchToken !== entitySwitchSequence || isComponentUnmounted;
	}

	// LSP-17483 (break-it round 2): entityId.value and entitySwitchSequence only update once the
	// route.params.id watcher's callback actually RUNS — but that callback is merely SCHEDULED the instant
	// route.params.id changes (Vue's default watch flush timing), not synchronous with it. A continuation
	// resuming from an unrelated awaited promise (e.g. autoUpdateReviewedIfReady's upload-status fetch, or a
	// user-action fetch like refreshProject/cancelScheduledCall) can therefore run BETWEEN the route param
	// changing and the watcher observing it — at which point entityId.value/entitySwitchSequence still
	// describe the OUTGOING entity even though a switch is already under way. Comparing against the live
	// route.params.id closes that gap: it reflects the change immediately, with no scheduler lag. Skipped
	// when route.params.id is absent (embedded/prop-driven usage falls back to entityId/switchToken alone).
	function isStaleAction(actionEntityId: string, switchToken: number): boolean {
		const routeHasMovedOn = typeof route.params.id === "string" && route.params.id !== actionEntityId;
		return entityId.value !== actionEntityId || routeHasMovedOn || isStaleSwitch(switchToken);
	}

	// Single ownership-checked setter: every switch-driven entity.value assignment must go through here so
	// a newly-added assignment site can't silently reintroduce the LSP-17483 clobber bug. Returns whether
	// the value was applied — false means a newer switch (or an unmount) already took ownership while this
	// response was in flight, and the response is discarded rather than overwriting the current entity.
	//
	// break-it round 3: takes the id this specific response is FOR (not just switchToken) and checks via
	// isStaleAction rather than the narrower isStaleSwitch. A promise resolving in the gap between
	// route.params.id changing and the watcher observing it previously slipped through the switchToken-only
	// check (entitySwitchSequence hadn't advanced yet either), briefly applying a stale entity for an id the
	// route no longer names.
	function applyEntityForSwitch(
		actionEntityId: string,
		switchToken: number,
		value: Project | Conversation | undefined,
		context: string
	): boolean {
		if (isStaleAction(actionEntityId, switchToken)) {
			console.warn(
				`EntityDetailPage: discarding a superseded ${context} response — a newer switch (or unmount) already took ownership`,
				{
					actionEntityId,
					switchToken,
					currentSwitchToken: entitySwitchSequence,
					isComponentUnmounted,
					currentEntityId: entityId.value,
					currentRouteParamId: route.params.id
				}
			);
			return false;
		}
		entity.value = value;
		return true;
	}

	watch(
		() => route.params.id,
		async (newId, oldId) => {
			if (!newId || newId === oldId) {
				// Diagnostic: a watcher that fires and does nothing is exactly the shape the LSP-16420
				// "lost work" bug hid behind — no flush runs, so the outgoing entity's pending autosave is
				// dropped. Name the ids so a future report doesn't need to reconstruct which condition failed.
				console.warn("EntityDetailPage: entity-id watcher fired without a usable id change — no flush, no reload", {
					incomingEntityId: newId ?? null,
					outgoingEntityId: oldId ?? null,
					entityType: entityType.value
				});
				return;
			}

			// An in-place entity switch (same route, :id change) does NOT fire onBeforeRouteLeave, so
			// flush the outgoing entity's pending autosave before tearing it down — otherwise a debounced
			// edit made in the last ~10s is silently dropped (LSP-16420 "never lose work").
			logInfo("EntityDetailPage: in-place entity switch — flushing the outgoing About form", {
				outgoingEntityId: oldId ?? null,
				incomingEntityId: newId,
				entityType: entityType.value
			});
			// entityType still describes the OUTGOING entity here (initializeComponent re-resolves it
			// below), which is exactly the one whose edit failed to save.
			const outgoingEntityType = entityType.value;
			// This switch owns the reload only while it stays the newest one.
			forbiddenFlow.cancel();
			const { switchToken: thisSwitch, loadToken } = advanceEntityLoad(true);
			const isSupersededSwitch = (): boolean => isStaleSwitch(thisSwitch);

			// Rapid A -> B -> C switches fire this callback again before the first one has torn the
			// outgoing form down (entity.value is cleared only after the await below), so both callbacks
			// would flush the SAME still-mounted form and each toast — two toasts for one lost edit. The
			// first caller owns the flush and the user-facing report; later ones await it and stay quiet.
			// break-it round 1
			let flushed: FlushPendingSaveResult | undefined;
			let flushReportBelongsToAnotherSwitch = false;
			if (inFlightOutgoingFlush) {
				flushReportBelongsToAnotherSwitch = true;
				const ownedFlushResult = await inFlightOutgoingFlush.catch(() => undefined);
				console.warn("EntityDetailPage: a rapid entity switch joined an in-flight flush — deferring the report to its owner", {
					outgoingEntityId: oldId ?? null,
					incomingEntityId: newId,
					entityType: outgoingEntityType,
					ownedFlushResult: ownedFlushResult ?? null
				});
			} else {
				try {
					// The CALL itself is inside the try: flushPendingSave can throw synchronously (before
					// returning a promise), and with the call outside, that throw escaped this handler
					// entirely — unhandled rejection, no warning toast, reload never ran. break-it round 4
					inFlightOutgoingFlush = Promise.resolve(entityAboutRef.value?.flushPendingSave?.());
					flushed = await inFlightOutgoingFlush;
				} catch (flushError) {
					// A throwing flush must never strand the page on the outgoing entity — warn, treat it as
					// "we could not confirm the save", and continue the teardown/reload below.
					// break-it round 2
					console.warn("EntityDetailPage: the outgoing About form's flush threw — continuing the switch", {
						outgoingEntityId: oldId ?? null,
						incomingEntityId: newId,
						entityType: outgoingEntityType,
						error: (flushError as Error)?.message ?? String(flushError)
					});
					// Swallowing the exception keeps navigation alive, but it must not disappear: console.error
					// is forwarded to Datadog, so the stack is still recoverable.
					console.error(flushError);
					// Treat an unconfirmed save as blocked so the user is warned rather than losing the edit.
					flushed = "blocked";
				} finally {
					inFlightOutgoingFlush = null;
				}
			}
			if (!flushReportBelongsToAnotherSwitch && flushed !== undefined && flushed !== "saved") {
				console.warn("EntityDetailPage: the outgoing About form's pending autosave did not persist", {
					outgoingEntityId: oldId ?? null,
					incomingEntityId: newId,
					entityType: outgoingEntityType,
					flushResult: flushed
				});
			}
			// Only "blocked" needs a toast from here: it means nothing persisted AND EntityAbout stayed
			// silent (its validation/editability gates), while the field carrying the inline red error is
			// about to unmount. "failed" already showed EntityAbout's own error toast — toasting again
			// would stack two toasts on one failure (LSP-17265 review).
			if (!flushReportBelongsToAnotherSwitch && flushed === "blocked") {
				const entityNoun = outgoingEntityType === "project" ? "project" : "session";
				const message = `We couldn't save your last change to this ${entityNoun} — please reopen it and re-enter it.`;
				toast.error(message, { timeout: calculateTimeout(message) });
			}
			// A newer switch (or an unmount) took over while we were flushing — it owns the reload, so
			// stop here rather than racing it and letting a slower stale response win. break-it round 2
			if (isSupersededSwitch()) {
				return;
			}
			entity.value = undefined;
			// Navigating to a different entity remounts EntityAbout in its idle state, which won't
			// re-emit — so reset here to stop the header showing the previous entity's "Last saved …".
			saveState.value = { ...IDLE_SAVE_STATE };
			// The reschedule dialog needs no teardown here any more: it is keyed by conversation id, so it
			// simply does not render for the incoming entity. Clearing a boolean at this point was the
			// first of the three defects that keying removed.
			await initializeComponent(thisSwitch, loadToken);
			// LSP-17483: initializeComponent's load path (loadEntity / handleProjectAccess /
			// loadUserAndProject) now threads thisSwitch through to every entity.value assignment site via
			// applyEntityForSwitch, so an OLDER getEntity response that resolves after a newer one is
			// discarded instead of overwriting the current entity. This check below still covers what it
			// always did: stopping a superseded switch from restarting the upload poll. Deliberately NOT
			// serialized: queuing each reload behind the previous one makes the newest switch wait on a
			// stale in-flight request, so one slow response would block the entity the user actually asked
			// for — a common stall traded for a rare clobber.
			if (isSupersededSwitch()) return;
			await restartUploadStatusPollForCurrentEntity();
		}
	);

	const isScheduled = computed(() => {
		const status = (entity.value as any)?.status as string | undefined;
		return (entityType.value === "conversation" && status?.toLowerCase?.() === "scheduled") || false;
	});

	const entityNumber = computed(() => {
		if (!entity.value) {
			return "";
		}

		if (entityType.value === "conversation") {
			return (entity.value as Conversation).number || "";
		}

		return "";
	});

	const customers = computed(() => {
		if (entityType.value === "project") {
			return (entity.value as Project).Customers ?? [];
		} else if (entityType.value === "conversation") {
			return (entity.value as Conversation).Customers ?? [];
		}

		return [];
	});

	// LSP-20787: the invite's SMS delivery state, shown in EntityHeader's row (desktop), the mobile block below md,
	// and — for a scheduled call — the Confirmed card's banner instead (Task 15).
	const delivery = ref<ConversationDelivery | null>(null);
	// Which inline box the mobile block has open (EntityHeader and SmsDeliveryBanner own theirs).
	const deliveryMode = ref<"phone" | "email" | null>(null);
	let deliveryRefreshTimer: ReturnType<typeof setTimeout> | null = null;
	// LSP-22012: a refetch that came due while the tab was hidden; onVisibilityChange runs it on the way back.
	let deferredDeliveryRefresh: { conversationId: string; switchToken: number } | null = null;
	const DELIVERY_CHECKING_POLL_MS = 30_000;

	// D15: a project-shared session another org owns is readable but not repairable — the resend and the
	// contact edit are scoped to the caller's org and would 404. S9: the chip still shows, the buttons do not.
	const canRepairDelivery = computed(() => !freeTrialEnded.value && !viewOnly.value && delivery.value?.repairable === true);
	// Final review I1: the booking forms' own reading of "Allow Email Invitations" — off hides every Send via email.
	const emailInvitationsAllowed = computed(() => me.value?.organization?.allowEmailInvitations !== false);
	// D4: once the contact has engaged, whether the invite text landed no longer matters.
	const showDelivery = computed(() => delivery.value !== null && isDeliveryRelevantStatus(entity.value?.status));

	function clearDeliveryRefresh() {
		if (deliveryRefreshTimer !== null) clearTimeout(deliveryRefreshTimer);
		deliveryRefreshTimer = null;
		deferredDeliveryRefresh = null;
	}

	// ONE timer at a time; a newer schedule always replaces the older one. A hidden tab does not fetch: the refetch waits
	// for the tab to come back (LSP-22012).
	function scheduleDeliveryRefresh(conversationId: string, switchToken: number, delayMs: number) {
		clearDeliveryRefresh();
		deliveryRefreshTimer = setTimeout(() => {
			deliveryRefreshTimer = null;
			if (document.visibilityState !== "visible") {
				deferredDeliveryRefresh = { conversationId, switchToken };
				return;
			}
			void loadDelivery(conversationId, switchToken);
		}, delayMs);
	}

	function runDeferredDeliveryRefresh() {
		const deferred = deferredDeliveryRefresh;
		deferredDeliveryRefresh = null;
		if (!deferred || isStaleAction(deferred.conversationId, deferred.switchToken)) return;
		void loadDelivery(deferred.conversationId, deferred.switchToken);
	}

	// LSP-22012 (Ankit 2026-10-15) supersedes D10 for the checking state: while the shown state is checking, look again
	// every 30 s (sooner if its refreshAfter edge comes first) so Checking… — on the header chip, the mobile block and the
	// scheduled-call card's banner, which all read `delivery` — turns into the outcome without a reload. The first AWS
	// status check now runs ~1 min after the send. Any other state schedules nothing: polling stops once it settles.
	// A failed fetch is logged; under a checking chip the 30 s cadence carries on, otherwise nothing retries (B7).
	async function loadDelivery(conversationId: string, switchToken: number) {
		if (authSession.sessionLost) return;
		if (entityType.value !== "conversation") return;
		try {
			// `?? []`: a hand-built test mock can resolve undefined; never throw while destructuring (A2).
			const [state] = (await api.getConversationsDelivery([conversationId])) ?? [];
			// Same ownership guard every other load path uses (LSP-17483): a response for a session
			// the page has since left, or for an unmounted page, is dropped.
			if (isStaleAction(conversationId, switchToken)) return;
			// S7: a new state closes the mobile block's box, as EntityHeader's row and the banner close theirs.
			if ((state?.state ?? null) !== (delivery.value?.state ?? null)) deliveryMode.value = null;
			delivery.value = state ?? null;
			// refreshAfter is checking-only (contact-api deriveSmsDeliveryState). One already past on this clock (the client
			// runs ahead of the server) or weeks ahead (behind) still waits the 30 s cadence: skew never becomes a fetch loop.
			if (state?.state === "checking") {
				const untilRefresh = state.refreshAfter ? Date.parse(state.refreshAfter) - Date.now() : Number.NaN;
				scheduleDeliveryRefresh(
					conversationId,
					switchToken,
					untilRefresh > 0 ? Math.min(untilRefresh, DELIVERY_CHECKING_POLL_MS) : DELIVERY_CHECKING_POLL_MS
				);
			}
		} catch (error) {
			if (isStaleAction(conversationId, switchToken)) return;
			console.warn("EntityDetailPage: delivery state fetch failed; rendering without the delivery chip", { conversationId }, error);
			if (delivery.value?.state === "checking") scheduleDeliveryRefresh(conversationId, switchToken, DELIVERY_CHECKING_POLL_MS);
		}
	}

	// After a send — from the header row, the mobile block or the card banner — close the mobile block's box
	// and look again ONCE at +5 s. That fetch returns checking + refreshAfter, and loadDelivery schedules the
	// single window-edge refetch from it (D10) — no second post-send timer (B7: it would clear that schedule).
	//
	// Bugbot: sentConversationId is the id the send was actually made for, captured by the sending component
	// before its await — NOT re-derived from entityId.value here, which would silently apply to whatever
	// session an in-place switch has since moved the page to. A send that resolves after such a switch must
	// refresh nothing: the session it was for is no longer on screen, and the session now on screen never sent.
	function refetchDeliveryAfterSend(sentConversationId: string) {
		if (sentConversationId !== entityId.value) {
			console.warn("EntityDetailPage: ignoring a resend/invitation-sent event for a session this page no longer shows", {
				sentConversationId,
				currentConversationId: entityId.value
			});
			return;
		}
		deliveryMode.value = null;
		const switchToken = entitySwitchSequence; // the same monotonic token isStaleSwitch/isStaleAction compare against
		scheduleDeliveryRefresh(sentConversationId, switchToken, 5_000);
	}

	// §6 S2: the repair wrote the contact; merge the server's copy IN PLACE. EntityAbout holds this same object
	// as its selected contact and keys dirtiness by the contact id only, so the About panel, the header and the
	// next repair's expectedUpdatedAt are current and the form is not dirtied. Deliberately NOT loadEntity(): a
	// full reload would fight a dirty About form. Same in-place shape as onConversationRescheduled.
	function mergeDeliveryCustomer(customer: SmsDeliveryRepairCustomer) {
		const current = customers.value[0];
		if (!current || current.id !== customer?.id) {
			console.warn("EntityDetailPage: a repaired contact arrived for a contact this session does not show — not merged", {
				conversationId: entityId.value,
				customerId: customer?.id,
				currentCustomerId: current?.id
			});
			return;
		}
		Object.assign(current, customer);
	}

	const customerName = computed(() => {
		if (entityType.value === "project") {
			return (entity.value as Project).Customers?.map((customer) => customer.name).join(", ");
		} else if (entityType.value === "conversation" && (entity.value as Conversation).Customers) {
			return (entity.value as Conversation).Customers?.map((customer) => customer.name).join(", ");
		}

		return "";
	});

	const customerId = computed(() => {
		if (entityType.value === "project") {
			return (entity.value as Project).Customers?.[0]?.id;
		} else if (entityType.value === "conversation" && (entity.value as Conversation).Customers) {
			return (entity.value as Conversation).Customers?.[0]?.id;
		}

		return undefined;
	});

	// LSP-21219: whether the upload reminder reaches a CUSTOMER, which picks the banner's "send your customer a
	// link" copy over "send a link to the device". Mirrors the API's resolveUploadReminderEmailRecipient
	// (helpers/contentNotification.ts) — keep them in step: a customer participant with no email and no phone is
	// the record-only row an agent's own dashboard recording leaves, and that reminder goes to the recording user.
	const uploadReminderReachesCustomer = computed(() => {
		if (entityType.value !== "conversation") return !!customerId.value;
		const participants = (entity.value as Conversation).Participants;
		if (!Array.isArray(participants)) return !!customerId.value;
		return participants.some((participant) => !!participant.customerId && (!!participant.email || !!participant.phone));
	});

	type ConversationWithAppointment = Conversation & { Appointment?: Record<string, any> };

	const conversationForConfirmation = computed<Record<string, any> | undefined>(() => {
		return entityType.value === "conversation" ? (entity.value as unknown as Record<string, any>) : undefined;
	});

	const appointmentForConfirmation = computed<Record<string, any> | undefined>(() => {
		if (entityType.value === "conversation") {
			const conv = entity.value as unknown as ConversationWithAppointment | undefined;
			return conv?.Appointment as Record<string, any> | undefined;
		}
		return undefined;
	});
	// LSP-21861: a scheduled call reads in the zone picked when it was booked — the zone the server writes the
	// session title in — so the title, the Confirmed card, its banner and the Reschedule picker all show one
	// time. Validated because `moment.tz` renders an unknown zone as UTC silently; the browser zone is the
	// fallback for a missing or unknown stored zone.
	const timeZone = computed<string>(() => {
		const bookingZone = appointmentForConfirmation.value?.timeZone;
		return typeof bookingZone === "string" && moment.tz.zone(bookingZone) ? bookingZone : moment.tz.guess();
	});

	// The same source AppointmentConfirmation renders its scheduled time from, so the dialog's
	// "Currently scheduled" line and the card behind it can never disagree on M/D vs D/M.
	const organizationDateFormat = computed<string | null>(() => timezones.getDateFormat() ?? null);

	/**
	 * LSP-16658 — what the reschedule dialog renders its message from.
	 *
	 * The same three values `ActionSchedule.vue` renders the BOOKING message from, read from the same
	 * place (`me.organization`), so a reschedule produces copy in the organization's own wording rather
	 * than wording invented by this feature.
	 *
	 * The team member is the conversation's ASSIGNED user, not the admin doing the rescheduling — the
	 * contact's copy says "your call with <the person they booked>", and the server substitutes
	 * `${teamMember}` from `Conversation.User` for exactly that reason. Same firstname → lastname →
	 * email tiering the server uses, so what the user edits is what gets sent. It falls back to the
	 * current user only when the conversation carries no assigned user at all.
	 */
	const scheduledCallMessageTemplate = computed<string>(() => me.value?.organization?.notificationTemplateScheduled ?? "");

	const organizationName = computed<string>(() => me.value?.organization?.name ?? "");

	/**
	 * The zone the CONTACT-facing copy is written in: the booking zone stored on the appointment.
	 *
	 * Kept separate from `timeZone` above, which falls back to this admin's browser zone when the stored
	 * zone is missing or unknown — the dialog resolves this one the way the server does. The server writes the
	 * session name and the ICS event summary in the stored booking zone, both contact-visible, so a
	 * message rendered in the admin's zone told the contact two wall-clock times for the same call
	 * whenever the two differed. // Bugbot review (High) "Contact copy uses admin timezone"
	 *
	 * Passed through unvalidated on purpose: the dialog validates whatever it is given (the column has
	 * no constraint and the create route no body schema), and the server validates it a third time and
	 * self-heals the row.
	 */
	const scheduledCallMessageTimeZone = computed<string>(() => (appointmentForConfirmation.value?.timeZone as string | undefined) ?? "");

	const scheduledCallTeamMemberName = computed<string>(() => {
		const assignedUser = (entity.value as { User?: { firstname?: string; lastname?: string; email?: string } } | undefined)?.User;
		const source = assignedUser ?? me.value?.user;
		const name = [source?.firstname, source?.lastname].filter(Boolean).join(" ").trim();
		return name || source?.email || "";
	});

	const contactFirstName = computed<string>(() => {
		const conversation = entity.value as Conversation | undefined;
		const fullName = (conversation?.Customers?.[0]?.name ?? "").trim();
		return fullName.split(/\s+/)[0] || "Your contact";
	});

	const customerPhone = computed<string>(() => {
		if (entityType.value === "conversation") {
			const conv = entity.value as Conversation | undefined;
			const phone = conv?.Customers?.[0]?.PhoneNumbers?.[0]?.phoneNumber;
			return phone ?? "";
		}
		return "";
	});

	const projectName = computed(() => {
		if (entityType.value == "project") {
			return (entity.value as Project).name;
		} else if (entityType.value == "conversation" && (entity.value as Conversation).Project) {
			return (entity.value as Conversation).Project.name;
		}

		return "";
	});

	const currentConversationProject = computed<Project | null>(() => {
		if (entityType.value !== "conversation") return null;
		return ((entity.value as Conversation | undefined)?.Project as Project | undefined) ?? null;
	});

	const canAssignConversationProject = computed<boolean>(() => {
		if (entityType.value !== "conversation" || !entity.value) return false;
		const conversation = entity.value as Conversation;
		return (
			isAuthenticated.value &&
			!viewOnly.value &&
			!freeTrialEnded.value &&
			!isExternalEditor.value &&
			conversation.accessVia !== "project"
		);
	});

	const projectId = computed(() => {
		if (entityType.value === "conversation" && (entity.value as Conversation).Project) {
			return (entity.value as Conversation).Project?.id;
		}

		return undefined;
	});

	async function assignConversationProject(project: Project | null): Promise<void> {
		if (!canAssignConversationProject.value || projectAssignmentSaving.value) return;
		const conversationId = entity.value?.id;
		if (!conversationId || currentConversationProject.value?.id === project?.id) {
			projectAssignmentDialogVisible.value = false;
			return;
		}

		projectAssignmentSaving.value = true;
		try {
			const updatedConversation = await api.updateConversation(conversationId, { projectId: project?.id ?? null });
			if (entity.value?.id !== conversationId) return;

			const conversation = entity.value as Conversation;
			Object.assign(conversation, updatedConversation);
			conversation.projectId = project?.id ?? null;
			conversation.Project = project ?? undefined;
			entityAboutRef.value?.acceptProgrammaticProject(project);
			projectAssignmentDialogVisible.value = false;
			toast.success(project ? `This session was added to ${project.name}.` : "This session was removed from the project.");
		} catch (error) {
			const message = getMessageFromError(error, "Sorry, we were unable to add this session to the project. Please try again.");
			toast.error(message, { timeout: calculateTimeout(message) });
		} finally {
			projectAssignmentSaving.value = false;
		}
	}

	const hiddenFilters = computed(() => {
		if (entityType.value === "conversation") {
			return ["status", "customer", "user"] as FilterType[];
		}

		return ["status"] as FilterType[];
	});

	const scribeType = computed<"project" | "conversation">(() => {
		return entityType.value === "project" ? "project" : "conversation";
	});

	const hasScribeFeature = computed(() => {
		if (!entity.value || !me.value) return false;
		if (viewOnly.value) return false;
		const orgId = me.value?.organization?.id;
		if (!orgId) return false;
		if (entityType.value === "project") {
			// If project record lacks organizationId, default to enabling based on view-only status only
			const projectOrgId = (entity.value as any)?.organizationId as string | undefined;
			if (!projectOrgId || projectOrgId === orgId) return true;
			// LSP-17772 (QA): a cross-org EDITOR reaching this project through a share must be able to
			// run Sparky reports too — the org-only match above wrongly left the run-report button a
			// dead no-op for exactly the population sharing exists to serve. viewOnly already returned
			// false above (:2601), so external VIEWERS stay blocked; isExternalEditor is share-origin,
			// different-org, non-view-only. The report is created in and billed to the caller's own
			// Scribe tenant (LSP-18409), so enabling it here is the correct, self-contained grant.
			return isExternalEditor.value;
		}
		// conversation
		return (entity.value as Conversation)?.organizationId === orgId;
	});

	// Removed Scribe preflight gating per new strategy

	// Sparky's Reports visibility: show when authenticated
	const canViewLuckyReports = computed<boolean>(() => isAuthenticated.value);

	// Enable Scribe when the page has analyzable timeline media or ready PDF documents.
	// LSP-16659: the grid's `count` now includes documents, and the orchestrator analyzes PDFs only, so a
	// grid of only .docx files must NOT enable Sparky. Photos/videos come from the per-type totals
	// (hasPhotoOrVideoMedia); documents contribute only through the ready-PDF count.
	const hasMedia = computed(() => {
		return hasPhotoOrVideoMedia(mediaResponse.value) || readyPdfDocumentCount.value > 0;
	});

	// LSP-16659: photos + videos only, for surfaces that cannot take a document (the checklist media picker).
	const photoAndVideoCount = computed(() => {
		const counts = mediaResponse.value?.countsByEntityType;
		if (counts) return (counts.snapshot ?? 0) + (counts.recording ?? 0);
		return mediaResponse.value?.count ?? 0;
	});

	const hasMediaProcessing = computed(() => {
		return pendingImages.value > 0 || pendingDocs.value > 0 || pendingVideos.value > 0;
	});

	// Reactive clock so the reminder threshold (VITE_ASYNC_REMINDER_THRESHOLD_MINUTES) flips
	// on naturally when the page stays open across the threshold.
	const asyncReminderNow = useNow({ interval: 60_000 });

	// Production defaults to 24h; every other environment defaults to 5 minutes so reviewers
	// can exercise the empty state without backdating a conversation. The env var overrides both.
	const ASYNC_REMINDER_THRESHOLD_MINUTES =
		Number(import.meta.env.VITE_ASYNC_REMINDER_THRESHOLD_MINUTES) || (import.meta.env.MODE === "production" ? 1440 : 5);

	const showAsyncReminderPrompt = computed<boolean>(() => {
		if (entityType.value !== "conversation" || !entity.value) return false;
		if (!isAuthenticated.value || viewOnly.value) return false;
		if (loading.value || timelineLoadFailed.value) return false;
		if (currentSearchTerm.value) return false;

		// Any active filter narrows mediaResponse.count, which would falsely flip "no submitted media"
		// to true on a conversation that actually has uploads outside the current filter set.
		const request = searchRequest.value;
		if (
			request &&
			(!!request.contentTypes?.length ||
				!!request.statuses?.length ||
				!!request.customerIds?.length ||
				!!request.userIds?.length ||
				!!request.labels?.length ||
				!!request.startDate ||
				!!request.endDate ||
				!!request.dateFilter)
		) {
			return false;
		}

		const conversation = entity.value as Conversation;
		if (conversation.type?.toLowerCase() !== "async") return false;

		// Match EntityActions.isUserInitiatedRecording exactly — strict `""` only, not null/undefined.
		const isUserInitiatedRecording = conversation.initiationType?.toLowerCase() === "admin" && conversation.customerUrl === "";
		if (isUserInitiatedRecording) return false;

		// Mirrors EntityActions.hasResendableParticipant — update both.
		const hasResendableParticipant = (conversation.Participants ?? []).some((participant) => {
			const hasCustomerId =
				typeof participant.customerId === "string" ? participant.customerId.trim().length > 0 : !!participant.customerId;
			const hasDeliverableChannel = !!participant.phone || (participant.notificationEmail && !!participant.email);
			return hasCustomerId && participant.notificationSMS && hasDeliverableChannel;
		});
		if (!hasResendableParticipant) return false;

		if ((mediaResponse.value?.count ?? 0) > 0) return false;
		if (hasMediaProcessing.value) return false;

		// LSP-16842: this prompt is an EMPTY STATE, but every signal that proves the session is
		// empty reads "nothing here" before its request lands — uploadStatus starts null, so
		// pendingRecordingCount returns 0, and mediaResponse starts as an empty response. On first
		// paint each suppression term above is therefore false, the prompt renders, and it vanishes
		// a beat later when the real counts arrive. That is the flash reported on My Stuff, and it
		// is most obvious on a session that already has an upload in flight (the pending recording
		// is exactly what should have suppressed it).
		//
		// So wait for the upload-status fetch to have settled before offering to send a reminder:
		// absent data is not evidence of an empty session. Gate on "the attempt finished" rather
		// than "uploadStatus is populated" — an empty response is deliberately never committed, so
		// the latter would hide the prompt forever on a session whose status endpoint returns
		// no content.
		// Deliberately not logged here: this is a computed that re-evaluates on every dependency
		// change, so a warn would fire continuously during normal startup rather than marking an
		// anomaly. fetchUploadStatus already logs its own failures.
		if (!uploadStatusFetchSettled.value) return false;

		// Complements the check above rather than duplicating it: a file picked in THIS tab is
		// in flight before the next uploadStatus poll reflects it.
		if (isUploading.value) return false;

		// LSP-15571: the server is authoritative for a video the admin's client never saw. An interrupted
		// existing-video upload (or one still transcoding) is a pending RecordingSession — pendingRecordingCount
		// > 0 with mediaResponse.count still 0 and the client in-flight refs (hasMediaProcessing) reset by
		// navigation. Yield to the Timeline's server-pending processing placeholder (driven by the SAME
		// pendingRecordingCount signal) instead of pre-empting the Timeline with the "resend reminder" empty
		// state — the two are v-if/v-else-if siblings, so without this the placeholder added in PR #2076 never
		// mounts and the session wrongly shows "no media here yet" for an in-flight upload. Diagnostic for this
		// path is emitted by Timeline.vue's existing edge-triggered placeholder watch (PR #2076), which this
		// return now lets mount — so no separate log is needed here (silent-failure rule, CLAUDE.md).
		if (pendingRecordingCount.value > 0) return false;

		// LSP-15573 — THE RULE, written down because this line has now moved three times (Bugbot):
		//
		//   The reminder and the failed-upload tile are NOT alternatives. Neither suppresses the other.
		//
		// Why there is no origin test here: contact-async's public /recording/multipart/initiate reaches the
		// SAME controller as the admin's authenticated route and sets the same isFileUpload flag, and no
		// column records which one created the row (createdVia is null from both web clients, sessionType
		// only has clip_merge, RecordingSession has no createdBy). Origin exists only as the presence of ctx
		// at the route boundary. So `abandonedVideoUploads` cannot answer "was this the admin's upload or
		// the customer's", and every attempt to pick a winner from it was really a guess about origin:
		//   1. no yield          -> the reminder pre-empted the Timeline, so a failed ADMIN upload showed the
		//                           original empty state. That is the bug this ticket exists to fix.
		//   2. yield on count>0  -> a failed CUSTOMER upload lost its nudge and got an admin-facing
		//                           "retry it yourself" tile instead.
		//   3. no yield again    -> back to (1) for admin uploads onto customer sessions, which is the COMMON
		//                           shape: EntityActions.processFiles uploads into the OPEN conversation, so
		//                           an admin uploading from a customer async session keeps that session's
		//                           customerUrl and participants and passes every gate above.
		//
		// The premise all three shared — that only one of these may render — was the actual defect. It is a
		// v-if/v-else-if artifact, not a product requirement. Both signals are now shown when both are true:
		// this computed keeps its gates unchanged (so the nudge is always offered where one is possible), and
		// the Timeline additionally mounts when there is a failed upload to report, rendering the reminder as
		// a prompt above the tile. Neither fact can hide the other, so no origin test is needed.

		const createdAt = conversation.createdAt;
		if (!createdAt) return false;
		return moment(asyncReminderNow.value).diff(moment(createdAt), "minutes") >= ASYNC_REMINDER_THRESHOLD_MINUTES;
	});

	// LSP-15573: the "reminder outranks the failed-upload state" diagnostic that used to sit here is gone
	// with the branch it described — the Timeline now mounts whenever there is a failed upload, so its own
	// failed-upload watch reports the outcome and no branch silently swallows the signal.

	// LSP-15966: Sparky reports must not start while media is still uploading
	// or transcoding. "Pending" comes from two independent signals — server
	// documents flagged status='pending' (transcoding/processing on the API
	// side) and client-tracked upload counters incremented while a multipart
	// upload is still in flight. Both must contribute or the warning would
	// undercount and we would still ship partial reports without telling the
	// user. The banner uses these counts; handleRunReport() interlocks on the
	// same totals so the warning cannot be missed if the banner has not yet
	// rendered when the user clicks.
	// LSP-15966: server-side /conversations/:id/upload-status is authoritative —
	// counts every pending File, Snapshot, in-flight RecordingSession upload,
	// and post-upload-pre-transcode Recording across ALL timeline pages. The
	// previous client-side `mediaResponse.documents.filter(...)` was scoped to
	// the first PAGE_SIZE=75 documents and silently missed off-page pending
	// items (Cursor Bugbot). Falsy when the endpoint has not responded yet or
	// for project entities (the route is conversation-only).
	const uploadStatusPending = computed(() => {
		if (!uploadStatus.value || uploadStatus.value.isComplete) return 0;
		return (
			(uploadStatus.value.pendingFiles ?? 0) +
			(uploadStatus.value.pendingSnapshots ?? 0) +
			(uploadStatus.value.pendingRecordings ?? 0) +
			(uploadStatus.value.pendingTranscoding ?? 0)
		);
	});

	const pendingMediaTotalPending = computed(() => {
		// LSP-15966 (race-fix iteration 3): use the MAX of server uploadStatus
		// and client-tracked counters rather than either alone. Both sides
		// race in opposite directions and naive composition produces bugs:
		//
		//   • SUM both → "2 files" flash at the upload→transcoding handoff
		//     because FileUploaded incremented the client counter at the same
		//     instant the server began reporting the same session under
		//     pendingTranscoding (Cursor Bugbot, iteration 2).
		//   • Server-only when uploadStatus exists → banner stays off while
		//     uploads are still in flight if the server has not yet created
		//     the pending stub (e.g. async recording redirect that seeds
		//     ?pendingImages=N before the first poll catches up) (Cursor
		//     Bugbot, iteration 3).
		//
		// max() fails CLOSED — the gate prefers the more pessimistic count.
		// At the handoff both sides report 1 for the same file → max=1 (no
		// double-count). When client leads (just-seeded counter, server has
		// not seen the upload) → max=client (banner stays up briefly until
		// server catches up). When server leads (loadTimeline already
		// drained the client counter) → max=server.
		const clientCounters = pendingImages.value + pendingDocs.value + pendingVideos.value;
		if (uploadStatus.value) {
			return Math.max(uploadStatusPending.value, clientCounters);
		}
		return clientCounters;
	});

	// A permissions outage for a signed-in viewer is "unknown", never "different organization".
	const isSameOrganization = computed(() => entityType.value !== "project" ? true : verdictStatus.value === "loading" || (verdictStatus.value === "unavailable" && isAuthenticated.value) ? undefined : !!projectVerdict.value && !projectVerdict.value.differentOrg);
	const isExternalEditor = computed<boolean>(() => entityType.value === "project" && projectVerdict.value?.isExternalEditor === true && !viewOnly.value);
	const hasAuthenticatedProjectAccess = computed(() => !isAuthenticated.value ? false : verdictStatus.value === "loading" && entityType.value === "project" ? undefined : entityType.value === "project" && projectVerdict.value?.hasAccess === true);

	// Which media/timeline endpoint `api.search` hits: authenticated org-scoped `/search/media` vs the
	// shareId-gated public `/search/public/media`. An authenticated cross-org viewer reaching a shared
	// project only via ?shareId has no project access, so `/search/media` 404s and the media list shows
	// empty — they must use the public path (the shareId is already on the request). Conversations keep
	// the plain authenticated behaviour (they carry no projectVerdict).
	const useAuthenticatedMediaSearch = computed<boolean>(() => {
		if (entityType.value !== "project") return isAuthenticated.value;
		// A confirmed denial selects public/share-link reads. Loading (including a write-403
		// refresh) and outages retain member reads; the server enforces read access.
		// A share link does not remove an authenticated member's own read access.
		const status = projectVerdict.value?.hasAccess === false ? "denied" : verdictStatus.value;
		const authenticatedMember = isAuthenticated.value && !!me.value?.organization?.id &&
			me.value.organization.id === entity.value?.organizationId;
		return status !== "denied" && (authenticatedMember || hasAuthenticatedProjectAccess.value === true);
	});

	// View-only members and share visitors can ask; guests retain the public-link flow.
	const canRequestEditAccess = computed<boolean>(() => {
		return (
			entityType.value === "project" &&
			viewOnly.value &&
			!verdictUnavailable.value &&
			(!isAuthenticated.value || (projectVerdict.value?.can.requestEditAccess === true && (projectVerdict.value.origin === "share" || projectVerdict.value.origin === "org-member" || (!!sharingId.value && !projectVerdict.value.hasAccess))))
		);
	});
	const requestAccessDialogRef = ref<{ open: () => void } | null>(null);
	const openRequestAccessDialog = (): void => {
		requestAccessDialogRef.value?.open();
	};

	// Mirror ProjectPage.vue's showCommentBox logic for controlling Comments tab visibility
	const showCommentsTab = computed(() => {
		// Permission Retry keeps an established project surface visible while it reloads.
		return !!entity.value && !!me.value && (!loading.value || (entityType.value === "project" && projectVerdict.value?.hasAccess === true));
	});

	// LSP-17772 (QA): the project content tabs after About/Media, by persona (product decision):
	//   - external view-only viewer (authenticated, no project access) → About + Media.
	//   - owner / same-org member / bound-share (hasAuthenticatedProjectAccess) → the full set.
	//   - anonymous public-link visitor → About-less (About is auth-gated) Media.
	// LSP-16659: there is no Documents tab any more — documents are tiles in the media grid, which every
	// viewer of the project already sees. An old `#documents` link resolves through LEGACY_TAB_ALIASES.
	// Sparky's Reports, Sheets and Checklists are editor/member surfaces — access-holders only.
	// Comments is likewise an editor/member surface and is hidden from an external view-only viewer.
	// Shared by the desktop and mobile project branches so the two tab lists can't drift and stay
	// aligned with the v-window panes (Yasir #12/#1).
	const pushProjectContentTabs = (tabList: Array<{ id: string; name: string }>, includeComments: boolean): void => {
		if (hasAuthenticatedProjectAccess.value || (isAuthenticated.value && verdictStatus.value === "loading")) {
			tabList.push({ id: tabIds["sparkys-reports"], name: "Sparky's Reports" });
			tabList.push({ id: tabIds.checklists, name: "Checklists" });
			tabList.push({ id: tabIds.sheets, name: "Sheets" });
		}
		if (includeComments && hasAuthenticatedProjectAccess.value) {
			tabList.push({ id: tabIds.comments, name: "Comments" });
		}
	};

	const tabs = computed(() => {
		const includeComments = showCommentsTab.value;
		if (isMobileLayout.value) {
			if (entityType.value === "conversation") {
				const tabs = [{ id: tabIds.media, name: "Media" }];
				if (isAuthenticated.value) tabs.push({ id: tabIds.about, name: "About" });
				if (canViewLuckyReports.value) tabs.push({ id: tabIds["sparkys-reports"], name: "Sparky's Reports" });
				if (includeComments) tabs.push({ id: tabIds.comments, name: "Comments" });
				return tabs;
			} else {
				const tabs = [{ id: tabIds.media, name: "Media" }];
				if (isAuthenticated.value) tabs.push({ id: tabIds.about, name: "About" });
				pushProjectContentTabs(tabs, includeComments);
				return tabs;
			}
		}

		if (entityType.value === "conversation") {
			const tabs = [{ id: tabIds.about, name: "About" }];
			if (canViewLuckyReports.value) tabs.push({ id: tabIds["sparkys-reports"], name: "Sparky's Reports" });
			if (includeComments) tabs.push({ id: tabIds.comments, name: "Comments" });
			return tabs;
		} else {
			const tabs = [{ id: tabIds.about, name: "About" }];
			pushProjectContentTabs(tabs, includeComments);
			return tabs;
		}
	});

	function addEventListeners() {
		Socket.on(constants.SOCKET_EVENT.MASTERDOCUMENT_CREATE as any, onMasterDocument);
		Socket.on(constants.SOCKET_EVENT.MASTERDOCUMENT_DELETE as any, onMasterDocumentDelete);
		Socket.on(constants.SOCKET_EVENT.MASTERRECORDING_DELETE as any, onMasterRecordingDelete);
		Socket.on(constants.SOCKET_EVENT.RECORDINGSESSION_UPDATE as any, onRecordingSessionUpdate);
		Socket.on(constants.SOCKET_EVENT.CONVERSATION_UPDATE as any, onConversationUpdate);
		// LSP-16086: reconcile on socket reconnect to catch media missed while down.
		Socket.on("connect" as any, onSocketConnect);
		Socket.on("disconnect" as any, onSocketDisconnect);
	}

	// LSP-17745 (Bugbot review): the strip lives under `v-if="entity"`, and an in-place entity switch
	// sets `entity.value = undefined` before reloading — so its DOM is destroyed and rebuilt. Binding
	// once from onMounted left BOTH the scroll listener (pre-existing) and the geometry observer on
	// detached nodes, so from the first switch onward the chevrons stopped updating and nothing clamped
	// the offset. Re-point at whatever container is live now; a no-op when it is the same node.
	function bindTabStripListeners() {
		const container = document.querySelector(".entity-tabs .v-slide-group__container") as HTMLElement | null;
		if (container === tabsContainer) return;

		unbindTabStripListeners();
		if (!container) return;

		tabsContainer = container;
		handleTabsContainerScroll = () => {
			checkTabsScrollPosition();
		};
		// Vuetify 3 uses native scrolling on the container
		container.addEventListener("scroll", handleTabsContainerScroll);

		// Observe the strip's own boxes so the overflow flag and the scroll offset are re-derived from
		// whatever the layout actually is, rather than from one snapshot taken before webfonts and the
		// tab list have settled. Observing the content as well as the container matters: a tab
		// appearing/disappearing changes the content width while the container's stays put, and only
		// the content's box reports that.
		if (typeof ResizeObserver !== "undefined") {
			tabsGeometryObserver = new ResizeObserver(() => syncTabsScrollState());
			tabsGeometryObserver.observe(container);
			const content = container.querySelector(".v-slide-group__content");
			if (content) tabsGeometryObserver.observe(content);
		} else {
			// Fallback for engines without ResizeObserver: the original single late probe.
			setTimeout(() => checkTabsScrollability(), 100);
		}
	}

	function unbindTabStripListeners() {
		if (tabsContainer && handleTabsContainerScroll) {
			tabsContainer.removeEventListener("scroll", handleTabsContainerScroll);
		}
		tabsContainer = null;
		handleTabsContainerScroll = null;
		tabsGeometryObserver?.disconnect();
		tabsGeometryObserver = null;
	}

	// Rebind whenever the strip is torn down and rebuilt. `entity` going undefined and back is exactly
	// the in-place-switch sequence above; nextTick lets the new strip mount before we look for it.
	watch(entity, () => {
		nextTick(() => {
			if (!isTabStripAlive()) return;
			bindTabStripListeners();
		});
	});

	// LSP-17745 (Bugbot rounds 2-3): every registration in this component's deferred setup can land
	// AFTER teardown — the
	// watcher fires, then navigation unmounts the page before the callback runs. That matters more than
	// a normal late callback here because bindTabStripListeners resolves its target with a
	// document-wide querySelector: an unguarded late bind attaches a scroll listener and a
	// ResizeObserver to whatever strip is on screen by then — potentially the NEXT entity page's — with
	// this instance's teardown already finished and nothing left to remove them.
	//
	// `isComponentUnmounted` alone is not enough: it is set in onUnmounted, which runs AFTER
	// onBeforeUnmount has already unbound, so there is a window where teardown is done but the flag is
	// still false. tabStripTornDown closes exactly that window; both are checked because either can be
	// the one that is true first.
	function isTabStripAlive(): boolean {
		return !tabStripTornDown && !isComponentUnmounted;
	}

	function addPostInitEventListeners() {
		nextTick(() => {
			// LSP-17745 (Bugbot round 3): guard the WHOLE deferred body, not one call inside it. An
			// earlier pass guarded only the bind and left the three registrations below exposed — they
			// are equally able to land after onBeforeUnmount has already run its removals, and then
			// nothing ever takes them off: the visualViewport handler keeps calling syncTabsScrollState
			// (which resolves its target with a document-wide querySelector, so it can clamp a LATER
			// page's strip), and the ?debug=true overlay leaves a permanent rAF loop.
			// A partial guard was worse than none here — it read as handled.
			if (!isTabStripAlive()) return;

			bindTabStripListeners();

			window.addEventListener("resize", checkTabsScrollability);

			// LSP-17745: iOS Safari reports pinch/double-tap zoom ONLY here — `window.resize` stays
			// silent because the layout viewport never changed. Without this the strip has no way to
			// notice that a zoom left it parked outside its scrollable range.
			if (window.visualViewport) {
				handleTabsViewportChange = () => syncTabsScrollState();
				window.visualViewport.addEventListener("resize", handleTabsViewportChange);
				window.visualViewport.addEventListener("scroll", handleTabsViewportChange);
			}

			// LSP-17745: this strip's failure mode is only reachable on a real phone, where there is no
			// console to read. Behind the same ?debug=true switch as the upload alerts above.
			if (isDebugMode) teardownTabStripDebugOverlay = mountTabStripDebugOverlay();
		});
	}

	onBeforeUnmount(() => {
		if (entityType.value === "project") verdictStore.invalidate([entityId.value]);
		tabStripTornDown = true;
		unbindTabStripListeners();
		window.removeEventListener("resize", checkTabsScrollability);
		if (window.visualViewport && handleTabsViewportChange) {
			window.visualViewport.removeEventListener("resize", handleTabsViewportChange);
			window.visualViewport.removeEventListener("scroll", handleTabsViewportChange);
		}
		cancelAnimationFrame(tabsGeometryFrame);
		teardownTabStripDebugOverlay?.();
		teardownTabStripDebugOverlay = null;
		document.removeEventListener("visibilitychange", onVisibilityChange);
	});

	function onMasterDocument({ masterdocument }: any) {
		if (!validateEntityId(masterdocument.conversationId, masterdocument.projectId)) {
			return;
		}

		// If the lightbox is open, wait for the user to close it to reload the timeline.
		// Otherwise, the content the user is viewing will be reloaded or changed

		if (lightboxOpen.value) {
			// Queue only the surface this subtype actually affects: snapshots surface in
			// the timeline; files surface in the timeline too (LSP-16659) plus the ready-PDF
			// count (the `documents` surface). Unknown subtypes queue both defensively.
			// Checklists/checklist-media always refresh on close.
			if (masterdocument.documentRelatedEntity === constants.DOCUMENT_TYPE.SNAPSHOT) {
				pendingReloads.value.timeline = true;
			} else if (masterdocument.documentRelatedEntity === constants.DOCUMENT_TYPE.FILE) {
				pendingReloads.value.timeline = true;
				pendingReloads.value.documents = true;
			} else {
				pendingReloads.value.timeline = true;
				pendingReloads.value.documents = true;
			}
			pendingReloads.value.checklists = true;
			pendingReloads.value.checklistMedia = true;
			return;
		}

		// LSP-16086: coalesce create/update reloads through one scheduler (trailing
		// debounce + ceiling) instead of one independent setTimeout per event, so a
		// burst of uploads collapses into a single bounded reload per surface.
		if (masterdocument.documentRelatedEntity === constants.DOCUMENT_TYPE.SNAPSHOT) {
			timelineReloadScheduler.schedule({ timeline: true, checklists: true, checklistMedia: true }, "delta");
		} else if (masterdocument.documentRelatedEntity === constants.DOCUMENT_TYPE.FILE) {
			// LSP-16659: a new file is a new grid tile, and possibly a new PDF for the Sparky count.
			timelineReloadScheduler.schedule({ timeline: true, documents: true, checklists: true, checklistMedia: true }, "delta");
		} else {
			timelineReloadScheduler.schedule({ timeline: true, documents: true, checklists: true, checklistMedia: true }, "delta");
		}
	}

	function onRecordingSessionUpdate({ recordingsession }: any) {
		if (!validateEntityId(recordingsession.conversationId, recordingsession.projectId)) {
			return;
		}

		// If the lightbox is open, wait for the user to close it to reload the timeline.
		// Otherwise, the content the user is viewing will be reloaded or changed

		if (lightboxOpen.value) {
			pendingReloads.value.timeline = true;
			pendingReloads.value.checklists = true;
			pendingReloads.value.checklistMedia = true;
			return;
		}

		// LSP-16086: recordings need a FULL reload (created = session-start can predate
		// the delta window). Coalesce through the scheduler; "full" wins for the burst.
		timelineReloadScheduler.schedule({ timeline: true, checklists: true, checklistMedia: true }, "full");
	}

	// Refresh the locally-held conversation status when the backend advances it (e.g. →
	// ReadyForReview after transcode). ScribeReportDialog's async edit gate reads the status
	// from these props; the recordings API that previously re-checked readiness was removed,
	// so without this live update the Edit button can stay disabled after the session is
	// actually ready. Status-only update, mirroring ConversationsPage.handleConversationUpdate.
	async function onConversationUpdate({ conversation }: any) {
		if (!conversation?.id) return;
		if (entityType.value === "conversation") {
			const current = entity.value as Conversation | undefined;
			if (!current || conversation.id !== entityId.value) return;

			// LSP-16701: notes saved in contact-live during a live call must show up here promptly. The
			// Conversations UPDATE trigger already fires this webhook for ANY column change and
			// concierge-socket's handleConversationWebhook emits the FULL conversation row to the org's
			// admin room, so the new notes are already in this payload — they were simply being dropped
			// because this handler only read `status`.
			//
			// The write below is deliberately UNCONDITIONAL (review 5346441261 finding #5 — the previous
			// comment here claimed it "can never overwrite notes an admin is composing", which was true of
			// the FIELD but not of this object). `current` is the deep-reactive entity EntityAbout receives
			// as props.entity, and onNotesInput mutates entity.notes in place, so while an admin is typing
			// this can write the server value back over their in-place text on the shared object. That is
			// intended and must not be gated on isDirty:
			//   - The admin's text lives in EntityAbout's own selectedNotes buffer, which this never
			//     touches, and save() omits the `notes` key entirely unless the user edited it — so no
			//     typed text is displayed wrong or persisted wrong.
			//   - EntityAbout's props.entity.notes watcher is what STASHES a pushed value into
			//     pendingServerNotes while the form is dirty, to be adopted when it goes clean (on save or
			//     on revert). Skipping the write while dirty would mean a note pushed mid-typing never
			//     reaches that watcher at all and is silently lost — the opposite of the fix.
			// The cost is that a direct reader of entity.notes sees the server value rather than the
			// in-progress text until the next keystroke; EntityAbout is the only reader today. A future
			// change that binds the field straight to props.entity.notes would need selectedNotes' role
			// re-thought, not this line removed.
			if (typeof conversation.notes === "string" || conversation.notes === null) {
				const incomingNotes = conversation.notes ?? "";
				if ((current.notes ?? "") !== incomingNotes) {
					console.log("EntityDetailPage: applying socket notes update", {
						conversationId: conversation.id,
						incomingLength: incomingNotes.length
					});
					current.notes = incomingNotes as any;
				}
			}

			if (conversation.status && current.status !== conversation.status) {
				current.status = conversation.status;
				// Socket-driven status advance (e.g. recording → ReadyForReview) is NOT a user edit. Accept it into
				// the About form's dirty baseline once the prop has flushed, so leaving doesn't falsely prompt "Save?".
				// Genuine edits to other About fields stay dirty and still prompt (LSP-16361, LSP-16626).
				await nextTick();
				entityAboutRef.value?.acceptProgrammaticStatus();
			}
			return;
		}
		if (!conversation.status) return;
		const match = reportConversations.value.find((item) => item.id === conversation.id);
		if (match && match.status !== conversation.status) {
			match.status = conversation.status;
		}
	}

	// Broadcast point for recording deletes is MasterRecordings (covers both
	// RecordingSessions and RecordingChannels via DB sync triggers). The
	// lightbox keys artifacts on the source row id, not the master id, so we
	// use sourceId for the lookup.
	function onMasterRecordingDelete({ masterrecording }: any) {
		if (
			!masterrecording ||
			typeof masterrecording !== "object" ||
			typeof masterrecording.sourceId !== "string" ||
			typeof masterrecording.conversationId !== "string"
		) {
			logError("Ignoring malformed masterrecording_delete payload", new Error("Malformed masterrecording_delete payload"), {
				idType: typeof masterrecording?.id,
				sourceIdType: typeof masterrecording?.sourceId,
				conversationIdType: typeof masterrecording?.conversationId,
				projectIdType: typeof masterrecording?.projectId
			});
			return;
		}

		if (
			!validateEntityId(
				masterrecording.conversationId,
				typeof masterrecording.projectId === "string" ? masterrecording.projectId : ""
			)
		) {
			return;
		}

		// A remote delete is as final as a local one, so it retires the latch too (Cursor Bugbot, PR #3242).
		// It sits here rather than inside handleArtifactRemovedInLightbox for two reasons: that helper is
		// shared with snapshot/file deletes, whose ids the recording latch never holds; and it takes two
		// different routes depending on whether the lightbox is open (immediate removal vs a coalesced
		// reload), neither of which tells the latch anything. Placing it in the recording-specific handler,
		// after validateEntityId, covers both routes and cannot be reached by another conversation's delete.
		retireIncompleteRecordingLatch([masterrecording.sourceId]);

		handleArtifactRemovedInLightbox(
			masterrecording.sourceId,
			"masterrecording_delete",
			"This recording was just deleted by another user."
		);
	}

	// Broadcast point for snapshot AND file deletes is MasterDocuments (covers
	// both Snapshots and Files via DB sync triggers). Snapshots flow through
	// the lightbox; files surface in the documents tab. Unknown subtypes fall
	// through to a defensive reload-everything path so a future
	// documentRelatedEntity value (e.g. a new media type) doesn't silently
	// stop refreshing the UI.
	function onMasterDocumentDelete({ masterdocument }: any) {
		if (
			!masterdocument ||
			typeof masterdocument !== "object" ||
			typeof masterdocument.id !== "string" ||
			typeof masterdocument.conversationId !== "string"
		) {
			logError("Ignoring malformed masterdocument_delete payload", new Error("Malformed masterdocument_delete payload"), {
				idType: typeof masterdocument?.id,
				conversationIdType: typeof masterdocument?.conversationId,
				projectIdType: typeof masterdocument?.projectId,
				documentRelatedEntity:
					typeof masterdocument?.documentRelatedEntity === "string"
						? masterdocument.documentRelatedEntity
						: typeof masterdocument?.documentRelatedEntity
			});
			return;
		}

		if (
			!validateEntityId(masterdocument.conversationId, typeof masterdocument.projectId === "string" ? masterdocument.projectId : "")
		) {
			return;
		}

		if (lightboxOpen.value) {
			if (masterdocument.documentRelatedEntity === constants.DOCUMENT_TYPE.SNAPSHOT) {
				if (typeof masterdocument.sourceId !== "string") {
					// Snapshot subtype with no resolvable sourceId is a sender-
					// side contract violation — admin can't reconcile the
					// lightbox or the timeline without it. Log loudly so the
					// regression is visible instead of silently falling through
					// to the file branch (which would touch the wrong tab).
					logError(
						"Ignoring masterdocument_delete with SNAPSHOT subtype but missing sourceId",
						new Error("Snapshot masterdocument_delete missing sourceId"),
						{ idType: typeof masterdocument.id, sourceIdType: typeof masterdocument.sourceId }
					);
					return;
				}
				handleArtifactRemovedInLightbox(
					masterdocument.sourceId,
					"masterdocument_delete",
					"This photo was just deleted by another user."
				);
				return;
			}

			if (masterdocument.documentRelatedEntity === constants.DOCUMENT_TYPE.FILE) {
				// LSP-16659: files are grid tiles now. The lightbox never holds a document, so there is
				// nothing to remove from it — queue the grid reload for close and refresh the ready-PDF
				// count eagerly so Sparky's gate is right the moment the lightbox closes.
				pendingReloads.value.timeline = true;
				Promise.resolve(refreshReadyPdfDocumentCount()).catch((error) => {
					logError("Failed to reload documents after remote masterdocument_delete", error);
				});
				Promise.resolve(entityChecklistsRef.value?.loadProjectChecklists()).catch((error) => {
					logError("Failed to reload checklists after remote masterdocument_delete", error);
				});
				// Route through the visibility gate so a delete arriving while the
				// checklist tab is hidden does not fire the heavy 1000-row media query.
				refreshChecklistMediaIfVisible();
				return;
			}

			// Unknown subtype — refresh both surfaces defensively. Timeline
			// reload is queued via pendingReloads.timeline because doing it now would
			// race with the lightbox's render against it.
			pendingReloads.value.timeline = true;
			Promise.resolve(refreshReadyPdfDocumentCount()).catch((error) => {
				logError("Failed to reload documents after remote masterdocument_delete", error);
			});
			Promise.resolve(entityChecklistsRef.value?.loadProjectChecklists()).catch((error) => {
				logError("Failed to reload checklists after remote masterdocument_delete", error);
			});
			// Route through the visibility gate (see refreshChecklistMediaIfVisible).
			refreshChecklistMediaIfVisible();
			return;
		}

		// Lightbox not open — schedule the right type-specific reload. The
		// scheduler accumulates flags across coalesced calls so a burst of
		// mixed snapshot + file deletes still ends up running both timeline
		// and documents reloads at the end of the window.
		if (masterdocument.documentRelatedEntity === constants.DOCUMENT_TYPE.SNAPSHOT) {
			scheduleMasterDeleteReload({ timeline: true, checklists: true, media: true }, "masterdocument_delete");
		} else if (masterdocument.documentRelatedEntity === constants.DOCUMENT_TYPE.FILE) {
			// LSP-16659: a deleted file is a grid tile too, so the timeline reloads alongside the PDF count.
			scheduleMasterDeleteReload({ timeline: true, documents: true, checklists: true, media: true }, "masterdocument_delete");
		} else {
			// Unknown subtype: refresh both timeline and documents defensively
			// so a new media type doesn't silently fail to update the UI.
			scheduleMasterDeleteReload({ timeline: true, documents: true, checklists: true, media: true }, "masterdocument_delete");
		}
	}

	// Lightbox-aware remote-delete path. The lightbox advances to the next/
	// previous artifact when the deleted item is the current one, only
	// closing when it was the last item. When the lightbox isn't open,
	// schedules a coalesced timeline + checklists + media reload.
	function handleArtifactRemovedInLightbox(sourceId: string, eventName: string, toastMessage: string) {
		if (lightboxOpen.value) {
			const wasCurrent = mediaLightBox.value?.currentArtifactId === sourceId;

			// Set pendingReloads.timeline BEFORE removeArtifactById — when the deleted
			// item is the last in the list, removeArtifactById emits
			// close-lightbox synchronously, and the parent's closeLightBox
			// handler reads the per-surface pending flags immediately. Setting it after the
			// call would skip the timeline reload on the empty-list path.
			pendingReloads.value.timeline = true;
			const handled = mediaLightBox.value?.removeArtifactById?.(sourceId) ?? false;

			if (handled && wasCurrent) {
				toast.info(toastMessage);
			}

			// Checklists and media list reference the artifact directly, so
			// refresh them now rather than waiting for the lightbox to close.
			Promise.resolve(entityChecklistsRef.value?.loadProjectChecklists()).catch((error) => {
				logError(`Failed to reload checklists after remote ${eventName}`, error);
			});
			// Route through the visibility gate (see refreshChecklistMediaIfVisible).
			refreshChecklistMediaIfVisible();
			return;
		}

		scheduleMasterDeleteReload({ timeline: true, checklists: true, media: true }, eventName);
	}

	// Coalescing scheduler for master-table delete reloads. Multiple delete
	// events arriving within 5s collapse into one trailing reload, but the
	// flags accumulate across calls — so a recording delete followed by a
	// file delete still ends up running both the timeline and documents
	// reload, rather than the second clearTimeout dropping the first's work.
	// Uses its own timeout handle (not the shared reloadTimeout) so it
	// doesn't conflict with reloadTimeline()'s narrower scheduling.
	// LSP-16086: deletes deliberately stay on this coalescer rather than the
	// create/update timelineReloadScheduler — deletes are always a FULL reload (a
	// created-based delta window can't reconcile a removal), do lightbox reconciliation
	// up front, and carry per-flag event-name attribution for the failure logs. Folding
	// them into the delta/full-mode scheduler would force concurrent creates to full and
	// drop that attribution, so the two coalescers are kept separate by design.
	function scheduleMasterDeleteReload(events: Partial<typeof masterDeleteReloadFlags>, eventName: string) {
		// Capture the eventName per-flag at flag-set time so a timeline
		// reload's error log cites the recording delete that requested it,
		// not a later masterdocument delete that happened to schedule the
		// trailing-edge timer. Same flag re-set by a later event overwrites
		// the name — last-setter wins for log attribution.
		if (events.timeline) {
			masterDeleteReloadFlags.timeline = true;
			masterDeleteReloadEventNames.timeline = eventName;
		}
		if (events.documents) {
			masterDeleteReloadFlags.documents = true;
			masterDeleteReloadEventNames.documents = eventName;
		}
		if (events.checklists) {
			masterDeleteReloadFlags.checklists = true;
			masterDeleteReloadEventNames.checklists = eventName;
		}
		if (events.media) {
			masterDeleteReloadFlags.media = true;
			masterDeleteReloadEventNames.media = eventName;
		}

		if (masterDeleteReloadTimeout) {
			clearTimeout(masterDeleteReloadTimeout);
		}
		masterDeleteReloadTimeout = setTimeout(() => {
			if (masterDeleteReloadFlags.timeline) {
				const flagEvent = masterDeleteReloadEventNames.timeline;
				masterDeleteReloadFlags.timeline = false;
				masterDeleteReloadEventNames.timeline = "";
				Promise.resolve(loadTimeline(true)).catch((error) => {
					logError(`Failed to reload timeline after remote ${flagEvent}`, error);
				});
			}
			if (masterDeleteReloadFlags.documents) {
				const flagEvent = masterDeleteReloadEventNames.documents;
				masterDeleteReloadFlags.documents = false;
				masterDeleteReloadEventNames.documents = "";
				Promise.resolve(refreshReadyPdfDocumentCount()).catch((error) => {
					logError(`Failed to reload documents after remote ${flagEvent}`, error);
				});
			}
			if (masterDeleteReloadFlags.checklists) {
				const flagEvent = masterDeleteReloadEventNames.checklists;
				masterDeleteReloadFlags.checklists = false;
				masterDeleteReloadEventNames.checklists = "";
				Promise.resolve(entityChecklistsRef.value?.loadProjectChecklists()).catch((error) => {
					logError(`Failed to reload checklists after remote ${flagEvent}`, error);
				});
			}
			if (masterDeleteReloadFlags.media) {
				const flagEvent = masterDeleteReloadEventNames.media;
				masterDeleteReloadFlags.media = false;
				masterDeleteReloadEventNames.media = "";
				// Route through the visibility gate (see refreshChecklistMediaIfVisible).
				refreshChecklistMediaIfVisible();
			}
			masterDeleteReloadTimeout = null;
		}, 5000);
	}

	function validateEntityId(conversationId: string, projectId: string) {
		if (entityType.value === "conversation") {
			return conversationId === entityId.value;
		} else if (entityType.value === "project") {
			return projectId === entityId.value || projectConversationIds.value.includes(conversationId);
		}

		console.error("Invalid entity id", conversationId, projectId, entityId.value);
		return false;
	}

	function reset() {
		entityResetSequence += 1;
		settledAccess = null;
		dismissAccessOutcome?.();
		dismissAccessOutcome = null;
		uploadStatusRequestSequence += 1;
		resetEntityScopedAsyncWork();
		stopScribeWarmup();
		// LSP-19423: entity-scoped, exactly like the warm-up above — a retry armed for the outgoing
		// conversation must not fire against the incoming one.
		stopNotesReconcileRetry();
		projectScribeMonitoringReady.value = false;
		skipNextProjectConversationIdsWatchSeed = false;
		forbiddenFlow.cancel();
		mediaResponse.value = emptyEntitySearchResponse();
		showTimelineResultsFor(null);
		uploadStatus.value = null;
		// LSP-16842 (cursor review): the flag must fall back to "not yet settled" alongside the
		// status it guards. reset() runs on every in-place entity switch, and leaving it true there
		// would let the reminder prompt flash on the new session against the previous session's
		// (or a null) status — the exact defect the flag exists to prevent.
		uploadStatusFetchSettled.value = false;
		searchRequest.value = null;
		checklistSearchRequest.value = null;
		readyPdfDocumentCount.value = 0;
		pendingImages.value = 0;
		pendingDocs.value = 0;
		pendingVideos.value = 0;
		// LSP-16659: entity-scoped like the counters above — the next entity must not inherit either.
		documentUploadedSinceLastFlush = false;
		timelinePdfDocument.value = null;
		// ls-heroku-ci (HIGH): the handoff state belongs with the counters it feeds, not outside them.
		// uploadHandedOffOnMount is the read-state equivalent of pendingDocs (see flushStaleCounters),
		// but it was the one member of that group reset() did not clear -- so handing an upload off to
		// project A and then switching in-place to project B left it set, and B's first completed status
		// poll fired reloadDocumentsAfterUploadComplete() for a project that never had an upload.
		//
		// Safe here because consumeUploadHandoff() runs LATER in initializeComponent (after this reset
		// and after the id seed), so a genuine handoff for the entity being switched TO is claimed after
		// this clears the previous one.
		//
		// Only the BOOLEAN is cleared here. handedOffUploadConversationId and handedOffUploadEntityId
		// are already entity-scoped at their consumer -- handedOffConversationApplies() requires
		// handedOffUploadEntityId === entityId.value -- so a stale pair cannot leak into another
		// entity. Clearing them here would additionally change what withHandedOffConversation() seeds
		// on this pass, which is behaviour this fix has no reason to touch.
		uploadHandedOffOnMount = false;
		lastUploadActivityAt = 0;
		preserveUploadMediaOnReconnectUntil = 0;
		isUploading.value = false;
		uploadFinishedSinceLastStart = false;
		uploadFinishedAt = 0;
		uploadMayFinalizeAfterIdle = false;
		latestUploadStatusFetchIsComplete = false;
		pendingDocumentReloadPromise = null;
		// LSP-16086 (Bugbot #2/#5): in-place navigation (route.params.id change → this
		// reset()) — clear the prior entity's reconcile stamp so the new entity's
		// safety net isn't suppressed for ~minGap. Set to "now" so the safety net
		// starts gated from now (the new entity's loadTimeline will re-stamp on
		// success); a failed initial load leaves it at this value, and the periodic
		// safety tick will then recover ~15s later.
		lastTimelineReconcileAt = Date.now();
		// Review 5346441261 finding #7: the notes reconcile throttle is per-entity too, and was the one
		// per-entity mutable here that navigation never cleared — so a reconcile for conversation A
		// suppressed conversation B's FIRST return-to-visible/reconnect reconcile for up to 3s. Cleared
		// outright rather than stamped to "now" (as the timeline stamp above is): the incoming conversation
		// has never been reconciled, so its first attempt should run immediately. notesReconcileInFlight is
		// deliberately NOT touched — it is a live in-flight latch owned by its own `finally`, and that
		// call's isStaleAction guard already discards a result that lands after this switch.
		lastNotesReconcileAt = 0;
		lastNotesReconcileFailed = false;
		// LSP-16706 (Cursor Bugbot): clear the prior entity's consecutive-deferral counters for the same
		// reason — otherwise the new entity's first deferral could inherit the prior entity's streak and
		// get misjudged as persistently stuck, suppressing its own tight retry.
		consecutiveRestartsExhaustedCount = 0;
		consecutiveIncompleteCount = 0;
		// LSP-16086 (Bugbot #3): clear the LSP-16072 "deleted entity" latch so the
		// new entity is not stuck without polling.
		uploadStatusPollDisabled = false;
		// Per-entity, like everything else here: without this, navigating in-place from a conversation that
		// already escalated the reconcile into another one that also holds an unfinished upload logs no
		// mode-change line for the new conversation (ls-heroku-ci review).
		lastReconcileEscalationLogged = null;
		// LSP-15373 (Bugbot): drop the prior entity's matched project shares on in-place navigation so
		// the access gate can't read stale shares if the new entity's access load fails.
		projectVerdictEnabled.value = false;
		projectAccessResolved.value = false;
		verdictUnavailable.value = false;
	}

	function resetEntityScopedAsyncWork(): void {
		clearUploadStatusTimer();
		timelineReloadScheduler.cancel();
		pendingVisibilityReloadMode.value = null;
		pendingReloads.value = { timeline: false, documents: false, checklists: false, checklistMedia: false };
		checklistMediaDirty.value = false;

		if (reloadTimeout) {
			clearTimeout(reloadTimeout);
			reloadTimeout = null;
		}
		if (masterDeleteReloadTimeout) {
			clearTimeout(masterDeleteReloadTimeout);
			masterDeleteReloadTimeout = null;
		}
		masterDeleteReloadFlags.timeline = false;
		masterDeleteReloadFlags.documents = false;
		masterDeleteReloadFlags.checklists = false;
		masterDeleteReloadFlags.media = false;
		masterDeleteReloadEventNames.timeline = "";
		masterDeleteReloadEventNames.documents = "";
		masterDeleteReloadEventNames.checklists = "";
		masterDeleteReloadEventNames.media = "";
	}

	// ls-heroku-ci: takes the caller's switch token so an A -> B -> A revert can be ordered. The
	// identity-only check at the write-back below cannot see it: after returning to A both an OLD in-flight
	// call for A and a NEWER one pass `requestedEntityId === entityId.value`, so the older response could
	// overwrite the newer count. Defaults to the live sequence for callers outside a switch.
	async function refreshReadyPdfDocumentCount(switchToken: number = entitySwitchSequence): Promise<void> {
		if (authSession.sessionLost) return;
		const requestedEntityId = entityId.value;
		const requestedEntityType = entityType.value;
		if (!requestedEntityId || !requestedEntityType) return;

		try {
			let files: any[] = [];
			if (requestedEntityType === "conversation") {
				files = await (api as any).getFiles({
					where: { conversationId: requestedEntityId },
					order: [["index", "DESC"]],
					pageSize: 99999,
					offset: 0
				});
			} else {
				// LSP-19516 — /projects/:id/files is gated by requireProjectReadAccess, so being logged in
				// is not enough: an authenticated visitor reaching a shared project only through ?shareId
				// has no project access and 404s there, which would silently report a 0 PDF count and
				// disable Sparky. Try the authenticated read, then fall back to the public artifacts route
				// with the shareId they already carry. A fallback rather than gating on
				// `hasAuthenticatedProjectAccess`: that computed fails closed until project access
				// resolves, and this count runs early enough that an owner would get 0.
				let loadedFromAuthenticatedRoute = false;
				let authenticatedReadError: unknown = null;
				if (isAuthenticated.value) {
					try {
						files = await fetchAllProjectFiles(requestedEntityId);
						loadedFromAuthenticatedRoute = true;
					} catch (error) {
						authenticatedReadError = error;
						console.warn("refreshReadyPdfDocumentCount: authenticated project files read failed; trying the share-link route", {
							projectId: requestedEntityId,
							hasShareId: !!shareId.value,
							error
						});
					}
				}
				if (!loadedFromAuthenticatedRoute) {
					if (shareId.value) {
						const response = await (api as any).getArtifactsForProjectInsecure(requestedEntityId, shareId.value, "files");
						files = response?.artifacts || [];
					} else if (authenticatedReadError) {
						// A FAILED read is not "no documents": with no shareId to fall back to, rethrow so the
						// outer catch logs it and readyPdfDocumentCount keeps its previous value. Writing []
						// here would let a transient 500 on refresh silently disable Sparky.
						throw authenticatedReadError;
					} else {
						console.warn("refreshReadyPdfDocumentCount: no project access and no shareId", {
							projectId: requestedEntityId,
							isAuthenticated: isAuthenticated.value
						});
						files = [];
					}
				}
			}

			if (
				requestedEntityId !== entityId.value ||
				requestedEntityType !== entityType.value ||
				// ls-heroku-ci: the token check is what catches an A -> B -> A revert, where identity alone
				// cannot distinguish an older in-flight call for A from the current one.
				isStaleAction(requestedEntityId, switchToken)
			) {
				return;
			}
			readyPdfDocumentCount.value = countReadyPdfDocuments(files);
		} catch (error) {
			console.warn("Failed to refresh ready PDF document count for Sparky", error, {
				entityType: requestedEntityType,
				entityId: requestedEntityId
			});
		}
	}

	function isAnonymousShareVisitor(): boolean {
		return !establishedSession.value && !isAuthenticated.value && !authSession.organizationId &&
			rootAuthentication() !== true && !!(route.query?.shareId || props.shareId);
	}
	function markSessionLost(): void {
		if (isAnonymousShareVisitor()) {
			logInfo("EntityDetailPage: anonymous share access outcome; no session to retire", { entityId: entityId.value });
			return;
		}
		if (authSession.sessionLost && lostAtGeneration !== null) {
			// Retirement is idempotent, but a caller may have begun another token since it.
			if (detailLoad.loading.value) detailLoad.supersede("auth-error");
			return;
		}
		lostAtGeneration = advanceEntityLoad(false).switchToken;
		if (!authSession.sessionLost) logInfo("EntityDetailPage: session lost; suspending authenticated work", { entityId: entityId.value });
		// Retire once before clearing auth: no observer may see signed-out + ready. The
		// shared flag owns loss; its clearing edge starts the replacement through the watcher.
		detailLoad.supersede("auth-error");
		isAuthenticated.value = false;
		viewOnly.value = true;
		projectAccountReady.value = false;
		detailLoad.resetStages();
		resetEntityScopedAsyncWork();
		stopNotesReconcileRetry();
		clearDeliveryRefresh();
		authSession.markSessionLost();
	}
	// Network, timeout, 429 and 5xx failures may be retried and must not discard a retained grant; a denial,
	// 401/403, a malformed (drifted) response or any other 4xx is terminal.
	function isTransientVerdictFailure(error: unknown): boolean {
		if (error instanceof Error && error.message === "malformed verdict") return false;
		const status = projectVerdictFailure(error).status;
		return status === undefined || status === 408 || status === 429 || status >= 500;
	}
	// A visitor who never signed in and is not on a share link keeps the pre-existing signed-out flow: an
	// "Error" prompt, then back to the list (no session-loss latch, no auth-check Retry loop).
	function isNeverSignedInVisit(): boolean {
		return entityType.value === "project" && !hadEstablishedSession() && !isAuthenticated.value && !(route.query?.shareId || props.shareId);
	}
	function isNeverSignedInFailure(error: unknown): boolean {
		return !hadEstablishedSession() && !(route.query?.shareId || props.shareId) &&
			(error instanceof UnauthenticatedError || getApiErrorStatus(error) === 401);
	}
	function promptNeverSignedIn(id: string, switchToken: number, error: unknown): void {
		showEntityNotFound(id, switchToken, getEntityLoadErrorMessage(error));
	}
	// A session that existed on this page (or in the shell) can be LOST; a visitor who never signed in
	// has nothing to lose and keeps the pre-existing signed-out flow (no session-loss latch, no Retry loop).
	function hadEstablishedSession(): boolean {
		return establishedSession.value || isAuthenticated.value || rootAuthentication() === true || !!authSession.organizationId;
	}
	function isMissingSession(error: unknown): boolean {
		return (establishedSession.value || isAuthenticated.value || rootAuthentication() === true || !!authSession.organizationId) &&
			(error instanceof UnauthenticatedError || getApiErrorStatus(error) === 401);
	}
	function detailStopReason(id: string, switchToken: number, loadToken?: number): EntityDetailStopReason | null {
		if (authSession.sessionLost || lostAtGeneration !== null) return "auth-lost";
		if (isComponentUnmounted) return "unmounted";
		if (isStaleAction(id, switchToken) || (loadToken !== undefined && !detailLoad.owns(loadToken))) return "superseded";
		return null;
	}
	type DetailStageOutcome = { status: "done" } | { status: "failed"; reason: EntityDetailStopReason } | { status: "superseded" };
	function stageStop(outcome: DetailStageOutcome): EntityDetailStopReason | null {
		return outcome.status === "done" ? null : outcome.status === "superseded" ? "superseded" : outcome.reason;
	}
	function verifyDetailStage(
		stage: EntityDetailRecoveryStage,
		id: string,
		switchToken: number,
		loadToken?: number,
		reason: EntityDetailStopReason | null = null
	): DetailStageOutcome {
		const stopped = detailStopReason(id, switchToken, loadToken) ?? reason;
		if (stopped === "superseded" || stopped === "unmounted") {
			logInfo("EntityDetailPage: discarded superseded stage result", { stage, entityId: id, switchToken, loadToken, reason: stopped });
			return { status: "superseded" };
		}
		if (stopped && stopped !== "access-denied") {
			if (stopped === "entity-missing")
				logWarn("EntityDetailPage: stage did not produce its completed result", {
					stage,
					entityId: id,
					actualEntityId: entity.value?.id
				});
			return { status: "failed", reason: stopped };
		}
		const needsEntity = stage === "project" || stage === "timeline";
		if (!detailLoad.hasCompleted(stage) || (needsEntity && (!detailLoad.hasCompleted("project") || entity.value?.id !== id))) {
			logWarn("EntityDetailPage: stage did not produce its completed result", {
				stage,
				entityId: id,
				actualEntityId: entity.value?.id,
				completed: detailLoad.hasCompleted(stage)
			});
			return { status: "failed", reason: needsEntity && entity.value?.id !== id ? "entity-missing" : `stage-failed:${stage}` };
		}
		return stopped ? { status: "failed", reason: stopped } : { status: "done" };
	}
	// One prompt per load: concurrent stages (access, project) that fail the same way must not stack dialogs.
	let notFoundPromptKey = "";
	function showEntityNotFound(id: string, switchToken: number, customMessage?: string): void {
		const promptKey = `${id}:${switchToken}`;
		if (notFoundPromptKey === promptKey) return;
		notFoundPromptKey = promptKey;
		const message = customMessage ?? (entityType.value === "conversation" ? "Sorry, we couldn't find your stuff." : "Sorry, we couldn't find that project.");
		const confirm = (
			instance?.proxy as unknown as {
				$root: {
					$confirm: {
						open: (
							title: string,
							message: string,
							angry: boolean,
							agree: string,
							cancel: null,
							hideCancel: boolean,
							persistent: boolean
						) => Promise<boolean>;
					};
				};
			}
		).$root.$confirm;
		void confirm.open("Error", message, false, "OK", null, true, true).then(() => {
			// A Retry may have loaded the same entity meanwhile; a leftover dialog must not navigate away from it.
			if (detailStopReason(id, switchToken) || (entity.value?.id === id && detailLoad.phase.value === "ready")) {
				logInfo("EntityDetailPage: discarded stale not-found dismissal", { entityId: id });
				return;
			}
			router.push(entityType.value === "conversation" ? "/conversations" : "/projects");
		});
	}
	async function runDetailStage(
		stage: EntityDetailRecoveryStage,
		id: string,
		switchToken: number,
		loadToken: number | undefined,
		work: () => Promise<EntityDetailStopReason | null>
	): Promise<DetailStageOutcome> {
		const previouslyLoaded = entity.value?.id === id;
		let reason: EntityDetailStopReason | null;
		try {
			reason = await work();
		} catch (error) {
			if (isMissingSession(error)) {
				markSessionLost();
				reason = "auth-lost";
			} else if (isNeverSignedInFailure(error)) {
				promptNeverSignedIn(id, switchToken, error);
				reason = "signed-out";
			} else {
				logWarn("EntityDetailPage: stage failed", { stage, entityId: id, ...projectVerdictFailure(error) });
				reason = isEntityGoneError(error) ? "not-found" : `stage-failed:${stage}`;
			}
		}
		// The existing signed-out flow already prompted and returns to the list: settle it quietly.
		if (reason === "entity-missing" && isNeverSignedInVisit()) reason = "signed-out";
		let outcome = verifyDetailStage(stage, id, switchToken, loadToken, reason);
		if (outcome.status === "failed" && outcome.reason === "entity-missing" && previouslyLoaded)
			outcome = { status: "failed", reason: "not-found" };
		if (outcome.status === "failed" && outcome.reason === "not-found") showEntityNotFound(id, switchToken);
		return outcome;
	}
	// A refresh started during a foreground load observes that owner instead of revoking it.
	// If it fails after the foreground has settled, it must claim a terminal owner of its own.
	// A project-only success must not erase another stage's standing error (and its Retry banner) that was showing
	// before this refresh began: those stages are only recovered by their own Retry.
	type OtherStageErrorPhase = "timeline-error" | "account-error" | "trial-error" | "auth-error";
	const isOtherStageError = (phase: EntityDetailLoadPhase): phase is OtherStageErrorPhase =>
		phase === "timeline-error" || phase === "account-error" || phase === "trial-error" || phase === "auth-error";
	function settleBackgroundStage(
		loadToken: number,
		outcome: DetailStageOutcome,
		deferredToForeground: boolean,
		previousPhase: EntityDetailLoadPhase = "ready",
		backgroundOnly = false
	): void {
		if (!detailLoad.owns(loadToken)) return;
		if (backgroundOnly) {
			detailLoad.settleBackground(loadToken, stageStop(outcome), isAuthenticated.value ? "ready" : "signed-out");
			return;
		}
		if (deferredToForeground) {
			if (detailLoad.loading.value || outcome.status !== "failed") return;
			loadToken = detailLoad.begin();
		}
		const success = outcome.status === "done" && isOtherStageError(previousPhase) ? previousPhase : isAuthenticated.value ? "ready" : "signed-out";
		detailLoad.settle(loadToken, stageStop(outcome), success);
	}

	async function ensureDetailReadiness(id: string, switchToken: number, loadToken?: number): Promise<DetailStageOutcome> {
		const outcome = await runDetailStage("auth", id, switchToken, loadToken, () => ensureDetailReadinessWork(id, switchToken, loadToken));
		if (outcome.status !== "done" || !isAuthenticated.value) return outcome;
		const account = verifyDetailStage("account", id, switchToken, loadToken);
		return account.status === "done" ? verifyDetailStage("trial", id, switchToken, loadToken) : account;
	}

	async function ensureDetailReadinessWork(id: string, switchToken: number, loadToken?: number): Promise<EntityDetailStopReason | null> {
		if (authSession.sessionLost) { markSessionLost(); return "auth-lost"; }
		const current = () => {
			const owned = !isStaleAction(id, switchToken) && (loadToken === undefined || detailLoad.owns(loadToken));
			if (!owned) logInfo("EntityDetailPage: discarded superseded readiness stage", { entityId: id, switchToken, loadToken });
			return owned;
		};
		let stage: EntityDetailRecoveryStage = "auth";
		try {
			if (!detailLoad.hasCompleted("auth")) {
				const authenticated = await (instance?.proxy as any).$auth.isAuthenticated();
				if (!current()) return detailStopReason(id, switchToken, loadToken);
				if (!authenticated) {
					// A visitor who never had a session may still read the public share.
					if (isAnonymousShareVisitor() || !hadEstablishedSession()) viewOnly.value = true;
					else { markSessionLost(); return "auth-lost"; }
				} else { establishedSession.value = true; isAuthenticated.value = true; }
				detailLoad.complete("auth");
			}
			if (!isAuthenticated.value) { viewOnly.value = true; return null; }
			if (entityType.value === "project") void verdictStore.prefetch(id);
			stage = "account";
			if (!detailLoad.hasCompleted(stage)) {
				const account = await api.getMe();
				if (!current()) return detailStopReason(id, switchToken, loadToken);
				me.value = account;
				const roleName = me.value?.account?.roles?.[0]?.name;
				isAdmin.value = !!roleName && ["administrator", "super administrator", "support"].includes(roleName.toLowerCase());
				detailLoad.complete(stage);
				// A known account role may update display state; unknown account data never grants writes.
				if (entityType.value !== "project") viewOnly.value = isViewOnlyUser(me.value);
			}
			stage = "trial";
			if (!detailLoad.hasCompleted(stage)) {
				const trialEnded = await checkHasFreeTrialEnded(me.value?.organization, me.value?.organizationSubscription);
				if (!current()) return detailStopReason(id, switchToken, loadToken);
				freeTrialEnded.value = trialEnded;
				detailLoad.complete(stage);
			}
			projectAccountReady.value = true;
			if (entityType.value === "project" && projectVerdict.value) applyVerdict(projectVerdict.value, switchToken, id);
			return null;
		} catch (error) {
			if (!current()) return detailStopReason(id, switchToken, loadToken);
			if (isMissingSession(error)) { markSessionLost(); return "auth-lost"; }
			// Transient failures preserve the last good auth/role/trial state. Unknown readiness
			// closes WRITE gates through projectAccountReady, never by pretending to log out.
			logWarn("EntityDetailPage: readiness stage unavailable", { stage, entityId: id, ...projectVerdictFailure(error) });
			return `stage-failed:${stage}`;
		}
	}

	// Direct/manual initialization defaults to the current switch ownership. Stage Retry never enters here.
	async function initializeComponent(
		switchToken: number = entitySwitchSequence,
		loadToken = detailLoad.begin()
	): Promise<DetailStageOutcome> {
		const requestedId = (route.params.id as string) ?? props.projectId;
		let stopReason: EntityDetailStopReason | null = "stage-failed:content";
		try {
			stopReason = await initializeComponentContent(switchToken, loadToken);
		} finally {
			stopReason = stageStop(verifyDetailStage("timeline", requestedId, switchToken, loadToken, stopReason));
			if (stopReason === "auth-lost")
				logWarn("EntityDetailPage: initializer deferred while session is lost", {
					entityId: requestedId,
					switchToken,
					loadToken,
					stopReason
				});
			detailLoad.settle(loadToken, stopReason, !isAuthenticated.value ? "signed-out" : "ready");
		}
		return verifyDetailStage("timeline", requestedId, switchToken, loadToken, stopReason);
	}

	async function initializeComponentContent(switchToken: number, loadToken: number): Promise<EntityDetailStopReason | null> {
		detailLoad.resetStages();
		reset();

		if (route.path.includes("conversation")) {
			entityType.value = "conversation";
			service.value = new ConversationService();
		} else {
			entityType.value = "project";
			service.value = new ProjectService();
		}

		const previousProjectId = entityType.value === "project" ? entityId.value : "";
		projectAccountReady.value = false;
		if (entityType.value === "project" || !me.value?.user) viewOnly.value = true;
		entityId.value = (route.params.id as string) ?? props.projectId;
		if (previousProjectId && previousProjectId !== entityId.value) verdictStore.invalidate([previousProjectId]);
		projectVerdictEnabled.value = entityType.value === "project";
		// LSP-17965: a genuine arrival at an entity — a mount, or an in-place route.params.id switch, which
		// are the only two callers of initializeComponent — is the "next visit" the incomplete-upload
		// banner's close button promises the warning will return on. Module-scoped dismissal outlives an
		// in-SPA navigation, so without this the promise only held across a hard reload and a closed banner
		// stayed closed for a still-unfinished upload.
		//
		// Deliberately HERE and not in the banner: crossing the `smAndDown` breakpoint destroys one banner
		// mount and creates the other with the entityId unchanged, and LSP-17328 specifically made a
		// dismissal survive that. This seam only runs when the entity itself changes, so it cannot.
		clearIncompleteUploadDismissal(entityId.value);
		// break-it round 3: captured once, up front, so the catch block below (and its dialog's detached
		// .then() dismiss handler, which can fire arbitrarily later) can recheck ownership against the id
		// THIS switch was loading for, via isStaleAction — not just switchToken, which alone can't detect a
		// route change the watcher hasn't observed yet.
		const initialEntityId = entityId.value;
		// LSP-15388: consumed HERE — after reset() (which would otherwise wipe the in-progress marking)
		// and after entityId is established (so the handoff is scoped to the right owner), but before
		// the id seed below, so that seed already carries it. Stripped further down in the same
		// router.replace as the pending-count params, so a refresh or back/forward cannot replay a
		// finished upload as in-flight.
		const handedOffConversationId = consumeUploadHandoff();
		projectConversationIds.value = withHandedOffConversation([entityId.value]);
		startingLoading.value = true;
		sharingId.value = props.shareId ?? "";

		// Restore persisted filter state from URL query params or Pinia store cache
		filterPersistence.restoreFromUrl();

		currentSearchTerm.value = filterPersistence.userFilters.value.searchTerm ?? "";

		const query = route.query;
		if (query?.pendingImages) {
			pendingImages.value = parseInt(query.pendingImages as string, 10) || 0;
		}
		if (query?.pendingDocs) {
			pendingDocs.value = parseInt(query.pendingDocs as string, 10) || 0;
		}
		if (query?.pendingVideos) {
			pendingVideos.value = parseInt(query.pendingVideos as string, 10) || 0;
		}

		if (query?.pendingImages || query?.pendingDocs || query?.pendingVideos) {
			if (pendingImages.value + pendingDocs.value + pendingVideos.value > 0) {
				markUploadInProgressWithoutTerminalEvent({ skipActivityStamp: true });
			}
		}

		if (query?.pendingImages || query?.pendingDocs || query?.pendingVideos || handedOffConversationId) {
			const newQuery = { ...query };
			delete newQuery.pendingImages;
			delete newQuery.pendingDocs;
			delete newQuery.pendingVideos;
			delete newQuery.uploadStarted;
			// This replace is what stops a refresh or back/forward from replaying a finished upload as
			// in-flight, so a silent rejection leaves ?uploadStarted armed in the URL. Vue Router rejects
			// on an aborted/redirected navigation; say so rather than surfacing an unhandled rejection.
			// Promise.resolve wraps it for the same reason navigateSafely does: a router whose replace
			// returns undefined (any mock, and some router variants) turns a bare .catch into a TypeError
			// thrown from inside the mounted hook -- which is worse than the unhandled rejection this catch
			// was added to prevent.
			const warnUploadHandoffStripFailed = (navigationError: unknown) => {
				console.warn("EntityDetailPage: could not strip the upload handoff query; it may replay on refresh", {
					tags: { method: "initializeComponent", file: "EntityDetailPage" },
					extra: { entityId: entityId.value, error: navigationError }
				});
			};
			// ls-heroku-ci: vue-router does not always REJECT a navigation it refuses -- an aborted or
			// duplicated one RESOLVES with a NavigationFailure instead. navigateSafely already checks both,
			// and a strip that only listened for a rejection would call an aborted strip a success and leave
			// ?uploadStarted armed for replay, silently.
			const strip = Promise.resolve(
				router.replace({
					query: newQuery
				})
			);
			strip.then((outcome) => {
				if (outcome instanceof Error) {
					warnUploadHandoffStripFailed(outcome);
				}
			}, warnUploadHandoffStripFailed);
		}

		const readinessStop = stageStop(await ensureDetailReadiness(initialEntityId, switchToken, loadToken));
		if (readinessStop) return readinessStop;

		// break-it round 5: a switch that stalls here (in isAuthenticated()/getMe()/checkHasFreeTrialEnded,
		// all above) has not yet reached loadEntity — but resuming from a long stall can still cascade into
		// calling handleProjectAccess again for THIS (now superseded) switch, which unconditionally clears
		// projectVerdict.value at its own top. Without this check, a switch delayed here can wipe out
		// a NEWER, already-established project's shares/viewOnly after the user has moved on. Stop before
		// ever entering loadEntity if a newer switch (or unmount) has since taken over.
		if (isStaleAction(initialEntityId, switchToken)) {
			console.warn(
				"EntityDetailPage.initializeComponent: switch superseded during authentication setup — not proceeding to loadEntity",
				{
					initialEntityId,
					switchToken,
					currentSwitchToken: entitySwitchSequence,
					currentEntityId: entityId.value,
					currentRouteParamId: route.params.id,
					isComponentUnmounted
				}
			);
			return detailStopReason(initialEntityId, switchToken, loadToken);
		}

		try {
			let entityStop: EntityDetailStopReason | null = null;
			const promises: Promise<unknown>[] = [loadEntity(switchToken, loadToken).then((reason) => {
				entityStop = stageStop(reason);
			}), refreshReadyPdfDocumentCount(switchToken)];

			// Load the timeline immediately for conversations because we only need the id as the parent.
			// For projects, we need to fetch all of the conversation ids first to filter the media by conversationId

			let openLightBox: boolean = false;
			let provisionalTimeline: Promise<boolean> | null = null;

			if (entityType.value === "conversation") {
				// LSP-19608: mark this initial attempt provisional — it races loadEntity, so a just-created
				// conversation the backend hasn't finished persisting yet can 404/fail here. A provisional
				// failure logs quietly (see loadTimeline's catch) instead of raising a Datadog error; below,
				// once loadEntity has confirmed the entity exists, a failed provisional attempt gets exactly
				// one refetch — the same one-shot pattern LSP-17519 already uses for project-derived readers.
				provisionalTimeline = loadTimeline(false, { provisional: true, detailLoadToken: loadToken });
				promises.push(provisionalTimeline);
				openLightBox = true;
			}

			await Promise.all(promises);
			const stopped = detailStopReason(initialEntityId, switchToken, loadToken) ?? entityStop;
			if (stopped) return stopped;

			// LSP-17519 (Bugbot, HIGH): the timeline above races loadEntity, so its request goes out
			// before `entity` exists and therefore without the parent projectId that /search/media needs
			// to authorize a PROJECT-DERIVED reader across the project. Those readers matched nothing on
			// the default scope and landed on an empty timeline.
			//
			// Re-fetch for them specifically rather than sequencing the two: making every conversation
			// wait for its entity before requesting media would delay first paint for everyone — and it
			// also changed the initializer's ordering contract, which the switch-sequencing specs pin.
			// Only a project-derived read pays the extra request; a direct read is untouched, both in
			// behaviour and in timing. loadTimeline's own reload sequence supersedes the first response.
			//
			// LSP-19608: merged with the SAME one-shot refetch here — if the provisional attempt above lost
			// its race against a just-created conversation (loadEntity has since confirmed the entity
			// exists), refetch now that the parent is known to exist. The two reasons never stack into two
			// refetches; a project-derived reader whose provisional attempt also failed still gets exactly one.
			const provisionalTimelineFailed = provisionalTimeline !== null && (await provisionalTimeline) === false;
			const isConversationWithConfirmedEntity =
				entityType.value === "conversation" && !!(entity.value as Conversation | undefined)?.id;
			const needsTimelineRefetch =
				isConversationWithConfirmedEntity &&
				((entity.value as Conversation | undefined)?.accessVia === "project" || provisionalTimelineFailed);

			if (needsTimelineRefetch) {
				if (isStaleAction(initialEntityId, switchToken)) {
					console.warn("EntityDetailPage.initializeComponent: switch superseded before the project-authorized timeline refetch", {
						initialEntityId,
						switchToken,
						currentSwitchToken: entitySwitchSequence,
						currentEntityId: entityId.value,
						isComponentUnmounted
					});
					return detailStopReason(initialEntityId, switchToken, loadToken);
				}
				await loadTimeline(false, { detailLoadToken: loadToken });
			}

			// break-it round 6: a superseded switch's guarded loads (loadEntity, loadTimeline) correctly
			// discard their OWN stale responses, but this initializer kept going regardless — tryOpenLightBox
			// reads the live route.query.artifactId against mediaResponse.value and could open the lightbox
			// using the CURRENT entity's media for a switch that no longer owns the page (or vice versa), and
			// consuming route.query.openSpark here would let a stale switch "steal" a run-report action meant
			// for the entity the user actually switched to. Stop before either runs for a superseded switch.
			if (isStaleAction(initialEntityId, switchToken)) {
				console.warn(
					"EntityDetailPage.initializeComponent: switch superseded after its guarded loads settled — not opening the lightbox or consuming openSpark",
					{
						initialEntityId,
						switchToken,
						currentSwitchToken: entitySwitchSequence,
						currentEntityId: entityId.value,
						currentRouteParamId: route.params.id,
						isComponentUnmounted
					}
				);
				return detailStopReason(initialEntityId, switchToken, loadToken);
			}

			if (openLightBox) {
				await tryOpenLightBox();
			}

			if (route.query.openSpark === "true") {
				router.replace({ query: { ...route.query, openSpark: undefined } });
				await handleRunReport();
			}
		} catch (err) {
			// LSP-17483: a superseded switch's load can still REJECT after a newer switch has taken over
			// (e.g. a slow getEntity for an abandoned entity finally errors). Without this check, the
			// abandoned switch's own catch here would still show a "not found" dialog and redirect away
			// from the entity the user actually asked for — a second, error-path variant of the same
			// clobber bug this ticket fixes on the success path. isStaleAction (not just switchToken) also
			// catches a route change the watcher hasn't observed yet (break-it round 3).
			if (isStaleAction(initialEntityId, switchToken)) {
				console.warn(
					"EntityDetailPage.initializeComponent: a superseded switch's load rejected — suppressing its not-found redirect",
					{
						initialEntityId,
						switchToken,
						currentSwitchToken: entitySwitchSequence,
						currentEntityId: entityId.value,
						currentRouteParamId: route.params.id,
						isComponentUnmounted,
						error: (err as Error)?.message ?? String(err)
					}
				);
				return detailStopReason(initialEntityId, switchToken, loadToken);
			}

			detailLoad.settle(loadToken, "stage-failed:content", "error");
			console.error(err);
			console.error(err, {
				tags: {
					entityType: entityType.value,
					entityId: entityId.value,
					sharingId: sharingId.value
				},
				extra: {
					method: "initializeComponent",
					file: "EntityDetailPage"
				}
			});


			// Show the not-found dialog consistent with ProjectPage/ConversationPage and redirect (one prompt per
			// load; a stale dismissal never navigates away from an entity the user has since switched to).
			showEntityNotFound(initialEntityId, switchToken);
			return "stage-failed:content";
		}
		return verdictUnavailable.value ? "stage-failed:verdict" : !entity.value?.id ? "stage-failed:project" : timelineLoadFailed.value ? "stage-failed:timeline" : null;
	}

	async function tryOpenLightBox() {
		await nextTick(() => {
			if (route.query.artifactId) {
				const artifactType = route.query.artifactType;
				const artifactId = route.query.artifactId;
				// Match EITHER identity. `searchResultId` is unconditionally set for recordings, so the
				// `?? s.id` fallback never fires for them — and every deep link minted before this change
				// (existing Notification rows, already-shared `?artifactId=` URLs) carries the LEGACY id.
				// Checking only the canonical identity turned all of those into "we couldn't find that
				// media". The delete path in this same file already matches on `[item.id, item.legacyId]`.
				//
				// Resolve to the CANONICAL id before opening: MediaLightBox keys artifactList on
				// `searchResultId ?? id`, so handing it a legacy id finds no artifact and opens an empty
				// lightbox — a presence check alone would trade the wrong toast for a blank viewer.
				const match = mediaResponse.value.documents.find((s) => (s.searchResultId ?? s.id) === artifactId || s.id === artifactId);

				if (match && isTimelineDocument(match)) {
					// LSP-16659: a document never opens in the lightbox.
					openTimelineDocument(match);
				} else if (match) {
					openLightBox((match.searchResultId ?? match.id) as string);
				} else {
					// Every artifact type gets feedback, not just photos. A recording/video deep link that
					// misses is MORE likely than a photo one now that the match resolves the canonical
					// identity (searchResultId ?? id), and it used to no-op with no toast and no log —
					// the user clicked a notification and simply landed on a page where nothing opened.
					console.warn("EntityDetailPage.tryOpenLightBox: deep-linked artifact is not in the loaded media", {
						artifactId,
						artifactType,
						loadedDocuments: mediaResponse.value.documents.length
					});
					const errorMessage = "We couldn't find that media — it may have been deleted.";
					toast.error(errorMessage, { timeout: calculateTimeout(errorMessage) });
				}
			}
		});
	}
	// LSP-16086: fire loadMedia immediately when the checklist tab is active, otherwise
	// set a dirty flag so the next switch to that tab flushes one deferred load.
	function refreshChecklistMediaIfVisible() {
		// Gate on BOTH the checklist tab being active AND the browser tab being visible,
		// so a backgrounded tab never fires the heavy 1000-row checklist-media query.
		// onVisibilityChange re-invokes this when the tab returns to the foreground.
		const documentVisible = typeof document === "undefined" || document.visibilityState === "visible";
		if (documentVisible && activeTab.value === checklistsTabIndex.value) {
			checklistMediaDirty.value = false;
			Promise.resolve(entityChecklistsRef.value?.loadMedia(checklistSearchRequest.value)).catch((error) => {
				logError("Failed to reload checklist media", error);
			});
		} else {
			checklistMediaDirty.value = true;
		}
	}

	// LSP-16086: invoked once per coalesced burst. Routes each dirty surface to its
	// reload, re-checking the lightbox AT FLUSH TIME (it may have opened during the
	// debounce window) — when open, defer to closeLightBox via pendingReloads instead
	// of reloading underneath the user. Timeline goes through requestTimelineReload,
	// which itself defers when the tab is hidden.
	function flushScheduledReload(surfaces: ReloadSurfaces, mode: ReloadMode) {
		if (lightboxOpen.value) {
			if (surfaces.timeline) pendingReloads.value.timeline = true;
			if (surfaces.documents) pendingReloads.value.documents = true;
			if (surfaces.checklists) pendingReloads.value.checklists = true;
			if (surfaces.checklistMedia) pendingReloads.value.checklistMedia = true;
			return;
		}
		if (surfaces.timeline) requestTimelineReload(mode);
		// Documents (/documents) and checklists (/project-checklists) are intentionally
		// NOT visibility-deferred: they are small bounded queries, not the unbounded
		// /search/media query that caused the crowd-test storm. The two heavy
		// paths — timeline (via requestTimelineReload) and checklist-media (via
		// refreshChecklistMediaIfVisible) — ARE visibility-gated.
		// LSP-16659: the `documents` surface is the ready-PDF count; the file's grid tile rides `timeline`.
		if (surfaces.documents) {
			Promise.resolve(refreshReadyPdfDocumentCount()).catch((error) =>
				logError("Failed to reload documents after socket event", error)
			);
		}
		if (surfaces.checklists) {
			Promise.resolve(entityChecklistsRef.value?.loadProjectChecklists()).catch((error) =>
				logError("Failed to reload checklists after socket event", error)
			);
		}
		if (surfaces.checklistMedia) refreshChecklistMediaIfVisible();
	}

	// LSP-16086: non-socket safety reconcile. Socket is not reliable for every client,
	// so a low-frequency, visible-tab-gated, bounded delta keeps the timeline fresh
	// even when a socket event is missed or never arrives. Skipped when the timeline
	// was just reconciled, so it adds ~one bounded query per ~60s per visible tab —
	// far below the crowd-test storm — and coalesces with any concurrent socket burst.
	function periodicTimelineReconcile() {
		if (authSession.sessionLost) return;
		if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
		if (!entityId.value) return;
		// LSP-16086: reconcile fast (~15s) while an upload is pending so newly-uploaded media
		// surfaces promptly even when socket events aren't arriving; relax to ~60s when idle.
		const minGap = hasPendingUploads() || hasRecentUploadActivity() ? TIMELINE_RECONCILE_ACTIVE_MS : TIMELINE_RECONCILE_IDLE_MS;
		if (Date.now() - lastTimelineReconcileAt < minGap) return;
		// A DELTA cannot refresh a verdict that changes with time (Cursor Bugbot, PR #3242).
		// `isUploadAbandoned` is stamped during hydration, and the delta only requests rows created AFTER the
		// newest one already held, merging them alongside the existing rows — so an older recording keeps its
		// original verdict forever. An upload going quiet emits nothing, so no socket event forces a full
		// reload either. A recording that was mid-upload when we hydrated (verdict `false`, correctly
		// suppressed) therefore stayed suppressed after the upload actually died, and the banner never
		// appeared — worse than the old status gate, which would have been warning the whole time.
		//
		// Escalate to a FULL reload while the session holds a recording whose upload is UNFINISHED, whatever
		// its current verdict.
		//
		// This deliberately does NOT also require `isUploadAbandoned === false`. That narrower condition
		// refreshed the verdict in one direction only, and the omission was itself the LSP-17011 false
		// positive (Cursor Bugbot, second staleness finding): once a verdict was stamped `true` the row
		// stopped qualifying, so nothing refreshed it, and a customer RESUMING their upload could not clear
		// the banner. Chunk arrivals only touch `lastChunkReceivedAt`, and the RecordingSessions socket
		// webhook fires on `hasMinimumData` / a terminal `jobStatus` — not on chunk activity — so no event
		// forces a reload either. The session kept offering a real SMS for an upload that was actively
		// arriving, which is precisely what this feature exists to avoid.
		//
		// `isUploadComplete === false` is the honest boundary: while an upload is unfinished its verdict can
		// move either way on the clock, so the timeline has to stay authoritative.
		//
		// KNOWN COST, and NOT self-limiting in the worst case (ls-heroku-ci review corrected an earlier claim
		// here that it was). For a genuinely truncated/abandoned recording `isUploadComplete` never flips true
		// — AsyncWorker's reconcile and TranscodeFinalizer both deliberately leave it unset when the tail is
		// truncated — so such a row keeps qualifying and this keeps choosing a FULL reload every 15-60s for as
		// long as a viewer has the page open. It self-limits only when the upload actually completes or the
		// recording is deleted. A conversation parked on a dead recording therefore costs one full reload per
		// interval per viewing tab — LSP-16706 (ls-heroku-ci): that reload is no longer a single
		// /search/media request, it's the multi-page reconcile, so this is now up to ~60 sequential requests
		// (MAX_RECONCILE_PAGES=20 × up to 3 attempts under MAX_RECONCILE_RESTARTS=2) per interval per viewing
		// tab, an order of magnitude worse than when this comment was first written. A project page
		// aggregates its children on top of that.
		//
		// Left as-is deliberately rather than bounded on a guess: the alternative is a client-side clock
		// against a server-side window it cannot see, and every clever narrowing of this escalation so far has
		// produced a new defect. Bounding it properly wants the server to publish the moment the verdict
		// becomes final, which belongs with the per-session-flag follow-up rather than here.
		const holdsTimeDependentVerdict = (mediaResponse.value?.documents ?? []).some(
			(item) => item.entityType === "recording" && item.isUploadComplete === false
		);
		// EDGE-TRIGGERED, not every tick: this runs every 15-60s per visible tab, so logging each decision
		// would bury the signal. Logging the CHANGE keeps "why didn't the banner refresh via the periodic
		// reconciler?" answerable from one line — the sibling escalation in closeLightBox logs for the same
		// reason, and CLAUDE.md requires it of a gate this PR's own bug class runs through. Mirrors the
		// edge-triggered placeholder watch pattern in Timeline.vue.
		if (holdsTimeDependentVerdict !== lastReconcileEscalationLogged) {
			lastReconcileEscalationLogged = holdsTimeDependentVerdict;
			console.warn(
				`[incomplete-upload] periodic reconcile mode now ${holdsTimeDependentVerdict ? "FULL" : "delta"}. ` +
					`conversationId=${entityId.value}, holdsUnfinishedUpload=${holdsTimeDependentVerdict}, ` +
					`documents=${(mediaResponse.value?.documents ?? []).length}`
			);
		}
		timelineReloadScheduler.schedule({ timeline: true }, holdsTimeDependentVerdict ? "full" : "delta");
	}

	function onSocketDisconnect() {
		socketWasDisconnected = true;
	}

	// LSP-16701: socket events missed while the tab was hidden or the socket was down are the real gap in
	// live notes propagation, so reconcile the conversation from the server on return-to-visible and on
	// reconnect. Guarded so it cannot storm the API: conversation entities only, one in-flight call at a
	// time, a MIN_RECONCILE_GAP_MS floor between calls, and skipped entirely while the About form has
	// unsaved edits (the merge would fight the user's own text).
	let lastNotesReconcileAt = 0;
	let notesReconcileInFlight = false;
	// Review 5346441261 finding #1: the attempt is stamped BEFORE the fetch so an in-flight failure can't
	// be re-entered instantly, but a FAILED attempt must not consume the full 3s floor — the retry it
	// suppressed (e.g. a socket-reconnect reconcile ~1s after a failed visibility reconcile) is precisely
	// the one that would have recovered the notes, and reconcile exists for exactly the case where the
	// socket path is NOT delivering them. A failure therefore only holds a short floor. This mirrors the
	// sibling timeline reconcile, which likewise refuses to stamp a failed reload (Bugbot #1).
	const FAILED_NOTES_RECONCILE_GAP_MS = 500;
	let lastNotesReconcileFailed = false;

	// LSP-19423: a reconcile that failed was a one-shot. Nothing re-ran it, so a conversation whose
	// refetch lost a race with the network coming back up kept the notes it had until the next
	// visibility change or entity load — the silent-staleness half of this ticket. Retry it on a
	// bounded front-loaded backoff instead, mirroring the scribe warm-up above (no unbounded polling).
	//
	// Deliberately NOT armed for an entity-gone failure. A 404 means the conversation was deleted:
	// there is no fresher state to fetch, so a retry would only re-request a resource that keeps
	// 404ing. That case was ~170 of the ~176 reconnect failures reported in staging over eight days.
	const NOTES_RECONCILE_RETRY_DELAYS_MS = [1000, 3000, 8000];
	let notesReconcileRetryTimeout: ReturnType<typeof setTimeout> | null = null;
	let notesReconcileRetryAttempts = 0;

	function stopNotesReconcileRetry() {
		if (notesReconcileRetryTimeout) {
			clearTimeout(notesReconcileRetryTimeout);
			notesReconcileRetryTimeout = null;
		}
		notesReconcileRetryAttempts = 0;
	}

	function scheduleNotesReconcileRetry(reason: "visibility" | "socket-reconnect") {
		if (notesReconcileRetryAttempts >= NOTES_RECONCILE_RETRY_DELAYS_MS.length) {
			// Logged, because this is the one path that ends with the view genuinely still stale. The
			// next visibility change or entity load is what recovers it from here.
			console.warn("reconcileConversationNotes: retries exhausted — notes may still be stale", {
				conversationId: entityId.value,
				reason,
				attempts: notesReconcileRetryAttempts
			});
			stopNotesReconcileRetry();
			return;
		}
		const delay = NOTES_RECONCILE_RETRY_DELAYS_MS[notesReconcileRetryAttempts];
		const retryEntityId = entityId.value;
		notesReconcileRetryAttempts++;
		// One chain only: a concurrent caller that fails while a tick is already pending replaces it
		// rather than arming a second timer against the same conversation.
		if (notesReconcileRetryTimeout) clearTimeout(notesReconcileRetryTimeout);
		notesReconcileRetryTimeout = setTimeout(() => {
			notesReconcileRetryTimeout = null;
			// The retry owns nothing on its own: if the component went away or the user switched
			// entities, the conversation it was recovering is no longer the one on screen.
			if (isComponentUnmounted || entityId.value !== retryEntityId) {
				console.log("reconcileConversationNotes: retry no longer owns the page — dropping", {
					retryEntityId,
					currentEntityId: entityId.value,
					isComponentUnmounted,
					reason
				});
				stopNotesReconcileRetry();
				return;
			}
			void reconcileConversationNotes(reason);
		}, delay);
	}

	async function reconcileConversationNotes(reason: "visibility" | "socket-reconnect") {
		if (authSession.sessionLost) return;
		if (entityType.value !== "conversation" || !entityId.value) {
			// LSP-19423: the page is no longer a conversation, so a retry chain armed for the previous
			// one has nothing left to recover. Every abandoning exit clears it; only the in-flight and
			// rate-limit gates below leave it alone, because there a live caller still owns the chain.
			stopNotesReconcileRetry();
			return;
		}
		if (isDirty.value) {
			console.log("Skipping conversation notes reconcile — About form has unsaved edits", {
				conversationId: entityId.value,
				reason
			});
			// Retrying into the user's own unsaved text is exactly what this gate exists to prevent, so
			// the chain stops here rather than re-attempting a merge that would keep being refused.
			stopNotesReconcileRetry();
			return;
		}
		// Diagnostics on both rate-limit gates (root CLAUDE.md: a fix for a silent failure logs EVERY
		// fail-condition in the gate, not just the one that caused this incident). Both of these used to
		// return with no trace, which is exactly why finding #1's suppressed-retry path was invisible.
		if (notesReconcileInFlight) {
			console.log("Skipping conversation notes reconcile — a reconcile is already in flight", {
				conversationId: entityId.value,
				reason
			});
			return;
		}
		const requiredGap = lastNotesReconcileFailed ? FAILED_NOTES_RECONCILE_GAP_MS : MIN_RECONCILE_GAP_MS;
		const elapsedSinceLastAttempt = Date.now() - lastNotesReconcileAt;
		if (elapsedSinceLastAttempt < requiredGap) {
			console.log("Skipping conversation notes reconcile — inside the rate-limit floor", {
				conversationId: entityId.value,
				reason,
				elapsedSinceLastAttempt,
				requiredGap,
				lastAttemptFailed: lastNotesReconcileFailed
			});
			return;
		}

		notesReconcileInFlight = true;
		lastNotesReconcileAt = Date.now();
		const reconcileEntityId = entityId.value;
		const reconcileSwitchToken = entitySwitchSequence;
		try {
			const conversation = await api.getConversation(reconcileEntityId);
			// The fetch itself succeeded, so the full floor applies again regardless of which discard
			// branch below (if any) declines to APPLY the result — a superseded switch or freshly started
			// edits are not API failures and must not earn a fast retry.
			lastNotesReconcileFailed = false;
			// LSP-19423: and for the same reason, the retry chain an earlier transient failure armed is
			// done — a discard is a reason not to merge, not a reason to keep re-requesting.
			stopNotesReconcileRetry();
			// Same ownership discipline as the other refetch sites (LSP-17483): a switch away while this
			// was in flight must not merge a stale conversation onto whatever entity is now current.
			if (isStaleAction(reconcileEntityId, reconcileSwitchToken)) {
				console.warn("reconcileConversationNotes: switch superseded during refetch — discarding", {
					reconcileEntityId,
					currentEntityId: entityId.value,
					reason
				});
				return;
			}
			// Re-check dirtiness: the user may have started typing while the request was in flight.
			if (isDirty.value) {
				console.log("reconcileConversationNotes: edits started during refetch — discarding", {
					conversationId: reconcileEntityId,
					reason
				});
				return;
			}
			const current = entity.value as Conversation | undefined;
			const incomingNotes = (conversation as any)?.notes ?? "";
			if (current && (current.notes ?? "") !== incomingNotes) {
				console.log("reconcileConversationNotes: applying fresher notes from the server", {
					conversationId: reconcileEntityId,
					reason,
					incomingLength: incomingNotes.length
				});
				current.notes = incomingNotes as any;
			}
		} catch (error) {
			// Still never surfaced to the user — it is a background freshness check and there is nothing
			// actionable for them. But it IS reported: logError reaches Datadog (review 5346441261 finding
			// #1 — the old console.warn left a persistent failure, e.g. an expired-session 401 or a 5xx,
			// completely untraced, inconsistent with the sibling reconnect path below). The previous
			// justification that "the socket path plus the next entity load both still deliver the notes"
			// was exactly false here: this runs precisely when the socket was down or events were missed.
			//
			// LSP-19423: split the two outcomes, because they need opposite handling. An entity-gone
			// failure (404/410) is not stale data — the conversation was deleted, the entity load has
			// already told the user it could not be found, and there is nothing fresher to fetch. It is a
			// breadcrumb, not an error, and it must not arm a retry. Anything else IS a real failure to
			// read current state, so it stays an error and now gets a bounded retry.
			const status = getApiErrorStatus(error);
			if (isEntityGoneError(error)) {
				// The full floor applies again: a deleted conversation has not earned a fast re-attempt.
				lastNotesReconcileFailed = false;
				stopNotesReconcileRetry();
				logInfo("reconcileConversationNotes: conversation no longer exists — nothing to reconcile", {
					conversationId: reconcileEntityId,
					reason,
					httpStatus: status
				});
				return;
			}
			lastNotesReconcileFailed = true;
			logError("reconcileConversationNotes: refetch failed", error, {
				conversationId: reconcileEntityId,
				reason,
				httpStatus: status
			});
			scheduleNotesReconcileRetry(reason);
		} finally {
			notesReconcileInFlight = false;
		}
	}

	// LSP-16086: a RE-connect (after a real disconnect) reconciles to catch media
	// created while the socket was down. Ignore the initial connect on mount.
	function onSocketConnect() {
		if (authSession.sessionLost) return;
		if (!socketWasDisconnected) return;
		socketWasDisconnected = false;
		if (entityType.value === "project" && entityId.value) {
			// Skip reload when user has unsaved edits — overwriting entity would discard their in-flight changes.
			if (!isDirty.value) {
				// LSP-17483: this refresh isn't driven by the switch watcher, but it can still race an
				// in-place switch that starts while it's in flight. Snapshotting entitySwitchSequence now
				// gives it the same ownership check as a real switch — if the user navigates to a different
				// entity before this resolves, applyEntityForSwitch discards it instead of overwriting the
				// entity the user actually asked for.
				//
				// break-it round 3/4: also claims its own auxiliaryRefreshToken so two overlapping
				// reconnect-driven refreshes for the SAME project are ordered against each other (via
				// projectLoadSequence) without that ordering ever being able to invalidate an actual route
				// switch's own loadUserAndProject call, which never passes this option.
				const socketRefreshSwitchToken = entitySwitchSequence;
				// Review round 2 (cursor): claims BOTH tokens. projectLoadSequence orders this refresh's
				// seeding against OTHER reconnect refreshes; projectEntityWriteSequence orders its entity
				// write against every auxiliary refresh of either kind, so a user-triggered refreshProject
				// racing this one can no longer overwrite it with — or be overwritten by — an older snapshot.
				const socketRefreshOrderToken = ++projectLoadSequence;
				const socketRefreshEntityWriteToken = ++projectEntityWriteSequence;
				Promise.resolve(
					loadUserAndProject(entityId.value, socketRefreshSwitchToken, {
						skipNavigationOnError: true,
						auxiliaryRefreshToken: socketRefreshOrderToken,
						auxiliaryEntityWriteToken: socketRefreshEntityWriteToken
					})
				).catch((error) =>
					logError("Failed to refresh project conversations after socket reconnect", error, { entityId: entityId.value })
				);
			}
		}
		// LSP-16701: a conversation's notes can have changed (from contact-live) while the socket was down.
		void reconcileConversationNotes("socket-reconnect");
		const timelineMode = Date.now() <= preserveUploadMediaOnReconnectUntil ? "delta" : "full";
		timelineReloadScheduler.schedule({ timeline: true, checklists: true, checklistMedia: true }, timelineMode);
	}

	// LSP-16086 (P3): route a create/update reload through the visibility gate.
	// When the tab is hidden, remember the deferred mode (escalating to "full",
	// never downgrading) instead of fetching. When visible, proceed immediately.
	function requestTimelineReload(mode: "delta" | "full") {
		if (typeof document !== "undefined" && document.visibilityState !== "visible") {
			if (mode === "full" || pendingVisibilityReloadMode.value === null) {
				pendingVisibilityReloadMode.value = mode;
			}
			return;
		}
		// LSP-19529: "full" here means "not safe as a created-windowed delta" (a recording's `created` can predate
		// the newest loaded row), NOT "re-fetch everything the user has scrolled". One page from the top covers
		// that case; the whole-window multi-page reconcile stays reserved for explicit reloads.
		void (mode === "delta" ? deltaReloadTimeline() : headReloadTimeline());
	}

	// LSP-16086 (P3): when the user returns to a hidden tab, flush deferred reloads
	// using the deferred mode (a deferred recording update must flush as a full reload).
	function onVisibilityChange() {
		if (authSession.sessionLost) return;
		if (document.visibilityState !== "visible") {
			// LSP-16086 (C): pause the upload-status poll while hidden so N background
			// tabs don't each hit /upload-status every 5s. Gate the INTERVAL only —
			// fetchUploadStatus stays directly callable for the run-report interlock.
			if (uploadStatusTimer) {
				clearTimeout(uploadStatusTimer);
				uploadStatusTimer = null;
			}
			return;
		}
		// LSP-16086 (C): on return, resume the poll with one immediate fetch.
		if (!uploadStatusTimer && shouldPollUploadStatus()) {
			void restartUploadStatusPollForCurrentEntity({ allowIdleUploadFinalization: true });
		}
		// LSP-16701: Jake's "visibility updater" — a hidden tab may have missed the conversation_update
		// event carrying notes saved during a live call, so re-read them on return.
		void reconcileConversationNotes("visibility");
		// LSP-22012: the delivery refetch that came due while hidden.
		runDeferredDeliveryRefresh();
		if (pendingVisibilityReloadMode.value) {
			const mode = pendingVisibilityReloadMode.value;
			pendingVisibilityReloadMode.value = null;
			// LSP-19529: same bounded head reconcile as the scheduler flush it was deferred from.
			void (mode === "full" ? headReloadTimeline() : deltaReloadTimeline());
			Promise.resolve(entityChecklistsRef.value?.loadProjectChecklists()).catch((error) =>
				logError("Failed to reload checklists on visibility flush", error)
			);
		} else if (entityId.value && Date.now() - lastTimelineReconcileAt >= MIN_RECONCILE_GAP_MS) {
			// LSP-16086: freshness reconcile on return — while hidden the tab may have
			// missed socket events (or never had a socket). Routed through the scheduler
			// (bounded delta, coalesces with any concurrent burst); guarded so rapid
			// hide/show toggles don't refetch repeatedly. LSP-21085: the same missed
			// master-document event can carry a checklist-media attachment. Refresh the
			// bounded checklist read model alongside the timeline so returning from the
			// Take Photos tab renders the attachment even when no socket event arrived.
			timelineReloadScheduler.schedule({ timeline: true, checklists: true }, "delta");
		}
		// Flush a checklist-media refresh deferred while the tab was hidden, even when no
		// timeline reload was pending (a checklist-only event can leave it dirty).
		if (checklistMediaDirty.value) {
			refreshChecklistMediaIfVisible();
		}
	}

	function reloadTimeline() {
		if (reloadTimeout) {
			clearTimeout(reloadTimeout);
		}

		reloadTimeout = setTimeout(async () => {
			await loadTimeline(true);
		}, UPLOAD_FINISHED_RELOAD_DELAY_MS);
	}

	function reloadTimelineAfterUploadFinished() {
		if (reloadTimeout) {
			clearTimeout(reloadTimeout);
		}
		preserveUploadMediaOnReconnectUntil = Date.now() + UPLOAD_FINISHED_RELOAD_DELAY_MS + RELOAD_DEBOUNCE_MS;

		reloadTimeout = setTimeout(async () => {
			// Upload completion can race a reconnect-driven reload. A full replacement from the older
			// response removes media that the socket/delta path already rendered until the next reload.
			// Merge the completion reconcile instead; deltaReloadTimeline retains visible media while
			// still falling back to a bounded full reload when it cannot prove the merge is safe.
			await deltaReloadTimeline();
		}, UPLOAD_FINISHED_RELOAD_DELAY_MS);
	}

	async function loadTimeline(reload: boolean = false, options: { provisional?: boolean; detailLoadToken?: number } = {}): Promise<boolean> {
		if (authSession.sessionLost) return false;
		if (reload) {
			console.log("Reloading timeline from Socket event");
			lastWholeWindowReconcileAt = Date.now();
		}

		const reloadSequence = ++timelineReloadSequence;
		// True when a reconcile defers (keeps the current list) instead of committing — declared here (not
		// inside the try block) so the final `return !reconcileDeferred` below can still see it: a deferral
		// did not actually reconcile anything, so callers must not see `true` (liveswitch-ai-pr-reviewer).
		let reconcileDeferred = false;
		// ls-heroku-ci: declared here (not inside try) for the same reason as reconcileDeferred above — the
		// catch block needs to see it too, to report how far a multi-page reconcile got before throwing.
		let pagesFetchedInReconcile = 0;
		try {
			const requestedEntityId = entityId.value;
			const requestedEntityType = entityType.value;
			searchRequest.value = filterPersistence.buildRequest(reload);
			// Captured BEFORE the await: this response must be judged by its own request's narrowing.
			const requestWasNarrowed =
				!!searchRequest.value?.searchTerm || userFiltersNarrowResults((searchRequest.value ?? {}) as Partial<SearchRequest>);
			const requestedTimelineRequest: Partial<SearchRequest> = { ...(searchRequest.value ?? {}) };

			if (!checklistSearchRequest.value) {
				checklistSearchRequest.value = { ...searchRequest.value } as SearchRequest;
			}
			// Don't load the timeline if there are no conversations related to the project since the media is loaded based
			// on conversation id. Passing an empty parentIds filter will retrieve all of the media in the org.
			// TODO: Need to refactor indexes and add projectId to media

			if (entityType.value === "project" && projectConversationIds.value.length === 0) {
				mediaResponse.value = emptyEntitySearchResponse();
				showTimelineResultsFor(requestedTimelineRequest);
				timelineLoadFailed.value = false;
				lastTimelineReconcileAt = Date.now();
				detailLoad.complete("timeline");
				// LSP-16706 (liveswitch-ai-pr-reviewer): this replaces the row universe (to empty) the same
				// as every other replacement point, so a stale consecutive-deferral streak from before must
				// not survive into whatever repopulates it later.
				consecutiveRestartsExhaustedCount = 0;
				consecutiveIncompleteCount = 0;
				// ls-heroku-ci: console.log is not Datadog-forwarded (per src/plugins/datadog.ts) — switched
				// to logInfo alongside the reconcile-state resets just added on this same branch.
				logInfo("Timeline: not searching, no related conversations found", {
					entityId: entityId.value,
					entityType: entityType.value,
					sharingId: sharingId.value
				});
				return true;
			}

			const previousImageCount = reload
				? (mediaResponse.value?.documents?.filter((item) => item.entityType === "snapshot").length ?? 0)
				: 0;
			const previousVideoCount = reload
				? (mediaResponse.value?.documents?.filter((item) => item.entityType === "recording").length ?? 0)
				: 0;
			const previousDocumentCount = reload
				? (mediaResponse.value?.documents?.filter((item) => isTimelineDocument(item)).length ?? 0)
				: 0;
			// LSP-16706 (ls-heroku-ci): exclude client-synthetic `temp-annotation-…` placeholder rows (see
			// MediaLightBox.vue) from the reconcile's recovery target. `handleAnnotationSaved` calls
			// loadTimeline(true) while the lightbox is still open — before `closeLightBox` strips the
			// placeholder — and the server can never return that id, so leaving it in would force every
			// annotation-save reconcile onto the (now page-1-only, see below) count-based fallback or fail
			// outright with page-cap-exceeded on a large timeline.
			const existingDocuments = reload
				? (mediaResponse.value?.documents ?? []).filter((document) => !document.id.startsWith("temp-annotation-"))
				: [];
			// True when the deferral is `"page-cap-exceeded"`, or an "incomplete" deferral whose unrecovered
			// ids are IDENTICAL to the previous "incomplete" deferral's (persistently un-hydratable, not
			// transiently stub-inflated) — in both cases an immediate retry against unchanged server state
			// can't do any better than this attempt just did. Unlike a genuinely transient deferral (worth an
			// immediate retry), stamping still applies here so the periodic safety net falls back to its
			// normal cadence instead of hot-looping the full reconcile every check interval indefinitely
			// (liveswitch-ai-pr-reviewer / ls-heroku-ci).
			let reconcileStructurallyStuck = false;
			// Snapshot of the documents array in place BEFORE the first await, so isSupersededFetch can
			// detect a filter/search change landing mid-reconcile: searchTimeline/onSearchRequestChange
			// replace mediaResponse.value without bumping timelineReloadSequence. pageTimeline's own stale
			// guard now carries the identical reference+length check below (Cursor Bugbot: it was
			// reference-only until then, which this comment previously — and wrongly — implied already
			// covered the splice case too) — without this check here, the multi-page reconcile (up to ~60
			// sequential requests across restarts) can hold that race window open for seconds and silently
			// clobber a just-applied search/filter with a stale unfiltered commit (ls-heroku-ci).
			const documentsSnapshotBeforeFetch = mediaResponse.value?.documents;
			// Reference equality alone misses artifactDeleted/onDeletedMediaItems: both splice the SAME
			// documents array in place (no replacement, no sequence bump) rather than reassigning it. A
			// splice always changes .length, so pairing the length with the reference catches an in-place
			// delete landing mid-reconcile too — otherwise the reconcile could commit a snapshot that
			// re-adds the row the user just deleted (liveswitch-ai-pr-reviewer).
			const documentsLengthSnapshotBeforeFetch = documentsSnapshotBeforeFetch?.length;

			// The guard that aborts a superseded fetch, shared by the initial load and every reconcile page.
			const isSupersededFetch = () =>
				(options.detailLoadToken !== undefined && !detailLoad.owns(options.detailLoadToken)) ||
				reloadSequence !== timelineReloadSequence ||
				requestedEntityId !== entityId.value ||
				requestedEntityType !== entityType.value ||
				mediaResponse.value?.documents !== documentsSnapshotBeforeFetch ||
				mediaResponse.value?.documents?.length !== documentsLengthSnapshotBeforeFetch;

			if (!reload) {
				// Initial load: a single page is authoritative.
				const response = await service.value!.getTimeline(searchRequest.value, useAuthenticatedMediaSearch.value);
				if (isSupersededFetch()) {
					// CLAUDE.md "Diagnostic logging when fixing silent failures": this gate has a sibling at
					// the reload/reconcile call site below that already logs when it fires; this one silently
					// returned false with no signal (ls-heroku-ci).
					logInfo("Timeline initial load aborted (superseded by a newer reload/filter change)", {
						entityId: entityId.value,
						entityType: entityType.value,
						sharingId: sharingId.value
					});
					return false;
				}
				// LSP-16761: keep the object identity of rows this load did not actually change (a no-op on
				// first mount since mediaResponse.value is still empty, but shares the merge path with reload).
				mediaResponse.value = {
					...response,
					documents: preserveUnchangedDocuments(mediaResponse.value?.documents ?? [], response?.documents ?? [])
				};
				showTimelineResultsFor(requestedTimelineRequest);
				// ls-heroku-ci: this replaces the row universe like every other commit path — a stale
				// consecutive-deferral streak from a prior context must not survive into it.
				consecutiveRestartsExhaustedCount = 0;
				consecutiveIncompleteCount = 0;
				// A full reload is authoritative when nothing narrowed it: a latched recording missing from it has
				// been deleted, not filtered out (break-it round 7). Narrowing is read off the request actually
				// sent, so a late response cannot be judged against a since-cleared filter.
				reconcileLatchAgainstAuthoritativeTimeline(response, requestWasNarrowed);
			} else {
				// LSP-16706: authoritative multi-page reconcile. A single request is clamped by the server
				// (MAX_PAGE_SIZE=150) and cannot see rows below the newest window, so deletions and backdated
				// inserts (a recording whose created = session-start) would be missed and a spike would drop
				// the scrolled tail. Re-fetch sequential pages from the top until every already-loaded item is
				// re-covered plus the new head, then replace with that contiguous list.
				const RECONCILE_PAGE_SIZE = 150; // server MAX_PAGE_SIZE
				const MAX_RECONCILE_PAGES = 20; // safety cap (~3000 rows) to bound Azure requests per reconcile
				const MAX_RECONCILE_RESTARTS = 2; // bound retries when the list shifts mid-reconcile
				let superseded = false;
				// LSP-16659: the reconcile helper carries only rows + count; keep the latest page's per-type totals.
				let latestReconcileCountsByEntityType: EntitySearchResponse["countsByEntityType"];
				const result = await reconcileTimelinePages(
					async (pageNumber) => {
						const pageResponse = await service.value!.getTimeline(
							{ ...searchRequest.value, page: pageNumber, pageSize: RECONCILE_PAGE_SIZE } as SearchRequest,
							useAuthenticatedMediaSearch.value
						);
						pagesFetchedInReconcile += 1;
						if (isSupersededFetch()) {
							superseded = true;
							return null; // abort: a newer reload/page took over
						}
						latestReconcileCountsByEntityType = pageResponse.countsByEntityType;
						return { documents: pageResponse.documents ?? [], count: pageResponse.count };
					},
					{
						existingDocuments,
						pageSize: RECONCILE_PAGE_SIZE,
						maxPages: MAX_RECONCILE_PAGES,
						maxRestarts: MAX_RECONCILE_RESTARTS
					}
				);
				if (superseded || result === null) {
					// LSP-16706 (ls-heroku-ci): a scroll-triggered pageTimeline() bumps the same
					// timelineReloadSequence this reconcile's isSupersededFetch() checks on every page, so it
					// can abort a reconcile mid-run — discarding up to MAX_RECONCILE_PAGES already-fetched
					// pages of work with no signal. Not a new race (the pre-PR single-request reload had the
					// same one-shot vulnerability), but this multiplies the discarded window, so it earns its
					// own log line rather than sharing the silent pre-existing behavior.
					logInfo("Timeline reconcile aborted (superseded by a newer reload/page); discarding fetched pages", {
						pagesFetchedInReconcile,
						entityId: entityId.value,
						entityType: entityType.value,
						sharingId: sharingId.value
					});
					// ls-heroku-ci: stamp here too, same as the structurally-stuck deferral cases below — an
					// abort isn't a deferral (nothing was learned to defer), but leaving the stamp stale would
					// let periodicTimelineReconcile retry at the tight 15s cadence with no backoff, and this
					// PR's own broadened isSupersededFetch (reference-only -> reference-or-length) makes an
					// abort MORE likely under exactly the upload-churn conditions this feature targets. An
					// abort's whole reason for existing is that something ELSE just updated (or is updating)
					// the timeline, so treating this moment as a reconcile boundary is accurate, not a
					// false-fresh claim the way a genuine failure would be.
					lastTimelineReconcileAt = Date.now();
					return false;
				}
				if (result.inconsistent && existingDocuments.length > 0) {
					// Keep the last known-good list rather than commit a snapshot that could drop a still-existing
					// loaded row. The next reconcile retries; this never drops loaded media, it only defers new rows.
					// logInfo (not console.log) so the deferral is queryable in Datadog — admin's
					// forwardConsoleLogs does not forward console.log (per src/plugins/datadog.ts).
					reconcileDeferred = true;
					// liveswitch-ai-pr-reviewer: a SECOND (or later) consecutive "incomplete" deferral means
					// retrying immediately isn't converging, whether the unrecovered set stays identical
					// (permanently un-hydratable rows) or keeps SHIFTING (IndexService post-filters a
					// different pending-upload stub each pass while count stays inflated) — an earlier
					// version required an identical set across exactly two attempts and missed the shifting
					// case entirely, never backing off for exactly the busy-cadence scenario this exists to
					// catch. Counted like restarts-exhausted below, not compared by id-set.
					const isPersistentlyIncomplete = result.inconsistentReason === "incomplete" && consecutiveIncompleteCount >= 1;
					// ls-heroku-ci: a ONE-OFF restart exhaustion is transient concurrent-write churn worth
					// retrying promptly, but a conversation exhausting restarts on EVERY attempt (fast enough
					// writes that the total never settles within maxRestarts) never advances the stamp under
					// the un-folded original logic, pinning periodicTimelineReconcile to the busy 15s cadence
					// forever instead of backing off to the 60s idle one — sustained extra load on exactly the
					// busiest conversations. Same reasoning/shape as isPersistentlyIncomplete.
					const isPersistentRestartsExhaustion =
						result.inconsistentReason === "restarts-exhausted" && consecutiveRestartsExhaustedCount >= 1;
					reconcileStructurallyStuck =
						result.inconsistentReason === "page-cap-exceeded" || isPersistentlyIncomplete || isPersistentRestartsExhaustion;
					consecutiveIncompleteCount = result.inconsistentReason === "incomplete" ? consecutiveIncompleteCount + 1 : 0;
					consecutiveRestartsExhaustedCount =
						result.inconsistentReason === "restarts-exhausted" ? consecutiveRestartsExhaustedCount + 1 : 0;
					// Disambiguate the cause instead of a single hardcoded message (CLAUDE.md "Diagnostic logging
					// when fixing silent failures"): restart-exhaustion (concurrent writes), a same-attempt
					// incomplete reconcile, a persistently-incomplete reconcile, a persistently-restart-exhausted
					// reconcile, and a structurally too-deep timeline are different on-call investigations — all
					// but the first specifically explain why the safety net will NOT retry this soon.
					const deferralMessage = isPersistentRestartsExhaustion
						? "Timeline reconcile has exhausted its restart budget on every recent attempt; keeping current media and falling back to periodic cadence"
						: result.inconsistentReason === "restarts-exhausted"
							? "Timeline reconcile saw persistent mid-flight changes; keeping current media"
							: isPersistentlyIncomplete
								? "Timeline reconcile has been incomplete on consecutive attempts; keeping current media and falling back to periodic cadence"
								: reconcileStructurallyStuck
									? // LSP-16706 (liveswitch-ai-pr-reviewer): "page-cap-exceeded" is a property of the ATTEMPT
										// (every maxPages fetch consumed without completing), not of loaded depth — a small
										// loaded set can land here too if every page comes back empty/stub-filtered while
										// count stays inflated, so the message must not claim depth is the cause.
										"Timeline reconcile exhausted its page budget without confirming every loaded row; keeping current media and falling back to periodic cadence"
									: "Timeline reconcile could not confirm every loaded row within the page/restart budget; keeping current media";
					logInfo(deferralMessage, {
						reason: result.inconsistentReason,
						loaded: existingDocuments.length,
						reconciledCount: result.count,
						restartsUsed: result.restartsUsed,
						maxRestarts: MAX_RECONCILE_RESTARTS,
						unrecoveredCount: result.unrecoveredIds.length,
						// Cursor Bugbot: the previous "kept for diagnostic log context" comment on the removed
						// lastIncompleteUnrecoveredIds tracking was never actually wired into this call — log the
						// real, current unrecovered ids directly instead, which is more useful for triage anyway.
						unrecoveredIds: result.unrecoveredIds,
						persistentlyIncomplete: isPersistentlyIncomplete,
						persistentRestartsExhaustion: isPersistentRestartsExhaustion,
						entityId: entityId.value,
						entityType: entityType.value,
						sharingId: sharingId.value
					});
				} else {
					consecutiveRestartsExhaustedCount = 0;
					consecutiveIncompleteCount = 0;
					// ls-heroku-ci: reconcileTimelinePages itself does no logging (kept a pure, dependency-free
					// helper — see its own tests) — a reconcile that had to restart 1-2 times before settling
					// is otherwise invisible in logs, so a performance complaint on a write-heavy conversation
					// would be undiagnosable. restartsUsed exists on ReconcileResult specifically for this.
					if (result.restartsUsed > 0) {
						logInfo("Timeline reconcile succeeded after restarting on mid-flight changes", {
							restartsUsed: result.restartsUsed,
							maxRestarts: MAX_RECONCILE_RESTARTS,
							committedCount: result.documents.length,
							entityId: entityId.value,
							entityType: entityType.value,
							sharingId: sharingId.value
						});
					}
					// ls-heroku-ci: unrecoveredIds non-empty on a COMMITTED (non-deferred) result proves
					// completeness was proven by the count-fallback disjunct (accumulated.length >= latestCount),
					// not by id-recovery (which requires it empty) — this is the known, accepted
					// count-preserving-churn limitation (a between-page delete+insert can commit a snapshot
					// that omits a still-existing row). Not fixing the race itself here, only making this
					// specific commit path distinguishable from an ordinary clean reconcile in Datadog.
					if (result.unrecoveredIds.length > 0) {
						logInfo("Timeline reconcile committed via the count-fallback proof, not full id-recovery", {
							unrecoveredCount: result.unrecoveredIds.length,
							committedCount: result.documents.length,
							reconciledCount: result.count,
							previouslyLoadedCount: existingDocuments.length,
							entityId: entityId.value,
							entityType: entityType.value,
							sharingId: sharingId.value
						});
					}
					// LSP-16761: preserve identity of unchanged rows across the reconcile too, and let the
					// authoritative-response latch reconcile see the merged result the same way it sees a
					// single-page load — its own shy guard (documents.length < count) already handles a reconcile
					// that stopped short of the full count, the same way a clamped single page did before this PR.
					const reconciledResponse = {
						count: result.count,
						documents: result.documents,
						countsByEntityType: latestReconcileCountsByEntityType
					};
					mediaResponse.value = {
						...reconciledResponse,
						documents: preserveUnchangedDocuments(existingDocuments, result.documents)
					};
					showTimelineResultsFor(requestedTimelineRequest);
					reconcileLatchAgainstAuthoritativeTimeline(reconciledResponse, requestWasNarrowed);
				}
			}
			// LSP-16706 (ls-heroku-ci): NOT when the reconcile deferred — mediaResponse.value was
			// deliberately left untouched, so re-baselining lastTimelinePageRequested from that unchanged
			// document count would undo any advance pageTimeline's barren-page-skip logic already made,
			// re-trapping the next scroll on the same known-barren page every deferral during churn.
			if (!reconcileDeferred) {
				normalizeTimelinePagingAfterReplacement();
			}
			// LSP-15763: only clear the failure flag once the new response has arrived.
			// Resetting it at the top of the try block exposed stale zero-count
			// mediaResponse to showAsyncReminderPrompt during socket-driven retries
			// and flashed the empty-state "Send a Reminder" before the fetch settled.
			timelineLoadFailed.value = false;

			// reduce skeleton placeholders based on newly loaded items — NOT on a deferral: mediaResponse.value
			// is deliberately untouched there, so imageDiff/videoDiff are always 0 and the `else if
			// (!isUploading.value)` branch would zero out an upload's still-pending skeleton for no reason
			// (the same "deferral is not a reconcile boundary" reasoning normalizeTimelinePagingAfterReplacement
			// and the timestamp stamp below are already guarded with — this block wasn't, ls-heroku-ci).
			if (!reconcileDeferred && mediaResponse.value?.documents) {
				const newImageCount = mediaResponse.value.documents.filter((item) => item.entityType === "snapshot").length;
				const newVideoCount = mediaResponse.value.documents.filter((item) => item.entityType === "recording").length;
				const imageDiff = newImageCount - previousImageCount;
				const videoDiff = newVideoCount - previousVideoCount;

				if (imageDiff > 0 && pendingImages.value > 0) {
					pendingImages.value = Math.max(0, pendingImages.value - imageDiff);
				} else if (!isUploading.value) {
					pendingImages.value = 0;
				}

				if (videoDiff > 0 && pendingVideos.value > 0) {
					pendingVideos.value = Math.max(0, pendingVideos.value - videoDiff);
				} else if (!isUploading.value) {
					pendingVideos.value = 0;
				}
				retirePendingDocumentSkeletons(
					mediaResponse.value.documents.filter((item) => isTimelineDocument(item)).length - previousDocumentCount,
					"full"
				);
			}
			// LSP-16086 (Bugbot #1/#5): stamp on SUCCESS only so a failed reload doesn't
			// look "fresh" and gate the safety net. Stamp regardless of `reload` so
			// the initial load after navigation also counts as a reconcile boundary.
			// LSP-16706: but NOT when the reconcile deferred (kept the current list) — a deferral is not
			// a reconcile boundary, so leave the stamp stale to let the safety net retry promptly. EXCEPT a
			// structurally-too-deep timeline, a reconcile that failed to recover the same row(s) across
			// consecutive attempts, or a conversation exhausting restarts on every recent attempt
			// (reconcileStructurallyStuck covers all three — see its assignment above): retrying immediately
			// reproduces the identical shortfall every time, so stamp anyway and let it fall back to the
			// normal periodic cadence instead of hot-looping (or, for restarts, hammering with a full
			// multi-restart attempt) every check interval (liveswitch-ai-pr-reviewer / ls-heroku-ci).
			if (!reconcileDeferred || reconcileStructurallyStuck) {
				lastTimelineReconcileAt = Date.now();
			}
		} catch (err) {
			if (reloadSequence !== timelineReloadSequence || (options.detailLoadToken !== undefined && !detailLoad.owns(options.detailLoadToken))) {
				// ls-heroku-ci: this guard predates the PR, but the multi-page reconcile widened its window
				// from a single request to up to ~60 sequential requests across restarts, raising the odds a
				// REAL thrown error coincides with a sequence bump and gets silently discarded here with zero
				// trace. Log it rather than swallow it outright.
				logInfo("Timeline loadTimeline discarded a thrown error from a superseded reload", {
					error: (err as Error)?.message ?? String(err),
					pagesFetchedInReconcile,
					reload,
					entityId: entityId.value,
					entityType: entityType.value,
					sharingId: sharingId.value
				});
				return false;
			}
			detailLoad.invalidate("timeline");
			timelineLoadFailed.value = true;
			if (isMissingSession(err)) markSessionLost();
			if (isTransientAuthTokenError(err)) {
				// LSP-17079: transient/offline token failure — the timeline reload retries on the next
				// poll/socket event. Log at info (not console.error) so an offline blip does not surface
				// as a Datadog error; control flow is unchanged (still returns false, still retries).
				logInfo("[Upload] loadTimeline skipped; transient auth token failure (will retry)", {
					entityType: entityType.value,
					entityId: entityId.value,
					sharingId: sharingId.value
				});
				return false;
			}
			// LSP-18854: same gap as the upload poll above, and it must be fixed in the SAME change —
			// the production evidence is one session emitting 2399 errors here plus 802 on this path,
			// against the same entityId, so fixing only one leaves half the storm.
			if (isOfflineFailure(err)) {
				logInfo("[Upload] loadTimeline skipped; offline/network failure (will retry)", {
					entityType: entityType.value,
					entityId: entityId.value,
					sharingId: sharingId.value
				});
				return false;
			}
			// LSP-19608: the initial conversation timeline load runs concurrently with loadEntity (deliberate,
			// see initializeComponent) so it can lose the race against a just-created conversation the backend
			// hasn't finished persisting yet. This attempt is not the final word — the initializer refetches
			// once loadEntity confirms the entity exists — so log it quietly instead of raising a Datadog error.
			if (options.provisional) {
				logInfo("Timeline provisional initial load failed before the entity was confirmed; initializer will refetch", {
					error: (err as Error)?.message ?? String(err),
					entityId: entityId.value,
					entityType: entityType.value,
					sharingId: sharingId.value
				});
				return false;
			}
			console.error(err);
			console.error(err, {
				tags: {
					entityType: entityType.value,
					entityId: entityId.value,
					sharingId: sharingId.value
				},
				extra: {
					method: "loadTimeline",
					file: "EntityDetailPage",
					// LSP-17772 (Yasir #9, silent-failure discipline): name which endpoint was chosen and
					// the access signals behind it, so a "no media" incident on a shared/cross-org view is
					// triageable from this one line (was the org-scoped /search/media 404'd, or the public
					// shareId path?) instead of reconstructing it.
					mediaEndpoint: useAuthenticatedMediaSearch.value ? "authenticated /search/media" : "public /search/public/media",
					hasAuthenticatedProjectAccess: hasAuthenticatedProjectAccess.value,
					projectVerdictAvailable: !!projectVerdict.value,
					projectAccessResolved: projectAccessResolved.value,
					// ls-heroku-ci: this catch wraps both the single-page initial load and the up-to-20-page
					// reconcile — without these, a failure on reconcile page 14 of 20 was indistinguishable
					// in logs from a failure on the very first request.
					reload,
					pagesFetchedInReconcile
				}
			});
			return false;
		}
		// LSP-16706 (liveswitch-ai-pr-reviewer): a deferred reconcile leaves mediaResponse.value untouched —
		// from a caller's point of view that's the same "did not actually reconcile" outcome as an error, not
		// a success. handleAnnotationSaved's pendingReloads.timeline backstop (and any future caller) relies
		// on `false` meaning "the temp/optimistic row was not swapped for server truth, try again later"; a
		// deferral returning `true` here would have silently cleared that backstop.
		if (!reconcileDeferred) detailLoad.complete("timeline");
		return !reconcileDeferred;
	}

	// LSP-16086: create/update socket events only ever append a small tail of new
	// media to an already-loaded timeline. Instead of issuing a deep full reload
	// (which fetches up to MAX_RELOAD_PAGE_SIZE rows and was a load-test request-slot
	// starvation source), fetch just the delta window after the newest loaded item
	// and merge it in place. Falls back to a full reload whenever a gap cannot be
	// ruled out: nothing loaded yet, an active user filter (which would clobber the
	// delta startDate window), no resolvable startDate, a saturated delta page, or
	// a fetch failure.
	/**
	 * LSP-19529: bounded head reconcile for scheduler-driven "full" reloads (socket events, the
	 * upload-status poll, reconnect, the periodic safety tick and their visibility-deferred flushes).
	 *
	 * Fetches ONE page of SCHEDULED_FULL_RELOAD_PAGE_SIZE rows from the top with no delta window and
	 * merges it over the loaded list via mergeTimelineHead: the page is authoritative for its window, the
	 * already-loaded tail below it is kept. This replaces the previous behaviour of running the
	 * whole-window multi-page reconcile (`loadTimeline(true)`) on every such flush, which fetched two to
	 * six pages per flush per tab during the 2026-09-03 crowd test. Explicit reloads (delete handlers,
	 * filter changes, annotation saves) still call loadTimeline(true) and remain authoritative for the
	 * entire loaded window. Falls back to loadTimeline(true) when nothing is loaded yet or a user filter
	 * narrows the view, for the same reasons deltaReloadTimeline does.
	 *
	 * A tab scrolled past the head window additionally runs loadTimeline(true) once per
	 * SCHEDULED_WHOLE_WINDOW_RECONCILE_MIN_INTERVAL_MS, because the head page cannot observe inserts,
	 * deletes or verdict changes below its floor and the offset pager cannot recover a gap (AI PR review).
	 */
	async function headReloadTimeline(): Promise<boolean> {
		if (authSession.sessionLost) return false;
		const currentDocuments = mediaResponse.value?.documents ?? [];
		if (currentDocuments.length === 0 || userFiltersActive(filterPersistence.userFilters.value)) {
			return loadTimeline(true);
		}
		const sinceWholeWindowReconcileMs = Date.now() - lastWholeWindowReconcileAt;
		if (
			currentDocuments.length >= SCHEDULED_FULL_RELOAD_PAGE_SIZE &&
			sinceWholeWindowReconcileMs >= SCHEDULED_WHOLE_WINDOW_RECONCILE_MIN_INTERVAL_MS
		) {
			console.info(
				`[timeline-reload] scrolled tab (${currentDocuments.length} rows) is due its whole-window reconcile ` +
					`(${sinceWholeWindowReconcileMs}ms since last); running loadTimeline(true) instead of the head page. entityId=${entityId.value}`
			);
			return loadTimeline(true);
		}
		const reloadSequence = ++timelineReloadSequence;
		const requestedEntityId = entityId.value;
		const requestedEntityType = entityType.value;
		// Bugbot (PR review): same supersede guard as loadTimeline — artifactDeleted / onDeletedMediaItems
		// splice the documents array IN PLACE (same reference, new length, no sequence bump), and an entity
		// switch mid-fetch must not commit the old entity's page. Reference alone missed both.
		const documentsLengthBeforeFetch = currentDocuments.length;
		const headRequest = { ...getSearchRequest(), page: 1, pageSize: SCHEDULED_FULL_RELOAD_PAGE_SIZE } as SearchRequest;
		const requestWasNarrowed = !!headRequest.searchTerm || userFiltersNarrowResults(headRequest as Partial<SearchRequest>);
		const previousImageCount = currentDocuments.filter((item) => item.entityType === "snapshot").length;
		const previousVideoCount = currentDocuments.filter((item) => item.entityType === "recording").length;
		const previousDocumentCount = currentDocuments.filter((item) => isTimelineDocument(item)).length;
		try {
			const response = await service.value!.getTimeline(headRequest, useAuthenticatedMediaSearch.value);
			if (
				reloadSequence !== timelineReloadSequence ||
				requestedEntityId !== entityId.value ||
				requestedEntityType !== entityType.value ||
				mediaResponse.value?.documents !== currentDocuments ||
				mediaResponse.value?.documents?.length !== documentsLengthBeforeFetch
			) {
				logInfo("Timeline head reload discarded (superseded by a newer reload/filter change)", {
					entityId: entityId.value,
					entityType: entityType.value,
					sharingId: sharingId.value
				});
				return false;
			}
			// break-it round 3: a response without a finite `count` must not become an authoritative zero —
			// keep the last trustworthy total instead (mergeTimelineHead likewise treats it as unknown).
			const serverCount = typeof response?.count === "number" && Number.isFinite(response.count) ? response.count : null;
			const headPage = { documents: response?.documents ?? [], count: serverCount };
			const merged = mergeTimelineHead(currentDocuments, headPage, SCHEDULED_FULL_RELOAD_PAGE_SIZE);
			const adoptedCount = serverCount ?? mediaResponse.value?.count ?? merged.length;
			const mergedResponse = { ...response, documents: merged, count: adoptedCount };
			// Skeleton placeholders: same rule as loadTimeline(true) — the head window is where a pending
			// upload lands, so a reload that brings nothing new while no upload is running clears them. Applied
			// BEFORE the no-change early return below (break-it round 1): an idle flush that changes no row is
			// exactly the case where stale skeletons must still be cleared.
			const newImageCount = merged.filter((item) => item.entityType === "snapshot").length;
			const newVideoCount = merged.filter((item) => item.entityType === "recording").length;
			const imageDiff = newImageCount - previousImageCount;
			const videoDiff = newVideoCount - previousVideoCount;
			if (imageDiff > 0 && pendingImages.value > 0) {
				pendingImages.value = Math.max(0, pendingImages.value - imageDiff);
			} else if (!isUploading.value) {
				pendingImages.value = 0;
			}
			if (videoDiff > 0 && pendingVideos.value > 0) {
				pendingVideos.value = Math.max(0, pendingVideos.value - videoDiff);
			} else if (!isUploading.value) {
				pendingVideos.value = 0;
			}
			retirePendingDocumentSkeletons(merged.filter((item) => isTimelineDocument(item)).length - previousDocumentCount, "head");
			// The page's `count` is the server's grand total, so it is safe to adopt (unlike a delta's
			// windowed count). Skip the reactive reassignment entirely when neither the rows nor the total
			// changed, for the same no-flash reason as deltaReloadTimeline.
			if (!timelineDocumentsChanged(currentDocuments, merged) && (mediaResponse.value?.count ?? 0) === adoptedCount) {
				// LSP-16659: the head page's per-type totals are grand totals like its `count` (mergedResponse
				// carries them via the spread); keep them current even when no row changed.
				if (response?.countsByEntityType && mediaResponse.value)
					mediaResponse.value.countsByEntityType = response.countsByEntityType;
				timelineLoadFailed.value = false;
				lastTimelineReconcileAt = Date.now();
				return true;
			}
			mediaResponse.value = mergedResponse;
			showTimelineResultsFor(headRequest);
			consecutiveRestartsExhaustedCount = 0;
			consecutiveIncompleteCount = 0;
			// Authoritative for the newest window, where a latched recording would surface; the latch's own
			// documents.length < count guard covers a tail this page did not re-fetch.
			reconcileLatchAgainstAuthoritativeTimeline(mergedResponse, requestWasNarrowed);
			normalizeTimelinePagingAfterReplacement();
			timelineLoadFailed.value = false;
			lastTimelineReconcileAt = Date.now();
			return true;
		} catch (err) {
			if (reloadSequence !== timelineReloadSequence) {
				return false;
			}
			timelineLoadFailed.value = true;
			if (isMissingSession(err)) markSessionLost();
			if (isTransientAuthTokenError(err) || isOfflineFailure(err)) {
				logInfo("[Upload] headReloadTimeline skipped; transient auth or offline failure (will retry)", {
					entityType: entityType.value,
					entityId: entityId.value,
					sharingId: sharingId.value
				});
				return false;
			}
			console.error(err, {
				tags: { entityType: entityType.value, entityId: entityId.value, sharingId: sharingId.value },
				extra: { method: "headReloadTimeline", file: "EntityDetailPage" }
			});
			return false;
		}
	}

	async function deltaReloadTimeline(): Promise<boolean> {
		if (authSession.sessionLost) return false;
		const reloadSequence = ++timelineReloadSequence;
		const currentDocuments = mediaResponse.value?.documents ?? [];
		if (currentDocuments.length === 0 || userFiltersActive(filterPersistence.userFilters.value)) {
			return loadTimeline(true);
		}

		const startDate = deltaStartDate(getNewestCreated(currentDocuments));
		if (!startDate) {
			return loadTimeline(true);
		}

		const deltaRequest = { ...getSearchRequest(), page: 1, pageSize: PAGE_SIZE, startDate } as SearchRequest;

		try {
			const response = await service.value!.getTimeline(deltaRequest, useAuthenticatedMediaSearch.value);
			const deltaDocuments = response?.documents ?? [];
			if (reloadSequence !== timelineReloadSequence || mediaResponse.value?.documents !== currentDocuments) {
				return false;
			}

			// A full delta page means the create/update window may extend past what we
			// fetched, so we can't safely merge — reconcile with a bounded full reload.
			// Check BOTH the returned length and the windowed total count: the API can
			// post-filter hydrated stubs below pageSize even when more rows match the
			// window, so length alone can under-detect saturation and silently drop rows.
			if (deltaDocuments.length >= PAGE_SIZE || (response?.count ?? 0) >= PAGE_SIZE) {
				// LSP-19529: a saturated delta during an upload burst used to escalate to the whole-window
				// multi-page reconcile on every tab at once; the bounded head page covers the new rows.
				return headReloadTimeline();
			}

			const merged = mergeTimelineDelta(currentDocuments, deltaDocuments);

			// LSP-16761: an idle poll usually returns nothing new — the merge then reproduces
			// the same items in the same order. Replacing mediaResponse anyway hands Timeline a
			// fresh `documents` array reference every ~60s, which re-renders the grid (regroups by
			// date, tears down/rebuilds the IntersectionObserver, re-evaluates lazy media) and
			// shows as a visible flash. When nothing actually changed, leave the reactive state
			// untouched so the grid does not re-render at all. Still stamp success below so the
			// safety-net reconcile stays gated and the failure flag clears.
			if (!timelineDocumentsChanged(currentDocuments, merged)) {
				timelineLoadFailed.value = false;
				lastTimelineReconcileAt = Date.now();
				return true;
			}

			// Recompute skeleton placeholders from the MERGED list vs the previous one
			// (not the delta response), mirroring loadTimeline's reload decrement.
			const newImageCount = merged.filter((item) => item.entityType === "snapshot").length;
			const newVideoCount = merged.filter((item) => item.entityType === "recording").length;
			const previousImageCount = currentDocuments.filter((item) => item.entityType === "snapshot").length;
			const previousVideoCount = currentDocuments.filter((item) => item.entityType === "recording").length;
			const imageDiff = newImageCount - previousImageCount;
			const videoDiff = newVideoCount - previousVideoCount;

			// LSP-16086: unlike loadTimeline (which is authoritative over the whole
			// window and resets the pending counters to 0 when a reload brings no new
			// items), a delta only covers the tail window. A zero/negative diff here
			// means the awaited item simply was not in this delta (still processing,
			// or its `created` fell outside the window) — NOT that it will never come.
			// So we only decrement on positive diffs and intentionally OMIT the
			// else-reset-to-0. Do not "restore" that else branch: it would clear a
			// valid skeleton early. A stale skeleton self-heals on the next capped
			// full reload (delete/date-edit) or when the item lands in a later delta.
			if (imageDiff > 0 && pendingImages.value > 0) {
				pendingImages.value = Math.max(0, pendingImages.value - imageDiff);
			}
			if (videoDiff > 0 && pendingVideos.value > 0) {
				pendingVideos.value = Math.max(0, pendingVideos.value - videoDiff);
			}
			retirePendingDocumentSkeletons(
				merged.filter((item) => isTimelineDocument(item)).length -
					currentDocuments.filter((item) => isTimelineDocument(item)).length,
				"delta"
			);

			// The delta query's `count` is the windowed total (items matching startDate),
			// NOT the full timeline total — assigning it would corrupt the media count
			// shown in the UI. Adjust the existing total by the net-new unique items the
			// merge added (an update re-fetched in place contributes 0).
			const netNewCount = merged.length - currentDocuments.length;
			// LSP-16659: the per-type totals are windowed the same way, so they are adjusted the same way.
			mediaResponse.value = {
				...mediaResponse.value,
				documents: merged,
				count: mediaResponse.value.count + netNewCount,
				countsByEntityType: adjustCountsByEntityType(mediaResponse.value.countsByEntityType, currentDocuments, merged)
			};
			timelineLoadFailed.value = false;
			// ls-heroku-ci: this commits a new row universe like every other replacement path — a stale
			// consecutive-deferral streak from a prior attempt must not survive into it.
			consecutiveRestartsExhaustedCount = 0;
			consecutiveIncompleteCount = 0;
			// LSP-16086 (Bugbot #1): stamp on SUCCESS only — a failed delta fetch must
			// not look "fresh" and gate the safety net's retry.
			lastTimelineReconcileAt = Date.now();
		} catch (err) {
			if (reloadSequence !== timelineReloadSequence) {
				return false;
			}
			timelineLoadFailed.value = true;
			// LSP-18854 (Bugbot): THIS is the catch the production evidence points at — the 802-event row
			// is tagged `method: deltaReloadTimeline`. deltaReloadTimeline is a separate function from
			// loadTimeline, and it had neither guard: not the offline one this ticket adds, and not even
			// LSP-17079's transient-auth one, so it reported every flap as an error. Both are applied
			// here so all three sibling catches (fetchUploadStatus, loadTimeline, deltaReloadTimeline)
			// classify a retryable condition identically. Control flow is unchanged — still sets
			// timelineLoadFailed, still returns false, still retries on the next poll/socket event.
			if (isTransientAuthTokenError(err) || isOfflineFailure(err)) {
				logInfo("[Upload] deltaReloadTimeline skipped; transient auth or offline failure (will retry)", {
					entityType: entityType.value,
					entityId: entityId.value,
					sharingId: sharingId.value
				});
				return false;
			}
			console.error(err, {
				tags: {
					entityType: entityType.value,
					entityId: entityId.value,
					sharingId: sharingId.value
				},
				extra: {
					method: "deltaReloadTimeline",
					file: "EntityDetailPage"
				}
			});
			return false;
		}
		return true;
	}

	async function searchTimeline(searchTerm: string) {
		if (authSession.sessionLost) return;
		if (isSearchTooLong(searchTerm)) return;
		if (!searchRequest.value) {
			return;
		}

		currentSearchTerm.value = searchTerm;
		filterPersistence.onSearchTermChange(searchTerm);
		// LSP-20939: stamp the term onto the page's request too. Filters is handed `searchRequest` and echoes it
		// back on every filter change, and pageTimeline spreads it for the next page — so leaving the load-time
		// term here sent a filter change, or the next page of a search, without the term the user searched.
		searchRequest.value = { ...searchRequest.value, searchTerm } as SearchRequest;
		const request = { ...getSearchRequest(), ...searchRequest.value, searchTerm: searchTerm };
		// Captured BEFORE the await, per the reconcile's contract.
		const requestWasNarrowed = !!request.searchTerm || userFiltersNarrowResults(request as Partial<SearchRequest>);

		try {
			const requestedEntityId = entityId.value;
			const requestedEntityType = entityType.value;
			const response = await service.value!.getTimeline(request, useAuthenticatedMediaSearch.value);
			if (requestedEntityId !== entityId.value || requestedEntityType !== entityType.value) {
				// CLAUDE.md "Diagnostic logging when fixing silent failures": the structurally identical guard
				// in loadTimeline's !reload branch and pageTimeline's stale-fetch guard both log when they
				// fire; this one silently returned with no signal (ls-heroku-ci).
				logInfo("Timeline search aborted (superseded by an entity change)", {
					entityId: entityId.value,
					entityType: entityType.value,
					sharingId: sharingId.value
				});
				return;
			}
			mediaResponse.value = response;
			showTimelineResultsFor(request);
			// LSP-16706 (liveswitch-ai-pr-reviewer): this replaces the row universe the same way reset() does
			// on in-place navigation, and for the identical reason those consecutive-deferral counters need
			// clearing too — otherwise a pre-search run of deferrals could carry a stale streak into the
			// post-search reconcile's first "incomplete"/"restarts-exhausted" deferral and misjudge it as
			// persistently stuck.
			consecutiveRestartsExhaustedCount = 0;
			consecutiveIncompleteCount = 0;
			// Clearing a search replaces the timeline HERE, not via loadTimeline, so the latch reconcile has to
			// run on this path too (Cursor Bugbot). `currentSearchTerm` is already updated above, so the
			// reconcile's own narrowed-response guard sees the new term and stays shy while a term is active.
			reconcileLatchAgainstAuthoritativeTimeline(response, requestWasNarrowed);
			normalizeTimelinePagingAfterReplacement();
			timelineLoadFailed.value = false;
		} catch (err) {
			console.error(err);
			console.error(err, {
				tags: {
					entityType: entityType.value,
					entityId: entityId.value,
					sharingId: sharingId.value
				},
				extra: {
					method: "searchTimeline",
					file: "EntityDetailPage"
				}
			});

			if (!accessRevoked.value) {
				toast.error("Sorry, we were unable to search the media.");
			}
		}
	}

	async function pageTimeline() {
		if (authSession.sessionLost) return;
		if (!hasMoreMedia.value || pagingTimeline.value) {
			return;
		}

		pagingTimeline.value = true;

		const requestedEntityId = entityId.value;
		const requestedEntityType = entityType.value;
		// LSP-16618: join the same monotonic guard the reload paths use, and snapshot the
		// documents array we are paging against. A refresh (socket / periodic / upload-driven)
		// that lands while this page is in flight replaces mediaResponse and resets the paging
		// counters — without this guard the stale page would push onto the new array and
		// overwrite currentTimelinePage / hasMoreMedia, breaking further paging.
		const pageSequence = ++timelineReloadSequence;
		const pagedDocuments = mediaResponse.value.documents;
		// Cursor Bugbot: artifactDeleted/onDeletedMediaItems splice this SAME array in place (no reference
		// swap, no sequence bump) — loadTimeline's reconcile already pairs a length snapshot with the
		// reference check for exactly this reason, but this guard was still reference-only. Tracked as the
		// length WE expect (updated after each of our own pushes below, since this loop itself grows the
		// array across iterations) so an EXTERNAL splice — not our own commits — is what trips it.
		let expectedDocumentsLength = pagedDocuments.length;

		try {
			// liveswitch-ai-pr-reviewer: a barren fetch (every row filtered, e.g. stubs) is a no-op push —
			// mediaResponse.value.documents doesn't change, so Timeline.vue's watch on mediaItems never
			// fires and nothing else asks for the NEXT page. Loop internally (bounded, matching the
			// reconcile's own page cap) until a page adds real rows or hasMoreMedia goes false, rather than
			// relying on the external scroll observer to retry.
			const MAX_BARREN_PAGES_PER_CALL = 20;
			let barrenPagesConsumed = 0;
			let exitedViaBreak = false;
			for (let barrenAttempt = 0; barrenAttempt < MAX_BARREN_PAGES_PER_CALL; barrenAttempt++) {
				// LSP-16706: the next page is the page containing the first unloaded row —
				// floor(loaded/PAGE_SIZE)+1 — so a partial last page after a reconcile is re-requested rather
				// than skipped. But clamp it to advance past a BARREN page: if the previous fetch added no new
				// rows (an empty/all-stub page while `count` still exceeds loaded), the derived page would not
				// move and paging would re-request it forever, so never go below lastTimelinePageRequested + 1.
				const nextPage = Math.max(Math.floor(mediaResponse.value.documents.length / PAGE_SIZE) + 1, lastTimelinePageRequested + 1);
				// `nextPage` is derived in PAGE_SIZE(75)-sized units, so pageSize must be pinned to PAGE_SIZE here
				// rather than inherited from searchRequest.value: a reload/reconcile in flight (or superseded
				// before it could call normalizeTimelinePagingAfterReplacement) can leave searchRequest.value.pageSize
				// inflated to MAX_RELOAD_PAGE_SIZE, and spreading that in would misalign the server offset
				// (page * pageSize) against the loaded-row-count this cursor assumes, opening a gap of un-fetched rows.
				const request = { ...searchRequest.value, page: nextPage, pageSize: PAGE_SIZE } as SearchRequest;

				const response = await service.value!.getTimeline(request, useAuthenticatedMediaSearch.value);
				if (
					pageSequence !== timelineReloadSequence ||
					mediaResponse.value.documents !== pagedDocuments ||
					mediaResponse.value.documents.length !== expectedDocumentsLength ||
					requestedEntityId !== entityId.value ||
					requestedEntityType !== entityType.value
				) {
					// CLAUDE.md "Diagnostic logging when fixing silent failures" (ls-heroku-ci): this fetched page
					// is discarded with no trace of WHICH condition fired.
					//
					// Cursor Bugbot: do NOT nudge refreshObservedItems() here — this branch fires precisely when
					// something ELSE is (or just was) actively touching the timeline (a reload/reconcile bumped
					// the sequence, or an in-place delete spliced the array), and that other operation's own
					// commit already drives Timeline.vue's reactivity when it lands. Nudging here risked a
					// ping-pong: the nudge could re-trigger pageTimeline while a multi-page reconcile is still
					// in flight, bumping the sequence again and aborting the reconcile that would have refreshed
					// the view itself. The nudge instead lives at loop-exhaustion below, where no concurrent
					// operation could have intervened without this guard already having caught it.
					logInfo("Timeline pageTimeline discarded a stale/superseded page fetch", {
						pageSequenceStale: pageSequence !== timelineReloadSequence,
						documentsReplaced: mediaResponse.value.documents !== pagedDocuments,
						documentsSplicedInPlace:
							mediaResponse.value.documents === pagedDocuments &&
							mediaResponse.value.documents.length !== expectedDocumentsLength,
						entityChanged: requestedEntityId !== entityId.value || requestedEntityType !== entityType.value,
						requestedPage: nextPage,
						entityId: entityId.value,
						entityType: entityType.value,
						sharingId: sharingId.value
					});
					return;
				}

				// LSP-16086: a delta reload prepends newer items, shifting the server-side
				// offset this page request was computed against — so the returned page can
				// overlap rows already loaded. Dedupe by canonical search-result identity
				// before appending to avoid duplicate timeline entries at the page boundary.
				const existingIdentities = new Set(mediaResponse.value.documents.map((document) => document.searchResultId ?? document.id));
				const newPageDocuments = (response?.documents ?? []).filter((document) => {
					const identity = document.searchResultId ?? document.id;
					if (existingIdentities.has(identity)) return false;
					existingIdentities.add(identity);
					return true;
				});
				// LSP-16706: advance the paging cursor only AFTER a committed fetch (past the stale guard) —
				// never before the await. A failed request or a superseded (stale) page must leave the cursor
				// where it was so the next scroll retries this page instead of skipping it.
				//
				// liveswitch-ai-pr-reviewer: only ratchet forward past a page that added ZERO new rows (fully
				// barren — the intended "advance past a barren page" case). Unconditionally advancing here also
				// caught any PARTIALLY-populated page (IndexService strips pending-upload stubs from `documents`
				// without lowering `count`, so a page can come back with 1..74 real rows instead of 0 or 75): the
				// next call's floor(loaded/PAGE_SIZE)+1 would correctly re-derive THIS SAME page (loaded isn't a
				// full PAGE_SIZE multiple yet) to pick up the rest once the stubs hydrate, but the unconditional
				// ratchet forced lastTimelinePageRequested+1 to win the Math.max anyway, permanently skipping the
				// still-un-fetched remainder of that page. Leaving the cursor unadvanced on a partial page lets
				// both terms of the Math.max agree on re-requesting it.
				const wasBarren = newPageDocuments.length === 0;
				if (wasBarren) {
					lastTimelinePageRequested = nextPage;
					barrenPagesConsumed += 1;
				}
				mediaResponse.value.documents.push(...newPageDocuments);
				expectedDocumentsLength += newPageDocuments.length;
				mediaResponse.value.count = response?.count ?? 0;
				// LSP-16659: a page's per-type totals are grand totals, like its `count`.
				if (response?.countsByEntityType) mediaResponse.value.countsByEntityType = response.countsByEntityType;
				currentTimelinePage.value = request.page;
				// LSP-16618: more remains whenever fewer items are loaded than the total count,
				// rather than inferring it from the raw returned page length.
				hasMoreMedia.value = mediaResponse.value.documents.length < (response?.count ?? 0);

				if (!wasBarren || !hasMoreMedia.value) {
					exitedViaBreak = true;
					break; // got real rows to show, or reached the true end — stop looping
				}
				// else: this page was barren and more remains — loop again for the next page instead of
				// waiting on a scroll-observer re-trigger that a no-op push would never produce.
			}
			// ls-heroku-ci: every one of the MAX_BARREN_PAGES_PER_CALL iterations came back barren — a
			// heavily stub-inflated conversation exhausted this call's budget without surfacing anything.
			// Log for diagnosability, but deliberately do NOT nudge refreshObservedItems() here (Cursor
			// Bugbot, second pass): a still-visible sentinel would immediately re-emit page() once
			// pagingTimeline clears in `finally`, re-running this same exhausting walk indefinitely and
			// hammering /search/media forever — worse than the stall it would "fix". A stub-inflated
			// conversation genuinely needs either a user-driven re-scroll or the periodic safety net's next
			// full reconcile (which walks fresh from page 1 and can discover hydration this incremental path
			// cannot) — auto-retrying a walk that just proved it can't make progress isn't safe without a
			// real backoff/cooldown design, which is out of scope for this pass.
			if (!exitedViaBreak) {
				logInfo("Timeline pageTimeline exhausted its barren-page budget without surfacing new rows", {
					barrenPagesConsumed,
					maxBarrenPagesPerCall: MAX_BARREN_PAGES_PER_CALL,
					loadedCount: mediaResponse.value.documents.length,
					entityId: entityId.value,
					entityType: entityType.value,
					sharingId: sharingId.value
				});
			}
		} catch (err) {
			console.error(err);
			console.error(err, {
				tags: {
					entityType: entityType.value,
					entityId: entityId.value,
					sharingId: sharingId.value
				},
				extra: {
					method: "pageTimeline",
					file: "EntityDetailPage"
				}
			});

			if (!accessRevoked.value) {
				toast.error("Sorry, we were unable to load the next page of media.");
			}
		} finally {
			pagingTimeline.value = false;
		}
	}

	async function onSearchRequestChange(updatedFilters: SearchRequest) {
		// LSP-20939: the search box owns the term, never the Filters payload. Filters echoes whatever term its
		// modelValue carried (it never authors one — "Clear" only preserves it), and `??` kept that echo even
		// when it was a stale "" — so a filter change after a search dropped the term the box still showed,
		// or resurrected one the user had cleared. ListPageHeader applies the same rule for the list pages.
		const searchTerm = currentSearchTerm.value;
		if (isSearchTooLong(searchTerm)) return;
		filterPersistence.onFilterChange({ ...updatedFilters, searchTerm });
		const requestedEntityId = entityId.value;
		const requestedEntityType = entityType.value;

		const request = {
			...getSearchRequest(),
			...updatedFilters,
			searchTerm
		};
		// v-model already replaced `searchRequest` with the raw Filters payload (stale term; after "Clear", only
		// `{ organization, searchTerm }`). Keep the request this fetch actually sends, so paging and the next
		// filter echo continue from it.
		searchRequest.value = request;
		// Captured BEFORE the await, per the reconcile's contract.
		const requestWasNarrowed = !!request.searchTerm || userFiltersNarrowResults(request as Partial<SearchRequest>);

		const response = await service.value!.getTimeline(request, useAuthenticatedMediaSearch.value);
		if (requestedEntityId !== entityId.value || requestedEntityType !== entityType.value) {
			// CLAUDE.md "Diagnostic logging when fixing silent failures": same reason as searchTimeline's
			// identical guard — this one also silently returned with no signal (ls-heroku-ci).
			logInfo("Timeline filter change aborted (superseded by an entity change)", {
				entityId: entityId.value,
				entityType: entityType.value,
				sharingId: sharingId.value
			});
			return;
		}
		mediaResponse.value = response;
		showTimelineResultsFor(request);
		// LSP-16706 (liveswitch-ai-pr-reviewer): same reason as searchTimeline's identical line — this also
		// replaces the row universe, so a stale pre-filter consecutive-deferral streak must not survive into it.
		consecutiveRestartsExhaustedCount = 0;
		consecutiveIncompleteCount = 0;
		// Clearing filters replaces the timeline HERE, not via loadTimeline (Cursor Bugbot).
		// `filterPersistence.onFilterChange` ran at the top of this function, so the reconcile's guard reads
		// the NEW filter state — it stays shy while a filter is active and only concludes once cleared.
		reconcileLatchAgainstAuthoritativeTimeline(response, requestWasNarrowed);
		normalizeTimelinePagingAfterReplacement();
		timelineLoadFailed.value = false;
	}

	function normalizeTimelinePagingAfterReplacement(): void {
		const loadedDocumentCount = mediaResponse.value?.documents?.length ?? 0;
		const totalCount = mediaResponse.value?.count ?? 0;
		currentTimelinePage.value = Math.max(1, Math.ceil(loadedDocumentCount / PAGE_SIZE));
		// LSP-16618: there is more to load whenever fewer items are loaded than the
		// server's total count. The old test compared the loaded count to the (inflated)
		// requested page size, so a refresh that returned fewer rows than it asked for
		// (e.g. requested 300, only 205 exist) looked like the end of the list and
		// silently disabled further paging.
		hasMoreMedia.value = loadedDocumentCount < totalCount;
		// LSP-16706: the list was just replaced, so re-baseline the paging cursor to the number of fully
		// loaded pages. The next pageTimeline then derives floor(loaded/PAGE_SIZE)+1 (re-requesting a
		// partial last page); anchoring to the loaded depth — rather than 0 — also drops the cursor after
		// a shrink so paging cannot skip past the now-shorter list.
		lastTimelinePageRequested = Math.floor(loadedDocumentCount / PAGE_SIZE);
		if (searchRequest.value) {
			searchRequest.value = { ...searchRequest.value, page: 1, pageSize: PAGE_SIZE } as SearchRequest;
		}
	}

	function getSearchRequest(reload: boolean = false): SearchRequest {
		const pageSize = reload ? Math.min((currentTimelinePage.value + 1) * PAGE_SIZE, MAX_RELOAD_PAGE_SIZE) : PAGE_SIZE;

		const request = {
			page: 1,
			pageSize: pageSize,
			entitySearch: true,
			searchTerm: currentSearchTerm.value,
			orderBy: "created",
			orderByDescending: true,
			organizationId: searchOrganizationId.value ?? undefined,
			parentIds: entityType.value === "project" ? undefined : projectConversationIds.value,
			// LSP-17519: a session page sends its PARENT project id so /search/media takes its
			// hasAccessToProject branch and authorizes across the project — without it, a viewer who
			// reached this session THROUGH the project matched nothing on `authorizedProjectIds`
			// (explicit ProjectShare rows only) and got an empty timeline for media they can plainly see
			// on the project page. `parentIds` above still narrows results to this one session
			// (authorization.parentIds and filters.parentIds are independent in PostgresSearch).
			//
			// Sent ONLY for a project-derived read (ai-pr-review, HIGH). That route HARD-404s the whole
			// search when `projectId` is present and hasAccessToProject fails, with no fallback to the
			// conversation-scoped default. `Conversation.projectId` is a plain nullable UUID with no FK
			// and project deletion only soft-deletes the Project row, so a DIRECT-access caller
			// (assignment or READ_OTHERS) can legitimately hold a conversation whose parent project is
			// cross-org or soft-deleted. Sending it unconditionally turned their previously-working
			// timeline into a 404 — a worse regression than the bug this ticket fixes. Direct callers
			// keep `null` and the default scope that already admitted their media.
			projectId:
				entityType.value === "project"
					? entity.value?.id
					: (entity.value as Conversation | undefined)?.accessVia === "project"
						? ((entity.value as Conversation | undefined)?.projectId ?? null)
						: null,
			shareId: sharingId.value
		} as SearchRequest;

		return request;
	}

	async function loadEntity(switchToken: number = entitySwitchSequence, loadToken?: number): Promise<DetailStageOutcome> {
		const id = entityId.value;
		const stopped = detailStopReason(id, switchToken, loadToken);
		if (stopped) return verifyDetailStage("project", id, switchToken, loadToken, stopped);
		const background = loadToken === undefined;
		const deferredToForeground = background && detailLoad.loading.value;
		const previousPhase = detailLoad.phase.value;
		const token = loadToken ?? (deferredToForeground ? detailLoad.currentToken() : detailLoad.begin());
		if (!deferredToForeground) detailLoad.invalidate("project");
		const outcome = await runDetailStage("project", id, switchToken, token, () => loadEntityWork(switchToken, token));
		if (background) settleBackgroundStage(token, outcome, deferredToForeground, previousPhase);
		return outcome;
	}

	async function loadEntityWork(switchToken: number = entitySwitchSequence, loadToken?: number): Promise<EntityDetailStopReason | null> {
		if (authSession.sessionLost) { markSessionLost(); return "auth-lost"; }
		// LSP-20787: every load — mount, switch, or the CONTACT_CHANGED reload — starts with no delivery state, no
		// open box and no refresh timer, so nothing of the previous session (or of a session left for a project) carries over.
		clearDeliveryRefresh();
		delivery.value = null;
		deliveryMode.value = null;
		if (entityType.value === "project") {
			// break-it round 3: capture the id THIS switch is loading for once, up front. Every later
			// ownership recheck in this function compares against it via isStaleAction — comparing against
			// switchToken alone (or the live entityId.value ref) can lag behind an already-changed
			// route.params.id until the watcher's own callback actually runs.
			const projectId = entityId.value;

			if (route.query?.shareId) {
				sharingId.value = route.query.shareId as string;
			} else {
				sharingId.value = props.shareId as string;
			}

			const [, acquisitionStop] = await Promise.all([handleProjectAccess(projectId, switchToken, true), loadUserAndProjectWork(projectId, switchToken, { detailLoadToken: loadToken, throwOnError: true })]);
			if (acquisitionStop) return detailStopReason(projectId, switchToken, loadToken) ?? acquisitionStop;
			// LSP-17483: a newer switch (or unmount) took ownership while handleProjectAccess was in
			// flight — stop here rather than continuing this switch's project-access logic against data
			// that may already be stale.
			if (detailStopReason(projectId, switchToken, loadToken)) {
				console.warn("EntityDetailPage.loadEntity: switch superseded after handleProjectAccess — stopping this switch's load", {
					projectId,
					switchToken,
					currentSwitchToken: entitySwitchSequence,
					isComponentUnmounted
				});
				return detailStopReason(projectId, switchToken, loadToken) ?? "superseded";
			}

			// LSP-17483: same reasoning as above, after loadUserAndProject — the entity.value assignments
			// inside it are already ownership-checked via applyEntityForSwitch, but the rest of this
			// function's project-only logic (access decisions, free-trial gating, etc.) must not keep
			// running for a switch that no longer owns the page.
			if (detailStopReason(projectId, switchToken, loadToken)) {
				console.warn("EntityDetailPage.loadEntity: switch superseded after loadUserAndProject — stopping this switch's load", {
					projectId,
					switchToken,
					currentSwitchToken: entitySwitchSequence,
					isComponentUnmounted
				});
				return detailStopReason(projectId, switchToken, loadToken) ?? "superseded";
			}

			if (!entity.value?.id) {
				startingLoading.value = false;
				return detailStopReason(projectId, switchToken, loadToken) ?? "entity-missing";
			}

			const projectStop = stageStop(await settleProjectDetails(projectId, switchToken, loadToken));
			if (projectStop) return projectStop;

			const timelineReady = await loadTimeline(false, { detailLoadToken: loadToken });
			// ls-heroku-ci (HIGH): the conversation branch in initializeComponent guards tryOpenLightBox with
			// this exact check (break-it round 6) precisely because a lightbox is a VISIBLE side effect — a
			// superseded switch would pop open media belonging to the entity the user just navigated away
			// from. The project branch here was left unguarded, so it had the bug the sibling already fixed.
			// Checked BEFORE the call, not after: the recheck below cannot un-open a lightbox.
			if (detailStopReason(projectId, switchToken, loadToken)) {
				console.warn("EntityDetailPage.loadEntity: switch superseded before opening the lightbox — skipping it", {
					entityId: entityId.value,
					switchToken,
					currentSwitchToken: entitySwitchSequence,
					currentRouteParamId: route.params.id,
					isComponentUnmounted
				});
				return detailStopReason(projectId, switchToken, loadToken) ?? "superseded";
			}
			if (timelineReady) await tryOpenLightBox();

			// Bugbot (post-review, HIGH): a newer switch (or unmount) may have taken ownership while
			// loadTimeline/tryOpenLightBox were in flight — stop here rather than letting a superseded switch
			// navigate away (router.push("/projects")), overwrite viewOnly, or run
			// setAuthenticatedProjectDetails against the entity the user has already moved to.
			if (detailStopReason(projectId, switchToken, loadToken)) {
				console.warn(
					"EntityDetailPage.loadEntity: switch superseded after loadTimeline/tryOpenLightBox — stopping this switch's load",
					{
						projectId,
						switchToken,
						currentSwitchToken: entitySwitchSequence,
						isComponentUnmounted
					}
				);
				return detailStopReason(projectId, switchToken, loadToken) ?? "superseded";
			}

			if (!timelineReady) return "stage-failed:timeline";

		} else {
			// LSP-17483: capture the id THIS switch requested before the await — entityId.value may have
			// already moved on to a newer switch by the time getEntity resolves, and applyEntityForSwitch
			// (not this captured id) is what decides whether the response still gets applied.
			const conversationId = entityId.value;
			const conversationResult = await service.value?.getEntity(conversationId);
			const acquisitionStop = detailStopReason(conversationId, switchToken, loadToken);
			if (acquisitionStop) return acquisitionStop;
			if (!conversationResult?.id) return "entity-missing";
			if (!applyEntityForSwitch(conversationId, switchToken, conversationResult, `loadEntity getEntity(${conversationId})`)) {
				return detailStopReason(conversationId, switchToken, loadToken) ?? "superseded";
			}
			// LSP-17519: this caller reached the session only through its parent project, not through
			// their own conversation permission. The write gates are still assignment-scoped, so every
			// edit affordance we render here would come back as a confusing 404 — show it read-only.
			// Only ever raises view-only (the trial-ended / view-only-role rules above also set it), so
			// the ordering against them does not matter.
			if ((conversationResult as Conversation | undefined)?.accessVia === "project") {
				viewOnly.value = true;
			}
			// LSP-20787: once the read-only flag is settled. Not awaited: the chip is additive and a failed fetch only logs.
			void loadDelivery(conversationId, switchToken);
			await autoUpdateReviewedIfReady(switchToken);
			const completionStop = detailStopReason(conversationId, switchToken, loadToken);
			if (completionStop) return completionStop;
			// LSP-16028: the session interior's counterpart to the project seed below — without it a
			// hard refresh on a session leaves the Sparky indicator showing "ready" while a report runs.
			// Seed first: the warm-up's refreshAnalyses is a no-op until the store has a scope.
			seedScribeMonitoring();
			startScribeWarmup();
			if (entity.value?.id === conversationId && !detailStopReason(conversationId, switchToken, loadToken)) detailLoad.complete("project");
		}
		return detailStopReason(entityId.value, switchToken, loadToken) ??
			(entityType.value === "project" && verdictStatus.value === "ready" && projectVerdict.value?.hasAccess === false ? "access-denied" : null);
	}

	// Entity acquisition alone is not project completion: apply access and details before recording it.
	async function settleProjectDetails(id: string, switchToken: number, loadToken?: number): Promise<DetailStageOutcome> {
		return runDetailStage("project", id, switchToken, loadToken, () => settleProjectDetailsWork(id, switchToken, loadToken));
	}

	async function settleProjectDetailsWork(id: string, switchToken: number, loadToken?: number): Promise<EntityDetailStopReason | null> {
		const stopped = detailStopReason(id, switchToken, loadToken);
		if (stopped) return stopped;
		if (entity.value?.id !== id) return "entity-missing";
		if (!(await settleProjectAccessOutcome(id, switchToken))) return detailStopReason(id, switchToken, loadToken) ?? "stage-failed:verdict";

		const afterAccess = detailStopReason(id, switchToken, loadToken);
		if (afterAccess) return afterAccess;
		if (isAuthenticated.value) {
			// The access prompt can await logout; recheck ownership before stamping address/customer/owner.
			if (detailStopReason(id, switchToken, loadToken)) {
				console.warn("EntityDetailPage.loadEntity: switch superseded across the logout await — not stamping project details", {
					id,
					switchToken,
					currentSwitchToken: entitySwitchSequence,
					currentEntityId: entityId.value,
					currentRouteParamId: route.params.id,
					isComponentUnmounted
				});
				return detailStopReason(id, switchToken, loadToken) ?? "superseded";
			}
			setAuthenticatedProjectDetails();
			// break-it round 6: capture-recheck-assign — the previous unconditional assignment here let a
			// switch stalled on this specific await overwrite a NEWER, already-established project's
			// freeTrialEnded/viewOnly state (and skip its own showProjectActionFAB toggle) on resume.
			const freeTrialEndedResult = detailLoad.hasCompleted("trial") ? freeTrialEnded.value : await hasFreeTrialEnded(me.value?.organization, me.value?.organizationSubscription);
			if (detailStopReason(id, switchToken, loadToken)) {
				console.warn(
					"EntityDetailPage.loadEntity: switch superseded after hasFreeTrialEnded() resolved — not overwriting freeTrialEnded/viewOnly",
					{
						id,
						switchToken,
						currentSwitchToken: entitySwitchSequence,
						isComponentUnmounted
					}
				);
				return detailStopReason(id, switchToken, loadToken) ?? "superseded";
			}
			freeTrialEnded.value = freeTrialEndedResult;
			projectAccountReady.value = true;

			if (freeTrialEnded.value) {
				viewOnly.value = true;
				showProjectActionFAB.value = false;
			}

			// Project ownership is a stronger server-derived EDITOR grant than the account's ordinary
			// organization role. Keep the legacy role override for every non-owner access path.
			if (accessOrigin.value !== "owner" && isViewOnlyUser(me.value)) {
				viewOnly.value = true;
			}

			// LSP-17773: recipient onboarding — a shared editor landing on the project (e.g. a
			// newly-signed-up invitee redirected here) sees the "getting around a project" tour
			// once per user. Not shown to owners, org members, or view-only visitors. Runs AFTER
			// the free-trial-ended / view-only-role overrides above (Bugbot): isExternalEditor
			// requires !viewOnly, so evaluating earlier showed the EDITOR tour to a collaborator
			// about to be downgraded to read-only — and burned the once-per-user flag on it.
			maybeShowWelcomeVideo();
		}
		detailLoad.complete("project");
		return null;
	}

	// Project loading

	function applyVerdict(verdict: ProjectAccessVerdict | undefined, switchToken: number, id: string): void {
		if (entityId.value !== id || isStaleSwitch(switchToken)) return;
		projectVerdictEnabled.value = true;
		verdictUnavailable.value = verdictStatus.value === "unavailable";
		accessOrigin.value = verdict?.origin ?? null;
		projectAccessResolved.value = verdictStatus.value !== "loading";
		viewOnly.value = !projectAccountReady.value || projectVerdictGates(verdict, { isAuthenticated: isAuthenticated.value, viewOnlyRole: isViewOnlyUser(me.value), freeTrialEnded: freeTrialEnded.value, sharingId: sharingId.value }).viewOnly;
	}
	// One semantic outcome per page context, shared by initial load, Retry and background writers.
	// Cache metadata and decoded object identity never invalidate the user's view-only choice.
	let settledAccess: { key: string; promise: Promise<boolean> } | null = null;
	function projectAccessOutcomeKey(id: string): string {
		const verdict = projectVerdict.value;
		return JSON.stringify([authSession.organizationId, id,
			!isAuthenticated.value ? "signed-out" : verdict?.hasAccess === false ? "denied" : verdict?.hasAccess ? "granted" : "pending",
			verdict?.hasAccess, verdict?.origin, verdict?.accessType, verdict?.differentOrg,
			Boolean(sharingId.value || route.query?.shareId || props.shareId)]);
	}
	function settleProjectAccessOutcome(id: string, switchToken: number, ownDialog?: (dismiss: () => void) => void): Promise<boolean> {
		if (isStaleAction(id, switchToken)) {
			logInfo("EntityDetailPage: skipping stale access outcome", { id, switchToken });
			return Promise.resolve(false);
		}

		const verdict = projectVerdict.value;
		// Temporary permission acquisition is not a new access outcome. Keep the last denial
		// settlement across loading/unavailable windows until a real grant/denial arrives.
		if (isAuthenticated.value && !verdict) return Promise.resolve(true);
		const key = projectAccessOutcomeKey(id);
		if (settledAccess?.key === key) return settledAccess.promise;
		dismissAccessOutcome?.();
		dismissAccessOutcome = null;
		const requestSequence = verdictRequestSequence;
		const resetSequence = entityResetSequence;
		const isCurrent = () =>
			!isStaleAction(id, switchToken) &&
			requestSequence === verdictRequestSequence &&
			resetSequence === entityResetSequence &&
			key === projectAccessOutcomeKey(id);
		const promise = (async () => {
			if (projectVerdictEnabled.value) applyVerdict(verdict, switchToken, id);
			const shareLink = Boolean(sharingId.value || route.query?.shareId || props.shareId);
			if (!isAuthenticated.value && !shareLink) {
				logInfo("EntityDetailPage: signed-out access outcome", { id });
				router.push("/projects");
				return false;
			}
			if (isAuthenticated.value && verdict?.hasAccess === false && verdictStatus.value === "ready" && !shareLink) {
				logInfo("EntityDetailPage: confirmed denied access outcome", { id });
				const confirm = (instance?.proxy as any).$root.$confirm;
				const prompt = confirm.open(
					"Heads Up!",
					"The account associated with this email address does not have access to this project. Switch accounts to contribute to this project or continue as a viewer.",
					false,
					"Switch Accounts",
					"Continue as view only"
				);
				const dismiss = () => confirm.dismissIfCurrent(prompt);
				dismissAccessOutcome = dismiss;
				ownDialog?.(dismiss);
				let output: boolean;
				try {
					output = await prompt;
				} finally {
					if (dismissAccessOutcome === dismiss) dismissAccessOutcome = null;
				}
				if (!isCurrent()) {
					logInfo("EntityDetailPage: discarded stale access confirmation", { id, switchToken });
					return false;
				}
				if (output) await (instance?.proxy as any).$auth.doDefaultLogout();
			}
			if (!isCurrent()) {
				console.warn("EntityDetailPage: switch superseded across the logout await — not stamping project details", { id, switchToken });
				return false;
			}
			projectAccessResolved.value = verdictStatus.value !== "loading";
			return true;
		})();
		settledAccess = { key, promise };
		// A failed prompt has no user choice to retain. Promise identity here protects a
		// newer settlement from cleanup by an older failure; it does not identify verdicts.
		void promise.catch(() => {
			if (settledAccess?.promise === promise) settledAccess = null;
		});
		return promise;
	}
	// Timer/store refreshes can discover denial without a foreground load. The foreground owner
	// settles while loading; background verdict changes settle only when no foreground owner is active.
	watch(
		() => [projectVerdict.value, verdictStatus.value],
		async () => {
			if (entityType.value !== "project" || loading.value || !entity.value?.id || !contextAvailable() || accessRevoked.value) return;
			try {
				await settleProjectAccessOutcome(entityId.value, entitySwitchSequence);
			} catch (error) {
				logWarn("EntityDetailPage: access outcome failed", projectVerdictFailure(error));
			}
		},
		{ flush: "post" }
	);
	watch(() => [projectVerdict.value, verdictStore.access(entityId.value).verdictStatus, me.value, freeTrialEnded.value, projectAccountReady.value], () => {
		if (entityType.value === "project" && projectVerdictEnabled.value) applyVerdict(projectVerdict.value, entitySwitchSequence, entityId.value);
	}, { flush: "sync" });
	verdictStore.watchVisibleIds(() => entityType.value === "project" && isAuthenticated.value && projectVerdictEnabled.value && projectAccessResolved.value ? [entityId.value] : []);
	async function retryDetailLoad(): Promise<void> {
		if (authSession.sessionLost) return;
		await detailLoad.retry(entityType.value, {
			initialize: async (loadToken) => { await recoverDetailStage("project", entityId.value, entitySwitchSequence, loadToken); },
			projectVerdict: retryProjectVerdict,
			projectStage: async (loadToken, stage) => { await recoverDetailStage(stage, entityId.value, entitySwitchSequence, loadToken); }
		});
	}

	async function retryProjectVerdict(operationToken?: number): Promise<void> {
		if (contextReload) { await contextReload; return; }
		if (!contextAvailable() || isComponentUnmounted) {
			logInfo("EntityDetailPage: Retry deferred while context unavailable", { entityId: entityId.value });
			return;
		}
		const id = entityId.value;
		const switchToken = entitySwitchSequence;
		const requestSequence = verdictRequestSequence + 1;
		const loadToken = operationToken ?? detailLoad.begin();
		const isCurrent = () => detailLoad.owns(loadToken) && !isStaleAction(id, switchToken) && requestSequence === verdictRequestSequence;
		let stopReason: EntityDetailStopReason | null = "stage-failed:verdict";
		try {
			await handleProjectAccess(id, switchToken, false, loadToken);
			if (!isCurrent() || detailLoad.phase.value !== "loading") { stopReason = detailStopReason(id, switchToken, loadToken); return; }
			if (!(await settleProjectAccessOutcome(id, switchToken)) || !isCurrent()) { stopReason = detailStopReason(id, switchToken, loadToken) ?? "stage-failed:verdict"; return; }
			if (!hasAuthenticatedProjectAccess.value) {
				if (verdictStatus.value === "ready" && projectVerdict.value?.hasAccess === false) {
					if (!detailLoad.hasCompleted("project")) {
						stopReason = stageStop(await settleProjectDetails(id, switchToken, loadToken));
						if (stopReason) return;
					}
					stopReason = "access-denied";
				}
				return;
			}
			detailLoad.invalidate("timeline");
			await recoverDetailStage("project", id, switchToken, loadToken);
		} catch (error) {
			logWarn("EntityDetailPage: detail Retry failed", { errorClass: projectVerdictFailure(error).errorClass, entityId: id });
			if (isCurrent()) {
				// Access acquisition owns the verdict state. Later recovery failures retain
				// the fresh verdict and surface through project/timeline error handling.
				detailLoad.finish(loadToken, "error");
			}
		} finally {
			if (detailLoad.loading.value) detailLoad.settle(loadToken, stageStop(verifyDetailStage(stopReason === "access-denied" ? "project" : "timeline", id, switchToken, loadToken, stopReason)), "ready");
		}
	}

	async function recoverDetailStage(
		stage: EntityDetailRecoveryStage,
		id: string,
		switchToken: number,
		loadToken: number
	): Promise<DetailStageOutcome> {
		const reason = await recoverDetailStageWork(stage, id, switchToken, loadToken);
		return verifyDetailStage("timeline", id, switchToken, loadToken, reason);
	}
	async function recoverDetailStageWork(stage: EntityDetailRecoveryStage, id: string, switchToken: number, loadToken: number): Promise<EntityDetailStopReason | null> {
		const current = () => {
			const owned = detailLoad.owns(loadToken) && !isStaleAction(id, switchToken);
			if (!owned) logInfo("EntityDetailPage: discarded superseded content recovery", { entityId: id, switchToken, loadToken, stage });
			return owned;
		};
		// The failed stage alone is invalidated. All completed prerequisites retain their results.
		detailLoad.invalidate(stage);
		if (entity.value?.id !== id) {
			detailLoad.invalidate("project");
			detailLoad.invalidate("timeline");
		}
		let stopReason: EntityDetailStopReason | null = null;
		try {
			stopReason = stageStop(await ensureDetailReadiness(id, switchToken, loadToken));
			if (stopReason || !current()) return stopReason ?? detailStopReason(id, switchToken, loadToken);
			if (entityType.value === "project" && !detailLoad.hasCompleted("project") && (!projectVerdict.value || verdictUnavailable.value)) {
				await handleProjectAccess(id, switchToken, true);
				if (!current() || !(await settleProjectAccessOutcome(id, switchToken))) { stopReason = "stage-failed:verdict"; return stopReason; }
			}
			stage = "project";
			if (!detailLoad.hasCompleted(stage)) {
				if (entityType.value === "project") {
					stopReason = stageStop(await recoverProjectStage(id, switchToken, loadToken));
				} else { stopReason = stageStop(await loadEntity(switchToken, loadToken)); }
				stopReason = stageStop(verifyDetailStage("project", id, switchToken, loadToken, stopReason));
				if (stopReason || !current()) return stopReason ?? detailStopReason(id, switchToken, loadToken);
			}
			stage = "timeline";
			if (!detailLoad.hasCompleted(stage)) {
				stopReason = stageStop(await recoverTimelineStage(id, switchToken, loadToken));
				if (stopReason || !current()) return stopReason ?? detailStopReason(id, switchToken, loadToken);
			}

			if (current()) {
				stopReason = stageStop(verifyDetailStage("timeline", id, switchToken, loadToken, verdictUnavailable.value ? "stage-failed:verdict" : null));
			}
		} catch (error) {
			if (!current()) return detailStopReason(id, switchToken, loadToken);
			if (isMissingSession(error)) { markSessionLost(); stopReason = "auth-lost"; return stopReason; }
			logWarn("EntityDetailPage: detail Retry failed", { ...projectVerdictFailure(error), entityId: id, stage });
			stopReason = `stage-failed:${stage}`;
		} finally {
			const outcome = verifyDetailStage("timeline", id, switchToken, loadToken, stopReason);
			detailLoad.settle(loadToken, stageStop(outcome), isAuthenticated.value ? "ready" : "signed-out");
		}
		return stopReason;
	}
	async function recoverTimelineStage(id: string, switchToken: number, loadToken: number): Promise<DetailStageOutcome> {
		return runDetailStage("timeline", id, switchToken, loadToken, async () => {
			if (!(await loadTimeline(false, { detailLoadToken: loadToken }))) return "stage-failed:timeline";
			return null;
		});
	}

	async function recoverProjectStage(id: string, switchToken: number, loadToken: number): Promise<DetailStageOutcome> {
		return runDetailStage("project", id, switchToken, loadToken, async () => {
			const reason = await loadUserAndProjectWork(id, switchToken, { detailLoadToken: loadToken, throwOnError: true });
			if (reason) return reason;
			return stageStop(await settleProjectDetails(id, switchToken, loadToken));
		});
	}

	async function handleProjectAccess(id: string, switchToken: number = entitySwitchSequence, reuseCache = false, loadToken?: number) {
		if (authSession.sessionLost) return;
		if (isAnonymousShareVisitor()) {
			projectVerdictEnabled.value = false;
			projectAccessResolved.value = true;
			viewOnly.value = true;
			return;
		}
		forbiddenFlow.cancel();
		const requestSequence = ++verdictRequestSequence;
		let requestedVerdict = false;
		let readinessStage: EntityDetailRecoveryStage = "account";
		const retainsKnownAccess = entity.value?.id === id && projectAccessResolved.value && !!projectVerdict.value;
		if (!retainsKnownAccess) {
			// A different/unhydrated project has no established permission decision to retain.
			if (!reuseCache && entity.value?.id !== id) verdictStore.invalidate([id]);
			viewOnly.value = true;
		}
		// LSP-15373 / Cursor Bugbot: clear matched shares on every entry so a failed getMe/getProjectVerdict
		// for a new project can't leave the previous project's share rows in the gate — which could otherwise
		// grant access (incl. editor) on the wrong project. The success paths below repopulate it.
		projectVerdictEnabled.value = true;
		verdictUnavailable.value = false;
		applyVerdict(verdictStore.access(id).verdict, switchToken, id);
		// LSP-17772: clear the resolved origin too so a prior project's origin can't leak into the
		// external-editor mirrors before load() re-resolves it against this project's org id.
		accessOrigin.value = null;
		projectAccessResolved.value = false;
		try {
			// break-it round 6: me.value is a SHARED ref, also read/written by initializeComponent for the
			// CURRENT entity. Assigning it unconditionally let a stale switch's getMe() response (resumed
			// after a newer switch already established its own user data) overwrite that newer state, even
			// before this function's OWN later ownership checks ran. Capture the result, recheck, then assign.
			const meResult = (reuseCache || loadToken !== undefined) && detailLoad.hasCompleted("account") ? me.value : await api.getMe();
			if (entityId.value !== id || isStaleSwitch(switchToken) || requestSequence !== verdictRequestSequence) {
				console.warn("EntityDetailPage.handleProjectAccess: discarding superseded getMe response", {
					id,
					switchToken,
					currentSwitchToken: entitySwitchSequence,
					currentEntityId: entityId.value,
					isComponentUnmounted
				});
				return;
			}
			// This request owns fresh account/subscription data even when mount setup failed.
			// Settle its trial gate before granting writes; failure uses the existing Retry banner.
			readinessStage = "trial";
			const trialEnded = (reuseCache || loadToken !== undefined) && detailLoad.hasCompleted("trial") ? freeTrialEnded.value : await hasFreeTrialEnded(meResult?.organization, meResult?.organizationSubscription);
			if (entityId.value !== id || isStaleSwitch(switchToken) || requestSequence !== verdictRequestSequence) {
				logWarn("EntityDetailPage.handleProjectAccess: discarding superseded account readiness", { id, switchToken, requestSequence });
				return;
			}
			me.value = meResult;
			detailLoad.complete("account");
			freeTrialEnded.value = trialEnded;
			detailLoad.complete("trial");
			projectAccountReady.value = true;

			readinessStage = "auth";
			const authenticated = (reuseCache || loadToken !== undefined) && detailLoad.hasCompleted("auth") ? isAuthenticated.value : await (instance?.proxy as any).$auth.isAuthenticated();
			// break-it round 6: recheck again after this second await, before either branch below writes
			// projectVerdict/viewOnly/isAuthenticated unconditionally — the unauthenticated branch in
			// particular had NO ownership check at all before this fix, so a switch stalled on
			// isAuthenticated() could clear a newer, already-established project's shares on resume.
			if (entityId.value !== id || isStaleSwitch(switchToken) || requestSequence !== verdictRequestSequence) {
				console.warn("EntityDetailPage.handleProjectAccess: discarding superseded isAuthenticated response", {
					id,
					switchToken,
					currentSwitchToken: entitySwitchSequence,
					currentEntityId: entityId.value,
					isComponentUnmounted
				});
				return;
			}

			detailLoad.complete("auth");
			if (authenticated && me.value?.user) {
				establishedSession.value = true;
				isAuthenticated.value = true;
				const requestOrganizationId = authSession.organizationId;
				requestedVerdict = true;
				if (!reuseCache && entity.value?.id !== id) verdictStore.invalidate([id]);
				if (!reuseCache && entity.value?.id === id) await verdictStore.refresh([id], true);
				else if (!reuseCache || !verdictStore.access(id).verdict || !verdictStore.access(id).transientFailure) await verdictStore.prefetch(id);
				if (requestOrganizationId !== authSession.organizationId) return;

				// LSP-15373 / LSP-17483: if the route entity changed while this request was in flight
				// (in-place navigation A→B), drop this stale response — applying it would repopulate the
				// access gate with the previous project's share rows. Checked via entityId AND switchToken
				// (not isStaleAction's live route.params.id comparison, which is unnecessary here — this
				// function is only ever entered from loadEntity synchronously after entityId/route are
				// already in sync — and would misfire in a direct unit-test call that sets entityId without
				// a real route transition) so a switch back to the SAME id (still a different switch) is
				// still caught.
				if (entityId.value !== id || isStaleSwitch(switchToken) || requestSequence !== verdictRequestSequence) {
					console.warn("EntityDetailPage.handleProjectAccess: discarding superseded getProjectVerdict response", {
						id,
						switchToken,
						currentSwitchToken: entitySwitchSequence,
						currentEntityId: entityId.value,
						isComponentUnmounted
					});
					return;
				}

				applyVerdict(projectVerdict.value, switchToken, id);
			} else if (!authenticated) {
				// Only a session that existed can be lost; a never-signed-in visitor stays signed out.
				if (hadEstablishedSession()) markSessionLost();
				verdictStore.invalidate([id]);
				projectVerdictEnabled.value = false;
				verdictUnavailable.value = false;
				viewOnly.value = true;
			} else throw new Error("Account user unavailable");
		} catch (err) {
			// LSP-17483: a superseded switch's project-access request can also REJECT after a newer switch
			// has taken over. Without this check, the abandoned switch's own catch here would still flip
			// viewOnly and show a "couldn't find project" dialog/redirect for the project the user actually
			// asked for — an error-path variant of the same clobber bug this ticket fixes on the success
			// path. Same entityId+switchToken check as the success path above (see its comment for why the
			// live route.params.id comparison is intentionally not used here).
			if (entityId.value !== id || isStaleSwitch(switchToken) || requestSequence !== verdictRequestSequence) {
				console.warn("EntityDetailPage.handleProjectAccess: a superseded request rejected — suppressing its error handling", {
					id,
					switchToken,
					currentSwitchToken: entitySwitchSequence,
					currentEntityId: entityId.value,
					isComponentUnmounted,
					...projectVerdictFailure(err)
				});
				return;
			}

			const missingSession = isMissingSession(err);
			if (!missingSession && isNeverSignedInFailure(err)) {
				promptNeverSignedIn(id, switchToken, err);
				startingLoading.value = false;
				projectVerdictEnabled.value = false;
				verdictUnavailable.value = false;
				viewOnly.value = true;
				if (loadToken !== undefined) detailLoad.finish(loadToken, "signed-out");
				return;
			}
			if (missingSession) markSessionLost();
			if (!requestedVerdict && loadToken !== undefined) {
				detailLoad.invalidate(readinessStage);
				detailLoad.finish(loadToken, readinessStage === "account" ? "account-error" : readinessStage === "trial" ? "trial-error" : "auth-error");
			}
			// A transient verdict failure keeps the last server verdict; only terminal outcomes delete it.
			const retainedVerdict = requestedVerdict && !missingSession && isTransientVerdictFailure(err) ? verdictStore.access(id).verdict : undefined;
			if (retainedVerdict) {
				applyVerdict(retainedVerdict, switchToken, id);
			} else if (requestedVerdict || missingSession || !retainsKnownAccess) {
				// An unknown authoritative permission verdict closes writes; account outages retain it.
				verdictStore.invalidate([id]);
				projectVerdictEnabled.value = false;
				viewOnly.value = true;
			}
			// A signed-out share visitor legitimately has no authenticated verdict. Every other
			// access-loading failure needs visible recovery, including a session lost while mounted.
			const enteredViaShareLink = Boolean(route.query?.shareId || props.shareId);
			verdictUnavailable.value = !retainedVerdict && (requestedVerdict || loadToken === undefined) && !(missingSession && enteredViaShareLink);
			if (verdictUnavailable.value) logWarn("projectVerdicts: verdict unavailable", { count: 1, ...projectVerdictFailure(err) });

		} finally {
			// Every current request settles, including signed-out/error paths. Superseded work
			// must not settle a newer request's flag; an absent verdict keeps its gates closed.
			if (entityId.value === id && !isStaleSwitch(switchToken) && requestSequence === verdictRequestSequence) {
				projectAccessResolved.value = true;
			}
		}
	}

	let backgroundRefreshToken: number | null = null;
	let backgroundRefreshesInFlight = 0;
	/**
	 * A refresh of an already-ready page (socket reconnect, manual refresh) must not claim the page
	 * spinner: comments and every `!loading` surface would vanish while content is on screen.
	 * Overlapping refreshes share one background token, as they shared the foreground one before.
	 */
	function acquireRefreshToken(deferredToForeground: boolean, previousPhase: EntityDetailLoadPhase): { loadToken: number; backgroundOnly: boolean } {
		if (deferredToForeground) return { loadToken: detailLoad.currentToken(), backgroundOnly: false };
		const shared = backgroundRefreshToken !== null && detailLoad.owns(backgroundRefreshToken);
		if (!shared && previousPhase !== "ready") return { loadToken: detailLoad.begin(), backgroundOnly: false };
		if (!shared) backgroundRefreshToken = detailLoad.beginBackground();
		backgroundRefreshesInFlight++;
		return { loadToken: backgroundRefreshToken!, backgroundOnly: true };
	}
	function releaseRefreshToken(backgroundOnly: boolean): void {
		if (backgroundOnly && --backgroundRefreshesInFlight === 0) backgroundRefreshToken = null;
	}
	async function loadUserAndProject(...args: Parameters<typeof loadUserAndProjectWork>): Promise<DetailStageOutcome> {
		const [id, switchToken = entitySwitchSequence, options] = args;
		const stopped = detailStopReason(id, switchToken);
		if (stopped) return verifyDetailStage("project", id, switchToken, undefined, stopped);
		const deferredToForeground = detailLoad.loading.value;
		const previousPhase = detailLoad.phase.value;
		const { loadToken, backgroundOnly } = acquireRefreshToken(deferredToForeground, previousPhase);
		if (!deferredToForeground) detailLoad.invalidate("project");
		let outcome: DetailStageOutcome;
		try {
			outcome = await runDetailStage("project", id, switchToken, loadToken, async () => {
				const reason = await loadUserAndProjectWork(id, switchToken, { ...options, detailLoadToken: loadToken, throwOnError: true });
				if (reason) return reason;
				return stageStop(await settleProjectDetails(id, switchToken, loadToken));
			});
		} finally {
			releaseRefreshToken(backgroundOnly);
		}
		settleBackgroundStage(loadToken, outcome, deferredToForeground, previousPhase, backgroundOnly);
		return outcome;
	}
	async function loadUserAndProjectWork(
		id: string,
		switchToken: number = entitySwitchSequence,
		options?: { skipNavigationOnError?: boolean; auxiliaryRefreshToken?: number; auxiliaryEntityWriteToken?: number; detailLoadToken?: number; throwOnError?: boolean }
	): Promise<EntityDetailStopReason | null> {
		startingLoading.value = false;

		// break-it round 3: switchToken/entityId alone cannot order two loadUserAndProject calls that are
		// NOT a route switch at all — e.g. two overlapping socket-reconnect refreshes share the exact same
		// switchToken/entityId snapshot, since entitySwitchSequence never advances between them.
		//
		// break-it round 4: that check must NEVER gate the switch-driven call (the one loadEntity makes for
		// an actual route switch) — an unrelated auxiliary refresh (socket reconnect) finishing after the
		// switch's own call started would otherwise wrongly invalidate the switch's own successful response,
		// because projectLoadSequence is a single shared counter. So this check is opt-in: only a caller that
		// passes its own auxiliaryRefreshToken (onSocketConnect) is ordered against OTHER auxiliary refreshes
		// via projectLoadSequence; the switch-driven call (no token passed) is never subject to it — it is
		// already correctly protected by switchToken/isStaleAction instead.
		const auxiliaryRefreshToken = options?.auxiliaryRefreshToken;
		const isStaleProjectLoad = (): boolean =>
			(auxiliaryRefreshToken !== undefined && auxiliaryRefreshToken !== projectLoadSequence) ||
			(options?.detailLoadToken !== undefined && !detailLoad.owns(options.detailLoadToken));

		/**
		 * ls-heroku-ci review: `isStaleProjectLoad() || applyEntityForSwitch(...)` short-circuits, so the
		 * stale-load discard returned SILENTLY while every sibling gate in this diff logged — the exact
		 * shape CLAUDE.md's "Diagnostic logging when fixing silent failures" rule exists to stop, on a
		 * gate whose whole job is discarding responses. Same check, but it names the site that discarded.
		 */
		const discardStaleProjectLoad = (label: string): boolean => {
			if (!isStaleProjectLoad()) return false;
			console.warn("EntityDetailPage.loadUserAndProject: discarding a superseded auxiliary refresh response", {
				id,
				label,
				auxiliaryRefreshToken,
				currentAuxiliaryToken: projectLoadSequence,
				switchToken,
				currentSwitchToken: entitySwitchSequence
			});
			return true;
		};

		// Review round 2 (cursor, second pass): the entity WRITE is ordered separately from the seeding
		// above, against every auxiliary refresh of either kind — see projectEntityWriteSequence. Losing
		// that race is not fatal: the winner wrote a fresher entity for the same project, so this call
		// skips its own write and seeds from the fresher entity.value instead of abandoning the seeding
		// (which is how the dropped-reconciliation bug happened in the first place). Switch ownership is
		// still re-checked, because a lost SWITCH means the seeding would target the wrong entity.
		const auxiliaryEntityWriteToken = options?.auxiliaryEntityWriteToken;
		// Snapshotted BEFORE isSupersededEntityWrite is defined so it can never be read in TDZ — the arrow
		// below closes over it and is only invoked after an await, but relying on that ordering is a trap
		// for the next edit.
		const orderedWriteCountAtStart = orderedEntityWriteCount;

		// ls-heroku-ci (HIGH): the switch-driven call (no auxiliaryEntityWriteToken) used to skip entity-write
		// ordering ENTIRELY, so a switch load that started before an auxiliary refresh's write could resolve
		// after it and overwrite fresher data for the same entity — applyEntityForSwitch only compares
		// entityId/switchToken/route, none of which look stale in that case.
		//
		// It is gated on ordered WRITES, not on refresh STARTS, and that distinction is what keeps break-it
		// round 4's exemption intact: round 4 removed a check that let a merely-STARTED auxiliary refresh
		// invalidate the switch's own successful response (see the "does not let an auxiliary socket refresh
		// invalidate an actual route switch's own project load" test, which still passes). A write that
		// landed is different — orderedEntityWriteCount only counts writes that already passed
		// applyEntityForSwitch, so every counted write is for THIS entity and switch, and is therefore
		// genuinely fresher than a response that was fetched earlier.
		const isSupersededEntityWrite = (): boolean =>
			auxiliaryEntityWriteToken !== undefined
				? auxiliaryEntityWriteToken !== projectEntityWriteSequence
				: orderedEntityWriteCount > orderedWriteCountAtStart;
		/** "applied" — wrote it | "superseded" — a fresher write won, keep going | "abandon" — ownership lost. */
		const applyAuxiliaryEntityWrite = (project: unknown, label: string): "applied" | "superseded" | "abandon" => {
			if (isSupersededEntityWrite()) {
				console.warn("EntityDetailPage.loadUserAndProject: a fresher auxiliary refresh already wrote this entity", {
					id,
					label,
					auxiliaryEntityWriteToken,
					currentEntityWriteToken: projectEntityWriteSequence
				});
				return isStaleAction(id, switchToken) ? "abandon" : "superseded";
			}
			if (!applyEntityForSwitch(id, switchToken, project, label)) return "abandon";
			// Only a token-claiming refresh's write is orderable; the switch-driven call's is not (see
			// orderedEntityWriteCount).
			if (auxiliaryEntityWriteToken !== undefined) orderedEntityWriteCount++;
			return "applied";
		};

		// cursor review: the entity-write token is claimed when a refresh STARTS, not when it resolves, so
		// the loser of that race can resolve BEFORE the winner has written anything. Seeding from
		// entity.value then reads the PRE-refresh snapshot rather than the fresher response this call is
		// holding — and refreshProject (the usual winner) never seeds at all, so conversations created
		// during an outage stayed invisible until a reload: the very bug the counter split was meant to fix,
		// just moved one step. Seed from THIS call's own response instead, which is correct whether it won
		// the write or not. Falls back to entity.value on paths that fetch nothing (the not-found dialog).
		let fetchedProject: Project | undefined;
		/**
		 * Seed from whichever source is genuinely fresher, which depends on an ordering neither of the two
		 * previous attempts distinguished:
		 *  - No write landed since this call started -> entity.value is still the PRE-refresh snapshot and
		 *    this call's own response is the freshest thing available (cursor round 3: the loser resolving
		 *    before the winner wrote, which left an outage's conversations invisible).
		 *  - A write DID land -> only the latest-started refresh is permitted to write, so entity.value is
		 *    at least as fresh as this call's response; seeding from our own older snapshot would leave
		 *    projectUser/sharingId/conversations inconsistent with the entity (cursor round 4).
		 * Our own write also bumps the count, in which case entity.value IS our response — same result.
		 */
		const seedSource = (): Project =>
			(orderedEntityWriteCount > orderedWriteCountAtStart ? entity?.value : (fetchedProject ?? entity?.value)) as Project;

		try {
			if (isAuthenticated.value) {
				try {
					const project = await service.value?.getEntity(id);
					if (discardStaleProjectLoad(`getEntity(${id})`)) return "superseded";
					if (!project?.id) return "entity-missing";
					if (applyAuxiliaryEntityWrite(project, `loadUserAndProject getEntity(${id})`) === "abandon") {
						return "superseded";
					}
					fetchedProject = project as Project | undefined;
					projectUser.value = seedSource().User;
					sharingId.value = seedSource().shareId;
					// Specify the organizationId for the search request if the project belongs to a different organization
					if (me.value?.organization?.id !== seedSource().organizationId) {
						searchOrganizationId.value = seedSource().organizationId;
					}
				} catch (err) {
					if (sharingId.value) {
						const projectInsecure = await (service.value as ProjectService)?.getProjectInsecure(id, sharingId.value);
						if (discardStaleProjectLoad(`getProjectInsecure(${id}) fallback`)) return "superseded";
						if (!projectInsecure?.id) return "entity-missing";
						if (
							applyAuxiliaryEntityWrite(projectInsecure, `loadUserAndProject getProjectInsecure(${id}) fallback`) ===
								"abandon"
						) {
							return "superseded";
						}
						fetchedProject = projectInsecure as Project | undefined;
					} else {
						throw err;
					}
				}
			} else if (sharingId.value) {
				const projectInsecure = await (service.value as ProjectService)?.getProjectInsecure(id, sharingId.value);
				if (discardStaleProjectLoad(`getProjectInsecure(${id}) unauthenticated`)) return "superseded";
				if (!projectInsecure?.id) return "entity-missing";
				if (
					applyAuxiliaryEntityWrite(projectInsecure, `loadUserAndProject getProjectInsecure(${id}) unauthenticated`) === "abandon"
				) {
					return "superseded";
				}
				fetchedProject = projectInsecure as Project | undefined;
			} else if (notFoundPromptKey !== `${id}:${switchToken}`) {
				notFoundPromptKey = `${id}:${switchToken}`;
				(instance?.proxy as any).$root.$confirm
					.open("Error", SHARED_PROJECT_NOT_FOUND_MESSAGE, false, "OK", null, true, true)
					.then(() => {
						// break-it round 3: recheck ownership on dismissal — a detached dialog can outlast a
						// switch to a different (working) project, and must not redirect it away.
						if (discardStaleProjectLoad("not-found dialog dismissal")) return;
						// ls-heroku-ci: logged separately because `||` short-circuits past
						// discardStaleProjectLoad's warn when isStaleAction is the true operand — this
						// discard used to be completely silent.
						if (isStaleAction(id, switchToken)) {
							console.warn(
								"EntityDetailPage.loadUserAndProject: switch superseded before the not-found dialog dismissal — discarding",
								{
									id,
									switchToken,
									currentSwitchToken: entitySwitchSequence,
									currentRouteParamId: route.params.id,
									isComponentUnmounted
								}
							);
							return;
						}
						router.push("/projects");
					});
			}

			// LSP-17483: a newer switch (or unmount) may have taken ownership while the branches above were
			// resolving, even on a path that never calls applyEntityForSwitch (e.g. the "not found" confirm
			// dialog above) — stop before seeding project-derived state for a switch that's no longer current.
			if (isStaleProjectLoad() || isStaleAction(id, switchToken)) {
				// ls-heroku-ci: this gate fires for EITHER reason, so the payload has to carry both sets of
				// fields — it previously logged only the switch ones and therefore misnamed the cause whenever
				// isStaleProjectLoad() was the actual trigger.
				console.warn("EntityDetailPage.loadUserAndProject: switch superseded before seeding project state", {
					id,
					supersededBy: isStaleProjectLoad() ? "auxiliary-refresh-token" : "switch-ownership",
					auxiliaryRefreshToken,
					currentAuxiliaryToken: projectLoadSequence,
					switchToken,
					currentSwitchToken: entitySwitchSequence,
					currentEntityId: entityId.value,
					currentRouteParamId: route.params.id,
					isComponentUnmounted
				});
				return "superseded";
			}

			// The explicit seed below covers this assignment; flag the watch to skip the duplicate it would fire.
			skipNextProjectConversationIdsWatchSeed = entityType.value === "project";
			// A conversation created seconds ago is very likely NOT in the server's project.Conversations
			// yet, and these seeds would otherwise drop it. Both go through one path so the handoff rule
			// cannot be applied to the ids and forgotten on the reports. LSP-15388
			seedConversationsFromServer(seedSource()?.Conversations);

			// LSP-15648: seed composite project+children Scribe monitoring at the page level so a running
			// child report is reflected regardless of which (lazy) tab is active on (re-)navigation.
			projectScribeMonitoringReady.value = true;
			seedProjectScribeMonitoring();
			// Bounded warm-up retries so a cold-load refresh (scribe token not ready) heals within seconds.
			startScribeWarmup();
		} catch (err) {
			// LSP-17483: a superseded switch's project request can also REJECT after a newer switch has
			// taken over. Without this check, the abandoned switch's own catch here would still show a
			// "couldn't find project" dialog and redirect for the project the user actually asked for — an
			// error-path variant of the same clobber bug this ticket fixes on the success path.
			if (isStaleProjectLoad() || isStaleAction(id, switchToken)) {
				// ls-heroku-ci: this gate fires for EITHER reason, so the payload has to carry both sets of
				// fields — it previously logged only the switch ones and therefore misnamed the cause whenever
				// isStaleProjectLoad() was the actual trigger.
				console.warn("EntityDetailPage.loadUserAndProject: a superseded request rejected — suppressing its error handling", {
					id,
					supersededBy: isStaleProjectLoad() ? "auxiliary-refresh-token" : "switch-ownership",
					auxiliaryRefreshToken,
					currentAuxiliaryToken: projectLoadSequence,
					switchToken,
					currentSwitchToken: entitySwitchSequence,
					currentEntityId: entityId.value,
					currentRouteParamId: route.params.id,
					isComponentUnmounted,
					error: (err as Error)?.message ?? String(err)
				});
				return "superseded";
			}

			if (accessRevoked.value) return "superseded";
			// A Retry refresh failure belongs to its loading stage when this project is already visible.
			// Throw before opening the shared navigation prompt so the acquired verdict and data stay intact.
			if (options?.throwOnError) throw err;
			if (options?.skipNavigationOnError) {
				// LSP-19423: a background refresh that 404s means the project was deleted, not that the
				// refresh mechanism failed. It was reported as an error either way — 114 in eight days of
				// staging, every sampled one an ordinary 404 — which buries the genuine refresh failures
				// this log exists to catch. Navigation stays suppressed in both cases: not yanking the
				// user off the page they are on is what `skipNavigationOnError` is for.
				const status = getApiErrorStatus(err);
				if (isEntityGoneError(err)) {
					logInfo("Project no longer exists on socket-reconnect refresh — leaving the current view in place", {
						entityId: id,
						httpStatus: status
					});
					return "superseded";
				}
				logError("Failed to load project after socket reconnect", err, { entityId: id, httpStatus: status });
				return "superseded";
			}
			const message = getEntityLoadErrorMessage(err);
			// One prompt per load: the access stage may already have asked about the same failure.
			if (notFoundPromptKey === `${id}:${switchToken}`) return "superseded";
			notFoundPromptKey = `${id}:${switchToken}`;
			(instance?.proxy as any).$root.$confirm.open("Error", message, false, "OK", null, true, true).then(() => {
				// break-it round 3: recheck ownership on dismissal — a detached dialog can outlast a switch
				// to a different (working) project, and must not redirect it away.
				if (discardStaleProjectLoad("error-path dialog dismissal")) return;
				// ls-heroku-ci: logged separately because `||` short-circuits past
				// discardStaleProjectLoad's warn when isStaleAction is the true operand — this
				// discard used to be completely silent.
				if (isStaleAction(id, switchToken)) {
					console.warn(
						"EntityDetailPage.loadUserAndProject: switch superseded before the error-path dialog dismissal — discarding",
						{
							id,
							switchToken,
							currentSwitchToken: entitySwitchSequence,
							currentRouteParamId: route.params.id,
							isComponentUnmounted
						}
					);
					return;
				}
				router.push("/projects");
			});
			console.error(err, {
				tags: {
					entityType: entityType.value,
					entityId: entityId.value,
					sharingId: sharingId.value
				},
				extra: {
					method: "loadUserAndProject",
					file: "EntityDetailPage"
				}
			});
			if (options?.throwOnError) throw err;
		}
		return null;
	}
	async function refreshProject(): Promise<DetailStageOutcome> {
		const id = entityId.value;
		const switchToken = entitySwitchSequence;
		const stopped = detailStopReason(id, switchToken);
		if (stopped) return verifyDetailStage("project", id, switchToken, undefined, stopped);
		const deferredToForeground = detailLoad.loading.value;
		const previousPhase = detailLoad.phase.value;
		const { loadToken, backgroundOnly } = acquireRefreshToken(deferredToForeground, previousPhase);
		if (!deferredToForeground) detailLoad.invalidate("project");
		let outcome: DetailStageOutcome;
		try {
			outcome = await runDetailStage("project", id, switchToken, loadToken, async () => {
				const reason = await refreshProjectWork(loadToken);
				return reason ?? stageStop(await settleProjectDetails(id, switchToken, loadToken));
			});
		} finally {
			releaseRefreshToken(backgroundOnly);
		}
		settleBackgroundStage(loadToken, outcome, deferredToForeground, previousPhase, backgroundOnly);
		return outcome;
	}
	async function refreshProjectWork(loadToken?: number): Promise<EntityDetailStopReason | null> {
		if (authSession.sessionLost) return "auth-lost";
		// LSP-17483: a manual refresh (e.g. after sharing) isn't switch-driven, but it can still race an
		// in-place switch that starts while its getEntity is in flight — snapshot ownership the same way
		// the socket-reconnect refresh does, so a switch away during this refresh doesn't get overwritten.
		//
		// break-it round 2: snapshotting ONLY entitySwitchSequence is not enough. The watcher increments the
		// sequence the instant it decides to handle a new switch, before that switch's own flush/load has
		// actually replaced entity/entityId — so if a switch to a DIFFERENT project is mid-flush right now,
		// entitySwitchSequence already describes that switch even though entityId.value still names this
		// (outgoing) project. A refresh snapshotting that already-advanced token would then be wrongly
		// treated as "current" once the new switch finishes, letting a stale response for the OLD project
		// overwrite the NEW one. Capturing entityId too, and checking both via isStaleAction, closes this.
		//
		// break-it round 3: also claims its own order token so two overlapping refreshes of the SAME
		// project (e.g. a rapid double-click on "share") are ordered against each other —
		// entitySwitchSequence alone can't distinguish two calls that share an identical snapshot because
		// no route switch happened between them.
		//
		// Review round 2 (cursor): that token comes from projectEntityWriteSequence, NOT the
		// projectLoadSequence it used to use. This function ONLY writes the entity, so ordering it on the
		// entity-write counter is exactly right: it still loses to (and beats) any newer auxiliary
		// refresh's write of the same project, including another refreshProject, while no longer
		// cancelling the socket-reconnect refresh's seeding of projectConversationIds /
		// reportConversations / projectUser / Scribe monitoring — work this function never does, whose
		// loss made conversations created during an outage invisible until a reload. See
		// projectEntityWriteSequence's declaration.
		const refreshEntityId = entityId.value;
		const refreshSwitchToken = entitySwitchSequence;
		const thisProjectRefresh = ++projectEntityWriteSequence;
		const refreshedProject = await service.value?.getEntity(refreshEntityId);
		const acquisitionStop = detailStopReason(refreshEntityId, refreshSwitchToken, loadToken);
		if (acquisitionStop) return acquisitionStop;
		if (!refreshedProject?.id) return "entity-missing";
		// ls-heroku-ci review: this `||` short-circuits before applyEntityForSwitch — the only warn in the
		// expression — so the "a newer auxiliary refresh won" discard used to return SILENTLY while every
		// sibling gate in this diff logged. CLAUDE.md's silent-failure diagnostic rule applies squarely to a
		// gate whose entire job is discarding a response, so it now names its own condition. (The
		// EntityDetailPage.refreshProject allowlist entry in tests/setup/env.ts described this message
		// before it existed — it was dead precisely because this branch never logged.)
		if (thisProjectRefresh !== projectEntityWriteSequence) {
			console.warn("EntityDetailPage.refreshProject: discarding a response for an entity that is no longer current", {
				refreshEntityId,
				refreshSwitchToken,
				thisProjectRefresh,
				currentEntityWriteToken: projectEntityWriteSequence,
				currentSwitchToken: entitySwitchSequence
			});
			return "superseded";
		}
		if (!applyEntityForSwitch(refreshEntityId, refreshSwitchToken, refreshedProject, `refreshProject getEntity(${refreshEntityId})`)) {
			return "superseded";
		}
		// This write IS orderable (it claimed a projectEntityWriteSequence token above), so a superseded
		// auxiliary refresh resolving later may safely seed from it — see orderedEntityWriteCount.
		orderedEntityWriteCount++;
		if (!entity.value?.id) return "entity-missing";
		sourceEntity.value = JSON.parse(JSON.stringify(entity.value));
		return null;
	}
	async function refreshProjectPermissions(item: any, accessType: any) {
		if (authSession.sessionLost) return;
		// Self-change detection via shareRowMatchesIdentity (helpers/projectAccess): both sides
		// must be present — resolved roster rows can carry no userIdentifier and `me` can be
		// momentarily unloaded, and `undefined === undefined` made removing ANYONE read as
		// self-removal, closing the whole project page (LSP-17767 testing-env report).
		console.debug("EntityDetailPage.refreshProjectPermissions: received permission change", {
			shareId: item?.id,
			accessType
		});
		if (!item?.userIdentifier) {
			console.warn("EntityDetailPage.refreshProjectPermissions: share row has no userIdentifier — ignoring permission change", {
				shareId: item?.id,
				accessType
			});
			return;
		}
		const myEmail = me.value?.user?.email;
		const myAccountId = me.value?.account?.id;
		if (!myEmail && !myAccountId) {
			console.warn(
				"EntityDetailPage.refreshProjectPermissions: current user identities are unavailable — ignoring permission change",
				{
					shareId: item.id,
					accessType
				}
			);
			return;
		}
		if (shareRowMatchesIdentity(item.userIdentifier, [myEmail, myAccountId])) {
			if (accessType === constants.REMOVE_ACCESS.value) {
				// redirect to projects page
				router.push("/projects");
			} else {
				const id = entityId.value;
				const switchToken = entitySwitchSequence;
				forbiddenFlow.cancel();
				const requestSequence = ++verdictRequestSequence;
				viewOnly.value = true;
				await verdictStore.refetch([id]);
				if (requestSequence !== verdictRequestSequence) return;
				applyVerdict(verdictStore.access(id).verdict, switchToken, id);
			}
		}
	}

	async function handleAccessRevokedError(isCurrent: () => boolean, ownDialog?: (dismiss: () => void) => void) {
		if (projectVerdict.value?.hasAccess === false) {
			await settleProjectAccessOutcome(entityId.value, entitySwitchSequence, ownDialog);
			return;
		}
		if (!entity.value?.id) return;
		if (entityType.value !== "project") return;

		// break-it round 4: capture ownership at entry — this confirm dialog is a detached continuation
		// that can outlast a switch to a different, working project (whose flow owns its own dialog).
		// Without rechecking operation ownership as well as the project, confirming
		// the STALE dialog would still log out of the session that now legitimately belongs to the newer
		// project.
		const accessRevokedEntityId = entityId.value;
		const accessRevokedSwitchToken = entitySwitchSequence;

		// The caller has applied the fresh verdict. Dialog outcomes must not change permissions.
		const confirm = (instance?.proxy as any).$root.$confirm;
		const prompt = confirm.open(
			"Heads Up!",
			"It looks like your access to this project has changed. Switch accounts to contribute to this project or continue as a viewer.",
			false,
			"Switch Accounts",
			"Continue as view only"
		);
		ownDialog?.(() => confirm.dismissIfCurrent(prompt));
		const output = await prompt;
		if (!isCurrent()) return;
		if (output) {
			if (isStaleAction(accessRevokedEntityId, accessRevokedSwitchToken)) {
				console.warn(
					"EntityDetailPage.handleAccessRevokedError: switch superseded before the Heads Up confirmation resolved — not logging out",
					{
						accessRevokedEntityId,
						accessRevokedSwitchToken,
						currentSwitchToken: entitySwitchSequence,
						currentEntityId: entityId.value,
						currentRouteParamId: route.params.id,
						isComponentUnmounted
					}
				);
				return;
			}
			await (instance?.proxy as any).$auth.doDefaultLogout();
		}
	}

	// The error bus is app-wide: a 401 from an unrelated background request (an upload heartbeat that raced a token
	// rotation, another entity) must not latch the shared session-lost flag. Only a 401 on this page's own entity or on the
	// account/verdict endpoints counts here; a 401 on a request the page awaits is caught by that stage's own handler.
	function isSessionScopedUrl(url: string): boolean {
		const id = entityId.value;
		return /\/(me|account)(\/|$|\?)/.test(url) || /\/projects\/access-verdicts(\/|$|\?)/.test(url) ||
			(!!id && (url.includes(`/projects/${id}`) || url.includes(`/conversations/${id}`)));
	}
	async function onApiError(error: any) {
		if (isComponentUnmounted || authSession.sessionLost) return;
		if (isMissingSession(error)) {
			if (isSessionScopedUrl(String(error?.url ?? ""))) markSessionLost();
			else logInfo("EntityDetailPage: ignored a 401 from an unrelated request", { errorClass: projectVerdictFailure(error).errorClass });
			return;
		}
		if (isComponentUnmounted || loading.value || !entity.value?.id || entityType.value !== "project") return;
		const errorUrl = error?.url ?? "";
		const isCommentEndpoint = /\/comments(\/|$|\?)/.test(errorUrl) && !errorUrl.includes("/public/");
		const isMediaOrDocEndpoint =
			(/\/files(\/|$|\?)/.test(errorUrl) || /\/snapshots(\/|$|\?)/.test(errorUrl) || /\/recordings(\/|$|\?)/.test(errorUrl)) &&
			!errorUrl.includes("/public/");
		const isConversationEndpoint = /\/conversations(\/|$|\?)/.test(errorUrl) && !errorUrl.includes("/public/");
		const isAppointmentEndpoint = /\/appointments(\/|$|\?)/.test(errorUrl) && !errorUrl.includes("/public/");
		const isSheetEndpoint = /\/sheets(\/|$|\?)/.test(errorUrl) && !errorUrl.includes("/public/");
		const isProjectRelated =
			errorUrl.includes(`/projects/${entity.value.id}`) ||
			isCommentEndpoint ||
			(isMediaOrDocEndpoint && isProjectAccessDeniedError(error)) ||
			((isConversationEndpoint || isAppointmentEndpoint || isSheetEndpoint) && isProjectAccessDeniedError(error));
		if (!isProjectRelated) {
			// Observability for message drift: a 403 from the project-write endpoints that
			// doesn't match the access-denied allowlist will skip the dialog. Route via
			// logError so it reaches Datadog (forwardConsoleLogs is "error" only — a bare
			// console.warn would be invisible to the monitoring pipeline). The message-string
			// contract guard (apps/contact-api/.../conversationProjectAccessMessageGuard)
			// covers compile-time drift; this covers runtime drift (localization layer,
			// proxy mutating the body, etc.).
			const status = error?.code ?? error?.status;
			if ((isConversationEndpoint || isAppointmentEndpoint || isSheetEndpoint) && status === 403) {
				logError("Unhandled 403 from project-write endpoint — message drift?", error, {
					url: errorUrl,
					message: error?.message
				});
			}
			return;
		}
		if (isProjectAccessDeniedError(error)) {
			// The flow owns coalescing and cleanup; permissions are exclusively verdict-derived.
			let requestSequence: number;
			const accessErrorEntityId = entityId.value;
			const accessErrorSwitchToken = entitySwitchSequence;
			const organizationId = authSession.organizationId;
			await forbiddenFlow.handle({
				isCurrent: () => requestSequence === verdictRequestSequence && organizationId === authSession.organizationId &&
					!isStaleAction(accessErrorEntityId, accessErrorSwitchToken),
				refresh: async () => {
					requestSequence = ++verdictRequestSequence;
					// Close all gates immediately while the authoritative verdict is being fetched.
					projectVerdictEnabled.value = false;
					viewOnly.value = true;
					await verdictStore.onForbidden(accessErrorEntityId);
					if (requestSequence !== verdictRequestSequence || organizationId !== authSession.organizationId ||
						isStaleAction(accessErrorEntityId, accessErrorSwitchToken)) return "settled";
					const verdict = verdictStore.access(accessErrorEntityId).verdict;
					applyVerdict(verdict, accessErrorSwitchToken, accessErrorEntityId);
					return verdict && !verdict.can.addContent ? "dialog" : "settled";
				},
				showDialog: handleAccessRevokedError,
				onError: (dialogError) => {
					// Safe diagnostic: no response message or customer identity. Never restore a snapshot.
					console.error("Failed to show access-revoked dialog", projectVerdictFailure(dialogError));
				}
			});
		}
	}

	function setAuthenticatedProjectDetails() {
		if (!entity.value) {
			console.warn(`Unable to load project: Id=${entityId.value}`);
			return;
		}
		if ((entity.value as Project).GeoAddress) {
			address.value = (entity.value as Project).GeoAddress.formattedAddress.trim();
		}

		sourceEntity.value = JSON.parse(JSON.stringify(entity.value));

		const customers = (entity.value as Project).Customers;
		if (customers && customers.length > 0) {
			newSelectedCustomer.value = customers[0];
		}
		newSelectedEntity.value = (entity.value as Project).Project;
		// if (newSelectedCustomer.value) { // TODO: Figure out if this is still needed
		// 	customerSelectItems.value.push(newSelectedCustomer.value);
		// }

		isProjectOwner.value = me.value?.user?.id === (entity.value as Project).createdBy;
	}

	// LSP-16746: cancel a scheduled call in place. Uses $confirm.open (not deleteOpen, which
	// hardcodes the agree button to "Delete") so the destructive confirm reads "Cancel Call".
	async function cancelScheduledCall() {
		if (!entity.value?.id) {
			console.warn("cancelScheduledCall: no entity loaded, aborting");
			return;
		}

		// LSP-17483: capture the id and switch ownership for THIS conversation before any awaits — the
		// confirm dialog and the cancel/refetch calls below can all outlast an in-place switch to a
		// different entity, and entity.value must not be cancelled or overwritten for the wrong conversation.
		const conversationIdToCancel = entity.value.id;
		const cancelSwitchToken = entitySwitchSequence;
		// break-it round 4: isStaleAction alone cannot distinguish two overlapping cancelScheduledCall()
		// calls for the SAME conversation (e.g. a double-click opening two confirm dialogs) — neither is a
		// route switch, so entityId/switchToken/route are identical for both. Only the LATEST invocation's
		// thisCancelAction may still match cancelActionSequence by the time its dialog resolves; an older
		// call's confirmation (even if confirmed) must not also fire the destructive API call.
		const thisCancelAction = ++cancelActionSequence;

		// The server only sends the cancellation notice while the appointment is still upcoming
		// (sendScheduledConversationCancellationNotification skips past-start calls), so don't
		// promise a notification the recipient will never get.
		const appointmentStartAt = appointmentForConfirmation.value?.startAt;
		const callAlreadyStarted = !!appointmentStartAt && moment(appointmentStartAt).isSameOrBefore(moment());
		// LSP-22023: when the invite text did not go through (an amber delivery state), the cancellation text likely won't
		// either — don't promise a notice.
		const confirmMessage = callAlreadyStarted
			? "This call's scheduled time has already passed, so no cancellation notice will be sent. This cannot be undone."
			: isAttentionState(delivery.value?.state)
				? "Their last text didn't go through, so they may not get the cancellation notice. This cannot be undone."
				: "The recipient will be notified that this call has been canceled. This cannot be undone.";

		const confirmed = await (instance?.proxy as any).$root.$confirm.open(
			"Cancel this call?",
			confirmMessage,
			false,
			"Cancel Call",
			"Keep Call",
			true
		);
		if (!confirmed) {
			return;
		}

		// break-it round 2: the confirm dialog can outlast a switch away from this conversation. Honoring a
		// stale "Cancel Call" confirmation would perform a destructive server-side action against — and show
		// a success toast seemingly about — an entity the user is no longer looking at. Abort the whole
		// action here rather than only guarding the post-cancel assignment further down.
		//
		// break-it round 4: also checks thisCancelAction, so a second (older) confirmed cancel for the SAME
		// conversation — no switch involved at all — cannot also reach the API once a newer cancel attempt
		// has started.
		//
		// break-it round 5: thisCancelAction only orders BEFORE the API call runs — it cannot stop a second
		// cancel (confirmed while an earlier one for the SAME conversation is already mid-flight, past this
		// very check) from ALSO reaching the API. cancelRequestInFlightForConversationId is a lock, not just
		// a sequence number: a second concurrent attempt for a conversation already in flight is rejected
		// even if its own thisCancelAction is the current "latest".
		if (
			thisCancelAction !== cancelActionSequence ||
			isStaleAction(conversationIdToCancel, cancelSwitchToken) ||
			cancelRequestInFlightForConversationId === conversationIdToCancel
		) {
			console.warn("cancelScheduledCall: switch superseded before the confirmation resolved — aborting the cancel", {
				conversationIdToCancel,
				cancelSwitchToken,
				thisCancelAction,
				currentCancelAction: cancelActionSequence,
				alreadyInFlightForConversationId: cancelRequestInFlightForConversationId,
				currentEntityId: entityId.value,
				currentSwitchToken: entitySwitchSequence,
				isComponentUnmounted
			});
			return;
		}

		cancelRequestInFlightForConversationId = conversationIdToCancel;
		try {
			try {
				await api.cancelScheduledCall(conversationIdToCancel);
				toast.success("The scheduled call has been canceled.");
				// LSP-20787 (22023): the cancellation notice now hides the invite's delivery state, and its rows exist once the
				// cancel returns (sendScheduledConversationCancellationNotification is awaited in the request) — look again now,
				// as onConversationRescheduled does, or the old chip and "Send via email" stay until the next load.
				void loadDelivery(conversationIdToCancel, cancelSwitchToken);
			} catch (error) {
				const errorMessage = getMessageFromError(error, "Sorry, we were unable to cancel this call. Please try again.");
				toast.error(errorMessage, { timeout: calculateTimeout(errorMessage) });
				return;
			}

			// The cancel call above already succeeded (we returned in the catch otherwise), so any
			// failure from here on is a refetch/local-state problem, not a cancellation failure — it
			// must never surface the cancel-failure error toast, which would contradict the success
			// toast already shown.
			try {
				const conversation = await api.getConversation(conversationIdToCancel);
				// LSP-17483: a switch away from this conversation while the refetch was in flight must not
				// merge the cancelled conversation's fields onto whatever entity is now current.
				if (isStaleAction(conversationIdToCancel, cancelSwitchToken)) {
					console.warn("cancelScheduledCall: switch superseded during post-cancel refetch — discarding the merge", {
						conversationIdToCancel,
						currentEntityId: entityId.value,
						currentSwitchToken: entitySwitchSequence,
						isComponentUnmounted
					});
					return;
				}
				entity.value = { ...(entity.value as any), ...conversation };
				// Cancelling flips status Scheduled → NotStarted programmatically — not a user edit. Accept it
				// into the About form's dirty baseline once the prop has flushed, or leaving the page falsely
				// prompts "You have unsaved changes" (same pattern as onConversationUpdate / LSP-16361).
				await nextTick();
				entityAboutRef.value?.acceptProgrammaticStatus?.();
			} catch (error) {
				console.warn("cancelScheduledCall: post-cancel refetch failed, applying local fallback", {
					conversationId: conversationIdToCancel,
					error
				});
				// LSP-17483: same ownership check on the fallback path — don't force a NotStarted status onto
				// an entity the user has since switched away from.
				if (isStaleAction(conversationIdToCancel, cancelSwitchToken)) {
					console.warn("cancelScheduledCall: switch superseded before the local NotStarted fallback — discarding it", {
						conversationIdToCancel,
						currentEntityId: entityId.value,
						currentSwitchToken: entitySwitchSequence,
						isComponentUnmounted
					});
					return;
				}
				// The server cancel succeeded even though the refetch failed, so force the local status to
				// NotStarted directly — otherwise isScheduled stays true, the scheduled-call card lingers, and
				// re-entering acceptProgrammaticStatus() never runs, arming a false "unsaved changes" prompt.
				entity.value = { ...(entity.value as any), status: constants.CONVERSATION_STATUS.NotStarted };
				await nextTick();
				entityAboutRef.value?.acceptProgrammaticStatus?.();
			}
		} finally {
			// break-it round 5: only clear the lock if it's still OURS — defensive against any future change
			// that might let another call claim it in between (not currently reachable, since this is a
			// single-threaded critical section with no intervening claim point, but cheap to guard).
			if (cancelRequestInFlightForConversationId === conversationIdToCancel) {
				cancelRequestInFlightForConversationId = null;
			}
		}
	}

	/**
	 * LSP-16658: the child performed the reschedule and hands up the conversation the server returned.
	 * All that is left for the page is what only the page can do — merge it and re-cut the About
	 * baseline.
	 *
	 * The merge is gated on the identity of the conversation. Keying the child by conversation id is
	 * not by itself the guarantee an earlier version of this comment claimed it was: the child's own
	 * `isUnmounted` flag is set in `onBeforeUnmount`, which runs during the switch render, so a
	 * response that lands in the window between the user navigating and Vue tearing the child down
	 * still reaches this handler — and would then merge conversation A's fields onto B and re-cut
	 * B's About baseline from them. // Bugbot review (Medium) "Reschedule merge skips identity recheck"
	 *
	 * ONE check is now enough, and that is a consequence of the fix below rather than a relaxation:
	 * merge and re-baseline are synchronous, so there is no await for an entity switch to interleave
	 * with. The second check this function used to carry existed only to cover the `nextTick` gap.
	 */
	function onConversationRescheduled(conversation: Record<string, any>): void {
		const currentEntityId = (entity.value as { id?: string } | undefined)?.id;
		if (!conversation?.id || conversation.id !== currentEntityId) {
			console.warn("EntityDetailPage: a reschedule response arrived for a conversation this page no longer shows — not merged", {
				rescheduledConversationId: conversation?.id,
				currentEntityId: currentEntityId
			});
			return;
		}
		// Merged IN PLACE, and re-baselined in the SAME tick — both halves matter, and the previous
		// version got both wrong in one line.
		//
		// A reschedule rewrites the conversation NAME server-side (the LSP-16663 date suffix moves on
		// every SYNC move), and `name` is in EntityAbout's dirty signature and watched directly. The
		// previous version replaced `entity.value` by reference and then `await nextTick()`ed before
		// re-cutting the baseline — so in that flush EntityAbout's tracked-field watcher saw the
		// programmatic rename as a user edit, emitted dirty to this page and armed a 3s autosave, and
		// the re-cut only undid it afterwards. // Bugbot review (Medium) "Reschedule dirties the About form"
		//
		// `Object.assign` rather than a spread because `entity` is a deep ref and EntityAbout holds this
		// same object as `props.entity`: mutating it makes the new name visible to the child
		// SYNCHRONOUSLY, which is what lets the baseline be re-cut from it before the watcher ever runs.
		// The watcher then computes `isFormDirty() === false` and stands autosave down instead of arming
		// it. This is the shape EntityAbout's own notes-adoption path already uses for exactly this
		// reason — it writes `props.entity.notes` in place, then re-cuts and emits clean, all in one tick.
		//
		// No name-splice is involved: the whole baseline is re-cut from the server's response, which is
		// safe only because the child refuses to start a reschedule while the form is dirty, so there is
		// no user edit here to swallow.
		//
		// Dropping the `await` also removes the gap's other hazard — the stale-identity return that used
		// to sit after it could leave the form dirty with that autosave still armed.
		Object.assign(entity.value as any, conversation);
		entityAboutRef.value?.rebaselineFromEntity?.();
		// LSP-20787 (22022): the reschedule notice is now the invite the delivery state describes, and its rows are written
		// before the response returns (afterCommit, awaited by the request transaction's commit) — so look again now.
		void loadDelivery(conversation.id, entitySwitchSequence);
	}

	/**
	 * Undo everything adopting `conversationId` claimed. Called when the conversation turns out to
	 * belong to another project, and when we could not establish ownership at all.
	 *
	 * There are three claims to give back, not one. The id is registered optimistically in both lists;
	 * consumeUploadHandoff latches the upload-in-progress state; and it claims the MODULE-scoped
	 * handoff marker, which holds a single slot for the life of the tab — keeping that would
	 * permanently stop the project that actually owns the upload from adopting it. Every undo is
	 * guarded on this handoff being the one that made the claim, so a caller that never went through
	 * the handoff (the ProjectActionFAB relay) cannot cancel someone else's.
	 */
	function retractAdoptedConversation(conversationId: string): void {
		// Cursor (Medium): `uploadHandedOffOnMount` says A handoff was adopted on this mount, not that
		// THIS conversation was it. Undoing the upload state whenever the flag is set meant a failed
		// getConversation from the ProjectActionFAB relay — a caller that never went through the
		// handoff — cancelled the in-flight upload state belonging to a different, legitimate handoff.
		// So every handoff-specific undo is gated on this id actually being the handoff, captured
		// before it is cleared. Dropping the id from the two lists is safe unconditionally: it only
		// removes the conversation this call was asked to register.
		const wasThisHandoff = handedOffUploadConversationId === conversationId;
		projectConversationIds.value = projectConversationIds.value.filter((id) => id !== conversationId);
		reportConversations.value = reportConversations.value.filter((report) => report.id !== conversationId);
		if (wasThisHandoff) {
			handedOffUploadConversationId = null;
			if (uploadHandedOffOnMount) {
				uploadHandedOffOnMount = false;
				// ls-heroku-ci: only when this handoff was the ONLY thing marking an upload in progress.
				// initializeComponent can arm pendingImages/Docs/Videos from ?pendingImages&... on the same
				// mount, and that is a DIFFERENT upload which really is in flight. finalizeActiveUpload flips
				// isUploading false, and that is the first guard flushStaleCounters checks -- so retracting a
				// foreign handoff would let the next poll zero the real upload's counters before it finished,
				// losing its progress and its post-upload document reload. Retract the handoff either way; just
				// do not declare the page idle while unrelated work is still pending.
				const otherUploadStillPending = pendingImages.value > 0 || pendingDocs.value > 0 || pendingVideos.value > 0;
				if (!otherUploadStillPending) {
					finalizeActiveUpload();
				}
			}
		}
		// Safe for any caller: clears only on an id match, so it cannot discard another handoff's slot.
		forgetUploadHandoffConsumed(conversationId);
	}

	async function conversationCreated(conversationId: string) {
		// Registered BEFORE the fetch, and retracted below if it turns out not to belong here.
		// Cursor (High): waiting for getConversation first meant a fast desktop upload could finish and
		// reload documents while the new conversation was still missing from the filter, so its photos
		// stayed off the project until a later refresh. Optimistic-then-retract is safe here precisely
		// because the ownership check below compares the conversation's own projectId against the
		// mounted one and never consults projectConversationIds — the earlier validateEntityId shape
		// did consult it, which is what forced the ordering the other way.
		if (!projectConversationIds.value.includes(conversationId)) {
			projectConversationIds.value.push(conversationId);
		}

		// ls-heroku-ci (HIGH): captured BEFORE the fetch. Read afterwards, a route switch away from the
		// project mid-flight leaves entityType "conversation", so this collapses to "" and
		// belongsToAnotherProject goes false on the empty-string guard alone -- letting a conversation
		// that really does belong to another project through, for the one case the check exists to catch.
		// The project->project-B case was already safe because both sides stay non-empty; this is the
		// project->non-project-page case, and it is the same read-after-await class as the identity
		// snapshots in the Action* components.
		const mountedEntityAtStart = { type: entityType.value, id: entityId.value };

		let conversation;
		try {
			conversation = await api.getConversation(conversationId);
		} catch (error) {
			// Deliberately retracts NOTHING. This reverses an earlier answer in this PR, so the reasoning
			// matters:
			//
			// Retracting on a failed fetch was meant to stop a stale ?uploadStarted parking a foreign id
			// in the mounted project. But a rejection is not evidence of foreignness — a transient 5xx,
			// a rate-limit or a dropped connection reads identically to a 404 here — and retracting also
			// unwound the handoff, which cleared isUploading, so flushStaleCounters() saw no pending docs
			// and never ran reloadDocumentsAfterUploadComplete(). With ?uploadStarted already stripped by
			// the router.replace, one transient blip made the just-uploaded files vanish from the project
			// until a manual refresh: LSP-15388's exact symptom, reintroduced by its own fix.
			//
			// The genuine leak is caught where the evidence actually exists — the ownership check below,
			// which only runs when the fetch SUCCEEDS and the conversation names another project. A
			// conversation that cannot be fetched at all cannot be displayed either: the server will not
			// seed it, so what remains is an inert id in a filter list. That is the smaller harm by a
			// wide margin, and it is the pre-existing behaviour rather than a new risk.
			// The wording is load-bearing: this said "retracted" while the code deliberately retracts
			// nothing, which would have had a debugger reading production logs believe the conversation was
			// removed and the handoff unwound. A diagnostic asserting an action it did not take is the same
			// defect class as a surface asserting a state it cannot prove.
			console.warn(
				"EntityDetailPage.conversationCreated: could not establish the conversation's owner; left registered and the handoff intact",
				{
					tags: { method: "conversationCreated", file: "EntityDetailPage" },
					extra: { conversationId, entityId: entityId.value, entityType: entityType.value, error }
				}
			);
			return;
		}

		// ls-heroku-ci (HIGH, verified): `?uploadStarted` is a plain router query param, so a stale
		// bookmark, a back/forward, or a copied URL can name a conversation belonging to a DIFFERENT
		// project — /projects/B?uploadStarted=<conversation-of-A> pulled A's conversation into B's
		// document and report lists.
		//
		// POSITIVE EVIDENCE only: refuse when the conversation names a project and it is not the one
		// mounted. The review suggested reusing validateEntityId, and that turned out to be the wrong
		// instrument here — its conversation-entity branch compares the conversation id to entityId,
		// which refuses every legitimate handoff on a conversation page, and its project branch treats
		// a null projectId as foreign. A conversation with no project is not another project's data,
		// and this method is also the ProjectActionFAB relay's entry point for a conversation the page
		// is about to adopt. So the check is narrowed to the actual leak: a conversation that states
		// it belongs somewhere else. KNOWN LIMIT: a project-less conversation is still adopted.
		//
		// A NOTE ON ORDER, since this changed twice. The id IS registered before the fetch (see the top
		// of this function) so a fast desktop upload's files are not missing from the filter while the
		// fetch is in flight; the refusal below retracts it. That is only safe because this check reads
		// the conversation's own projectId and never consults projectConversationIds — the earlier
		// validateEntityId shape did consult it, which would have made registering first
		// self-satisfying. A getConversation throw therefore leaves the id registered and inert, which
		// is deliberate (see the catch above).
		const conversationProjectId = typeof conversation?.projectId === "string" ? conversation.projectId : "";
		// Cursor: a snapshot ALONE was the wrong fix and broke the case the live read handled -- with
		// project A captured and the page now on project B, a conversation owned by A compares equal to
		// the snapshot and lands in B. The live read had the mirror hole (project -> non-project page
		// collapses to "" and passes on the empty-string guard). Neither value answers the question on
		// its own: what matters is whether the entity we registered against is still the one mounted.
		// If it moved at all, we cannot vouch for the conversation here -- retract and let the new
		// entity's own seed decide.
		const mountedEntityChanged = entityType.value !== mountedEntityAtStart.type || entityId.value !== mountedEntityAtStart.id;
		const mountedProjectId = entityType.value === "project" ? entityId.value : "";
		// Cursor: a moved entity is NOT the same event as a foreign conversation, and routing it through
		// the foreign path over-reached. That path also tears down handoff state -- and the handoff
		// belongs to the entity we left, which may be the conversation's rightful owner. Hopping away and
		// back during the fetch would then drop the union and hide a just-started upload from its own
		// project until a manual refresh. Undo the optimistic push and stop; leave the handoff alone.
		// handedOffConversationApplies() already scopes it by handedOffUploadEntityId, so the entity we
		// moved TO cannot re-inject it, and returning to the original restores it intact.
		if (mountedEntityChanged) {
			projectConversationIds.value = projectConversationIds.value.filter((id) => id !== conversationId);
			reportConversations.value = reportConversations.value.filter((report) => report.id !== conversationId);
			// Cursor (HIGH), the counterpart to the Medium above: leaving the handoff alone is right only
			// when the conversation belongs to the entity that ADOPTED it. Judge that against
			// mountedEntityAtStart, not the entity we happen to be on now -- the current one never adopted
			// anything. Foreign to the adopter and left standing, a stale ?uploadStarted keeps
			// handedOffUploadConversationId set and withHandedOffConversation re-injects it the moment the
			// user returns to that project.
			const adoptingProjectId = mountedEntityAtStart.type === "project" ? mountedEntityAtStart.id : "";
			const foreignToTheAdopter = !!conversationProjectId && !!adoptingProjectId && conversationProjectId !== adoptingProjectId;
			if (foreignToTheAdopter) {
				retractAdoptedConversation(conversationId);
			}
			logInfo("[Upload] conversationCreated: mounted entity changed during the fetch; not adopting here", {
				conversationId,
				entityIdAtStart: mountedEntityAtStart.id,
				entityId: entityId.value,
				entityType: entityType.value
			});
			return;
		}

		const belongsToAnotherProject = !!conversationProjectId && !!mountedProjectId && conversationProjectId !== mountedProjectId;
		if (belongsToAnotherProject) {
			// Refusing here is not enough on its own: the id has a SECOND way in. The server seeds run
			// through withHandedOffConversation()/withHandedOffReportConversation(), which re-inject
			// handedOffUploadConversationId gated only on handedOffConversationApplies() — that asks
			// whether the handoff was captured for this entity, not whether the conversation belongs to
			// it. So retract the candidate, and drop it from anything a seed (or the optimistic
			// registration above) already committed; otherwise this guard is cosmetic and the foreign
			// conversation arrives by the other route.
			retractAdoptedConversation(conversationId);
			console.warn("EntityDetailPage.conversationCreated: conversation does not belong to this entity; ignoring the handoff", {
				tags: { method: "conversationCreated", file: "EntityDetailPage" },
				extra: { conversationId, conversationProjectId, entityId: entityId.value, entityType: entityType.value }
			});
			return;
		}

		// Idempotent by state, not by trusting a one-shot caller: the ?uploadStarted handoff, the
		// ProjectActionFAB relay and two racing mounts can all reach this for the same conversation.
		// LSP-15388
		if (!projectConversationIds.value.includes(conversationId)) {
			projectConversationIds.value.push(conversationId);
		}

		// Re-checked after the await — another caller can have registered it while this was in flight.
		if (reportConversations.value.some((existing) => existing.id === conversationId)) {
			return;
		}
		reportConversations.value.push(toReportConversation({ ...conversation, id: conversationId }));
		console.log(`Add ConversationId ${conversationId} to search request filter`);
	}

	function handleUploadCounts(counts: FileUploadCounts) {
		const uploadedFileCount = counts.images + counts.documents + counts.videos;
		if (uploadedFileCount > 0) {
			const isTrailingFileUploadedAfterFinish =
				uploadFinishedSinceLastStart && Date.now() - uploadFinishedAt <= FILE_UPLOADED_AFTER_FINISH_GRACE_MS;
			if (!isUploading.value && !isTrailingFileUploadedAfterFinish) {
				markUploadInProgressWithoutTerminalEvent();
			} else {
				markUploadActivity();
			}
		}
		pendingImages.value += counts.images;
		pendingDocs.value += counts.documents;
		pendingVideos.value += counts.videos;
		if (counts.documents > 0) documentUploadedSinceLastFlush = true;
	}

	/**
	 * LSP-16659: retire document skeletons as document rows arrive in the grid — the job the Documents
	 * tab's `documentsLoaded` event used to do. Called once per COMMITTED reconcile (full, head, delta)
	 * with that reconcile's net-new document rows, so a row retires its skeleton exactly once: the next
	 * reconcile's baseline already includes it. Deferred and failed reloads never reach here. Unlike the
	 * photo/video counters there is deliberately no reset-to-0 branch — flushStaleCounters owns that, and
	 * it also has to know a document was uploaded so it can refresh the ready-PDF count.
	 */
	function retirePendingDocumentSkeletons(newDocumentRows: number, reconciler: "full" | "head" | "delta"): void {
		if (newDocumentRows <= 0 || pendingDocs.value <= 0) return;
		const before = pendingDocs.value;
		pendingDocs.value = Math.max(0, pendingDocs.value - newDocumentRows);
		logInfo("[Upload] retired pending document skeletons", {
			reconciler,
			newDocumentRows,
			pendingDocsBefore: before,
			pendingDocsAfter: pendingDocs.value,
			entityId: entityId.value,
			entityType: entityType.value
		});
	}

	// ── LSP-16659: documents in the media grid ────────────────────────────────────────────────────

	/**
	 * The lightbox's v-model. MediaLightBox cannot render a document and writes WHOLE replacement arrays
	 * back (annotation placeholders, removals, playback-heal merges), so it gets only the photo/video
	 * rows, in grid order, and every write is merged back around the document rows
	 * (mergeLightboxMediaWrite keeps the emitted order and each document's sorted slot).
	 */
	const lightboxMedia = computed<IndexableDocument[]>({
		get: () => (mediaResponse.value?.documents ?? []).filter((row) => isLightboxMediaRow(row)),
		set: (emittedMedia) => {
			if (!mediaResponse.value || !Array.isArray(emittedMedia)) {
				console.warn("EntityDetailPage.lightboxMedia: dropped a lightbox write — no media list to write into", {
					hasMediaResponse: !!mediaResponse.value,
					emittedIsArray: Array.isArray(emittedMedia),
					entityId: entityId.value,
					entityType: entityType.value
				});
				return;
			}
			const { documents, refusedDocumentRows } = mergeLightboxMediaWrite(mediaResponse.value.documents ?? [], emittedMedia);
			if (refusedDocumentRows.length) {
				console.warn(
					"EntityDetailPage.lightboxMedia: dropped document rows from a lightbox write — documents never belong to the lightbox",
					{
						refusedIds: refusedDocumentRows.map((row) => row.searchResultId ?? row.id),
						entityId: entityId.value,
						entityType: entityType.value
					}
				);
			}
			// Unchanged write: skip the replacement so the grid does not re-render (same no-flash rule as the reconcilers).
			if (documents) mediaResponse.value.documents = documents;
		}
	});

	// The document open in the PDF viewer (null when closed).
	const timelinePdfDocument = ref<IndexableDocument | null>(null);

	function closeTimelinePdfDocument(): void {
		timelinePdfDocument.value = null;
	}

	/**
	 * A document tile was clicked. PDFs open in the same viewer the Documents tab used (with tags via
	 * `fileData`); any other file opens in a new tab exactly as EntityDocuments.view did.
	 */
	function openTimelineDocument(row: IndexableDocument): void {
		const identity = row?.searchResultId ?? row?.id;
		const liveRow =
			mediaResponse.value?.documents?.find((candidate) => (candidate.searchResultId ?? candidate.id) === identity) ?? null;
		if (!liveRow || !isTimelineDocument(liveRow)) {
			// Silent-failure rule: the click would otherwise do nothing at all.
			console.warn("EntityDetailPage.openTimelineDocument: clicked document is not in the loaded media", {
				documentId: identity,
				foundEntityType: liveRow?.entityType ?? null,
				loadedDocuments: mediaResponse.value?.documents?.length ?? 0,
				entityId: entityId.value,
				entityType: entityType.value
			});
			return;
		}
		if (!liveRow.mediaUrl) {
			console.warn("EntityDetailPage.openTimelineDocument: document has no url — nothing to open", {
				documentId: identity,
				isPdf: isPdfTimelineDocument(liveRow),
				entityId: entityId.value,
				entityType: entityType.value
			});
			const missingUrlMessage = "This file isn't available to open right now. Please refresh the page and try again.";
			toast.error(missingUrlMessage, { timeout: calculateTimeout(missingUrlMessage) });
			return;
		}
		if (isPdfTimelineDocument(liveRow)) {
			// PDFViewerDialog writes saved tags back into `fileData.labels` in place; a null list would throw.
			if (!Array.isArray(liveRow.labels)) liveRow.labels = [];
			timelinePdfDocument.value = liveRow;
			return;
		}
		const openedWindow = window.open(liveRow.mediaUrl, "_blank");
		if (!openedWindow) {
			// A blocked popup and an exhausted tab limit look the same (LSP-15319), so one null-check covers both.
			console.warn("EntityDetailPage.openTimelineDocument: window.open returned null — popup blocked or tab limit reached", {
				documentId: identity,
				entityId: entityId.value,
				entityType: entityType.value
			});
			const message = "Unable to open a new window. Please check your popup blocker settings or close some tabs and try again.";
			toast.error(message, { timeout: calculateTimeout(message) });
		}
	}

	// Drag-and-drop upload onto the grid, gated exactly as the Documents tab gated it.
	const canUploadToMediaGrid = computed(() => projectAccountReady.value && isAuthenticated.value && (entityType.value !== "project" || (projectAccessResolved.value && projectVerdict.value?.can.addContent === true)) && !viewOnly.value && !freeTrialEnded.value);
	const mediaGridDropTarget = useMediaGridDropUpload({
		enabled: canUploadToMediaGrid,
		entity,
		entityType,
		onUploadCounts: handleUploadCounts,
		// Registers the new uploads session so its media shows up and its socket events pass validateEntityId.
		onConversationCreated: conversationCreated
	});

	/**
	 * The note after "Media & Documents (N)", from the APPLIED request (showTimelineResultsFor), never the
	 * live Filters UI — the LSP-20939 rule. The search term is not a filter here, as for filtersApplied.
	 */
	const timelineHeadingSuffix = computed(() => {
		if (timelineResultsShowingDocumentsOnly.value) return "· Showing documents";
		if (timelineResultsFiltersApplied.value) return "· Filtered";
		return "";
	});

	async function handleAnnotationSaved() {
		// Reload the timeline while the lightbox is still open so the optimistic
		// `temp-annotation-…` artifact gets swapped for the real one. `loadTimeline`
		// swallows its own errors via an internal try/catch and signals failure
		// through the returned boolean — gate the clear on that, otherwise the
		// `pendingReloads.timeline` backstop for closeLightBox would always be wiped even
		// when the reload silently failed and the temp artifact was never reconciled.
		pendingReloads.value.timeline = true;
		const ok = await loadTimeline(true);
		pendingReloads.value.timeline = !ok;
	}

	async function openLightBox(id: string) {
		console.log(`openLightBox ${id}`);

		await nextTick(() => {
			requestAnimationFrame(async () => {
				mediaLightBox.value?.setCurrentArtifactId(id);
			});
		});
		await nextTick(() => {
			lightboxOpen.value = true;
		});
	}

	async function closeLightBox() {
		lightboxOpen.value = false;

		// Revoke optimistic blob URLs from annotation placeholders AND remove those placeholder
		// documents from the timeline data. Revoking without removing left a broken (revoked-blob)
		// thumbnail in the timeline whenever the LSP-16940 re-injected placeholder survived to close
		// with no pending reload — handleAnnotationSaved clears pendingReloads.timeline on a successful
		// reload even when the real child has not indexed yet, so nothing else would clean it up
		// (ls-heroku review on #2591, HIGH). The parent row still shows; the real child arrives with
		// indexing on a later reload.
		if (mediaResponse.value?.documents) {
			mediaResponse.value.documents
				.filter((document) => document.id.startsWith("temp-annotation-"))
				.forEach((document) => {
					try {
						URL.revokeObjectURL(document.mediaUrl);
					} catch {
						// Ignore if already revoked
					}
				});
			mediaResponse.value.documents = mediaResponse.value.documents.filter((document) => !document.id.startsWith("temp-annotation-"));
		}

		const flags = pendingReloads.value;
		pendingReloads.value = { timeline: false, documents: false, checklists: false, checklistMedia: false };

		if (flags.timeline) {
			console.log("Pending timeline reload set, running full reload on lightbox close");
			// Timeline flush stays a (capped) FULL reload, NOT delta: this flag is also
			// set by delete (handleArtifactRemovedInLightbox) and annotation temp-swap
			// (handleAnnotationSaved) paths, which a created-based delta cannot reconcile.
			await loadTimeline(true);
		}
		if (flags.documents) {
			Promise.resolve(refreshReadyPdfDocumentCount()).catch((error) =>
				logError("Failed to reload documents on lightbox close", error)
			);
		}
		if (flags.checklists) {
			Promise.resolve(entityChecklistsRef.value?.loadProjectChecklists()).catch((error) =>
				logError("Failed to reload checklists on lightbox close", error)
			);
		}
		if (flags.checklistMedia) {
			refreshChecklistMediaIfVisible();
		}
	}

	function onClipSaved(_payload: { jobId: string; conversationId?: string }) {
		pendingVideos.value += 1;
	}

	function artifactDeleted(id: string) {
		if (!mediaResponse.value) {
			return;
		}

		const item = mediaResponse.value.documents.find((x) => (x.searchResultId ?? x.id) === id || x.id === id);
		const identities = [...new Set([id, item?.id].filter((value): value is string => !!value))];

		if (item) {
			mediaResponse.value.documents.splice(mediaResponse.value.documents.indexOf(item), 1);
			mediaResponse.value.count--;
			// LSP-16659: the per-type totals (Sparky gate, checklist media count) follow the same delete.
			decrementCountsByEntityType(mediaResponse.value, item.entityType);
			// LSP-16706 (ls-heroku-ci): re-baseline paging state after an in-place delete, the same way a
			// full reload does — otherwise lastTimelinePageRequested stays stale at the pre-delete depth and
			// pageTimeline's Math.max(..., lastTimelinePageRequested + 1) cursor permanently skips the row
			// that shifted into the now-partial last page.
			normalizeTimelinePagingAfterReplacement();
			// LSP-16706 (liveswitch-ai-pr-reviewer): an in-place delete changes the universe a prior
			// deferral was judged against, the same reason reset()/searchTimeline/onSearchRequestChange
			// already clear these — otherwise a stale pre-delete consecutive-deferral streak could
			// misjudge a genuinely transient post-delete deferral as persistent.
			consecutiveRestartsExhaustedCount = 0;
			consecutiveIncompleteCount = 0;
		}
		entityChecklistsRef.value?.filterOutMedia(identities);
		retireIncompleteRecordingLatch(identities);
		sheetListRef.value?.loadSheets();
	}

	function onDeletedMediaItems(items: SelectedMediaItem[], deleted: boolean) {
		console.log(`onDeletedMediaItems`, items, deleted);

		let anyRemoved = false;
		items.forEach((item) => {
			const selectedId = item.searchResultId ?? item.id;
			const existing = mediaResponse.value?.documents?.find((x) => (x.searchResultId ?? x.id) === selectedId);

			if (existing) {
				mediaResponse.value?.documents?.splice(mediaResponse.value?.documents?.indexOf(existing), 1);
				anyRemoved = true;

				if (mediaResponse.value?.count) {
					mediaResponse.value.count--;
				}
				// LSP-16659: the per-type totals (Sparky gate, checklist media count) follow the same delete.
				decrementCountsByEntityType(mediaResponse.value, existing.entityType);
			}
		});
		if (anyRemoved) {
			// LSP-16706 (ls-heroku-ci): same re-baseline as artifactDeleted — see its comment.
			normalizeTimelinePagingAfterReplacement();
			// LSP-16706 (liveswitch-ai-pr-reviewer): same reason as artifactDeleted — see its comment.
			consecutiveRestartsExhaustedCount = 0;
			consecutiveIncompleteCount = 0;
		}

		// LSP-16659: a deleted document may have been one of the PDFs Sparky's gate counts.
		if (deleted && items.some((item) => item.type === ArtifactType.Files)) {
			Promise.resolve(refreshReadyPdfDocumentCount()).catch((error) =>
				logError("Failed to refresh the ready PDF count after deleting documents", error)
			);
		}
		const identities = [...new Set(items.flatMap((item) => [item.id, item.legacyId]).filter((id): id is string => !!id))];
		entityChecklistsRef.value?.filterOutMedia(identities);
		// Only a confirmed delete retires the latch; `deleted` is false for flows that merely deselect.
		if (deleted) retireIncompleteRecordingLatch(identities);
		sheetListRef.value?.loadSheets();
	}

	function onSelectedMediaItems(items: SelectedMediaItem[]) {
		console.log(`onSelectedMediaItems`, items);

		if (items.length > 0) {
			// Pass selected media to checklist component only if it's expecting media selection
			if (entityChecklistsRef.value && entityChecklistsRef.value.isExpectingMediaSelection) {
				entityChecklistsRef.value.handleSelectedMedia(items);
			}
		}
	}

	async function share(artifactId: string, conversationId: string, type: ArtifactType) {
		await shareArtifact(
			artifactId,
			type,
			{
				conversation: {
					id: conversationId
				}
			},
			toast
		);
	}

	// Sheet handlers
	function handleSheetCreated(sheet: any) {
		editingSheet.value = sheet;
		sheetEditorVisible.value = true;
	}

	function handleEditSheet(sheet: any) {
		editingSheet.value = sheet;
		sheetEditorVisible.value = true;
	}

	function handleViewSheet(sheet: any) {
		editingSheet.value = sheet;
		sheetEditorVisible.value = true;
	}

	function handleSheetEditorClose() {
		sheetEditorVisible.value = false;
		editingSheet.value = null;
	}

	async function handleCreateSheetFromTimeline() {
		const allSelectedItems: SelectedMediaItem[] = timeline.value?.selectedMediaItems ?? [];
		if (allSelectedItems.length === 0) return;
		// LSP-16659: a sheet holds photos and videos only (the sheets API rejects anything else), so
		// documents in the selection are left out — and the user is told, rather than silently dropped.
		const selectedItems = allSelectedItems.filter((item) => item.type !== ArtifactType.Files);
		const droppedDocumentCount = allSelectedItems.length - selectedItems.length;
		if (droppedDocumentCount > 0) {
			const message =
				selectedItems.length === 0
					? "Sheets can only hold photos and videos. Select some to create a sheet."
					: `Sheets can only hold photos and videos, so ${droppedDocumentCount} ${droppedDocumentCount === 1 ? "document was" : "documents were"} left out.`;
			toast.info(message, { timeout: calculateTimeout(message) });
			if (selectedItems.length === 0) return;
		}

		try {
			const sheet = await api.createSheet({ projectId: entityId.value });

			const mediaItems = selectedItems.map((item: SelectedMediaItem, index: number) => ({
				id: item.id,
				type: item.type,
				sortOrder: index
			}));
			await api.updateSheetMedia(sheet.id, mediaItems);

			sheetListRef.value?.loadSheets();
			editingSheet.value = sheet;
			sheetEditorVisible.value = true;
		} catch (err) {
			if (!accessRevoked.value) {
				toast.error(couldNot("create the sheet"));
			}
		}
	}

	function handleSheetSaved() {
		sheetListRef.value?.loadSheets();
	}

	function toggleComments() {
		const idx = tabs.value.findIndex((tab) => tab.id === tabIds.comments);
		if (idx >= 0) {
			activeTab.value = idx;
		}
	}

	function checkTabsScrollability() {
		const container = document.querySelector(".entity-tabs .v-slide-group__container") as HTMLElement;
		if (!container) return;

		tabsScrollable.value = container.scrollWidth > container.clientWidth;
		checkTabsScrollPosition();
	}

	function checkTabsScrollPosition() {
		const container = document.querySelector(".entity-tabs .v-slide-group__container") as HTMLElement;
		if (!container) return;

		const scrollLeft = container.scrollLeft;
		const maxScroll = container.scrollWidth - container.clientWidth;

		// Use a small tolerance for floating point scroll positions (e.g. high DPI screens)
		tabsAtStart.value = scrollLeft <= 1;
		tabsAtEnd.value = scrollLeft >= maxScroll - 1;
	}

	// LSP-17745: pull the strip back inside its scrollable range and report whether it moved.
	//
	// The strip can end up parked PAST the end of its own content, which renders as a run of
	// empty tab-bar to the right of the last tab — the reported "black space". Two things make
	// that state reachable and, worse, permanent:
	//
	//  1. Nothing re-derives the range when the strip's own geometry changes. checkTabsScrollability
	//     runs once at mount+100ms and then only on a window `resize`; checkTabsScrollPosition runs
	//     only on `scroll`. A strip whose container widens (or whose tab list changes) while it is
	//     scrolled keeps the old, now out-of-range offset, and no code path corrects it.
	//  2. On iOS Safari a pinch / double-tap zoom moves only the VISUAL viewport, so `resize` never
	//     fires on window at all — the one listener that could have recovered is silent by design.
	//     Double-tapping the chevron (two quick taps on a `role="button"` div IS a double tap) is
	//     exactly how a user trips this without ever meaning to zoom.
	//
	// Clamping is written unconditionally rather than "only when out of range" for the maxScroll <= 0
	// case: a strip that no longer overflows must sit at 0, and reading scrollLeft back tells us
	// whether the browser actually accepted the write.
	function clampTabsScrollIntoRange(container: HTMLElement): boolean {
		if (!isScrolledPastContent(container)) return false;

		const maxScroll = maxScrollLeft(container);
		const before = container.scrollLeft;
		container.scrollLeft = maxScroll;
		// Not a silent recovery: a strip that had to be dragged back into range means some path
		// (zoom, a tab-list change, a late relayout) moved it out, and the next report of dead
		// space in this strip should not need re-deriving that from screenshots.
		console.warn("EntityDetailPage: tab strip was scrolled past its content — clamped", {
			entityId: entityId.value,
			from: before,
			to: container.scrollLeft,
			maxScroll,
			scrollWidth: container.scrollWidth,
			clientWidth: container.clientWidth,
			visualViewportScale: window.visualViewport?.scale ?? null
		});
		return true;
	}

	// LSP-17745: single re-validation entry point for every geometry change that is not a user
	// scroll — ResizeObserver on the container/content and the visual-viewport (zoom) events.
	// Coalesced into one animation frame because a tab-list change fires both observers plus a
	// layout pass in the same tick.
	function syncTabsScrollState() {
		cancelAnimationFrame(tabsGeometryFrame);
		tabsGeometryFrame = requestAnimationFrame(() => {
			const container = document.querySelector(".entity-tabs .v-slide-group__container") as HTMLElement;
			if (!container) return;

			clampTabsScrollIntoRange(container);
			checkTabsScrollability();
		});
	}

	// LSP-16975: the chevron indicators are clickable scroll controls. Clicking one nudges
	// the native slide-group scroller by roughly a viewport of tabs in the given direction
	// (-1 = toward earlier tabs, +1 = toward later tabs). The container's own scroll listener
	// then recomputes tabsAtStart/tabsAtEnd so the arrows hide once an edge is reached.
	function scrollTabs(direction: number) {
		const container = document.querySelector(".entity-tabs .v-slide-group__container") as HTMLElement;
		if (!container) return;

		// LSP-17745: scroll to an ABSOLUTE, pre-clamped target rather than handing the engine a relative
		// `scrollBy` and trusting it to stop at the content edge. Desktop engines do stop; iOS Safari
		// does not clamp an animated relative scroll reliably, so a press taken while the last tab was
		// only partly visible overshot the end and stayed there — the last tab left-aligned with dead
		// strip to its right, which is the reported "black space". nextScrollTarget keeps the 75%/120px
		// nudge but never returns a position past the last tab, so the strip's end stop no longer
		// depends on engine behaviour. See helpers/tabStripScroll.ts.
		container.scrollTo({ left: nextScrollTarget(container, direction), behavior: "smooth" });
	}

	function goBack() {
		if (!route.query.from) {
			router.push("/");
		} else {
			router.go(-1);
		}
	}

	const runSave = async (): Promise<boolean> => {
		isSaving.value = true;
		try {
			if (entityAboutRef.value) {
				// Entity About exposes Save function. It returns false when nothing was persisted
				// (invalid form / blocked reassignment / caught server error) so the unsaved-changes
				// guard can keep the user on the page instead of discarding their edits (LSP-16279).
				return (await entityAboutRef.value.save()) !== false;
			}
			// Nothing to save (About panel not mounted) — treat as success so navigation isn't blocked.
			return true;
		} finally {
			isSaving.value = false;
		}
	};

	const runReset = async () => {
		if (entityAboutRef.value) {
			// Entity About exposes Save function.
			await entityAboutRef.value.reset();
		}
	};

	async function autoUpdateReviewedIfReady(switchToken: number = entitySwitchSequence): Promise<void> {
		if (entityType.value !== "conversation") return;
		// LSP-17519 (Bugbot, MEDIUM): a project-derived reader can now open this session, but the write
		// gates are still assignment-scoped — so the PATCH this function issues comes back 404 for them.
		// The optimistic local flip is not rolled back on failure, so they would see the session marked
		// Reviewed when the server never accepted it, plus a console error on every open. Auto-review is
		// an owner action; a reader who is here through the project's access boundary must not trigger it.
		// Scoped to that population deliberately: other view-only cases (trial ended, view-only role)
		// predate this ticket and are not what this change made reachable.
		if ((entity.value as Conversation | undefined)?.accessVia === "project") {
			return;
		}
		const currentStatus = (entity.value as Conversation | undefined)?.status as string | undefined;
		if (!currentStatus) return;
		if (currentStatus.toLowerCase?.() !== "readyforreview") return;

		// Only auto-flip to Reviewed once the conversation actually has reviewable content. A files/photos
		// conversation whose uploads ALL failed reaches ReadyForReview with nothing received; flipping it to
		// Reviewed on open would hide that it is empty. LSP-16121.
		//
		// We decide from a freshly-fetched status captured locally and tied to THIS conversation — NOT the
		// shared uploadStatus ref. The ref is unsafe to trust here for two reasons (Cursor Bugbot, High):
		//   1. fetchUploadStatus() returns success even on an empty body, leaving the ref holding the
		//      PREVIOUS conversation's metrics (a stale "reviewable" could wrongly flip this one).
		//   2. Background polling / a prior conversation's in-flight fetch can overwrite the ref mid-open.
		// So: capture the conversation id, fetch directly, re-validate the id after the await, and fail
		// closed on any error / empty body / id mismatch.
		const conversationId = entityId.value;
		let status: typeof uploadStatus.value;
		try {
			status = (await api.get(`/conversations/${conversationId}/upload-status`)) ?? null;
		} catch (error) {
			console.warn(
				`Skipping auto-Reviewed: conversationId=${conversationId}, upload-status fetch failed — failing closed on stale state`,
				error
			);
			return;
		}

		// The active conversation changed while the request was in flight (open A → open B) — the response
		// is for a different conversation, so it must not drive this one's status. Uses isStaleAction (not
		// just an entityId/switchToken check) because entityId.value/entitySwitchSequence only update once
		// the route.params.id watcher's callback actually runs, which is merely SCHEDULED — not synchronous
		// — the instant the route changes. Without checking the live route.params.id too, this continuation
		// could resume in the gap between the route changing and the watcher observing it, and still PATCH
		// the outgoing conversation to Reviewed. break-it round 2.
		if (isStaleAction(conversationId, switchToken)) {
			console.warn(`Skipping auto-Reviewed: switch superseded during upload-status fetch (conversationId=${conversationId})`, {
				switchToken,
				currentEntityId: entityId.value,
				currentRouteParamId: route.params.id,
				currentSwitchToken: entitySwitchSequence,
				isComponentUnmounted
			});
			return;
		}

		// Empty body — no fresh server confirmation for this conversation. Fail closed.
		if (!status) {
			console.warn(`Skipping auto-Reviewed: conversationId=${conversationId}, upload-status returned no body — failing closed`);
			return;
		}

		// Keep the shared ref (Timeline pending counts) consistent with what we just confirmed for this id.
		uploadStatus.value = status;

		// Skip while media is still actively uploading — the set is not yet settled, so an auto-flip would
		// race the in-flight uploads.
		if (status.mediaState === "in_progress") {
			console.warn(
				`Skipping auto-Reviewed: conversationId=${conversationId}, mediaState=in_progress, ` +
					`uploaded=${status.uploaded ?? "?"}, expected=${status.expected ?? "?"} — uploads still arriving`
			);
			return;
		}

		// Opening is not reviewing: skip while a recording is incomplete. mediaState/uploaded/expected count
		// only File/Snapshot stubs, so a recording-only conversation reads "complete" there. pendingRecordings
		// covers a recording upload still arriving; hasIncompleteLatestRecording covers the partial-finalized
		// recording that aged out of pendingRecordings (reviewed-while-upload-arriving plan, V1, case C2). Not
		// hasAbandonedRecordingUpload: that banner verdict stays true after a newer retake completed.
		// Raw isComplete is not used: it is also false for abandoned photo stubs beside a playable recording,
		// which LSP-16121 allows through below. Manual Reviewed is unaffected.
		// pendingTranscoding: a fully uploaded recording isn't reviewable until it has transcoded.
		const pendingRecordings = status.pendingRecordings ?? 0;
		const pendingTranscoding = status.pendingTranscoding ?? 0;
		const hasIncompleteLatestRecording = status.hasIncompleteLatestRecording === true;
		if (pendingRecordings > 0 || pendingTranscoding > 0 || hasIncompleteLatestRecording) {
			console.warn(
				`Skipping auto-Reviewed: conversationId=${conversationId}, recording incomplete — ` +
					`pendingRecordings=${pendingRecordings}, pendingTranscoding=${pendingTranscoding}, ` +
					`hasIncompleteLatestRecording=${hasIncompleteLatestRecording}, ` +
					`hasAbandonedRecordingUpload=${status.hasAbandonedRecordingUpload}, isComplete=${status.isComplete}`
			);
			return;
		}

		// Skip the truly-empty case (the original LSP-16121 bug): stubs were expected but NOTHING reviewable
		// arrived. A PARTIAL set (some media uploaded, the rest abandoned) is intentionally allowed through —
		// the server finalizes it to ReadyForReview, and an agent should be able to review what did arrive
		// (Cursor Bugbot, LSP-16121). uploaded/expected count ONLY File/Snapshot stubs, so a conversation
		// with a playable recording but abandoned photo/file stubs reads as uploaded===0 here — recordingCount
		// (playable recordings) is checked too so that case is NOT treated as empty (Cursor Bugbot).
		// expected===0 (e.g. recording-only conversations with no photo/file stubs) is not "empty" either.
		const expected = status.expected ?? 0;
		const uploaded = status.uploaded ?? 0;
		const recordingCount = status.recordingCount ?? 0;
		if (expected > 0 && uploaded === 0 && recordingCount === 0) {
			console.warn(
				`Skipping auto-Reviewed: conversationId=${conversationId}, mediaState=${status.mediaState ?? "unknown"}, ` +
					`uploaded=${uploaded}, expected=${expected}, recordingCount=${recordingCount} — no reviewable media arrived`
			);
			return;
		}

		try {
			if (entity.value) {
				// try and set this right away so the about page can show the status change.
				entity.value.status = constants.CONVERSATION_STATUS.Reviewed;
			}
			// ls-heroku-ci: the captured `conversationId`, not the live ref. Harmless today (no await between
			// the guard and here) but it is the one line in this function that breaks the
			// capture-then-check-then-assign pattern the rest of this PR establishes — and the guard above
			// and applyEntityForSwitch below both already use the captured value.
			const updated = await (api as any).updateConversation(conversationId, {
				//...(entity.value as Conversation),
				status: constants.CONVERSATION_STATUS.Reviewed
			});
			if (
				!applyEntityForSwitch(
					conversationId,
					switchToken,
					updated as Conversation,
					`autoUpdateReviewedIfReady updateConversation(${conversationId})`
				)
			) {
				return;
			}
			sourceEntity.value = JSON.parse(JSON.stringify(updated));

			// Auto-review is a programmatic flip, not a user edit. Accept the new status into the About form's
			// dirty baseline once it has flushed, without clobbering genuine edits the user made to OTHER About
			// fields while this ran (those stay dirty and still prompt on leave) (LSP-16361).
			await nextTick();
			entityAboutRef.value?.acceptProgrammaticStatus();
		} catch (err) {
			console.error(err);
			console.error(err, {
				tags: {
					entityType: entityType.value,
					entityId: entityId.value,
					sharingId: sharingId.value
				},
				extra: {
					method: "autoUpdateReviewedIfReady",
					file: "EntityDetailPage"
				}
			});
		}
	}

	// Composition API route-leave guard mirroring Conversation/Project pages
	onBeforeRouteLeave(async () => {
		const shouldNavigate = await promptSaveBeforeLeave();
		return shouldNavigate ? undefined : false;
	});

	function toggleFilters(visible: boolean) {
		filterPersistence.setFiltersVisible(visible);
	}

	function hideLucky() {
		showScribeFAB.value = false;
	}

	function showLucky() {
		if (!fabManuallyDismissed.value) {
			showScribeFAB.value = true;
		}
	}

	function openLuckyUpgradeDialog() {
		if (!freeTrialEnded.value || !me.value?.organization) {
			return;
		}
		(instance?.proxy?.$root as any).$freeTrialEndedDialog.open(me.value.organization);
	}

	function openScribeMenu() {
		scribeFabRef.value?.openMenu();
	}

	function waitForRunReportUploadRetry(): Promise<void> {
		return new Promise((resolve) => {
			setTimeout(resolve, RUN_REPORT_UPLOAD_VERIFY_DELAY_MS);
		});
	}

	async function verifyUploadStatusForReport(context: EntityContextSnapshot): Promise<boolean> {
		const maxAttempts = RUN_REPORT_UPLOAD_VERIFY_RETRIES;
		for (let attemptIndex = 0; attemptIndex < maxAttempts; attemptIndex += 1) {
			// A torn-down page has nothing to verify FOR, and `isEntityContextCurrent` cannot see a
			// teardown (entityId/entityType/resetSequence are all unchanged by unmount). Without this
			// check the retry wait below outlives the component: on wake the loop fires another
			// upload-status GET and only then reads the "unmounted" sentinel — a request from a page
			// that no longer exists. Mirrors scheduleUploadStatusPoll's unmount bail.
			if (isComponentUnmounted || !isEntityContextCurrent(context)) return false;
			const committedSequenceBefore = uploadStatusCommittedSequence;
			const wasUploadingAtAttempt = isUploading.value;
			const uploadStatusFetchResult = await fetchUploadStatus();
			if (uploadStatusFetchResult === "unmounted") {
				// Torn-down component: nothing to verify FOR — fail fast BEFORE the verified-complete/
				// verified-pending returns below, which can otherwise still fire off a sibling's
				// committedSequence bump and let handleRunReport confirm/open on an unmounted page.
				return false;
			}
			const uploadStatusFetchSucceeded = uploadStatusFetchResult === true;
			if (!isEntityContextCurrent(context)) return false;
			const uploadStatusCommittedDuringAttempt = uploadStatusCommittedSequence > committedSequenceBefore;
			const uploadStatusFresh = uploadStatusCommittedDuringAttempt || (uploadStatusFetchSucceeded && !wasUploadingAtAttempt);
			// LSP-16677 verified-complete: fresh server state AND nothing in flight → open clean.
			if (uploadStatusFresh && !isUploading.value) return true;
			// LSP-17107 verified-pending: an upload is active AND the SERVER reports a positive pending
			// COUNT (uploadStatusPending > 0) — from this click's committed fetch or the current entity's
			// last-known uploadStatus (reset() nulls it on navigation, so it can never be another
			// entity's, and fetchUploadStatus's catch never clears it). Let handleRunReport reach the
			// existing pendingCount>0 "Run anyway / Wait" confirm instead of failing closed.
			//
			// Gate on uploadStatusPending, NOT hasPendingUploads() (`!isComplete` alone) and NOT
			// pendingMediaTotalPending (max of server + client counters), for two reasons:
			//   1. It cannot verify-open blind: a `{isComplete:false, all counts 0}` shape yields 0, so
			//      we fall through to the LSP-16677 hard-block rather than returning true while
			//      handleRunReport's confirm (which also sees 0) is skipped.
			//   2. It is SERVER-only: when client counters lead a server reporting complete (a stale
			//      client-lead state), LSP-16677 requires failing closed, not trusting the client count.
			if (isUploading.value && uploadStatusPending.value > 0) return true;
			// LSP-17772 (QA, staging 2026-08-18): a SUPERSEDED click-time fetch — it lost the race
			// against the 5s background poll (seq N dropped for in-flight seq N+1) — previously
			// landed here with isUploading false, took the not-retryable fast path, and
			// fail-closed the report on the FIRST attempt ("We could not verify pending
			// uploads"). Supersession is retryable by definition: the next attempt wins the race
			// or sees the sibling's commit via committedSequence. Real fetch FAILURES keep the
			// original LSP-16677 fast-fail.
			const shouldRetry = wasUploadingAtAttempt || isUploading.value || uploadStatusFetchResult === "superseded";
			if (!shouldRetry || attemptIndex === maxAttempts - 1) {
				// Diagnostic only on the fail-closed stop (silent-failure discipline): a blocked report
				// is the outcome worth triaging. `decisionBranch` distinguishes the pre-stub/empty block
				// (upload active but no server-confirmed count) from the not-uploading unverifiable case.
				logInfo("[Sparky] upload verification stopped", {
					reason: shouldRetry ? "exhausted-retries" : "not-retryable",
					decisionBranch: isUploading.value ? "pre-stub-or-empty-block" : "not-retryable",
					attempts: attemptIndex + 1,
					entityId: context.entityId,
					entityType: context.entityType,
					isUploading: isUploading.value,
					uploadStatusFetchSucceeded,
					// Raw tri-state (review): the whole point of the supersession fix is telling a
					// real fetch failure apart from a stale drop — the log must carry it.
					uploadStatusFetchResult,
					uploadStatusCommittedDuringAttempt,
					serverHasPendingUploads: hasPendingUploads(),
					uploadStatusPending: uploadStatusPending.value
				});
				return false;
			}
			await waitForRunReportUploadRetry();
		}
		return false;
	}

	async function handleRunReport() {
		const runReportContext = getEntityContextSnapshot();
		// LSP-15966: a 5-second polling cadence can miss the in-flight window for
		// short uploads — by the time the next tick fires, `multipart/complete`
		// has already flipped the server stub from `pending` to `complete`. To
		// guarantee the interlock sees the freshest server state at the moment
		// the user clicks, refresh uploadStatus synchronously here. Polling
		// remains as a backup so the banner can render and clear without a click.
		// LSP-16326: fetchUploadStatus returns "superseded" when a concurrent background poll /
		// UploadStarted / visibility-resume superseded this click-time request (the request-id
		// guard). That is a stale-drop, NOT a verification failure — the newer sibling already
		// committed fresh uploadStatus. Capture the committed sequence before our fetch: a true return
		// means our fetch resolved as a non-superseded success (including an empty no-content response
		// that intentionally preserves last-known state) — the sequence comparison is what proves a
		// sibling actually committed fresh data. Only fail closed when NOTHING committed (a real error:
		// 404/network with no successful concurrent poll). This is robust
		// to repeated supersession and to a stale-drop followed by an error, which a single retry
		// would not be (Cursor Bugbot).
		const uploadStatusVerified = await verifyUploadStatusForReport(runReportContext);
		if (!isEntityContextCurrent(runReportContext)) return;
		if (!uploadStatusVerified) {
			// Cursor Bugbot post-merge: if the click-time refresh fails we
			// cannot verify upload state, and stale client signals would let
			// Sparky open against a server that still has pending uploads —
			// re-introducing the exact partial-report path this PR closes.
			// Fail closed.
			console.error("[Sparky] Failed to verify pending uploads at click time; blocking report", {
				entityId: entityId.value,
				entityType: entityType.value
			});
			toast.error("We could not verify pending uploads. Please try again in a moment.");
			return;
		}
		const pendingCount = pendingMediaTotalPending.value;
		if (pendingCount > 0) {
			// LSP-15966: silent-gate diagnostic per feedback_silent_failure_diagnostics.
			// Future incidents in "the warning did not block" / "the warning blocked
			// when it should not have" become triage-able from this log alone.
			// Note: contact-admin's Datadog forwardConsoleLogs is "error"-only today
			// (tracked in LSP-15979) so this surfaces in the local browser console
			// only until that widens. The local signal is still useful for support.
			console.warn("[Sparky] Run-a-report pending-media warning", {
				entityId: entityId.value,
				entityType: entityType.value,
				pendingImages: pendingImages.value,
				pendingDocs: pendingDocs.value,
				pendingVideos: pendingVideos.value,
				documentsTotal: mediaResponse.value?.count ?? 0,
				uploadStatusPending: uploadStatusPending.value
			});
			// Count-only phrasing avoids the X-of-Y double-count risk between
			// mediaResponse.count (post-upload documents) and uploadStatus.*
			// (in-flight stubs). Singular vs plural keeps it natural.
			const noun = pendingCount === 1 ? "file" : "files";
			const verb = pendingCount === 1 ? "is" : "are";
			const message = `${pendingCount} ${noun} ${verb} not yet ready and will not be included in this report.`;
			let proceed: unknown;
			try {
				proceed = await (instance?.proxy as any).$root.$confirm.open(
					"Some files are not yet ready",
					message,
					false,
					"Run anyway",
					"Wait"
				);
			} catch (error) {
				// Fail closed — LSP-15966 exists to prevent partial reports, so if
				// the confirm plugin throws we must not silently fall through and
				// open Sparky. Surface to the user instead of swallowing.
				console.error("[Sparky] Pending-media confirm dialog failed; blocking report", {
					entityId: entityId.value,
					entityType: entityType.value,
					error
				});
				toast.error("We could not confirm pending uploads. Please try again in a moment.");
				return;
			}
			if (!isEntityContextCurrent(runReportContext)) return;
			if (!proceed) return;
		}
		if (pendingDocumentReloadPromise) {
			await pendingDocumentReloadPromise;
			if (!isEntityContextCurrent(runReportContext)) return;
		}
		showScribeFAB.value = true;
		await nextTick();
		if (!isEntityContextCurrent(runReportContext)) return;
		scribeFabRef.value?.openPopup();
	}

	function switchToReportsTab() {
		const reportsTabIndex = tabs.value.findIndex((tab) => tab.id === tabIds["sparkys-reports"]);
		if (reportsTabIndex !== -1) {
			activeTab.value = reportsTabIndex;
		}
	}

	const LEGACY_TAB_ALIASES: Record<string, string> = {
		"luckys-reports": "sparkys-reports",
		// LSP-16659: the Documents tab is gone; documents are in the media grid. The media tab on the
		// mobile layout; on desktop there is no media tab, so this falls through to the first tab.
		documents: "media"
	};

	function handleHashNavigation() {
		const hash = window.location.hash;
		if (!hash) return;

		const hashMatch = hash.match(/^#([^/]+)/);
		if (!hashMatch) return;

		let [, tabId] = hashMatch;

		const resolvedTabId = LEGACY_TAB_ALIASES[tabId];
		if (resolvedTabId) {
			tabId = resolvedTabId;
			const suffix = hash.slice(hashMatch[0].length);
			const newHash = `#${resolvedTabId}${suffix}`;
			router.replace({ hash: newHash, query: route.query });
		}

		let tabIndex = tabs.value.findIndex((value) => {
			return value.id + "" === tabId;
		});

		if (tabIndex < 0) {
			tabIndex = 0;
		}

		// Update the active tab
		updatingTabFromHash.value = true;
		activeTab.value = tabIndex;
		nextTick()
			.then(() => {
				updatingTabFromHash.value = false;
			})
			.catch((err) => {
				console.error(err);
				updatingTabFromHash.value = false;
			});

		// Check for subEntityId in the hash
		const subEntityIdMatch = hash.match(/^#([^/]+)\/(.+)$/);
		if (subEntityIdMatch) {
			subEntityId.value = subEntityIdMatch[2];
		} else {
			// Clear subEntityId if no ID in hash
			subEntityId.value = undefined;
		}
	}

	function updateUrlHash(tabIndex: number) {
		if (tabIndex < 0 || tabIndex >= tabs.value.length) return;
		const tabId = tabs.value[tabIndex].id;

		// Clear subEntityId when tab changes since IDs are tab-specific
		subEntityId.value = undefined;

		// Create hash with just the tab ID (no subEntityId)
		const newHash = `#${tabId}`;

		// Update the URL hash without triggering navigation
		window.history.replaceState(null, "", window.location.pathname + window.location.search + newHash);
	}

	function selectMediaFromProject() {
		// If on mobile, scroll to Media tab
		if (isMobileLayout.value) {
			activeTab.value = 0;
		}
		timeline.value?.toggleSelecting(null, true);
	}

	// Timeline cancelled an in-progress checklist media selection — reset the
	// ProjectChecklists attach state so "Add Checklist Item" re-appears (LSP-16140).
	// The optional chain is intentional, not a silent failure: entityChecklistsRef can be null
	// here — most commonly on mobile, before the Checklists tab has ever been the active tab
	// during this page instance. (Vuetify's VWindowItem renders its content lazily but, once a
	// tab HAS been visited, that content stays mounted and is only hidden with v-show on later
	// tab switches — it is not re-unmounted just by switching away. A real unmount after having
	// been visited would come from the entity-level `v-if` around the whole v-window, e.g. an
	// entity swap or a loading transition, not from ordinary tab navigation.) Either way this is
	// safe — isExpectingMediaSelection is component-local data() with nothing to reset if the
	// instance never existed yet or was genuinely torn down, so the editor re-appears correctly
	// once mounted. (If this attach state ever moves to a store or keep-alive, this no-op must
	// become a real reset.)
	function clearChecklistMediaSelection() {
		entityChecklistsRef.value?.clearExpectedMediaSelection();
	}

	// ProjectChecklists cancelled an active media-selection flow because the checklist it was
	// targeting got removed mid-add (LSP-17252). Mirror the user's own explicit Cancel button by
	// exiting Timeline's checklist-selection mode via its guarded cancelChecklistSelection (never
	// the raw toggleSelecting — that would unconditionally flip `selecting`, turning selection mode
	// back ON if it was already off). A null timeline.value here is not a silent failure — see
	// clearChecklistMediaSelection above for why the ref can be null (most commonly: the Media
	// tab's Timeline has never been the active tab yet in this page instance, not "switched away
	// from" — Vuetify's VWindowItem stays mounted-but-hidden once a tab has been visited).
	function cancelSelectMediaFromProject() {
		if (!timeline.value) {
			// A silent no-op still needs a breadcrumb — without it, a future "the checklist got
			// removed but the Add to Checklist button stayed up" report on mobile would require
			// re-deriving that Timeline had never mounted yet, from nothing.
			console.warn("EntityDetailPage.cancelSelectMediaFromProject: Timeline ref is null — nothing to cancel", {
				entityId: entityId.value,
				isMobileLayout: isMobileLayout.value,
				activeTab: activeTab.value
			});
			return;
		}
		timeline.value.cancelChecklistSelection();
	}
</script>

<style>
	.app-container.mobile-container-app {
		padding: 0 !important;
	}
</style>

<style scoped lang="scss">
	.md-divider-left {
		border-left: 3px solid #f3f5f9;
	}
	.entity-tab {
		font-size: 0.75rem;
		font-weight: 600;
		color: #000;
	}

	.v-tabs > .v-tabs-bar .entity-tab.v-tab:not(.v-tab--active) {
		color: #000;
	}

	body.mobile .v-tabs > .v-tabs-bar .entity-tab.v-tab:not(.v-tab--active) {
		color: #9b9ea5;
	}

	.v-tabs > .v-tabs-bar .entity-tab.v-tab {
		border-bottom: 2px solid #f3f4f5;
	}

	.entity-active-tab {
		background-color: #f3f5f9;
	}

	/* Remove Vuetify default tab overlay so background color matches design */
	.entity-tab.v-tab:before {
		background-color: transparent;
	}

	/* Ensure slider is solid black as per design */
	.entity-tabs :deep(.v-tab__slider) {
		background-color: #9b9ea5 !important;
	}

	body.mobile .entity-tab {
		background-color: #f3f5f9;
	}

	body.mobile .entity-tab.entity-active-tab {
		color: #000;
	}

	.mobile-info {
		background-color: #f3f5f9;
	}

	.hashtag {
		width: 20px;
		height: 20px;
		font-size: 1.25rem;
	}

	.icon-detail-field {
		font-size: 0.75rem;
		display: flex;
		align-items: center;
		gap: 0.25rem;
		margin-bottom: 4px;
		align-items: center;

		.v-icon {
			color: #000;
			font-size: 1rem;
		}

		.v-icon::before {
			margin-left: 0;
		}
	}

	.project-meta-action {
		border: 0;
		padding: 0;
		background: transparent;
		color: inherit;
		font: inherit;
		cursor: pointer;
	}

	.project-meta-action:focus-visible {
		outline: 2px solid rgb(var(--v-theme-primary));
		outline-offset: 2px;
	}
	.session-appointment {
		padding: 16px;
		border-bottom: 2px solid rgba(0, 0, 0, 0.14);
		box-shadow: none;
		border-radius: 0px;
	}

	/* Tab scroll indicators */
	.tabs-container-wrapper {
		position: relative;
	}

	.tab-scroll-indicator {
		position: absolute;
		top: 0;
		bottom: -2px;
		z-index: 1;
		display: flex;
		align-items: center;
		padding: 0 8px;
		/* LSP-16975: interactive scroll control — clicking scrolls the tab strip */
		cursor: pointer;
		pointer-events: auto;
	}

	.tab-scroll-indicator:focus-visible {
		outline: 2px solid rgb(var(--v-theme-primary));
		outline-offset: -2px;
	}

	.tab-scroll-left {
		left: 0;
		background: linear-gradient(90deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0.9) 80%, transparent 100%);
	}

	.tab-scroll-right {
		right: 0;
		background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.9) 20%, rgba(255, 255, 255, 1) 100%);
	}

	.tab-scroll-indicator .v-icon {
		color: #000;
		animation: pulse 1s infinite;
	}

	@keyframes pulse {
		0%,
		100% {
			opacity: 0.6;
		}
		50% {
			opacity: 1;
		}
	}

	/* LSP-17745: an earlier pass added a scoped override here to zero Vuetify's 52px tab-affix
	   reservation (VTabs.sass, `@media (max-width: 1279.98px)`). It was removed because it could
	   never apply: that rule is qualified with `.v-slide-group--horizontal`, and VSlideGroup only
	   ever emits `--vertical`, `--has-affixes` and `--is-overflowing` — `--horizontal` exists in
	   Vuetify's stylesheet but in no DOM. Vuetify's own reservation is therefore dead too (every
	   `.v-tab` here computes `margin-inline: 0px`), so it was never the source of the dead space.
	   The real cause is a scroll offset left outside the strip's range — see clampTabsScrollIntoRange. */

	/* Hide on desktop */
	@media (min-width: 960px) {
		.tab-scroll-indicator.mobile-only {
			display: none;
		}
	}
</style>
